import styled from 'styled-components';
import React, { Component, useState } from 'react';
import classNames from 'classnames';
import { formatNumber, parseNumber } from 'libphonenumber-js';
import DOMPurify from 'dompurify';
import Modal, { ModalProvider } from 'styled-react-modal';
import ClampLines from 'react-clamp-lines';
import { Container, ContainerHorizontalAlignment, LinkButton, ContainerPadding, ContainerWrap, Link, Text, TextAppearance, TextColor, BreakpointWrapper, Breakpoints, ContainerVerticalAlignment, TextAlignment, TextTag, Layout, RadioButton, ContainerGutter, Divider } from '@volkswagen-onehub/components-core';
import { Add, Bin, Hybrid, Checkmark, ChevronRight } from '@volkswagen-onehub/icons-core';
import Slider from 'react-slick';
import YouTube from 'react-youtube';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import GoogleMapReact from 'google-map-react';
var fonts = {
  headRegular: "VW Head Regular, Helvetica, sans-serif",
  headLight: "VW Head Light, Helvetica, sans-serif",
  headBold: "VW Head Bold, Helvetica, sans-serif",
  textRegular: "VW Text Regular, Helvetica, sans-serif",
  textBold: "VW Text Bold, Helvetica, sans-serif"
};
var breakpoints = {
  xs: "0",
  // reserved for bootstrap grid
  sm: "560px",
  md: "1024px",
  lg: "1280px",
  xl: "1600px",
  xxl: "1920px"
};
/**
 * Colour palette
 * Easy to use colour variables
 *
 * Note about Volkswagen D6. In the Sketch artwork file there are lots of
 * colours in its library with a naming convention of:
 * 'blue700', 'blue800', 'grey100', etc
 *
 * It makes sense to mimic that in this object.
 *
 * @usage
 * import Styled from "styled-components";
 * import { palette } from "components/theme";
 * const StyledDiv = Styled.div`
 *  color: ${palette.d6.blue800};
 * `;
 */

var palette = {
  blue: "#00287e",
  blueLight: "#00b0f0",
  black: "#0e0e0e",
  gray: "#6c787f",
  grayLight: "#e1e6ea",
  red: "#e4002c",
  white: "#ffffff",
  d6: {
    black000: "#000000",
    blue100: "#4cc7f4",
    blue200: "#00b0f0",
    // blue light
    blue300: "#008ac9",
    blue400: "#00659e",
    blue500: "#003d6f",
    blue600: "#001d4e",
    blue700: "#4c63a7",
    blue800: "#00287e",
    // blue
    blue900: "#0040c5",
    blueGradient100: "radial-gradient(78% -35%, #2970cc 36%, #012669 100%)",
    green100: "#9fd294",
    green200: "#006633",
    grey100: "#dfe4e8",
    // gray light
    grey200: "#c2cacf",
    grey300: "#96a3a8",
    grey400: "#6a767d",
    // gray
    grey500: "#3b474c",
    mint100: "#00e6e6",
    purple100: "#530796",
    red100: "#ff335c",
    red200: "#e4002c",
    // red
    red300: "#c90022",
    red400: "#a60016",
    red500: "#7d000a",
    red600: "#540003",
    yellow000: "#f9cc00",
    white000: "#ffffff"
  }
};

function _typeof2(obj) {
  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof2 = function _typeof2(obj) {
      return typeof obj;
    };
  } else {
    _typeof2 = function _typeof2(obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof2(obj);
}

function _typeof(obj) {
  if (typeof Symbol === "function" && _typeof2(Symbol.iterator) === "symbol") {
    _typeof = function _typeof(obj) {
      return _typeof2(obj);
    };
  } else {
    _typeof = function _typeof(obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : _typeof2(obj);
    };
  }

  return _typeof(obj);
}
/**
 * Static spacings
 */


var staticSpacing = [{
  s100: 4
}, {
  s150: 8
}, {
  s200: 12
}, {
  s250: 16
}, {
  s300: 20
}, {
  s350: 24
}, {
  s400: 32
}, {
  s450: 40
}, {
  s500: 44
}, {
  s550: 72
}, {
  s600: 92
}];
/**
 * Dynamic spacings
 */

var dynamicSpacing = [{
  d020: [{
    mobile: 4
  }, {
    tablet: 4
  }, {
    desktop: 8
  }]
}, {
  d100: [{
    mobile: 20
  }, {
    tablet: 24
  }, {
    desktop: 28
  }]
}, {
  d150: [{
    mobile: 28
  }, {
    tablet: 52
  }, {
    desktop: 64
  }]
}, {
  d200: [{
    mobile: 32
  }, {
    tablet: 56
  }, {
    desktop: 72
  }]
}, {
  d250: [{
    mobile: 44
  }, {
    tablet: 68
  }, {
    desktop: 84
  }]
}, {
  d300: [{
    mobile: 72
  }, {
    tablet: 96
  }, {
    desktop: 120
  }]
}, {
  d350: [{
    mobile: 100
  }, {
    tablet: 132
  }, {
    desktop: 196
  }]
}];
/**
 * Spacings
 * The D6 defined spacings.
 *
 * Format:
 * D5 name (static): px value
 * D6 name (dynamic): array of px values for different breakpoints
 */

var spacings = [].concat(staticSpacing, dynamicSpacing);
/**
 * Build up the CSS declarations. If the d6 parameter is a dynamic
 * value like 'd350' then we need media queries for the different CSS
 * declarations and if its a static value like 's200' we just need one CSS
 * declaration.
 *
 * @usage
 * import { spacings } from "components/theme";
 * const Div = styled.Div`
 *   ${spacings("padding-top", "d200")}
 * `;
 *
 * @param {string} position e.g. margin-bottom
 * @param {string} d6 e.g. 'd350' or 's200'
 * @return {string} cssDeclaration e.g. 'margin-bottom: 20px;'
 */

var D6Spacing = function D6Spacing(position, d6) {
  var cssDeclaration = "";
  var allowedProperties = ["margin", "margin-bottom", "margin-left", "margin-right", "margin-top", "padding", "padding-bottom", "padding-left", "padding-right", "padding-top"]; // don't let devs use any value here we don't want
  // `z-index` for example

  if (allowedProperties.indexOf(String(position)) > -1) {
    spacings.forEach(function (spacing) {
      var key = Object.keys(spacing);
      var values = key.map(function (e) {
        return spacing[e];
      });

      if (String(key) === String(d6)) {
        // Dynamic spacing = different sizing across breakpoints
        if (_typeof(values[0]) === "object") {
          cssDeclaration = "\n            ".concat(position, ": ").concat(values[0][0].mobile, "px;\n            @media only screen and (min-width: ").concat(breakpoints.sm, ") {\n              ").concat(position, ": ").concat(values[0][1].tablet, "px;\n            }\n            @media only screen and (min-width: ").concat(breakpoints.xl, ") {\n              ").concat(position, ": ").concat(values[0][2].desktop, "px;\n            }\n          ");
        } else {
          // Static spacing = always the same across breakpoints
          cssDeclaration = "".concat(position, ": ").concat(values[0], "px;");
        }
      }
    });
  }

  return cssDeclaration;
};
/**
 * VW Design 6 (D6)
 * Typography: static and dynamic
 *
 * 1. _Static_ - same across different breakpoints
 * e.g. with `s100 14px on mobile is always 14px on
 * tablet and on desktop
 *
 * 2. _Dynamic_ - different (but consistent) sizes across breakpoints
 * e.g. with `d100` 20px on mobile is always 24px on tablet
 * and 28px on desktop
 *
 * ## Typography usage:
 * import { typography } from "components/theme";
 * const StyledH1 = Styled.h1`
 *  {typography("header/bold300")}
 * `;
 */

/**
 * Fonts
 * The D6 defined fonts.
 *
 * Format:
 * D5 name (static): px value
 * D6 name (dynamic): array of px values for different breakpoints
 */


var fontsMap = [{
  name: "body/bold100",
  fontFamily: fonts.textBold,
  mobile: [12, 16],
  tablet: [12, 16],
  desktop: [14, 20]
}, {
  name: "body/bold150",
  fontFamily: fonts.textBold,
  mobile: [14, 20],
  tablet: [14, 20],
  desktop: [16, 24]
}, {
  name: "body/bold200",
  fontFamily: fonts.textBold,
  mobile: [16, 24],
  tablet: [16, 24],
  desktop: [20, 28]
}, {
  name: "body/regular100",
  fontFamily: fonts.textRegular,
  mobile: [12, 16],
  tablet: [12, 16],
  desktop: [14, 20],
  fontFamilyBold: fonts.textBold
}, {
  name: "body/regular150",
  fontFamily: fonts.textRegular,
  mobile: [14, 20],
  tablet: [14, 20],
  desktop: [16, 24],
  fontFamilyBold: fonts.textBold
}, {
  name: "body/regular200",
  fontFamily: fonts.textRegular,
  mobile: [16, 24],
  tablet: [16, 24],
  desktop: [20, 28],
  fontFamilyBold: fonts.textBold
}, {
  name: "header/bold200",
  fontFamily: fonts.headBold,
  mobile: [16, 24],
  tablet: [16, 24],
  desktop: [20, 28]
}, {
  name: "header/bold250",
  fontFamily: fonts.headBold,
  mobile: [20, 28],
  tablet: [20, 28],
  desktop: [24, 32]
}, {
  name: "header/bold300",
  fontFamily: fonts.headBold,
  mobile: [24, 32],
  tablet: [24, 32],
  desktop: [32, 44]
}, {
  name: "header/bold350",
  fontFamily: fonts.headBold,
  mobile: [28, 40],
  tablet: [32, 44],
  desktop: [48, 60]
}, {
  name: "header/bold400",
  fontFamily: fonts.headBold,
  mobile: [32, 44],
  tablet: [48, 60],
  desktop: [60, 72]
}, {
  name: "header/bold450",
  fontFamily: fonts.headBold,
  mobile: [44, 56],
  tablet: [60, 72],
  desktop: [76, 92]
}, {
  name: "header/bold500",
  fontFamily: fonts.headBold,
  mobile: [56, 68],
  tablet: [72, 88],
  desktop: [96, 116]
}, {
  name: "header/bold550",
  fontFamily: fonts.headBold,
  mobile: [84, 100],
  tablet: [96, 116],
  desktop: [120, 148]
}, {
  name: "header/light200",
  fontFamily: fonts.headLight,
  mobile: [16, 24],
  tablet: [16, 24],
  desktop: [20, 28],
  fontFamilyBold: fonts.headBold
}, {
  name: "header/light250",
  fontFamily: fonts.headLight,
  mobile: [20, 28],
  tablet: [20, 28],
  desktop: [24, 32],
  fontFamilyBold: fonts.headBold
}, {
  name: "header/light300",
  fontFamily: fonts.headLight,
  mobile: [24, 32],
  tablet: [24, 32],
  desktop: [32, 44],
  fontFamilyBold: fonts.headBold
}, {
  name: "header/light350",
  fontFamily: fonts.headLight,
  mobile: [28, 40],
  tablet: [32, 44],
  desktop: [48, 60],
  fontFamilyBold: fonts.headBold
}, {
  name: "header/light400",
  fontFamily: fonts.headLight,
  mobile: [32, 44],
  tablet: [48, 60],
  desktop: [60, 72],
  fontFamilyBold: fonts.headBold
}, {
  name: "header/light450",
  fontFamily: fonts.headLight,
  mobile: [44, 56],
  tablet: [60, 72],
  desktop: [76, 92],
  fontFamilyBold: fonts.headBold
}, {
  name: "header/light500",
  fontFamily: fonts.headLight,
  mobile: [56, 86],
  tablet: [72, 88],
  desktop: [96, 116],
  fontFamilyBold: fonts.headBold
}, {
  name: "header/light550",
  fontFamily: fonts.headLight,
  mobile: [84, 100],
  tablet: [96, 116],
  desktop: [120, 148],
  fontFamilyBold: fonts.headBold
}];
/**
 * Build up the CSS declarations. If the d6 parameter is a dynamic
 * value like 'd350' then we need media queries for the different CSS
 * declarations and if its a static value like 's200' we just need one CSS
 * declaration.
 *
 * @usage
 * import { typography } from "components/theme";
 * const H1 = styled.h1`
 *   ${typography("header/bold350")}}
 * `;
 * const Div = styled.Div`
 *   ${typography("body/regular200")}
 *   h1 {
 *     ${typography("header/bold350")}
 *   }
 *   h2 {
 *     ${typography("header/bold200")}
 *   }
 *   h3, h4, h5, h6 {
 *     ${typography("body/bold150")}
 *   }
 *   small {
 *     ${typography("body/regular100")}
 *   }
 * `;
 *
 * @param {string} d6 e.g. 'd350' or 's200'
 * @return {string} cssDeclaration e.g.
 * ```
 * font-size: 24px;
 * font-family: sans-serif;
 * line-height: 1.3;
 * ```
 */

var D6Typography = function D6Typography(d6) {
  var cssDeclaration = "";
  fontsMap.forEach(function (font) {
    if (String(font.name) === String(d6)) {
      // Start with the mobile rules
      cssDeclaration = "\n        font-family: ".concat(font.fontFamily, ";\n        font-size: ").concat(font.mobile[0], "px;\n        line-height: ").concat(font.mobile[1] / font.mobile[0], ";\n      "); // Add the tablet media query

      if (font.tablet) {
        cssDeclaration += "\n        @media only screen and (min-width: ".concat(breakpoints.sm, ") {\n          font-size: ").concat(font.tablet[0], "px;\n          line-height: ").concat(font.tablet[1] / font.tablet[0], ";\n        }");
      } // Add the desktop media query


      if (font.desktop) {
        cssDeclaration += "\n        @media only screen and (min-width: ".concat(breakpoints.xl, ") {\n          font-size: ").concat(font.desktop[0], "px;\n          line-height: ").concat(font.desktop[1] / font.desktop[0], ";\n        }");
      } // Finally, does this style need bolder fonts for inline strong/b tags?
      // A light heading may need a <strong> tag inside it. That's what this
      // is for e.g. `<h1>Light light <strong>bold</strong></h1>`


      if (font.fontFamilyBold) {
        cssDeclaration += "\n        strong, b {\n          font-family: ".concat(font.fontFamilyBold, ";\n        }");
      }
    }
  });
  return cssDeclaration;
};

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _templateObject3() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  ", "\n"]);

  _templateObject3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  ", "\n"]);

  _templateObject2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject() {
  var data = _taggedTemplateLiteral(["\n  ", ";\n  ", ";\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}

var section = styled.section(_templateObject(), D6Spacing("padding-top", "d150"), D6Spacing("padding-bottom", "d150")); // Headings

var h1 = styled.h1(_templateObject2(), palette.black, D6Typography("header/light450")); // Paragraphs

var p = styled.p(_templateObject3(), palette.black, D6Typography("body/regular200"));
/**
 * VW Design 6 (D6) Theme
 * Global variables, mixins, and styled components
 *
 * @usage
 * include variables and mixins in your component.style.js:
 * import { palette, breakpoints, spacings, typography } from "components/theme"
 *
 * @example
 * "components/organisms/0000_Welcome/Welcome.style.js"
 */

/**
 * Grid System - Container
 */

var Container$1 = function Container$$1(_ref) {
  var children = _ref.children,
      className = _ref.className,
      fluid = _ref.fluid,
      id = _ref.id,
      name = _ref.name;
  return React.createElement("div", {
    id: id,
    name: name,
    className: "".concat(className, " ").concat(fluid ? "ngw-container-fluid" : "ngw-container")
  }, children);
};

Container$1.defaultProps = {
  id: "",
  name: "",
  className: "",
  fluid: false
};

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var Row = function Row(_ref) {
  var children = _ref.children,
      className = _ref.className,
      props = _objectWithoutProperties(_ref, ["children", "className"]);

  return React.createElement("div", Object.assign({}, props, {
    className: "ngw-row ".concat(className)
  }), children);
};

Row.defaultProps = {
  className: ""
};
/* eslint-disable no-param-reassign */

var prefix = "ngw-col";
var breakpoints$1 = ["xl", "lg", "md", "sm", "xs"];

var Col = function Col(_ref) {
  var innerRef = _ref.innerRef,
      id = _ref.id,
      className = _ref.className,
      children = _ref.children,
      props = _objectWithoutProperties(_ref, ["innerRef", "id", "className", "children"]);

  var spans = [];
  var classes = [];
  breakpoints$1.forEach(function (breakpoint) {
    var infix = breakpoint !== "xs" ? "-".concat(breakpoint) : ""; // init offset

    var offsetProp = "".concat(breakpoint, "-offset");
    var offset = props[offsetProp];
    delete props[offsetProp]; // init col span

    var span = props[breakpoint];
    delete props[breakpoint]; // init order

    var orderProp = "".concat(breakpoint, "-order");
    var order = props[orderProp];
    delete props[orderProp]; // classes passed through object

    if (span != null && _typeof(span) === "object") {
      var _span = span;
      var _span$span = _span.span;
      span = _span$span === void 0 ? true : _span$span;
      offset = _span.offset;
      order = _span.order;
    } // col spans


    if (span != null) {
      spans.push(span === true ? "".concat(prefix).concat(infix) : "".concat(prefix).concat(infix, "-").concat(span));
    } // offset


    if (offset != null) classes.push("ngw-offset".concat(infix, "-").concat(offset)); // order

    if (order != null) classes.push("ngw-order".concat(infix, "-").concat(order));
  }); // if no spans found add a generic col class

  if (!spans.length) {
    spans.push(prefix);
  }

  return React.createElement("div", {
    id: id,
    className: classNames.apply(void 0, [className].concat(spans, classes)),
    ref: innerRef
  }, children);
};

Col.defaultProps = {
  xs: null,
  sm: null,
  md: null,
  lg: null,
  xl: null,
  innerRef: null,
  id: "",
  className: ""
};

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') {
    return;
  }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css = "/*!\n * Bootstrap Grid v4.0.0 (https://getbootstrap.com)\n * Copyright 2011-2018 The Bootstrap Authors\n * Copyright 2011-2018 Twitter, Inc.\n * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)\n */\n/*!\n * Bootstrap Grid v4.0.0 (https://getbootstrap.com)\n * Copyright 2011-2018 The Bootstrap Authors\n * Copyright 2011-2018 Twitter, Inc.\n * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)\n */\n@-ms-viewport {\n  width: device-width; }\n\nhtml {\n  box-sizing: border-box;\n  -ms-overflow-style: scrollbar; }\n\n*,\n*::before,\n*::after {\n  box-sizing: inherit; }\n\n.ngw-container {\n  width: 100%;\n  padding-right: 0;\n  padding-left: 0;\n  margin-right: auto;\n  margin-left: auto; }\n  @media (min-width: 560px) {\n    .ngw-container {\n      max-width: 1920px; } }\n  @media (min-width: 1024px) {\n    .ngw-container {\n      max-width: 1920px; } }\n  @media (min-width: 1280px) {\n    .ngw-container {\n      max-width: 1920px; } }\n  @media (min-width: 1600px) {\n    .ngw-container {\n      max-width: 1920px; } }\n\n.ngw-container-fluid {\n  width: 100%;\n  padding-right: 0;\n  padding-left: 0;\n  margin-right: auto;\n  margin-left: auto; }\n\n.ngw-row {\n  display: -webkit-box;\n  display: flex;\n  flex-wrap: wrap;\n  margin-right: 0;\n  margin-left: 0; }\n\n.ngw-no-gutters {\n  margin-right: 0;\n  margin-left: 0; }\n  .ngw-no-gutters > .ngw-col,\n  .ngw-no-gutters > [class*=\"col-\"] {\n    padding-right: 0;\n    padding-left: 0; }\n\n.ngw-col-1, .ngw-col-2, .ngw-col-3, .ngw-col-4, .ngw-col-5, .ngw-col-6, .ngw-col-7, .ngw-col-8, .ngw-col-9, .ngw-col-10, .ngw-col-11, .ngw-col-12, .ngw-col-13, .ngw-col-14, .ngw-col-15, .ngw-col-16, .ngw-col-17, .ngw-col-18, .ngw-col-19, .ngw-col-20, .ngw-col-21, .ngw-col-22, .ngw-col-23, .ngw-col-24, .ngw-col,\n.ngw-col-auto, .ngw-col-sm-1, .ngw-col-sm-2, .ngw-col-sm-3, .ngw-col-sm-4, .ngw-col-sm-5, .ngw-col-sm-6, .ngw-col-sm-7, .ngw-col-sm-8, .ngw-col-sm-9, .ngw-col-sm-10, .ngw-col-sm-11, .ngw-col-sm-12, .ngw-col-sm-13, .ngw-col-sm-14, .ngw-col-sm-15, .ngw-col-sm-16, .ngw-col-sm-17, .ngw-col-sm-18, .ngw-col-sm-19, .ngw-col-sm-20, .ngw-col-sm-21, .ngw-col-sm-22, .ngw-col-sm-23, .ngw-col-sm-24, .ngw-col-sm,\n.ngw-col-sm-auto, .ngw-col-md-1, .ngw-col-md-2, .ngw-col-md-3, .ngw-col-md-4, .ngw-col-md-5, .ngw-col-md-6, .ngw-col-md-7, .ngw-col-md-8, .ngw-col-md-9, .ngw-col-md-10, .ngw-col-md-11, .ngw-col-md-12, .ngw-col-md-13, .ngw-col-md-14, .ngw-col-md-15, .ngw-col-md-16, .ngw-col-md-17, .ngw-col-md-18, .ngw-col-md-19, .ngw-col-md-20, .ngw-col-md-21, .ngw-col-md-22, .ngw-col-md-23, .ngw-col-md-24, .ngw-col-md,\n.ngw-col-md-auto, .ngw-col-lg-1, .ngw-col-lg-2, .ngw-col-lg-3, .ngw-col-lg-4, .ngw-col-lg-5, .ngw-col-lg-6, .ngw-col-lg-7, .ngw-col-lg-8, .ngw-col-lg-9, .ngw-col-lg-10, .ngw-col-lg-11, .ngw-col-lg-12, .ngw-col-lg-13, .ngw-col-lg-14, .ngw-col-lg-15, .ngw-col-lg-16, .ngw-col-lg-17, .ngw-col-lg-18, .ngw-col-lg-19, .ngw-col-lg-20, .ngw-col-lg-21, .ngw-col-lg-22, .ngw-col-lg-23, .ngw-col-lg-24, .ngw-col-lg,\n.ngw-col-lg-auto, .ngw-col-xl-1, .ngw-col-xl-2, .ngw-col-xl-3, .ngw-col-xl-4, .ngw-col-xl-5, .ngw-col-xl-6, .ngw-col-xl-7, .ngw-col-xl-8, .ngw-col-xl-9, .ngw-col-xl-10, .ngw-col-xl-11, .ngw-col-xl-12, .ngw-col-xl-13, .ngw-col-xl-14, .ngw-col-xl-15, .ngw-col-xl-16, .ngw-col-xl-17, .ngw-col-xl-18, .ngw-col-xl-19, .ngw-col-xl-20, .ngw-col-xl-21, .ngw-col-xl-22, .ngw-col-xl-23, .ngw-col-xl-24, .ngw-col-xl,\n.ngw-col-xl-auto {\n  position: relative;\n  width: 100%;\n  min-height: 1px;\n  padding-right: 0;\n  padding-left: 0; }\n\n.ngw-col {\n  flex-basis: 0;\n  -webkit-box-flex: 1;\n          flex-grow: 1;\n  max-width: 100%; }\n\n.ngw-col-auto {\n  -webkit-box-flex: 0;\n          flex: 0 0 auto;\n  width: auto;\n  max-width: none; }\n\n.ngw-col-1 {\n  -webkit-box-flex: 0;\n          flex: 0 0 4.16667%;\n  max-width: 4.16667%; }\n\n.ngw-col-2 {\n  -webkit-box-flex: 0;\n          flex: 0 0 8.33333%;\n  max-width: 8.33333%; }\n\n.ngw-col-3 {\n  -webkit-box-flex: 0;\n          flex: 0 0 12.5%;\n  max-width: 12.5%; }\n\n.ngw-col-4 {\n  -webkit-box-flex: 0;\n          flex: 0 0 16.66667%;\n  max-width: 16.66667%; }\n\n.ngw-col-5 {\n  -webkit-box-flex: 0;\n          flex: 0 0 20.83333%;\n  max-width: 20.83333%; }\n\n.ngw-col-6 {\n  -webkit-box-flex: 0;\n          flex: 0 0 25%;\n  max-width: 25%; }\n\n.ngw-col-7 {\n  -webkit-box-flex: 0;\n          flex: 0 0 29.16667%;\n  max-width: 29.16667%; }\n\n.ngw-col-8 {\n  -webkit-box-flex: 0;\n          flex: 0 0 33.33333%;\n  max-width: 33.33333%; }\n\n.ngw-col-9 {\n  -webkit-box-flex: 0;\n          flex: 0 0 37.5%;\n  max-width: 37.5%; }\n\n.ngw-col-10 {\n  -webkit-box-flex: 0;\n          flex: 0 0 41.66667%;\n  max-width: 41.66667%; }\n\n.ngw-col-11 {\n  -webkit-box-flex: 0;\n          flex: 0 0 45.83333%;\n  max-width: 45.83333%; }\n\n.ngw-col-12 {\n  -webkit-box-flex: 0;\n          flex: 0 0 50%;\n  max-width: 50%; }\n\n.ngw-col-13 {\n  -webkit-box-flex: 0;\n          flex: 0 0 54.16667%;\n  max-width: 54.16667%; }\n\n.ngw-col-14 {\n  -webkit-box-flex: 0;\n          flex: 0 0 58.33333%;\n  max-width: 58.33333%; }\n\n.ngw-col-15 {\n  -webkit-box-flex: 0;\n          flex: 0 0 62.5%;\n  max-width: 62.5%; }\n\n.ngw-col-16 {\n  -webkit-box-flex: 0;\n          flex: 0 0 66.66667%;\n  max-width: 66.66667%; }\n\n.ngw-col-17 {\n  -webkit-box-flex: 0;\n          flex: 0 0 70.83333%;\n  max-width: 70.83333%; }\n\n.ngw-col-18 {\n  -webkit-box-flex: 0;\n          flex: 0 0 75%;\n  max-width: 75%; }\n\n.ngw-col-19 {\n  -webkit-box-flex: 0;\n          flex: 0 0 79.16667%;\n  max-width: 79.16667%; }\n\n.ngw-col-20 {\n  -webkit-box-flex: 0;\n          flex: 0 0 83.33333%;\n  max-width: 83.33333%; }\n\n.ngw-col-21 {\n  -webkit-box-flex: 0;\n          flex: 0 0 87.5%;\n  max-width: 87.5%; }\n\n.ngw-col-22 {\n  -webkit-box-flex: 0;\n          flex: 0 0 91.66667%;\n  max-width: 91.66667%; }\n\n.ngw-col-23 {\n  -webkit-box-flex: 0;\n          flex: 0 0 95.83333%;\n  max-width: 95.83333%; }\n\n.ngw-col-24 {\n  -webkit-box-flex: 0;\n          flex: 0 0 100%;\n  max-width: 100%; }\n\n.ngw-order-first {\n  -webkit-box-ordinal-group: 0;\n          order: -1; }\n\n.ngw-order-last {\n  -webkit-box-ordinal-group: 26;\n          order: 25; }\n\n.ngw-order-0 {\n  -webkit-box-ordinal-group: 1;\n          order: 0; }\n\n.ngw-order-1 {\n  -webkit-box-ordinal-group: 2;\n          order: 1; }\n\n.ngw-order-2 {\n  -webkit-box-ordinal-group: 3;\n          order: 2; }\n\n.ngw-order-3 {\n  -webkit-box-ordinal-group: 4;\n          order: 3; }\n\n.ngw-order-4 {\n  -webkit-box-ordinal-group: 5;\n          order: 4; }\n\n.ngw-order-5 {\n  -webkit-box-ordinal-group: 6;\n          order: 5; }\n\n.ngw-order-6 {\n  -webkit-box-ordinal-group: 7;\n          order: 6; }\n\n.ngw-order-7 {\n  -webkit-box-ordinal-group: 8;\n          order: 7; }\n\n.ngw-order-8 {\n  -webkit-box-ordinal-group: 9;\n          order: 8; }\n\n.ngw-order-9 {\n  -webkit-box-ordinal-group: 10;\n          order: 9; }\n\n.ngw-order-10 {\n  -webkit-box-ordinal-group: 11;\n          order: 10; }\n\n.ngw-order-11 {\n  -webkit-box-ordinal-group: 12;\n          order: 11; }\n\n.ngw-order-12 {\n  -webkit-box-ordinal-group: 13;\n          order: 12; }\n\n.ngw-order-13 {\n  -webkit-box-ordinal-group: 14;\n          order: 13; }\n\n.ngw-order-14 {\n  -webkit-box-ordinal-group: 15;\n          order: 14; }\n\n.ngw-order-15 {\n  -webkit-box-ordinal-group: 16;\n          order: 15; }\n\n.ngw-order-16 {\n  -webkit-box-ordinal-group: 17;\n          order: 16; }\n\n.ngw-order-17 {\n  -webkit-box-ordinal-group: 18;\n          order: 17; }\n\n.ngw-order-18 {\n  -webkit-box-ordinal-group: 19;\n          order: 18; }\n\n.ngw-order-19 {\n  -webkit-box-ordinal-group: 20;\n          order: 19; }\n\n.ngw-order-20 {\n  -webkit-box-ordinal-group: 21;\n          order: 20; }\n\n.ngw-order-21 {\n  -webkit-box-ordinal-group: 22;\n          order: 21; }\n\n.ngw-order-22 {\n  -webkit-box-ordinal-group: 23;\n          order: 22; }\n\n.ngw-order-23 {\n  -webkit-box-ordinal-group: 24;\n          order: 23; }\n\n.ngw-order-24 {\n  -webkit-box-ordinal-group: 25;\n          order: 24; }\n\n.ngw-offset-1 {\n  margin-left: 4.16667%; }\n\n.ngw-offset-2 {\n  margin-left: 8.33333%; }\n\n.ngw-offset-3 {\n  margin-left: 12.5%; }\n\n.ngw-offset-4 {\n  margin-left: 16.66667%; }\n\n.ngw-offset-5 {\n  margin-left: 20.83333%; }\n\n.ngw-offset-6 {\n  margin-left: 25%; }\n\n.ngw-offset-7 {\n  margin-left: 29.16667%; }\n\n.ngw-offset-8 {\n  margin-left: 33.33333%; }\n\n.ngw-offset-9 {\n  margin-left: 37.5%; }\n\n.ngw-offset-10 {\n  margin-left: 41.66667%; }\n\n.ngw-offset-11 {\n  margin-left: 45.83333%; }\n\n.ngw-offset-12 {\n  margin-left: 50%; }\n\n.ngw-offset-13 {\n  margin-left: 54.16667%; }\n\n.ngw-offset-14 {\n  margin-left: 58.33333%; }\n\n.ngw-offset-15 {\n  margin-left: 62.5%; }\n\n.ngw-offset-16 {\n  margin-left: 66.66667%; }\n\n.ngw-offset-17 {\n  margin-left: 70.83333%; }\n\n.ngw-offset-18 {\n  margin-left: 75%; }\n\n.ngw-offset-19 {\n  margin-left: 79.16667%; }\n\n.ngw-offset-20 {\n  margin-left: 83.33333%; }\n\n.ngw-offset-21 {\n  margin-left: 87.5%; }\n\n.ngw-offset-22 {\n  margin-left: 91.66667%; }\n\n.ngw-offset-23 {\n  margin-left: 95.83333%; }\n\n@media (min-width: 560px) {\n  .ngw-col-sm {\n    flex-basis: 0;\n    -webkit-box-flex: 1;\n            flex-grow: 1;\n    max-width: 100%; }\n  .ngw-col-sm-auto {\n    -webkit-box-flex: 0;\n            flex: 0 0 auto;\n    width: auto;\n    max-width: none; }\n  .ngw-col-sm-1 {\n    -webkit-box-flex: 0;\n            flex: 0 0 4.16667%;\n    max-width: 4.16667%; }\n  .ngw-col-sm-2 {\n    -webkit-box-flex: 0;\n            flex: 0 0 8.33333%;\n    max-width: 8.33333%; }\n  .ngw-col-sm-3 {\n    -webkit-box-flex: 0;\n            flex: 0 0 12.5%;\n    max-width: 12.5%; }\n  .ngw-col-sm-4 {\n    -webkit-box-flex: 0;\n            flex: 0 0 16.66667%;\n    max-width: 16.66667%; }\n  .ngw-col-sm-5 {\n    -webkit-box-flex: 0;\n            flex: 0 0 20.83333%;\n    max-width: 20.83333%; }\n  .ngw-col-sm-6 {\n    -webkit-box-flex: 0;\n            flex: 0 0 25%;\n    max-width: 25%; }\n  .ngw-col-sm-7 {\n    -webkit-box-flex: 0;\n            flex: 0 0 29.16667%;\n    max-width: 29.16667%; }\n  .ngw-col-sm-8 {\n    -webkit-box-flex: 0;\n            flex: 0 0 33.33333%;\n    max-width: 33.33333%; }\n  .ngw-col-sm-9 {\n    -webkit-box-flex: 0;\n            flex: 0 0 37.5%;\n    max-width: 37.5%; }\n  .ngw-col-sm-10 {\n    -webkit-box-flex: 0;\n            flex: 0 0 41.66667%;\n    max-width: 41.66667%; }\n  .ngw-col-sm-11 {\n    -webkit-box-flex: 0;\n            flex: 0 0 45.83333%;\n    max-width: 45.83333%; }\n  .ngw-col-sm-12 {\n    -webkit-box-flex: 0;\n            flex: 0 0 50%;\n    max-width: 50%; }\n  .ngw-col-sm-13 {\n    -webkit-box-flex: 0;\n            flex: 0 0 54.16667%;\n    max-width: 54.16667%; }\n  .ngw-col-sm-14 {\n    -webkit-box-flex: 0;\n            flex: 0 0 58.33333%;\n    max-width: 58.33333%; }\n  .ngw-col-sm-15 {\n    -webkit-box-flex: 0;\n            flex: 0 0 62.5%;\n    max-width: 62.5%; }\n  .ngw-col-sm-16 {\n    -webkit-box-flex: 0;\n            flex: 0 0 66.66667%;\n    max-width: 66.66667%; }\n  .ngw-col-sm-17 {\n    -webkit-box-flex: 0;\n            flex: 0 0 70.83333%;\n    max-width: 70.83333%; }\n  .ngw-col-sm-18 {\n    -webkit-box-flex: 0;\n            flex: 0 0 75%;\n    max-width: 75%; }\n  .ngw-col-sm-19 {\n    -webkit-box-flex: 0;\n            flex: 0 0 79.16667%;\n    max-width: 79.16667%; }\n  .ngw-col-sm-20 {\n    -webkit-box-flex: 0;\n            flex: 0 0 83.33333%;\n    max-width: 83.33333%; }\n  .ngw-col-sm-21 {\n    -webkit-box-flex: 0;\n            flex: 0 0 87.5%;\n    max-width: 87.5%; }\n  .ngw-col-sm-22 {\n    -webkit-box-flex: 0;\n            flex: 0 0 91.66667%;\n    max-width: 91.66667%; }\n  .ngw-col-sm-23 {\n    -webkit-box-flex: 0;\n            flex: 0 0 95.83333%;\n    max-width: 95.83333%; }\n  .ngw-col-sm-24 {\n    -webkit-box-flex: 0;\n            flex: 0 0 100%;\n    max-width: 100%; }\n  .ngw-order-sm-first {\n    -webkit-box-ordinal-group: 0;\n            order: -1; }\n  .ngw-order-sm-last {\n    -webkit-box-ordinal-group: 26;\n            order: 25; }\n  .ngw-order-sm-0 {\n    -webkit-box-ordinal-group: 1;\n            order: 0; }\n  .ngw-order-sm-1 {\n    -webkit-box-ordinal-group: 2;\n            order: 1; }\n  .ngw-order-sm-2 {\n    -webkit-box-ordinal-group: 3;\n            order: 2; }\n  .ngw-order-sm-3 {\n    -webkit-box-ordinal-group: 4;\n            order: 3; }\n  .ngw-order-sm-4 {\n    -webkit-box-ordinal-group: 5;\n            order: 4; }\n  .ngw-order-sm-5 {\n    -webkit-box-ordinal-group: 6;\n            order: 5; }\n  .ngw-order-sm-6 {\n    -webkit-box-ordinal-group: 7;\n            order: 6; }\n  .ngw-order-sm-7 {\n    -webkit-box-ordinal-group: 8;\n            order: 7; }\n  .ngw-order-sm-8 {\n    -webkit-box-ordinal-group: 9;\n            order: 8; }\n  .ngw-order-sm-9 {\n    -webkit-box-ordinal-group: 10;\n            order: 9; }\n  .ngw-order-sm-10 {\n    -webkit-box-ordinal-group: 11;\n            order: 10; }\n  .ngw-order-sm-11 {\n    -webkit-box-ordinal-group: 12;\n            order: 11; }\n  .ngw-order-sm-12 {\n    -webkit-box-ordinal-group: 13;\n            order: 12; }\n  .ngw-order-sm-13 {\n    -webkit-box-ordinal-group: 14;\n            order: 13; }\n  .ngw-order-sm-14 {\n    -webkit-box-ordinal-group: 15;\n            order: 14; }\n  .ngw-order-sm-15 {\n    -webkit-box-ordinal-group: 16;\n            order: 15; }\n  .ngw-order-sm-16 {\n    -webkit-box-ordinal-group: 17;\n            order: 16; }\n  .ngw-order-sm-17 {\n    -webkit-box-ordinal-group: 18;\n            order: 17; }\n  .ngw-order-sm-18 {\n    -webkit-box-ordinal-group: 19;\n            order: 18; }\n  .ngw-order-sm-19 {\n    -webkit-box-ordinal-group: 20;\n            order: 19; }\n  .ngw-order-sm-20 {\n    -webkit-box-ordinal-group: 21;\n            order: 20; }\n  .ngw-order-sm-21 {\n    -webkit-box-ordinal-group: 22;\n            order: 21; }\n  .ngw-order-sm-22 {\n    -webkit-box-ordinal-group: 23;\n            order: 22; }\n  .ngw-order-sm-23 {\n    -webkit-box-ordinal-group: 24;\n            order: 23; }\n  .ngw-order-sm-24 {\n    -webkit-box-ordinal-group: 25;\n            order: 24; }\n  .ngw-offset-sm-0 {\n    margin-left: 0; }\n  .ngw-offset-sm-1 {\n    margin-left: 4.16667%; }\n  .ngw-offset-sm-2 {\n    margin-left: 8.33333%; }\n  .ngw-offset-sm-3 {\n    margin-left: 12.5%; }\n  .ngw-offset-sm-4 {\n    margin-left: 16.66667%; }\n  .ngw-offset-sm-5 {\n    margin-left: 20.83333%; }\n  .ngw-offset-sm-6 {\n    margin-left: 25%; }\n  .ngw-offset-sm-7 {\n    margin-left: 29.16667%; }\n  .ngw-offset-sm-8 {\n    margin-left: 33.33333%; }\n  .ngw-offset-sm-9 {\n    margin-left: 37.5%; }\n  .ngw-offset-sm-10 {\n    margin-left: 41.66667%; }\n  .ngw-offset-sm-11 {\n    margin-left: 45.83333%; }\n  .ngw-offset-sm-12 {\n    margin-left: 50%; }\n  .ngw-offset-sm-13 {\n    margin-left: 54.16667%; }\n  .ngw-offset-sm-14 {\n    margin-left: 58.33333%; }\n  .ngw-offset-sm-15 {\n    margin-left: 62.5%; }\n  .ngw-offset-sm-16 {\n    margin-left: 66.66667%; }\n  .ngw-offset-sm-17 {\n    margin-left: 70.83333%; }\n  .ngw-offset-sm-18 {\n    margin-left: 75%; }\n  .ngw-offset-sm-19 {\n    margin-left: 79.16667%; }\n  .ngw-offset-sm-20 {\n    margin-left: 83.33333%; }\n  .ngw-offset-sm-21 {\n    margin-left: 87.5%; }\n  .ngw-offset-sm-22 {\n    margin-left: 91.66667%; }\n  .ngw-offset-sm-23 {\n    margin-left: 95.83333%; } }\n\n@media (min-width: 1024px) {\n  .ngw-col-md {\n    flex-basis: 0;\n    -webkit-box-flex: 1;\n            flex-grow: 1;\n    max-width: 100%; }\n  .ngw-col-md-auto {\n    -webkit-box-flex: 0;\n            flex: 0 0 auto;\n    width: auto;\n    max-width: none; }\n  .ngw-col-md-1 {\n    -webkit-box-flex: 0;\n            flex: 0 0 4.16667%;\n    max-width: 4.16667%; }\n  .ngw-col-md-2 {\n    -webkit-box-flex: 0;\n            flex: 0 0 8.33333%;\n    max-width: 8.33333%; }\n  .ngw-col-md-3 {\n    -webkit-box-flex: 0;\n            flex: 0 0 12.5%;\n    max-width: 12.5%; }\n  .ngw-col-md-4 {\n    -webkit-box-flex: 0;\n            flex: 0 0 16.66667%;\n    max-width: 16.66667%; }\n  .ngw-col-md-5 {\n    -webkit-box-flex: 0;\n            flex: 0 0 20.83333%;\n    max-width: 20.83333%; }\n  .ngw-col-md-6 {\n    -webkit-box-flex: 0;\n            flex: 0 0 25%;\n    max-width: 25%; }\n  .ngw-col-md-7 {\n    -webkit-box-flex: 0;\n            flex: 0 0 29.16667%;\n    max-width: 29.16667%; }\n  .ngw-col-md-8 {\n    -webkit-box-flex: 0;\n            flex: 0 0 33.33333%;\n    max-width: 33.33333%; }\n  .ngw-col-md-9 {\n    -webkit-box-flex: 0;\n            flex: 0 0 37.5%;\n    max-width: 37.5%; }\n  .ngw-col-md-10 {\n    -webkit-box-flex: 0;\n            flex: 0 0 41.66667%;\n    max-width: 41.66667%; }\n  .ngw-col-md-11 {\n    -webkit-box-flex: 0;\n            flex: 0 0 45.83333%;\n    max-width: 45.83333%; }\n  .ngw-col-md-12 {\n    -webkit-box-flex: 0;\n            flex: 0 0 50%;\n    max-width: 50%; }\n  .ngw-col-md-13 {\n    -webkit-box-flex: 0;\n            flex: 0 0 54.16667%;\n    max-width: 54.16667%; }\n  .ngw-col-md-14 {\n    -webkit-box-flex: 0;\n            flex: 0 0 58.33333%;\n    max-width: 58.33333%; }\n  .ngw-col-md-15 {\n    -webkit-box-flex: 0;\n            flex: 0 0 62.5%;\n    max-width: 62.5%; }\n  .ngw-col-md-16 {\n    -webkit-box-flex: 0;\n            flex: 0 0 66.66667%;\n    max-width: 66.66667%; }\n  .ngw-col-md-17 {\n    -webkit-box-flex: 0;\n            flex: 0 0 70.83333%;\n    max-width: 70.83333%; }\n  .ngw-col-md-18 {\n    -webkit-box-flex: 0;\n            flex: 0 0 75%;\n    max-width: 75%; }\n  .ngw-col-md-19 {\n    -webkit-box-flex: 0;\n            flex: 0 0 79.16667%;\n    max-width: 79.16667%; }\n  .ngw-col-md-20 {\n    -webkit-box-flex: 0;\n            flex: 0 0 83.33333%;\n    max-width: 83.33333%; }\n  .ngw-col-md-21 {\n    -webkit-box-flex: 0;\n            flex: 0 0 87.5%;\n    max-width: 87.5%; }\n  .ngw-col-md-22 {\n    -webkit-box-flex: 0;\n            flex: 0 0 91.66667%;\n    max-width: 91.66667%; }\n  .ngw-col-md-23 {\n    -webkit-box-flex: 0;\n            flex: 0 0 95.83333%;\n    max-width: 95.83333%; }\n  .ngw-col-md-24 {\n    -webkit-box-flex: 0;\n            flex: 0 0 100%;\n    max-width: 100%; }\n  .ngw-order-md-first {\n    -webkit-box-ordinal-group: 0;\n            order: -1; }\n  .ngw-order-md-last {\n    -webkit-box-ordinal-group: 26;\n            order: 25; }\n  .ngw-order-md-0 {\n    -webkit-box-ordinal-group: 1;\n            order: 0; }\n  .ngw-order-md-1 {\n    -webkit-box-ordinal-group: 2;\n            order: 1; }\n  .ngw-order-md-2 {\n    -webkit-box-ordinal-group: 3;\n            order: 2; }\n  .ngw-order-md-3 {\n    -webkit-box-ordinal-group: 4;\n            order: 3; }\n  .ngw-order-md-4 {\n    -webkit-box-ordinal-group: 5;\n            order: 4; }\n  .ngw-order-md-5 {\n    -webkit-box-ordinal-group: 6;\n            order: 5; }\n  .ngw-order-md-6 {\n    -webkit-box-ordinal-group: 7;\n            order: 6; }\n  .ngw-order-md-7 {\n    -webkit-box-ordinal-group: 8;\n            order: 7; }\n  .ngw-order-md-8 {\n    -webkit-box-ordinal-group: 9;\n            order: 8; }\n  .ngw-order-md-9 {\n    -webkit-box-ordinal-group: 10;\n            order: 9; }\n  .ngw-order-md-10 {\n    -webkit-box-ordinal-group: 11;\n            order: 10; }\n  .ngw-order-md-11 {\n    -webkit-box-ordinal-group: 12;\n            order: 11; }\n  .ngw-order-md-12 {\n    -webkit-box-ordinal-group: 13;\n            order: 12; }\n  .ngw-order-md-13 {\n    -webkit-box-ordinal-group: 14;\n            order: 13; }\n  .ngw-order-md-14 {\n    -webkit-box-ordinal-group: 15;\n            order: 14; }\n  .ngw-order-md-15 {\n    -webkit-box-ordinal-group: 16;\n            order: 15; }\n  .ngw-order-md-16 {\n    -webkit-box-ordinal-group: 17;\n            order: 16; }\n  .ngw-order-md-17 {\n    -webkit-box-ordinal-group: 18;\n            order: 17; }\n  .ngw-order-md-18 {\n    -webkit-box-ordinal-group: 19;\n            order: 18; }\n  .ngw-order-md-19 {\n    -webkit-box-ordinal-group: 20;\n            order: 19; }\n  .ngw-order-md-20 {\n    -webkit-box-ordinal-group: 21;\n            order: 20; }\n  .ngw-order-md-21 {\n    -webkit-box-ordinal-group: 22;\n            order: 21; }\n  .ngw-order-md-22 {\n    -webkit-box-ordinal-group: 23;\n            order: 22; }\n  .ngw-order-md-23 {\n    -webkit-box-ordinal-group: 24;\n            order: 23; }\n  .ngw-order-md-24 {\n    -webkit-box-ordinal-group: 25;\n            order: 24; }\n  .ngw-offset-md-0 {\n    margin-left: 0; }\n  .ngw-offset-md-1 {\n    margin-left: 4.16667%; }\n  .ngw-offset-md-2 {\n    margin-left: 8.33333%; }\n  .ngw-offset-md-3 {\n    margin-left: 12.5%; }\n  .ngw-offset-md-4 {\n    margin-left: 16.66667%; }\n  .ngw-offset-md-5 {\n    margin-left: 20.83333%; }\n  .ngw-offset-md-6 {\n    margin-left: 25%; }\n  .ngw-offset-md-7 {\n    margin-left: 29.16667%; }\n  .ngw-offset-md-8 {\n    margin-left: 33.33333%; }\n  .ngw-offset-md-9 {\n    margin-left: 37.5%; }\n  .ngw-offset-md-10 {\n    margin-left: 41.66667%; }\n  .ngw-offset-md-11 {\n    margin-left: 45.83333%; }\n  .ngw-offset-md-12 {\n    margin-left: 50%; }\n  .ngw-offset-md-13 {\n    margin-left: 54.16667%; }\n  .ngw-offset-md-14 {\n    margin-left: 58.33333%; }\n  .ngw-offset-md-15 {\n    margin-left: 62.5%; }\n  .ngw-offset-md-16 {\n    margin-left: 66.66667%; }\n  .ngw-offset-md-17 {\n    margin-left: 70.83333%; }\n  .ngw-offset-md-18 {\n    margin-left: 75%; }\n  .ngw-offset-md-19 {\n    margin-left: 79.16667%; }\n  .ngw-offset-md-20 {\n    margin-left: 83.33333%; }\n  .ngw-offset-md-21 {\n    margin-left: 87.5%; }\n  .ngw-offset-md-22 {\n    margin-left: 91.66667%; }\n  .ngw-offset-md-23 {\n    margin-left: 95.83333%; } }\n\n@media (min-width: 1280px) {\n  .ngw-col-lg {\n    flex-basis: 0;\n    -webkit-box-flex: 1;\n            flex-grow: 1;\n    max-width: 100%; }\n  .ngw-col-lg-auto {\n    -webkit-box-flex: 0;\n            flex: 0 0 auto;\n    width: auto;\n    max-width: none; }\n  .ngw-col-lg-1 {\n    -webkit-box-flex: 0;\n            flex: 0 0 4.16667%;\n    max-width: 4.16667%; }\n  .ngw-col-lg-2 {\n    -webkit-box-flex: 0;\n            flex: 0 0 8.33333%;\n    max-width: 8.33333%; }\n  .ngw-col-lg-3 {\n    -webkit-box-flex: 0;\n            flex: 0 0 12.5%;\n    max-width: 12.5%; }\n  .ngw-col-lg-4 {\n    -webkit-box-flex: 0;\n            flex: 0 0 16.66667%;\n    max-width: 16.66667%; }\n  .ngw-col-lg-5 {\n    -webkit-box-flex: 0;\n            flex: 0 0 20.83333%;\n    max-width: 20.83333%; }\n  .ngw-col-lg-6 {\n    -webkit-box-flex: 0;\n            flex: 0 0 25%;\n    max-width: 25%; }\n  .ngw-col-lg-7 {\n    -webkit-box-flex: 0;\n            flex: 0 0 29.16667%;\n    max-width: 29.16667%; }\n  .ngw-col-lg-8 {\n    -webkit-box-flex: 0;\n            flex: 0 0 33.33333%;\n    max-width: 33.33333%; }\n  .ngw-col-lg-9 {\n    -webkit-box-flex: 0;\n            flex: 0 0 37.5%;\n    max-width: 37.5%; }\n  .ngw-col-lg-10 {\n    -webkit-box-flex: 0;\n            flex: 0 0 41.66667%;\n    max-width: 41.66667%; }\n  .ngw-col-lg-11 {\n    -webkit-box-flex: 0;\n            flex: 0 0 45.83333%;\n    max-width: 45.83333%; }\n  .ngw-col-lg-12 {\n    -webkit-box-flex: 0;\n            flex: 0 0 50%;\n    max-width: 50%; }\n  .ngw-col-lg-13 {\n    -webkit-box-flex: 0;\n            flex: 0 0 54.16667%;\n    max-width: 54.16667%; }\n  .ngw-col-lg-14 {\n    -webkit-box-flex: 0;\n            flex: 0 0 58.33333%;\n    max-width: 58.33333%; }\n  .ngw-col-lg-15 {\n    -webkit-box-flex: 0;\n            flex: 0 0 62.5%;\n    max-width: 62.5%; }\n  .ngw-col-lg-16 {\n    -webkit-box-flex: 0;\n            flex: 0 0 66.66667%;\n    max-width: 66.66667%; }\n  .ngw-col-lg-17 {\n    -webkit-box-flex: 0;\n            flex: 0 0 70.83333%;\n    max-width: 70.83333%; }\n  .ngw-col-lg-18 {\n    -webkit-box-flex: 0;\n            flex: 0 0 75%;\n    max-width: 75%; }\n  .ngw-col-lg-19 {\n    -webkit-box-flex: 0;\n            flex: 0 0 79.16667%;\n    max-width: 79.16667%; }\n  .ngw-col-lg-20 {\n    -webkit-box-flex: 0;\n            flex: 0 0 83.33333%;\n    max-width: 83.33333%; }\n  .ngw-col-lg-21 {\n    -webkit-box-flex: 0;\n            flex: 0 0 87.5%;\n    max-width: 87.5%; }\n  .ngw-col-lg-22 {\n    -webkit-box-flex: 0;\n            flex: 0 0 91.66667%;\n    max-width: 91.66667%; }\n  .ngw-col-lg-23 {\n    -webkit-box-flex: 0;\n            flex: 0 0 95.83333%;\n    max-width: 95.83333%; }\n  .ngw-col-lg-24 {\n    -webkit-box-flex: 0;\n            flex: 0 0 100%;\n    max-width: 100%; }\n  .ngw-order-lg-first {\n    -webkit-box-ordinal-group: 0;\n            order: -1; }\n  .ngw-order-lg-last {\n    -webkit-box-ordinal-group: 26;\n            order: 25; }\n  .ngw-order-lg-0 {\n    -webkit-box-ordinal-group: 1;\n            order: 0; }\n  .ngw-order-lg-1 {\n    -webkit-box-ordinal-group: 2;\n            order: 1; }\n  .ngw-order-lg-2 {\n    -webkit-box-ordinal-group: 3;\n            order: 2; }\n  .ngw-order-lg-3 {\n    -webkit-box-ordinal-group: 4;\n            order: 3; }\n  .ngw-order-lg-4 {\n    -webkit-box-ordinal-group: 5;\n            order: 4; }\n  .ngw-order-lg-5 {\n    -webkit-box-ordinal-group: 6;\n            order: 5; }\n  .ngw-order-lg-6 {\n    -webkit-box-ordinal-group: 7;\n            order: 6; }\n  .ngw-order-lg-7 {\n    -webkit-box-ordinal-group: 8;\n            order: 7; }\n  .ngw-order-lg-8 {\n    -webkit-box-ordinal-group: 9;\n            order: 8; }\n  .ngw-order-lg-9 {\n    -webkit-box-ordinal-group: 10;\n            order: 9; }\n  .ngw-order-lg-10 {\n    -webkit-box-ordinal-group: 11;\n            order: 10; }\n  .ngw-order-lg-11 {\n    -webkit-box-ordinal-group: 12;\n            order: 11; }\n  .ngw-order-lg-12 {\n    -webkit-box-ordinal-group: 13;\n            order: 12; }\n  .ngw-order-lg-13 {\n    -webkit-box-ordinal-group: 14;\n            order: 13; }\n  .ngw-order-lg-14 {\n    -webkit-box-ordinal-group: 15;\n            order: 14; }\n  .ngw-order-lg-15 {\n    -webkit-box-ordinal-group: 16;\n            order: 15; }\n  .ngw-order-lg-16 {\n    -webkit-box-ordinal-group: 17;\n            order: 16; }\n  .ngw-order-lg-17 {\n    -webkit-box-ordinal-group: 18;\n            order: 17; }\n  .ngw-order-lg-18 {\n    -webkit-box-ordinal-group: 19;\n            order: 18; }\n  .ngw-order-lg-19 {\n    -webkit-box-ordinal-group: 20;\n            order: 19; }\n  .ngw-order-lg-20 {\n    -webkit-box-ordinal-group: 21;\n            order: 20; }\n  .ngw-order-lg-21 {\n    -webkit-box-ordinal-group: 22;\n            order: 21; }\n  .ngw-order-lg-22 {\n    -webkit-box-ordinal-group: 23;\n            order: 22; }\n  .ngw-order-lg-23 {\n    -webkit-box-ordinal-group: 24;\n            order: 23; }\n  .ngw-order-lg-24 {\n    -webkit-box-ordinal-group: 25;\n            order: 24; }\n  .ngw-offset-lg-0 {\n    margin-left: 0; }\n  .ngw-offset-lg-1 {\n    margin-left: 4.16667%; }\n  .ngw-offset-lg-2 {\n    margin-left: 8.33333%; }\n  .ngw-offset-lg-3 {\n    margin-left: 12.5%; }\n  .ngw-offset-lg-4 {\n    margin-left: 16.66667%; }\n  .ngw-offset-lg-5 {\n    margin-left: 20.83333%; }\n  .ngw-offset-lg-6 {\n    margin-left: 25%; }\n  .ngw-offset-lg-7 {\n    margin-left: 29.16667%; }\n  .ngw-offset-lg-8 {\n    margin-left: 33.33333%; }\n  .ngw-offset-lg-9 {\n    margin-left: 37.5%; }\n  .ngw-offset-lg-10 {\n    margin-left: 41.66667%; }\n  .ngw-offset-lg-11 {\n    margin-left: 45.83333%; }\n  .ngw-offset-lg-12 {\n    margin-left: 50%; }\n  .ngw-offset-lg-13 {\n    margin-left: 54.16667%; }\n  .ngw-offset-lg-14 {\n    margin-left: 58.33333%; }\n  .ngw-offset-lg-15 {\n    margin-left: 62.5%; }\n  .ngw-offset-lg-16 {\n    margin-left: 66.66667%; }\n  .ngw-offset-lg-17 {\n    margin-left: 70.83333%; }\n  .ngw-offset-lg-18 {\n    margin-left: 75%; }\n  .ngw-offset-lg-19 {\n    margin-left: 79.16667%; }\n  .ngw-offset-lg-20 {\n    margin-left: 83.33333%; }\n  .ngw-offset-lg-21 {\n    margin-left: 87.5%; }\n  .ngw-offset-lg-22 {\n    margin-left: 91.66667%; }\n  .ngw-offset-lg-23 {\n    margin-left: 95.83333%; } }\n\n@media (min-width: 1600px) {\n  .ngw-col-xl {\n    flex-basis: 0;\n    -webkit-box-flex: 1;\n            flex-grow: 1;\n    max-width: 100%; }\n  .ngw-col-xl-auto {\n    -webkit-box-flex: 0;\n            flex: 0 0 auto;\n    width: auto;\n    max-width: none; }\n  .ngw-col-xl-1 {\n    -webkit-box-flex: 0;\n            flex: 0 0 4.16667%;\n    max-width: 4.16667%; }\n  .ngw-col-xl-2 {\n    -webkit-box-flex: 0;\n            flex: 0 0 8.33333%;\n    max-width: 8.33333%; }\n  .ngw-col-xl-3 {\n    -webkit-box-flex: 0;\n            flex: 0 0 12.5%;\n    max-width: 12.5%; }\n  .ngw-col-xl-4 {\n    -webkit-box-flex: 0;\n            flex: 0 0 16.66667%;\n    max-width: 16.66667%; }\n  .ngw-col-xl-5 {\n    -webkit-box-flex: 0;\n            flex: 0 0 20.83333%;\n    max-width: 20.83333%; }\n  .ngw-col-xl-6 {\n    -webkit-box-flex: 0;\n            flex: 0 0 25%;\n    max-width: 25%; }\n  .ngw-col-xl-7 {\n    -webkit-box-flex: 0;\n            flex: 0 0 29.16667%;\n    max-width: 29.16667%; }\n  .ngw-col-xl-8 {\n    -webkit-box-flex: 0;\n            flex: 0 0 33.33333%;\n    max-width: 33.33333%; }\n  .ngw-col-xl-9 {\n    -webkit-box-flex: 0;\n            flex: 0 0 37.5%;\n    max-width: 37.5%; }\n  .ngw-col-xl-10 {\n    -webkit-box-flex: 0;\n            flex: 0 0 41.66667%;\n    max-width: 41.66667%; }\n  .ngw-col-xl-11 {\n    -webkit-box-flex: 0;\n            flex: 0 0 45.83333%;\n    max-width: 45.83333%; }\n  .ngw-col-xl-12 {\n    -webkit-box-flex: 0;\n            flex: 0 0 50%;\n    max-width: 50%; }\n  .ngw-col-xl-13 {\n    -webkit-box-flex: 0;\n            flex: 0 0 54.16667%;\n    max-width: 54.16667%; }\n  .ngw-col-xl-14 {\n    -webkit-box-flex: 0;\n            flex: 0 0 58.33333%;\n    max-width: 58.33333%; }\n  .ngw-col-xl-15 {\n    -webkit-box-flex: 0;\n            flex: 0 0 62.5%;\n    max-width: 62.5%; }\n  .ngw-col-xl-16 {\n    -webkit-box-flex: 0;\n            flex: 0 0 66.66667%;\n    max-width: 66.66667%; }\n  .ngw-col-xl-17 {\n    -webkit-box-flex: 0;\n            flex: 0 0 70.83333%;\n    max-width: 70.83333%; }\n  .ngw-col-xl-18 {\n    -webkit-box-flex: 0;\n            flex: 0 0 75%;\n    max-width: 75%; }\n  .ngw-col-xl-19 {\n    -webkit-box-flex: 0;\n            flex: 0 0 79.16667%;\n    max-width: 79.16667%; }\n  .ngw-col-xl-20 {\n    -webkit-box-flex: 0;\n            flex: 0 0 83.33333%;\n    max-width: 83.33333%; }\n  .ngw-col-xl-21 {\n    -webkit-box-flex: 0;\n            flex: 0 0 87.5%;\n    max-width: 87.5%; }\n  .ngw-col-xl-22 {\n    -webkit-box-flex: 0;\n            flex: 0 0 91.66667%;\n    max-width: 91.66667%; }\n  .ngw-col-xl-23 {\n    -webkit-box-flex: 0;\n            flex: 0 0 95.83333%;\n    max-width: 95.83333%; }\n  .ngw-col-xl-24 {\n    -webkit-box-flex: 0;\n            flex: 0 0 100%;\n    max-width: 100%; }\n  .ngw-order-xl-first {\n    -webkit-box-ordinal-group: 0;\n            order: -1; }\n  .ngw-order-xl-last {\n    -webkit-box-ordinal-group: 26;\n            order: 25; }\n  .ngw-order-xl-0 {\n    -webkit-box-ordinal-group: 1;\n            order: 0; }\n  .ngw-order-xl-1 {\n    -webkit-box-ordinal-group: 2;\n            order: 1; }\n  .ngw-order-xl-2 {\n    -webkit-box-ordinal-group: 3;\n            order: 2; }\n  .ngw-order-xl-3 {\n    -webkit-box-ordinal-group: 4;\n            order: 3; }\n  .ngw-order-xl-4 {\n    -webkit-box-ordinal-group: 5;\n            order: 4; }\n  .ngw-order-xl-5 {\n    -webkit-box-ordinal-group: 6;\n            order: 5; }\n  .ngw-order-xl-6 {\n    -webkit-box-ordinal-group: 7;\n            order: 6; }\n  .ngw-order-xl-7 {\n    -webkit-box-ordinal-group: 8;\n            order: 7; }\n  .ngw-order-xl-8 {\n    -webkit-box-ordinal-group: 9;\n            order: 8; }\n  .ngw-order-xl-9 {\n    -webkit-box-ordinal-group: 10;\n            order: 9; }\n  .ngw-order-xl-10 {\n    -webkit-box-ordinal-group: 11;\n            order: 10; }\n  .ngw-order-xl-11 {\n    -webkit-box-ordinal-group: 12;\n            order: 11; }\n  .ngw-order-xl-12 {\n    -webkit-box-ordinal-group: 13;\n            order: 12; }\n  .ngw-order-xl-13 {\n    -webkit-box-ordinal-group: 14;\n            order: 13; }\n  .ngw-order-xl-14 {\n    -webkit-box-ordinal-group: 15;\n            order: 14; }\n  .ngw-order-xl-15 {\n    -webkit-box-ordinal-group: 16;\n            order: 15; }\n  .ngw-order-xl-16 {\n    -webkit-box-ordinal-group: 17;\n            order: 16; }\n  .ngw-order-xl-17 {\n    -webkit-box-ordinal-group: 18;\n            order: 17; }\n  .ngw-order-xl-18 {\n    -webkit-box-ordinal-group: 19;\n            order: 18; }\n  .ngw-order-xl-19 {\n    -webkit-box-ordinal-group: 20;\n            order: 19; }\n  .ngw-order-xl-20 {\n    -webkit-box-ordinal-group: 21;\n            order: 20; }\n  .ngw-order-xl-21 {\n    -webkit-box-ordinal-group: 22;\n            order: 21; }\n  .ngw-order-xl-22 {\n    -webkit-box-ordinal-group: 23;\n            order: 22; }\n  .ngw-order-xl-23 {\n    -webkit-box-ordinal-group: 24;\n            order: 23; }\n  .ngw-order-xl-24 {\n    -webkit-box-ordinal-group: 25;\n            order: 24; }\n  .ngw-offset-xl-0 {\n    margin-left: 0; }\n  .ngw-offset-xl-1 {\n    margin-left: 4.16667%; }\n  .ngw-offset-xl-2 {\n    margin-left: 8.33333%; }\n  .ngw-offset-xl-3 {\n    margin-left: 12.5%; }\n  .ngw-offset-xl-4 {\n    margin-left: 16.66667%; }\n  .ngw-offset-xl-5 {\n    margin-left: 20.83333%; }\n  .ngw-offset-xl-6 {\n    margin-left: 25%; }\n  .ngw-offset-xl-7 {\n    margin-left: 29.16667%; }\n  .ngw-offset-xl-8 {\n    margin-left: 33.33333%; }\n  .ngw-offset-xl-9 {\n    margin-left: 37.5%; }\n  .ngw-offset-xl-10 {\n    margin-left: 41.66667%; }\n  .ngw-offset-xl-11 {\n    margin-left: 45.83333%; }\n  .ngw-offset-xl-12 {\n    margin-left: 50%; }\n  .ngw-offset-xl-13 {\n    margin-left: 54.16667%; }\n  .ngw-offset-xl-14 {\n    margin-left: 58.33333%; }\n  .ngw-offset-xl-15 {\n    margin-left: 62.5%; }\n  .ngw-offset-xl-16 {\n    margin-left: 66.66667%; }\n  .ngw-offset-xl-17 {\n    margin-left: 70.83333%; }\n  .ngw-offset-xl-18 {\n    margin-left: 75%; }\n  .ngw-offset-xl-19 {\n    margin-left: 79.16667%; }\n  .ngw-offset-xl-20 {\n    margin-left: 83.33333%; }\n  .ngw-offset-xl-21 {\n    margin-left: 87.5%; }\n  .ngw-offset-xl-22 {\n    margin-left: 91.66667%; }\n  .ngw-offset-xl-23 {\n    margin-left: 95.83333%; } }\n\n.d-none {\n  display: none !important; }\n\n.d-inline {\n  display: inline !important; }\n\n.d-inline-block {\n  display: inline-block !important; }\n\n.d-block {\n  display: block !important; }\n\n.d-table {\n  display: table !important; }\n\n.d-table-row {\n  display: table-row !important; }\n\n.d-table-cell {\n  display: table-cell !important; }\n\n.d-flex {\n  display: -webkit-box !important;\n  display: flex !important; }\n\n.d-inline-flex {\n  display: -webkit-inline-box !important;\n  display: inline-flex !important; }\n\n@media (min-width: 560px) {\n  .d-sm-none {\n    display: none !important; }\n  .d-sm-inline {\n    display: inline !important; }\n  .d-sm-inline-block {\n    display: inline-block !important; }\n  .d-sm-block {\n    display: block !important; }\n  .d-sm-table {\n    display: table !important; }\n  .d-sm-table-row {\n    display: table-row !important; }\n  .d-sm-table-cell {\n    display: table-cell !important; }\n  .d-sm-flex {\n    display: -webkit-box !important;\n    display: flex !important; }\n  .d-sm-inline-flex {\n    display: -webkit-inline-box !important;\n    display: inline-flex !important; } }\n\n@media (min-width: 1024px) {\n  .d-md-none {\n    display: none !important; }\n  .d-md-inline {\n    display: inline !important; }\n  .d-md-inline-block {\n    display: inline-block !important; }\n  .d-md-block {\n    display: block !important; }\n  .d-md-table {\n    display: table !important; }\n  .d-md-table-row {\n    display: table-row !important; }\n  .d-md-table-cell {\n    display: table-cell !important; }\n  .d-md-flex {\n    display: -webkit-box !important;\n    display: flex !important; }\n  .d-md-inline-flex {\n    display: -webkit-inline-box !important;\n    display: inline-flex !important; } }\n\n@media (min-width: 1280px) {\n  .d-lg-none {\n    display: none !important; }\n  .d-lg-inline {\n    display: inline !important; }\n  .d-lg-inline-block {\n    display: inline-block !important; }\n  .d-lg-block {\n    display: block !important; }\n  .d-lg-table {\n    display: table !important; }\n  .d-lg-table-row {\n    display: table-row !important; }\n  .d-lg-table-cell {\n    display: table-cell !important; }\n  .d-lg-flex {\n    display: -webkit-box !important;\n    display: flex !important; }\n  .d-lg-inline-flex {\n    display: -webkit-inline-box !important;\n    display: inline-flex !important; } }\n\n@media (min-width: 1600px) {\n  .d-xl-none {\n    display: none !important; }\n  .d-xl-inline {\n    display: inline !important; }\n  .d-xl-inline-block {\n    display: inline-block !important; }\n  .d-xl-block {\n    display: block !important; }\n  .d-xl-table {\n    display: table !important; }\n  .d-xl-table-row {\n    display: table-row !important; }\n  .d-xl-table-cell {\n    display: table-cell !important; }\n  .d-xl-flex {\n    display: -webkit-box !important;\n    display: flex !important; }\n  .d-xl-inline-flex {\n    display: -webkit-inline-box !important;\n    display: inline-flex !important; } }\n\n@media print {\n  .d-print-none {\n    display: none !important; }\n  .d-print-inline {\n    display: inline !important; }\n  .d-print-inline-block {\n    display: inline-block !important; }\n  .d-print-block {\n    display: block !important; }\n  .d-print-table {\n    display: table !important; }\n  .d-print-table-row {\n    display: table-row !important; }\n  .d-print-table-cell {\n    display: table-cell !important; }\n  .d-print-flex {\n    display: -webkit-box !important;\n    display: flex !important; }\n  .d-print-inline-flex {\n    display: -webkit-inline-box !important;\n    display: inline-flex !important; } }\n\n.visible {\n  visibility: visible !important; }\n\n.invisible {\n  visibility: hidden !important; }\n\n.flex-row {\n  -webkit-box-orient: horizontal !important;\n  -webkit-box-direction: normal !important;\n          flex-direction: row !important; }\n\n.flex-column {\n  -webkit-box-orient: vertical !important;\n  -webkit-box-direction: normal !important;\n          flex-direction: column !important; }\n\n.flex-row-reverse {\n  -webkit-box-orient: horizontal !important;\n  -webkit-box-direction: reverse !important;\n          flex-direction: row-reverse !important; }\n\n.flex-column-reverse {\n  -webkit-box-orient: vertical !important;\n  -webkit-box-direction: reverse !important;\n          flex-direction: column-reverse !important; }\n\n.flex-wrap {\n  flex-wrap: wrap !important; }\n\n.flex-nowrap {\n  flex-wrap: nowrap !important; }\n\n.flex-wrap-reverse {\n  flex-wrap: wrap-reverse !important; }\n\n.justify-content-start {\n  -webkit-box-pack: start !important;\n          justify-content: flex-start !important; }\n\n.justify-content-end {\n  -webkit-box-pack: end !important;\n          justify-content: flex-end !important; }\n\n.justify-content-center {\n  -webkit-box-pack: center !important;\n          justify-content: center !important; }\n\n.justify-content-between {\n  -webkit-box-pack: justify !important;\n          justify-content: space-between !important; }\n\n.justify-content-around {\n  justify-content: space-around !important; }\n\n.align-items-start {\n  -webkit-box-align: start !important;\n          align-items: flex-start !important; }\n\n.align-items-end {\n  -webkit-box-align: end !important;\n          align-items: flex-end !important; }\n\n.align-items-center {\n  -webkit-box-align: center !important;\n          align-items: center !important; }\n\n.align-items-baseline {\n  -webkit-box-align: baseline !important;\n          align-items: baseline !important; }\n\n.align-items-stretch {\n  -webkit-box-align: stretch !important;\n          align-items: stretch !important; }\n\n.align-content-start {\n  align-content: flex-start !important; }\n\n.align-content-end {\n  align-content: flex-end !important; }\n\n.align-content-center {\n  align-content: center !important; }\n\n.align-content-between {\n  align-content: space-between !important; }\n\n.align-content-around {\n  align-content: space-around !important; }\n\n.align-content-stretch {\n  align-content: stretch !important; }\n\n.align-self-auto {\n  align-self: auto !important; }\n\n.align-self-start {\n  align-self: flex-start !important; }\n\n.align-self-end {\n  align-self: flex-end !important; }\n\n.align-self-center {\n  align-self: center !important; }\n\n.align-self-baseline {\n  align-self: baseline !important; }\n\n.align-self-stretch {\n  align-self: stretch !important; }\n\n@media (min-width: 560px) {\n  .flex-sm-row {\n    -webkit-box-orient: horizontal !important;\n    -webkit-box-direction: normal !important;\n            flex-direction: row !important; }\n  .flex-sm-column {\n    -webkit-box-orient: vertical !important;\n    -webkit-box-direction: normal !important;\n            flex-direction: column !important; }\n  .flex-sm-row-reverse {\n    -webkit-box-orient: horizontal !important;\n    -webkit-box-direction: reverse !important;\n            flex-direction: row-reverse !important; }\n  .flex-sm-column-reverse {\n    -webkit-box-orient: vertical !important;\n    -webkit-box-direction: reverse !important;\n            flex-direction: column-reverse !important; }\n  .flex-sm-wrap {\n    flex-wrap: wrap !important; }\n  .flex-sm-nowrap {\n    flex-wrap: nowrap !important; }\n  .flex-sm-wrap-reverse {\n    flex-wrap: wrap-reverse !important; }\n  .justify-content-sm-start {\n    -webkit-box-pack: start !important;\n            justify-content: flex-start !important; }\n  .justify-content-sm-end {\n    -webkit-box-pack: end !important;\n            justify-content: flex-end !important; }\n  .justify-content-sm-center {\n    -webkit-box-pack: center !important;\n            justify-content: center !important; }\n  .justify-content-sm-between {\n    -webkit-box-pack: justify !important;\n            justify-content: space-between !important; }\n  .justify-content-sm-around {\n    justify-content: space-around !important; }\n  .align-items-sm-start {\n    -webkit-box-align: start !important;\n            align-items: flex-start !important; }\n  .align-items-sm-end {\n    -webkit-box-align: end !important;\n            align-items: flex-end !important; }\n  .align-items-sm-center {\n    -webkit-box-align: center !important;\n            align-items: center !important; }\n  .align-items-sm-baseline {\n    -webkit-box-align: baseline !important;\n            align-items: baseline !important; }\n  .align-items-sm-stretch {\n    -webkit-box-align: stretch !important;\n            align-items: stretch !important; }\n  .align-content-sm-start {\n    align-content: flex-start !important; }\n  .align-content-sm-end {\n    align-content: flex-end !important; }\n  .align-content-sm-center {\n    align-content: center !important; }\n  .align-content-sm-between {\n    align-content: space-between !important; }\n  .align-content-sm-around {\n    align-content: space-around !important; }\n  .align-content-sm-stretch {\n    align-content: stretch !important; }\n  .align-self-sm-auto {\n    align-self: auto !important; }\n  .align-self-sm-start {\n    align-self: flex-start !important; }\n  .align-self-sm-end {\n    align-self: flex-end !important; }\n  .align-self-sm-center {\n    align-self: center !important; }\n  .align-self-sm-baseline {\n    align-self: baseline !important; }\n  .align-self-sm-stretch {\n    align-self: stretch !important; } }\n\n@media (min-width: 1024px) {\n  .flex-md-row {\n    -webkit-box-orient: horizontal !important;\n    -webkit-box-direction: normal !important;\n            flex-direction: row !important; }\n  .flex-md-column {\n    -webkit-box-orient: vertical !important;\n    -webkit-box-direction: normal !important;\n            flex-direction: column !important; }\n  .flex-md-row-reverse {\n    -webkit-box-orient: horizontal !important;\n    -webkit-box-direction: reverse !important;\n            flex-direction: row-reverse !important; }\n  .flex-md-column-reverse {\n    -webkit-box-orient: vertical !important;\n    -webkit-box-direction: reverse !important;\n            flex-direction: column-reverse !important; }\n  .flex-md-wrap {\n    flex-wrap: wrap !important; }\n  .flex-md-nowrap {\n    flex-wrap: nowrap !important; }\n  .flex-md-wrap-reverse {\n    flex-wrap: wrap-reverse !important; }\n  .justify-content-md-start {\n    -webkit-box-pack: start !important;\n            justify-content: flex-start !important; }\n  .justify-content-md-end {\n    -webkit-box-pack: end !important;\n            justify-content: flex-end !important; }\n  .justify-content-md-center {\n    -webkit-box-pack: center !important;\n            justify-content: center !important; }\n  .justify-content-md-between {\n    -webkit-box-pack: justify !important;\n            justify-content: space-between !important; }\n  .justify-content-md-around {\n    justify-content: space-around !important; }\n  .align-items-md-start {\n    -webkit-box-align: start !important;\n            align-items: flex-start !important; }\n  .align-items-md-end {\n    -webkit-box-align: end !important;\n            align-items: flex-end !important; }\n  .align-items-md-center {\n    -webkit-box-align: center !important;\n            align-items: center !important; }\n  .align-items-md-baseline {\n    -webkit-box-align: baseline !important;\n            align-items: baseline !important; }\n  .align-items-md-stretch {\n    -webkit-box-align: stretch !important;\n            align-items: stretch !important; }\n  .align-content-md-start {\n    align-content: flex-start !important; }\n  .align-content-md-end {\n    align-content: flex-end !important; }\n  .align-content-md-center {\n    align-content: center !important; }\n  .align-content-md-between {\n    align-content: space-between !important; }\n  .align-content-md-around {\n    align-content: space-around !important; }\n  .align-content-md-stretch {\n    align-content: stretch !important; }\n  .align-self-md-auto {\n    align-self: auto !important; }\n  .align-self-md-start {\n    align-self: flex-start !important; }\n  .align-self-md-end {\n    align-self: flex-end !important; }\n  .align-self-md-center {\n    align-self: center !important; }\n  .align-self-md-baseline {\n    align-self: baseline !important; }\n  .align-self-md-stretch {\n    align-self: stretch !important; } }\n\n@media (min-width: 1280px) {\n  .flex-lg-row {\n    -webkit-box-orient: horizontal !important;\n    -webkit-box-direction: normal !important;\n            flex-direction: row !important; }\n  .flex-lg-column {\n    -webkit-box-orient: vertical !important;\n    -webkit-box-direction: normal !important;\n            flex-direction: column !important; }\n  .flex-lg-row-reverse {\n    -webkit-box-orient: horizontal !important;\n    -webkit-box-direction: reverse !important;\n            flex-direction: row-reverse !important; }\n  .flex-lg-column-reverse {\n    -webkit-box-orient: vertical !important;\n    -webkit-box-direction: reverse !important;\n            flex-direction: column-reverse !important; }\n  .flex-lg-wrap {\n    flex-wrap: wrap !important; }\n  .flex-lg-nowrap {\n    flex-wrap: nowrap !important; }\n  .flex-lg-wrap-reverse {\n    flex-wrap: wrap-reverse !important; }\n  .justify-content-lg-start {\n    -webkit-box-pack: start !important;\n            justify-content: flex-start !important; }\n  .justify-content-lg-end {\n    -webkit-box-pack: end !important;\n            justify-content: flex-end !important; }\n  .justify-content-lg-center {\n    -webkit-box-pack: center !important;\n            justify-content: center !important; }\n  .justify-content-lg-between {\n    -webkit-box-pack: justify !important;\n            justify-content: space-between !important; }\n  .justify-content-lg-around {\n    justify-content: space-around !important; }\n  .align-items-lg-start {\n    -webkit-box-align: start !important;\n            align-items: flex-start !important; }\n  .align-items-lg-end {\n    -webkit-box-align: end !important;\n            align-items: flex-end !important; }\n  .align-items-lg-center {\n    -webkit-box-align: center !important;\n            align-items: center !important; }\n  .align-items-lg-baseline {\n    -webkit-box-align: baseline !important;\n            align-items: baseline !important; }\n  .align-items-lg-stretch {\n    -webkit-box-align: stretch !important;\n            align-items: stretch !important; }\n  .align-content-lg-start {\n    align-content: flex-start !important; }\n  .align-content-lg-end {\n    align-content: flex-end !important; }\n  .align-content-lg-center {\n    align-content: center !important; }\n  .align-content-lg-between {\n    align-content: space-between !important; }\n  .align-content-lg-around {\n    align-content: space-around !important; }\n  .align-content-lg-stretch {\n    align-content: stretch !important; }\n  .align-self-lg-auto {\n    align-self: auto !important; }\n  .align-self-lg-start {\n    align-self: flex-start !important; }\n  .align-self-lg-end {\n    align-self: flex-end !important; }\n  .align-self-lg-center {\n    align-self: center !important; }\n  .align-self-lg-baseline {\n    align-self: baseline !important; }\n  .align-self-lg-stretch {\n    align-self: stretch !important; } }\n\n@media (min-width: 1600px) {\n  .flex-xl-row {\n    -webkit-box-orient: horizontal !important;\n    -webkit-box-direction: normal !important;\n            flex-direction: row !important; }\n  .flex-xl-column {\n    -webkit-box-orient: vertical !important;\n    -webkit-box-direction: normal !important;\n            flex-direction: column !important; }\n  .flex-xl-row-reverse {\n    -webkit-box-orient: horizontal !important;\n    -webkit-box-direction: reverse !important;\n            flex-direction: row-reverse !important; }\n  .flex-xl-column-reverse {\n    -webkit-box-orient: vertical !important;\n    -webkit-box-direction: reverse !important;\n            flex-direction: column-reverse !important; }\n  .flex-xl-wrap {\n    flex-wrap: wrap !important; }\n  .flex-xl-nowrap {\n    flex-wrap: nowrap !important; }\n  .flex-xl-wrap-reverse {\n    flex-wrap: wrap-reverse !important; }\n  .justify-content-xl-start {\n    -webkit-box-pack: start !important;\n            justify-content: flex-start !important; }\n  .justify-content-xl-end {\n    -webkit-box-pack: end !important;\n            justify-content: flex-end !important; }\n  .justify-content-xl-center {\n    -webkit-box-pack: center !important;\n            justify-content: center !important; }\n  .justify-content-xl-between {\n    -webkit-box-pack: justify !important;\n            justify-content: space-between !important; }\n  .justify-content-xl-around {\n    justify-content: space-around !important; }\n  .align-items-xl-start {\n    -webkit-box-align: start !important;\n            align-items: flex-start !important; }\n  .align-items-xl-end {\n    -webkit-box-align: end !important;\n            align-items: flex-end !important; }\n  .align-items-xl-center {\n    -webkit-box-align: center !important;\n            align-items: center !important; }\n  .align-items-xl-baseline {\n    -webkit-box-align: baseline !important;\n            align-items: baseline !important; }\n  .align-items-xl-stretch {\n    -webkit-box-align: stretch !important;\n            align-items: stretch !important; }\n  .align-content-xl-start {\n    align-content: flex-start !important; }\n  .align-content-xl-end {\n    align-content: flex-end !important; }\n  .align-content-xl-center {\n    align-content: center !important; }\n  .align-content-xl-between {\n    align-content: space-between !important; }\n  .align-content-xl-around {\n    align-content: space-around !important; }\n  .align-content-xl-stretch {\n    align-content: stretch !important; }\n  .align-self-xl-auto {\n    align-self: auto !important; }\n  .align-self-xl-start {\n    align-self: flex-start !important; }\n  .align-self-xl-end {\n    align-self: flex-end !important; }\n  .align-self-xl-center {\n    align-self: center !important; }\n  .align-self-xl-baseline {\n    align-self: baseline !important; }\n  .align-self-xl-stretch {\n    align-self: stretch !important; } }\n";
styleInject(css);

var formatPhoneNumber = function formatPhoneNumber(telephone) {
  return formatNumber(parseNumber(telephone, "GB"), "National");
};
/**
 * Format time from the API
 * @param {string} timeStr e.x: 18:00:00
 * @param {boolean} is24 24h format
 * @param {string} customFormat custom formatting
 * @return {string}
 */


var formatTime = function formatTime(timeStr, is24, customFormat) {
  var timeFormat = customFormat || "$1:$2$4";
  var timeParts = /(\d+)(:(\d+))?(:(\d+))?/g.exec(timeStr.replace(/[.:]/g, ":"));
  var amPM = timeStr.match(/(a|p)[\s.]*m/gi);

  if (!timeParts) {
    return null;
  }

  var hour = timeParts[1] || "0";
  var mins = timeParts[3] || "00";
  var secs = timeParts[5] || "00";

  if (amPM) {
    amPM = amPM[0].replace(/[\s.]*/g, "").toLowerCase();
  } else {
    amPM = hour >= 12 ? "pm" : "am";
  }

  if (is24 && amPM === "pm") {
    hour = hour % 12 + 12;
  } else if (!is24 && hour > 12) {
    hour %= 12;
  }

  return "".concat(hour, ":").concat(mins, ":").concat(secs, ":").concat(is24 ? "" : amPM).replace(/^(\d+):(\d+):(\d+):(\w*)/, timeFormat);
};
/**
 * Returning opening hours string
 * @param {number} open opening time
 * @param {number} close closing time
 * @return {string}
 */


var timeToDisplay = function timeToDisplay(open, close) {
  var itOpens = formatTime(open);
  var itCloses = formatTime(close);

  if (itOpens && itCloses) {
    return "".concat(itOpens, " - ").concat(itCloses);
  }

  if (itOpens) {
    return " from ".concat(itOpens);
  }

  if (itCloses) {
    return " until ".concat(itCloses);
  }

  if (open === "closed" || close === "closed") {
    return "Closed";
  }

  return open || close || "";
};
/**
 * Format to display openingHours
 * @param {*} openingHours - e.x: HH:mm:ss
 * @return {object} opening hours from Mon to Sun
 */


var openingHoursAdapter = function openingHoursAdapter(openingHours) {
  if (!openingHours) {
    return {};
  }

  return {
    mon: timeToDisplay(openingHours.monOpen, openingHours.monClose),
    tue: timeToDisplay(openingHours.tueOpen, openingHours.tueClose),
    wed: timeToDisplay(openingHours.wedOpen, openingHours.wedClose),
    thu: timeToDisplay(openingHours.thuOpen, openingHours.thuClose),
    fri: timeToDisplay(openingHours.friOpen, openingHours.friClose),
    sat: timeToDisplay(openingHours.satOpen, openingHours.satClose),
    sun: timeToDisplay(openingHours.sunOpen, openingHours.sunClose)
  };
};
/**
 * Generate Unique ID for array
 * + newDate() return timestamp
 * @return {string}
 */


var generateUuid = function generateUuid(index) {
  return index + +new Date();
};

function _templateObject2$1() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  border-bottom: 2px solid;\n  cursor: default;\n"]);

  _templateObject2$1 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$1() {
  var data = _taggedTemplateLiteral(["\n  ", "\n  ", "\n  ", "\n  ", "\n  border-bottom: 1px solid ", ";\n  color: ", ";\n  cursor: pointer;\n  display: inline-block;\n  flex: auto;\n  text-decoration: none;\n"]);

  _templateObject$1 = function _templateObject() {
    return data;
  };

  return data;
}

var AnchorLinkStyled = styled.a(_templateObject$1(), D6Typography("body/regular100"), D6Spacing("padding", "s250"), D6Spacing("padding-left", "s300"), D6Spacing("padding-right", "s300"), palette.d6.grey100, palette.d6.blue800);
var AnchorLinkActiveStyled = styled(AnchorLinkStyled)(_templateObject2$1(), palette.d6.blue900);
/**
 * PDB
 * Atom
 * AnchorLink
 */

var AnchorLink = function AnchorLink(_ref) {
  var text = _ref.text,
      handleClick = _ref.handleClick,
      active = _ref.active;
  return React.createElement(React.Fragment, null, text && active && React.createElement(AnchorLinkActiveStyled, null, text), text && !active && React.createElement(AnchorLinkStyled, {
    onClick: handleClick
  }, text));
};

AnchorLink.defaultProps = {
  active: false
};
/**
 * Expecting the client to send data as a simple HTML block from the Jahia CMS
 * with in the following format:
 *
 * - 1: Simple
 * 'This is my <strong>string</strong> with minimal HTML'
 * - 2: Complex i.e. multiple paragraphs
 * '<p>This is my string.</p><p>More <em>complex</em> <a href="#">HTML</a></p>'
 *
 * @param {string} copy
 * @return {object}
 */

var createMarkup = function createMarkup(copy, allowedTags) {
  DOMPurify.setConfig({
    ADD_ATTR: ["target"]
  });
  var sanitisedCopy = DOMPurify.sanitize(copy, {
    ALLOWED_TAGS: allowedTags
  });
  var obj = {
    __html: sanitisedCopy
  };
  return obj;
};

var RichText = function RichText(_ref) {
  var allowedTags = _ref.allowedTags,
      text = _ref.text,
      inline = _ref.inline,
      className = _ref.className;
  return React.createElement(React.Fragment, null, inline === true && React.createElement("span", {
    dangerouslySetInnerHTML: createMarkup("".concat(text), allowedTags),
    className: className
  }), inline !== true && React.createElement("div", {
    dangerouslySetInnerHTML: createMarkup("".concat(text), allowedTags),
    className: className
  }));
}; // By default assume only very simple HTML tags


RichText.defaultProps = {
  allowedTags: ["b", "br", "em", "i", "strong"],
  inline: false,
  className: ""
};

function _templateObject$2() {
  var data = _taggedTemplateLiteral(["\n  ", "\n"]);

  _templateObject$2 = function _templateObject() {
    return data;
  };

  return data;
}

var StyledBodyCopy = styled.div(_templateObject$2(), D6Typography("body/regular150"));
/**
 * VW NGW
 * Atom
 * BodyCopy
 * Notes: A string of <p></p> tags provided from the CMS
 */

var BodyCopy = function BodyCopy(_ref) {
  var text = _ref.text,
      className = _ref.className,
      allowedTags = _ref.allowedTags;
  return React.createElement(StyledBodyCopy, {
    className: "".concat(className)
  }, React.createElement(RichText, {
    text: text,
    allowedTags: allowedTags,
    inline: false
  }));
}; // by default, allow most types of HTML.


BodyCopy.defaultProps = {
  className: null,
  allowedTags: ["a", "b", "br", "em", "h1", "h2", "h3", "h4", "h5", "h6", "i", "li", "ol", "p", "strong", "ul"]
};

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (_typeof(call) === "object" || typeof call === "function")) {
    return call;
  }

  return _assertThisInitialized(self);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _templateObject$3() {
  var data = _taggedTemplateLiteral(["\n  display: inline-block;\n  height: ", ";\n  width: ", ";\n\n  &.ngw-icon-xs {\n    ", "\n  }\n\n  &.ngw-icon-sm {\n    ", "\n  }\n\n  &.ngw-icon-md {\n    ", "\n  }\n\n  &.ngw-icon-lg {\n    ", "\n  }\n\n  &.ngw-icon-xl {\n    ", "\n  }\n"]);

  _templateObject$3 = function _templateObject() {
    return data;
  };

  return data;
}

var StyledIcon = styled.i(_templateObject$3(), function (props) {
  return props.sizePX ? props.sizePX : "24px";
}, function (props) {
  return props.sizePX ? props.sizePX : "24px";
}, function (props) {
  return props.sizePX ? "" : "height: 20px; width: 20px;";
}, function (props) {
  return props.sizePX ? "" : "height: 24px; width: 24px;";
}, function (props) {
  return props.sizePX ? "" : "height: 28px; width: 28px;";
}, function (props) {
  return props.sizePX ? "" : "height: 32px; width: 32px;";
}, function (props) {
  return props.sizePX ? "" : "height: 48px; width: 48px;";
});
/**
 * This is a list of `d` attributes values from a `<path>` in an SVG element.
 * e.g.
 * <svg viewBox="0 0 1024 1024">
 *   <path d={VALUE} />
 * </svg>
 */

var icons = {
  ac: "M616.8 399.4l64.9 64.5c-21.9 22-32.4 47.7-32.4 79.8 0 17.3 3.8 31.2 14.5 56.1 2.2 5.1 2.2 5.1 4.6 10.5 18.4 42.3 26.1 68.8 26.6 107.5 1.1 76.9-15.7 133.6-52.2 194.8l-78.6-46.9c28.2-47.2 40.1-87.5 39.2-146.5-.3-23.9-5.1-40.3-19.1-72.4-2.3-5.3-2.3-5.3-4.7-10.9-15.1-35.3-21.9-59.8-21.9-92.1 0-56.2 20-105.2 59.1-144.4zm-228.8 0l64.9 64.5c-21.9 22-32.4 47.7-32.4 79.8 0 17.3 3.8 31.2 14.5 56.1 2.2 5.1 2.2 5.1 4.6 10.5 18.4 42.3 26.1 68.8 26.6 107.5 1.1 76.9-15.7 133.6-52.2 194.8l-78.6-46.9c28.2-47.2 40.1-87.5 39.2-146.5-.3-23.9-5.1-40.3-19.1-72.4-2.3-5.3-2.3-5.3-4.7-10.9-15-35.3-21.8-59.9-21.8-92.2 0-56.1 20-105.1 59-144.3zM212.5 646.8l-87.3 27.3L0 273.3l31.4-18.4C194 159.5 354.3 111.4 512 111.4s318 48.1 480.6 143.5l31.4 18.4-125.2 400.8-87.3-27.3L914.8 316C776.7 240.4 642.6 202.9 512 202.9S247.3 240.4 109.2 316l103.3 330.8z",
  add: "M476.748 465.455v-139.636h93.091v139.636h139.636v93.091h-139.636v150.332h-93.091v-150.332h-139.636v-93.091h139.636zM0 512c0-296.125 215.879-512 512-512 296.129 0 512 215.871 512 512 0 296.121-215.875 512-512 512-296.116 0-512-215.884-512-512zM93.091 512c0 244.704 174.205 418.909 418.909 418.909 244.712 0 418.909-174.201 418.909-418.909 0-244.716-174.193-418.909-418.909-418.909-244.708 0-418.909 174.197-418.909 418.909z",
  alert: "M0 512c0-296.125 215.879-512 512-512 296.129 0 512 215.871 512 512 0 296.121-215.875 512-512 512-296.116 0-512-215.884-512-512zM93.091 512c0 244.704 174.205 418.909 418.909 418.909 244.712 0 418.909-174.201 418.909-418.909 0-244.716-174.193-418.909-418.909-418.909-244.708 0-418.909 174.197-418.909 418.909zM465.455 186.182c15.742 0 31.373 0 46.893 0s30.919 0 46.198 0v465.455h-93.091v-465.455zM512 837.818c-25.706 0-46.545-20.839-46.545-46.545s20.839-46.545 46.545-46.545c25.706 0 46.545 20.839 46.545 46.545s-20.839 46.545-46.545 46.545z",
  "arrow-down": "M307.2 859.783v-859.783h102.4v857.937c50.644-50.685 123.226-126.791 217.6-228.177l74.953 69.769c-238.934 256.687-305.762 323.515-344.677 323.515s-105.743-66.828-344.677-323.515l74.953-69.769c94.937 101.991 167.821 178.398 218.503 229.081 0.315 0.315 0.63 0.63 0.943 0.943z",
  "arrow-left": "M164.3,462.1H1024v102.5l-859.6,0c50.7,50.7,127.2,123.7,229.3,218.7l-69.8,75C66.9,619.2,0,552.3,0,513.4 c0-39,66.9-105.8,323.8-345l69.8,75C291.6,338.4,215.1,411.4,164.3,462.1L164.3,462.1z",
  "arrow-right": "M859.6,462.1c-50.7-50.7-127.2-123.7-229.3-218.7l69.8-75c256.9,239.2,323.8,306,323.8,345c0,39-66.9,105.8-323.8,345 l-69.8-75c102.1-95,178.6-168,229.3-218.7L0,564.6V462.1L859.6,462.1L859.6,462.1z",
  "arrow-up": "M409.6 162.549v861.451h-102.4v-858.178c-50.611 50.685-122.955 126.552-216.887 227.463l-74.953-69.769c238.934-256.687 305.762-323.515 344.677-323.515s105.743 66.828 344.677 323.515l-74.953 69.769c-94.937-101.991-167.821-178.398-218.503-229.081-0.555-0.555-1.107-1.107-1.657-1.655z",
  assistance: "M441.4 547.1c-85.9 12.1-141.8 17.6-215 22.2l-5.6-88.2c70.5-4.5 124-9.7 206.7-21.3-12.3-25-31.7-58-54.3-90-6-8.5-11.9-17-17.6-25.5-110.8 11.8-228.4 17.6-352.9 17.6v350.3h352.9v97.2h85.8V547.1zm13.2-215c33.8 49.8 75.2 121.3 75.2 165.9v311.6c0 48.8-39.6 88.4-88.4 88.4h-85.8c-48.8 0-88.4-39.6-88.4-88.4v-8.9H2.8v-88.4h-.2V362.1h-.2l.2-47.1V186.4h.2V98h178.9c77.4 0 149.2 40.5 189.2 106.8 9.1 15.1 18.6 30.3 28.4 45.6 43.1-5.4 85-11.8 125.7-19.2l15.7 86.9c-28.1 5.2-56.8 9.8-86.1 14zM2.8 273.7c104.8 0 204.4-4.3 298.8-12.8-2.1-3.5-4.3-7-6.4-10.5-24-39.8-67-64.1-113.5-64.1H2.8v87.4zM848 208.3l76.7-43.8c62.6 109.5 94.2 220.1 94.2 331.1s-31.6 221.6-94.2 331.1L848 782.9c55.2-96.6 82.5-192.2 82.5-287.3S903.2 304.9 848 208.3zm-172.9 94l69-55.2c64.8 81 98 164.1 98 248.5s-33.2 167.5-98 248.5l-69-55.2c53-66.2 78.7-130.4 78.7-193.3s-25.7-127.1-78.7-193.3z",
  back: "M179.245 429.853c482.482-8.75 601.505-2.030 660.685 28.173 112.965 57.653 184.070 173.773 184.070 300.599l0.001 180.042h-113.778l-0.001-180.042c0-84.069-47.133-161.040-122.013-199.257-15.339-7.829-35.6-11.865-105.307-15.329-90.657-4.505-246.777-4.897-496.269-0.521 45.331 43.96 108.598 102.734 191.605 178.067l61.128 64.015-82.287 78.576-58.167-61.132c-234.852-212.645-298.911-276.044-298.911-319.489 0-43.239 74.253-117.492 359.462-382.974l77.521 83.282c-113.323 105.485-198.22 186.468-254.534 242.782-1.079 1.079-2.147 2.148-3.204 3.208z",
  bluetooth: "M0 512c0-347.971 104.918-512 414.659-512 310.966 0 423.159 168.623 423.159 512 0 343.365-112.201 512-423.159 512-221.948 0-346.647-111.073-393.079-293.159-17.536-68.769-21.58-126.271-21.58-218.841zM93.091 512c0 85.204 3.625 136.746 18.694 195.839 36.491 143.1 126.271 223.070 302.875 223.070 249.649 0 330.068-120.867 330.068-418.909 0-298.054-80.41-418.909-330.068-418.909-247.384 0-321.568 115.98-321.568 418.909zM324.454 489.853l-119.028-86.202 54.603-75.395 64.425 46.658v-96.364c0-51.413 41.678-93.091 93.091-93.091 17.323 0 34.303 4.834 49.028 13.957l138.565 85.85c43.704 27.078 57.183 84.458 30.105 128.163-8.043 12.982-19.157 23.786-32.362 31.459-38.195 22.195-71.408 42.097-99.517 59.597l103.877 75.23c41.64 30.156 50.949 88.358 20.793 129.998-6.464 8.925-14.46 16.631-23.618 22.76l-135.095 90.415c-42.726 28.596-100.544 17.141-129.14-25.586-10.254-15.321-15.728-33.341-15.728-51.777v-103.699c-19.216 15.989-40.077 33.016-62.589 51.087l-58.275-72.594c48.269-38.748 88.575-72.417 120.864-100.947v-29.519zM417.545 562.102v183.423l135.095-90.415-131.84-95.481c-1.114 0.839-2.199 1.663-3.254 2.474zM422.217 445.716c35.32-22.894 79.851-49.911 133.893-81.315l-138.565-85.851v163.782l4.672 3.384z",
  bin: "M187.014 899.799c-0.004 17.551 13.556 31.11 30.282 31.11h497.314c17.152 0 31.056-13.904 31.056-31.056v-620.581h-559.376l0.723 620.526zM93.091 186.182h96.25l27.020-91.686c16.512-56.030 67.949-94.496 126.362-94.496h246.734c57.119 0 107.964 36.195 126.663 90.167 14.815 42.764 23.82 73.748 27.144 96.015h187.644c0 16.081 0 32.161 0 48.24s0 31.029 0 44.851h-92.152v620.581c0 68.564-55.582 124.147-124.147 124.147h-497.314c-68.139 0-123.377-55.238-123.377-123.377 0.001-0.307-0.239-207.423-0.72-621.35h-93.199c0-15.515 0-31.030 0-46.545s0-31.030 0-46.545h93.091zM189.341 186.182h459.114c-3.654-14.693-10.444-37.1-20.297-65.541-5.713-16.491-21.249-27.55-38.702-27.55h-246.734c-17.135 0-32.224 11.284-37.067 27.72l-19.265 65.371h-97.049z",
  breakdown: "M512 198.547l-366.49 732.98h732.98l-366.49-732.98zM1024 1024h-1024l512-1024 512 1024zM512 885.291c-24.259 0-43.925-20.701-43.925-46.236s19.666-46.236 43.925-46.236c24.259 0 43.925 20.701 43.925 46.236s-19.666 46.236-43.925 46.236zM468.075 422.928h87.849v300.536h-87.849v-300.536z",
  calculator: "M188.92 465.455h94.46v93.091h-94.46v-93.091zM330.61 465.455h94.46v93.091h-94.46v-93.091zM472.299 465.455h94.46v93.091h-94.46v-93.091zM188.92 232.727h377.84v139.636h-377.84v-139.636zM472.299 605.091h94.46v232.727h-94.46v-232.727zM330.61 605.091h94.46v93.091h-94.46v-93.091zM330.61 744.727h94.46v93.091h-94.46v-93.091zM188.92 605.091h94.46v93.091h-94.46v-93.091zM188.92 744.727h94.46v93.091h-94.46v-93.091zM141.69 93.091c-26.084 0-47.23 20.839-47.23 46.545v744.727c0 25.706 21.146 46.545 47.23 46.545h472.299c26.084 0 47.23-20.839 47.23-46.545v-744.727c0-25.706-21.146-46.545-47.23-46.545h-472.299zM141.69 0h472.299c78.253 0 141.69 62.517 141.69 139.636v744.727c0 77.119-63.437 139.636-141.69 139.636h-472.299c-78.253 0-141.69-62.517-141.69-139.636v-744.727c0-77.119 63.437-139.636 141.69-139.636z",
  calendar: "M744.727 186.182h-465.455v93.091h-93.091v-93.091h-93.091v186.182h834.857l2.366-186.182h-92.496v93.091h-93.091v-93.091zM837.818 93.091h186.777l-9.952 783.158c-1.041 81.903-67.731 147.751-149.641 147.751h-713.078c-83.905 0-151.924-68.019-151.924-151.924v-778.985h186.182v-93.091h93.091v93.091h465.455v-93.091h93.091v93.091zM926.765 465.455h-833.674v406.621c0 32.493 26.341 58.833 58.833 58.833h713.078c30.958 0 56.164-24.888 56.557-55.843l5.205-409.611zM186.182 605.091c0-27.042 19.504-46.545 46.545-46.545 27.042 0 46.545 19.503 46.545 46.545 0 27.041-19.503 46.545-46.545 46.545-27.041 0-46.545-19.504-46.545-46.545zM372.364 605.091c0-27.042 19.504-46.545 46.545-46.545 27.042 0 46.545 19.503 46.545 46.545 0 27.041-19.503 46.545-46.545 46.545-27.041 0-46.545-19.504-46.545-46.545zM558.545 605.091c0-27.042 19.504-46.545 46.545-46.545 27.042 0 46.545 19.503 46.545 46.545 0 27.041-19.503 46.545-46.545 46.545-27.041 0-46.545-19.504-46.545-46.545zM744.727 605.091c0-27.042 19.504-46.545 46.545-46.545 27.042 0 46.545 19.503 46.545 46.545 0 27.041-19.503 46.545-46.545 46.545-27.041 0-46.545-19.504-46.545-46.545zM186.182 791.273c0-27.042 19.504-46.545 46.545-46.545 27.042 0 46.545 19.503 46.545 46.545 0 27.041-19.503 46.545-46.545 46.545-27.041 0-46.545-19.504-46.545-46.545zM372.364 791.273c0-27.042 19.504-46.545 46.545-46.545 27.042 0 46.545 19.503 46.545 46.545 0 27.041-19.503 46.545-46.545 46.545-27.041 0-46.545-19.504-46.545-46.545zM558.545 791.273c0-27.042 19.504-46.545 46.545-46.545 27.042 0 46.545 19.503 46.545 46.545 0 27.041-19.503 46.545-46.545 46.545-27.041 0-46.545-19.504-46.545-46.545zM744.727 791.273c0-27.042 19.504-46.545 46.545-46.545 27.042 0 46.545 19.503 46.545 46.545 0 27.041-19.503 46.545-46.545 46.545-27.041 0-46.545-19.504-46.545-46.545z",
  car: "M85.3 602.5v207.2h85.5v-85.2h682.5v85.2h85.3V603c-84.6 12-139 17.4-210.6 21.9l-5.4-85.2c72.6-4.6 126.6-10 216-22.9V511c0-12.8-28-63.9-69.6-122.4-5.6-7.8-11-15.6-16.4-23.4C747.1 377.1 633.5 383 512 383c-121.5 0-235.1-5.9-340.8-17.7-5.3 7.7-10.8 15.5-16.3 23.3-42.3 59.8-69.6 109.2-69.6 122.4v5.3c91.6 13.3 146.1 18.8 219.7 23.5l-5.4 85.2c-72.5-4.7-127.5-10.1-214.3-22.5zm-9.4-249.9c-25.8-4-51-8.3-75.8-13l16-83.8c36.5 7 74.3 13.1 113.3 18.3 9.5-14.7 18.7-29.4 27.4-43.9 38.7-64 108-103.2 182.8-103.2h344.7c74.8 0 144.2 39.2 182.7 103.3 8.7 14.5 17.9 29.1 27.4 43.8 39-5.2 76.7-11.4 113.2-18.3l16 83.8c-24.7 4.7-49.9 9.1-75.7 13 33.2 47.8 75.9 115.8 75.9 158.4v298.7c0 47.1-38.2 85.3-85.3 85.3h-85.4c-47.1 0-85.2-38.2-85.2-85.2H256.2c0 47.1-38.2 85.2-85.2 85.2H85.3C38.2 895 0 856.8 0 809.7V511c0-43.2 42.7-110.6 75.9-158.4zm147.7-67.8c90.2 8.5 186.3 12.8 288.4 12.8 102.1 0 198.2-4.3 288.4-12.8-2.2-3.5-4.3-7-6.4-10.5-23.2-38.4-64.8-62-109.6-62H339.6c-44.9 0-86.5 23.5-109.6 62-2.2 3.5-4.3 7-6.4 10.5zM512 639c-23.6 0-42.7-19.1-42.7-42.7s19.1-42.7 42.7-42.7 42.7 19.1 42.7 42.7S535.6 639 512 639z",
  cart: "M221.443 97.898h697.567c53.861 0 97.524 43.663 97.524 97.524 0 4.555-0.319 9.104-0.955 13.615l-44.635 316.595-30.259 8.806c-108.932 31.703-222.344 51.854-340.183 60.468-101.015 7.384-204.34 6.049-309.952-3.985l13.104 93.483 623.966-1.431 0.224 97.524-708.969 1.626-95.96-684.599h-122.914v-97.524h122.914c48.63 0 89.829 35.827 96.58 83.986l1.95 13.912zM235.113 195.422l41.5 296.069c108.349 11.625 213.933 13.668 316.78 6.151 100.198-7.324 196.796-23.46 289.833-48.399l35.785-253.82h-683.897zM828.952 1024c-53.861 0-97.524-43.663-97.524-97.524s43.663-97.524 97.524-97.524c53.861 0 97.524 43.663 97.524 97.524s-43.663 97.524-97.524 97.524zM341.333 1024c-53.861 0-97.524-43.663-97.524-97.524s43.663-97.524 97.524-97.524c53.861 0 97.524 43.663 97.524 97.524s-43.663 97.524-97.524 97.524z",
  chat: "M744.727 395.636h241.402c22.101 0 37.871 9.942 37.871 37.545v369.61c0 31.546-9.724 36.85-22.707 37.434l-15.164-0.2h-77.927v114.157l-163.841-100.154c-14.973-9.153-32.245-14.003-49.869-14.003h-237.293c-25.706 0-38.291-11.403-38.291-37.234v-177.487h-19.998c-17.352 0-34.359 4.85-49.101 14.003l-207.289 128.694v-142.696h-95.91c-24.341 0-46.61 7.246-46.61-46.542l-0-462.012c0-34.504 19.409-46.931 46.61-46.931l650.99-0c23.608 0 47.127 19.079 47.127 46.931v278.887zM744.727 488.727v90.034c0 32.289-15.489 46.542-47.127 46.542h-185.6v121.63h182.492c34.737 0 68.814 9.568 98.421 27.667l22.198 13.569v1.864l0.089-1.809-0.089-0.054v-41.236h115.798v-258.207h-186.182zM235.611 600.633l65.097-40.415c29.484-18.305 63.498-28.005 98.203-28.005h252.725v-369.304h-558.545v369.304h142.52v68.42z",
  "chat-video": "M139 791.8h-23.2C51.5 791.8 0 743 0 675.9V166.5c0-62 54-115.8 115.8-115.8h787.3c63.2 0 115.9 53 115.9 115.7v509.5c0 68.2-48.1 115.8-115.8 115.8l-368.5.3c-8 0-15.9 2.1-22.9 6.1L139 1010.7V791.8zm326.9-74.1c20.9-11.9 44.6-18.2 68.7-18.2l368.6-.3c16.8 0 23.2-6.4 23.2-23.2V166.5c0-11.7-11.3-23.1-23.2-23.1H115.8c-10.7 0-23.2 12.4-23.2 23.1V676c0 14.8 8.9 23.2 23.2 23.2h115.9v152.1l234.2-133.6zm-49.1-157.5V282.3l231.6 138.9-231.6 139z",
  check: "M749 255.6l78.5 80-424.7 432-205.5-169.7 69.9-87.9 127.9 105.6z",
  checklist: "M170.667 324.297h128v120.699h-128v-120.699zM170.667 525.462h128v120.699h-128v-120.699zM170.667 726.626h128v120.699h-128v-120.699zM426.667 203.598v40.233h-298.667v683.96h768v-683.96h-298.667v-40.233c0-44.44-38.205-80.466-85.333-80.466s-85.333 36.026-85.333 80.466zM346.71 163.365c18.946-69.411 85.766-120.699 165.29-120.699s146.344 51.288 165.29 120.699h304.043v844.891h-938.667v-844.891h304.043zM896 927.791v80.466l85.333-80.466h-85.333zM128 927.791h-85.333l85.333 80.466v-80.466zM128 243.831v-80.466l-85.333 80.466h85.333zM42.667 243.831h85.333v-80.466l-85.333 80.466zM128 927.791h-85.333l85.333 80.466v-80.466zM896 927.791v80.466l85.333-80.466h-85.333zM512 243.831c-23.564 0-42.667-18.013-42.667-40.233s19.103-40.233 42.667-40.233c23.564 0 42.667 18.013 42.667 40.233s-19.103 40.233-42.667 40.233zM536.994 639.258l241.359-227.592 60.34 56.898-301.699 284.49-181.019-170.694 60.34-56.898 120.68 113.796z",
  "check-circle": "M512 4C229.2 4 0 233.2 0 516s229.2 512 512 512 512-229.2 512-512S794.8 4 512 4zm0 929.5C281.4 933.5 94.5 746.6 94.5 516S281.4 98.5 512 98.5 929.5 285.4 929.5 516 742.6 933.5 512 933.5zm-95.3-321.1l-104-85.7-56.6 71.3 167 137.9L767.9 385l-63.7-64.9-287.5 292.3z",
  "chevron-down": "M54.081 327.714l62.505-58.094 376.193 404.753c9.866 10.615 26.47 11.223 37.086 1.356 0.468-0.435 0.921-0.888 1.356-1.356l376.193-404.753 62.505 58.094-376.193 404.753c-1.851 1.992-3.775 3.915-5.767 5.767-45.136 41.951-115.734 39.369-157.685-5.767l-376.193-404.753z",
  "chevron-left": "M753.735 109.094l-62.366-103.071-626.62 379.156-5.569 3.812c-10.529 8.109-19.854 17.449-27.856 27.874-50.939 66.366-38.432 161.46 27.933 212.398l5.465 3.736 626.62 379.607 62.421-103.038-623.040-377.439c-11.917-10.722-13.758-28.982-3.832-41.914 1.149-1.497 2.432-2.885 3.831-4.147l623.014-376.975z",
  "chevron-right": "M27.133 120.587l63.274-102.516 602.224 371.697 5.573 3.89c9.613 7.551 18.177 16.129 25.622 25.639 51.19 65.383 39.684 159.885-25.699 211.075l-5.469 3.813-602.224 372.138-63.328-102.483 598.623-369.913c11.326-10.449 12.907-28.019 3.241-40.366-0.985-1.258-2.068-2.434-3.24-3.517l-598.597-369.458z",
  clock: "M0 512c0-296.125 215.879-512 512-512 296.129 0 512 215.871 512 512 0 296.121-215.875 512-512 512-296.116 0-512-215.884-512-512zM93.091 512c0 244.704 174.205 418.909 418.909 418.909 244.712 0 418.909-174.201 418.909-418.909 0-244.716-174.193-418.909-418.909-418.909-244.708 0-418.909 174.197-418.909 418.909zM418.909 232.727h93.091l0.002 317.456-0 6.48c-0 1.040 0.843 1.883 1.881 1.883h230.845v93.091h-230.846c-52.452-0.002-94.972-42.524-94.971-94.976l0-6.478-0.002-317.455z",
  close: "M446.175 512l-106.724-106.724 65.825-65.825 106.724 106.724 106.724-106.724 65.825 65.825-106.724 106.724 106.724 106.724-65.825 65.825-106.724-106.724-106.724 106.724-65.825-65.825 106.724-106.724zM0 512c0-296.125 215.879-512 512-512 296.129 0 512 215.871 512 512 0 296.121-215.875 512-512 512-296.116 0-512-215.884-512-512zM93.091 512c0 244.704 174.205 418.909 418.909 418.909 244.712 0 418.909-174.201 418.909-418.909 0-244.716-174.193-418.909-418.909-418.909-244.708 0-418.909 174.197-418.909 418.909z",
  "contact-dealer": "M813.463 748.368h164.547v-654.994h-466.010v89.482c70.45 32.782 116.382 103.079 116.382 189.486 0 120.348-89.117 209.455-209.473 209.455s-209.473-89.107-209.473-209.455c0-120.355 89.114-209.455 209.473-209.455v-116.342c0-25.706 20.839-46.545 46.545-46.545h558.545c25.706 0 46.545 20.839 46.545 46.545v744.727c0 25.706-20.839 46.545-46.545 46.545h-196.443l9.38 59.533c6.122 38.851-20.371 75.025-60.038 78.461-68.794 5.886-247.101 12.115-345.978 12.115h-24.023c-98.877 0-277.184-6.229-346.027-12.119-39.619-3.432-66.112-39.606-59.99-78.456l28.877-183.267c7.69-46.397 27.034-78.548 70.114-103.82l110.929-51.66c23.452 26.188 51.775 47.923 83.544 63.781l-151.101 70.118c-13.441 7.969-18.172 15.832-21.589 36.435l-24.605 156.163c78.794 4.981 225.023 9.733 309.847 9.733h24.023c84.837 0 231.095-4.753 309.848-9.732l-24.547-155.798c-3.315-19.999-7.772-28.077-19.838-35.695l-152.322-71.52c31.646-15.891 59.855-37.62 83.212-63.774l110.531 51.884 4.242 2.228c38.978 23.109 58.322 55.26 66.071 102.022l5.344 33.917zM698.182 186.182v0h232.727v93.091h-232.727v-93.091zM698.182 372.364v0h232.727v93.091h-232.727v-93.091zM535.291 372.342c0-68.939-47.433-116.364-116.382-116.364s-116.382 47.425-116.382 116.364c0 68.932 47.437 116.364 116.382 116.364s116.382-47.431 116.382-116.364z",
  "contact-general": "M600.002 578.564l105.726 49.628 4.057 2.131c35.36 20.964 53.827 49.707 61.962 90.774 72.031-1.68 152.845-4.811 203.945-8.042l-23.479-149.024c-3.17-19.13-7.434-26.856-18.975-34.143l-147.831-66.907 0.265-0.575c30.519-15.027 57.753-35.708 80.335-60.677l106.852 48.375 4.057 2.131c37.283 22.104 55.787 52.857 63.198 97.587l27.565 174.948c5.856 37.162-19.485 71.763-57.428 75.049-45.035 3.853-139.132 7.86-224.361 10.005l14.658 93.032c5.856 37.162-19.485 71.763-57.428 75.049-65.803 5.63-236.358 11.588-330.936 11.588h-22.979c-94.578 0-265.132-5.958-330.982-11.592-37.896-3.283-63.237-37.884-57.381-75.045l27.622-175.298c7.355-44.379 25.858-75.133 67.066-99.306l106.106-49.414c48.912 54.617 119.972 88.986 199.059 88.986 79.221 0 150.387-34.485 199.306-89.262zM666.914 422.934c0.604-7.333 0.913-14.75 0.913-22.239 0-23.159-2.947-45.63-8.487-67.058 2.789 0.17 5.618 0.255 8.487 0.255 65.948 0 111.322-45.369 111.322-111.304 0-65.941-45.37-111.304-111.322-111.304-53.351 0-93.233 29.684-106.526 75.933-23.87-17.986-50.852-32.062-79.995-41.275 28.596-74.338 98.903-123.702 186.521-123.702 115.126 0 200.366 85.226 200.366 200.348 0 115.115-85.243 200.348-200.366 200.348-0.304 0-0.609-0.001-0.913-0.002zM281.548 639.848c35.87 17.905 76.332 27.978 119.148 27.978 43.040 0 83.703-10.179 119.712-28.261l145.699 68.41c11.541 7.287 15.805 15.013 18.975 34.143l23.479 149.024c-75.329 4.762-215.228 9.309-296.376 9.309h-22.979c-81.136 0-221.008-4.545-296.376-9.31l23.536-149.373c3.268-19.707 7.793-27.229 20.65-34.851l144.532-67.069zM601.061 400.675c0 115.115-85.243 200.348-200.366 200.348s-200.366-85.233-200.366-200.348c0-115.122 85.24-200.348 200.366-200.348s200.366 85.226 200.366 200.348zM512.018 400.675c0-65.941-45.37-111.304-111.322-111.304s-111.322 45.363-111.322 111.304c0 65.935 45.374 111.304 111.322 111.304s111.322-45.369 111.322-111.304z",
  "dashboard-light": "M541.638 730.621l148.698-148.698 60.34 60.34-154.474 154.474c0.744 4.533 1.131 9.186 1.131 13.93 0 47.128-38.205 85.333-85.333 85.333s-85.333-38.205-85.333-85.333c0-47.128 38.205-85.333 85.333-85.333 10.421 0 20.406 1.868 29.638 5.288zM1024 896h-85.333c0-274.69-178.423-469.333-426.667-469.333-248.235 0-426.667 194.649-426.667 469.333h-85.333c0-320.236 214.899-554.667 512-554.667 297.109 0 512 234.426 512 554.667zM469.333 128h85.333v128h-85.333v-128zM85.333 293.291l60.331-60.331 90.539 90.453-60.331 60.373-90.539-90.496zM787.541 323.499l90.581-90.453 60.288 60.416-90.581 90.453-60.288-60.416z",
  download: "M279.273 685.525v-685.525h93.091v689.425c28.109-28.774 63.956-66.182 107.498-112.184l67.607 63.993c-177.38 187.397-190.684 200.731-219.58 200.731-28.827 0-42.309-13.452-220.317-200.653l67.461-64.148c41.83 43.99 76.589 80.122 104.24 108.361zM0 1024v-93.091h651.636v93.091h-651.636z",
  edit: "M280.184 528.399l114.503 152.382 499.677-379.473-108.99-150.012-505.19 377.103zM320.521 737.044l-114.349-152.177c-3.476 3.93-6.279 8.436-8.268 13.341l-59.662 147.047h155.88c9.445 0 18.648-2.873 26.399-8.211zM805.258 20.288l217.988 300.035-644.673 489.588c-24.29 18.447-53.951 28.433-84.452 28.433h-294.111l111.632-275.137c9.581-23.614 25.442-44.157 45.864-59.401l647.752-483.519zM0 1024v-93.091h1024v93.091h-1024z",
  efficiency: "M576 192v-128h256v128h-256zM576 448v-128h384v128h-384zM576 704v-128h512v128h-512zM576 960v-128h640v128h-640zM208.77 583.604h-79.108c-17.574 0-34.852-4.522-50.173-13.13-49.315-27.71-66.829-90.15-39.12-139.465l223.835-398.36 111.591 62.702-202.423 360.253h84.145c55.202 0 99.951 44.75 99.951 99.951 0 17.645-4.671 34.976-13.539 50.231l-224.599 386.377-110.662-64.327 200.101-344.232z",
  energy: "M-0 977.461l-0.115-813.874 0.035-35.636c0.069-70.686 57.391-127.952 128.077-127.952l489.269-0c70.395 0 127.461 57.066 127.461 127.461v896.539h-744.721l-0.007-46.539zM93.084 930.909h558.552v-803.448c0-18.982-15.388-34.37-34.37-34.37h-489.269c-19.309 0-34.967 15.643-34.986 34.952l-0.035 35.584 0.108 767.283zM327.090 563.286c-14.098 0-27.889-4.117-39.679-11.846-33.429-21.914-42.764-66.779-20.85-100.208l159.966-244.022 77.854 51.036-138.941 211.949h56.085c38.567 0 69.832 31.265 69.832 69.832 0 13.975-4.193 27.629-12.037 39.195l-161.525 238.175-77.045-52.25 136.898-201.862h-50.559z",
  engine: "M256.847 392.533v338.247l149.946 88.42h362.054v-426.667h-512zM554.667 221.867v85.333h-85.333v-85.333h-128v-85.333h341.333v85.333h-128zM938.667 477.867v-128h85.333v341.333h-85.333v-128h-84.486v298.667c0 23.564-19.103 42.667-42.667 42.667h-416.364c-7.623 0-15.106-2.042-21.672-5.914l-180.969-106.714c-13.010-7.672-20.994-21.649-20.994-36.753v-191.953h-86.181v128h-85.333v-341.333h85.333v128h86.181v-128c0-23.564 19.103-42.667 42.667-42.667h597.333c23.564 0 42.667 19.103 42.667 42.667v128h84.486z",
  entertainment: "M731.354 360.411l-438.478 115.233c-0 84.081-0 120.467-0 230.196-0 125.666-0 125.666-0 174.329h-0.325c-1.31 79.66-66.294 143.832-146.266 143.832-80.791 0-146.286-65.494-146.286-146.286s65.494-146.286 146.286-146.286c17.212 0 33.729 2.972 49.067 8.432 0-10.206 0-21.489 0-34.021 0-164.167 0-164.167 0-405.294-0.048-53.69 35.145-92.511 85.067-104.772l548.458-162.842v791.309c0.050 1.565 0.074 3.136 0.074 4.713 0 80.791-65.494 146.286-146.286 146.286s-146.286-65.494-146.286-146.286c0-80.791 65.494-146.286 146.286-146.286 17.070 0 33.457 2.924 48.687 8.298v-330.553zM731.354 259.576v-95.965l-425.376 126.249c-11.064 2.729-13.107 4.983-13.102 10.641-0 27.758-0 52.321-0 74.307l438.478-115.233zM146.286 926.476c26.93 0 48.762-21.831 48.762-48.762s-21.831-48.762-48.762-48.762c-26.93 0-48.762 21.831-48.762 48.762s21.831 48.762 48.762 48.762zM682.667 877.714c26.93 0 48.762-21.831 48.762-48.762s-21.831-48.762-48.762-48.762c-26.93 0-48.762 21.831-48.762 48.762s21.831 48.762 48.762 48.762z",
  exterior: "M554.74 370.010l20.715 31.603c68.955 105.196 103.237 176.245 103.237 225.223 0 81.445-70.028 135.715-150.714 135.715-79.281 0-146.452-53.085-150.575-132.936-2.651-51.342 33.717-123.111 108.968-229.566l17.846-25.245c-23.587 1.62-47.978 2.46-73.059 2.46-132.825 0-246.229-23.429-323.374-61.291l0.005 517.749c6.18 8.54 16.67 18.087 32.954 27.896 54.7 32.947 148.618 53.985 290.939 53.985 199.887 0 304.994-32.146 322.844-77.566l0.003-522.326c-51.905 25.557-120.218 44.582-199.79 54.3zM0.002 188.302c0.652-105.314 182.816-188.302 431.156-188.302 248.015 0 430.501 83.31 431.156 188.302l0.002 664.376-1.596 6.412c-29.011 116.534-174.814 164.302-429.037 164.302-160.451 0-273.196-25.255-346.554-69.441-28.556-17.201-49.874-36.601-64.635-57-5.808-8.026-10.353-15.884-13.737-23.363-1.407-3.111-2.064-4.776-3.535-8.916-0.65-1.983-0.65-1.983-1.255-4.109-0.317-1.184-0.317-1.184-0.751-3.081-0.274-1.278-0.274-1.278-0.726-4.145-0.241-2.056-0.294-2.512-0.27-7.266l-0.225-657.769zM115.293 192.501c11.559 10.883 29.82 22.442 53.8 33.025 62.208 27.455 151.147 43.948 262.065 43.948 110.861 0 199.768-16.571 261.985-44.15 23.976-10.628 42.246-22.235 53.82-33.167 1.331-1.257 2.462-2.386 3.413-3.306-0.571-0.584-1.217-1.246-1.948-1.976-10.848-10.829-28.633-22.577-52.321-33.418-62.177-28.455-152.161-45.668-264.949-45.668-112.879 0-202.887 17.147-264.988 45.508-23.681 10.815-41.438 22.537-52.253 33.349-0.864 0.864-1.61 1.633-2.254 2.294 0.994 1.009 2.195 2.209 3.63 3.561zM485.049 624.056c0.921 17.837 17.201 30.704 42.928 30.704 26.3 0 42.925-12.884 42.925-27.925 0-13.938-14.1-47.323-42.166-95.988-29.776 48.158-44.323 80.885-43.687 93.21z",
  favourite: "M462.5 821.9l11.7 8.6c26.9 19.7 64.1 17 87.9-6.4 22.4-22 98.6-103.5 161-173.6 90.8-102 157.7-184.8 183.9-230.3 36-62.6 23.5-141.8-30.1-190.3C814 173.1 717 178 660.2 240.8l-145.5 161-146.9-160.2c-48.4-52.8-126.4-66.1-189.5-32.2-76.2 40.9-104.9 135.8-64 212.1C151 489.9 268 625 462.5 821.9zm51.7-559.3l76.5-84.6c91.5-101.2 247.7-109.1 349-17.6 86.3 78 106.5 205.6 48.5 306.5-52.5 91.3-264.9 329.9-360.3 423.9-56.5 55.7-145.1 62.1-209.1 15.1L404 895.2l-5.6-4.8c-202.7-205-324-345-366.7-424.7C-33.6 343.9 12.2 192.1 134 126.8c100.9-54.1 225.6-32.9 302.9 51.5l77.3 84.3z",
  "fast-forward": "M934 528.4L539.8 331.3v394.2L934 528.4zM449.8 658.2L133.3 816.8C88.1 838.7 33 821 10 776.4a89.8 89.8 0 0 1-10-41.2V332.4c0-50.6 41.7-91 92.5-91 14.2 0 28.2 3.2 40.4 9.1l317 148.7v-68c0-49.7 40.3-90 90-90 14 0 27.7 3.3 40.2 9.5l394.2 197.1c44.4 22.2 62.5 76.3 40.2 120.7-8.7 17.4-22.8 31.5-40.2 40.2L580 806c-44.4 22.2-98.5 4.2-120.7-40.2-6.2-12.5-9.5-26.3-9.5-40.2v-67.4zm0-100.6v-58.9l-355.7-167c-.5-.2-1-.4-1.6-.4-1.7 0-2.5.8-2.5 1v402.8c.5.9 2.5 1.5 3.6 1l356.2-178.5z",
  filter: "M104.792 93.163l220.257 407.324v307.64c0 4.387 2.311 8.449 6.083 10.691l170.83 101.525v-420.312l6.017-10.654 223.118-394.781-626.305-1.433zM231.958 524.044l-223.861-413.834c-19.701-36.146-6.369-81.42 29.777-101.121 10.944-5.965 23.208-9.090 35.672-9.090l696.048 1.593c38.068 0 68.928 30.86 68.928 68.928 0 10.025-2.187 19.93-6.408 29.023l-14.457 30.793-222.604 394.164-0.005 418.629c-0.682 46.583-36.623 81.488-80.32 80.849-13.835-0.202-27.374-4.029-39.269-11.098l-191.886-114.038c-32.001-19.018-51.615-53.49-51.615-90.716v-284.083z",
  fuel: "M938.7 232.8v-40c0-39.8-15.7-52.2-84.2-52.2V55.4c111.8 0 169.6 45.5 169.6 137.5v625.6c0 73.4-59.5 132.8-132.8 132.8s-132.8-59.5-132.8-132.8v-30.2c0-41.8-33.9-75.6-75.6-75.6V994H0v-42.7l.2-746-.1-32.6C0 108 52.4 55.5 117.1 55.3h448.8c64.5 0 116.8 52.3 116.8 116.8v455.1c88.9 0 161 72.1 161 161v30.2c0 26.2 21.3 47.5 47.5 47.5s47.5-21.3 47.5-47.5v-300c-49.5-2.3-95.8-32.1-116.9-80.7-7.3-16.9-11.1-35.1-11.1-53.4v-19c0-71.6 56.9-130 128-132.5zM85.4 908.7h512V172.2c0-17.4-14.1-31.5-31.5-31.5H117.3c-17.6 0-31.8 14.3-31.8 31.9l.1 32.7-.2 703.4zm853.3-475.8V318.3c-23.9 2.3-42.7 22.5-42.7 47.1v19c0 6.7 1.4 13.3 4 19.5a48.98 48.98 0 0 0 38.7 29zm-597.4-36.2c-47.1 0-85.3-38.2-85.3-85.3s38.2-85.3 85.3-85.3 85.3 38.2 85.3 85.3-38.1 85.3-85.3 85.3z",
  facebook: "M512,0C230.89,0,3,230.19,3,514.14c0,254.67,183.51,465.58,424.11,506.43V621.41H304.33V477.77H427.11V371.85C427.11,249,501.42,182,610,182c52,0,96.66,3.91,109.63,5.64V316.08l-75.28,0c-59,0-70.4,28.32-70.4,69.9v91.67H714.74L696.38,621.33H573.92V1024C825.76,993,1021,776.76,1021,514,1021,230.19,793.11,0,512,0Z",
  guide: "M108.396 0h684.657c67.683 0 122.55 55.193 122.55 123.276v769.637c0 67.302-54.238 121.862-121.144 121.862h-686.063v-1014.775zM209.297 101.499v811.778h585.162c11.18 0 20.243-9.117 20.243-20.363v-769.637c0-12.028-9.693-21.778-21.649-21.778h-583.756zM461.55 735.865h100.901v101.499h-100.901v-101.499zM562.45 659.741h-100.901c0-164.935 151.351-152.248 151.351-253.746 0-55.824-45.405-101.499-100.901-101.499s-100.901 45.674-100.901 101.499h-100.901c0-112.156 90.306-202.997 201.802-202.997s201.802 90.841 201.802 202.997c0 126.873-151.351 139.561-151.351 253.746z",
  info: "M0 512c0-296.125 215.879-512 512-512 296.129 0 512 215.871 512 512 0 296.121-215.875 512-512 512-296.116 0-512-215.884-512-512zM93.091 512c0 244.704 174.205 418.909 418.909 418.909 244.712 0 418.909-174.201 418.909-418.909 0-244.716-174.193-418.909-418.909-418.909-244.708 0-418.909 174.197-418.909 418.909zM465.455 372.364h93.091v418.909c-15.515 0-31.030 0-46.545 0s-31.030 0-46.545 0v-418.909zM512 325.818c-25.706 0-46.545-20.839-46.545-46.545s20.839-46.545 46.545-46.545c25.706 0 46.545 20.839 46.545 46.545s-20.839 46.545-46.545 46.545z",
  interior: "M104.968 461.316h261.305c19.949-61.241 75.895-102.916 145.727-102.916 69.638 0 125.465 41.44 145.559 102.4h261.421c-22.207-213.274-183.526-358.4-406.979-358.4-223.625 0-385.030 145.364-407.032 358.916zM105.075 563.716c20.663 196.327 159.296 334.806 355.725 355.263v-261.421c-44.914-14.806-79.232-49.010-94.187-93.842h-261.538zM918.979 563.2h-261.421c-14.862 45.086-49.272 79.496-94.358 94.358v261.421c196.607-20.473 335.305-159.174 355.779-355.779zM0 512c0-297.465 214.549-512 512-512 297.465 0 512 214.535 512 512 0 297.451-214.535 512-512 512-297.451 0-512-214.549-512-512zM460.8 512c0 31.213 19.987 51.2 51.2 51.2 31.215 0 51.2-19.986 51.2-51.2 0-31.216-19.984-51.2-51.2-51.2-31.214 0-51.2 19.985-51.2 51.2z",
  internet: "M592.768 99.725c79.641 133.899 126.534 255.661 140.111 365.73h195.874c-18.142-193.162-149.288-334.223-335.986-365.73zM638.948 465.455c-13.972-95.209-57.36-203.91-130.703-325.682-72.934 121.936-116.324 230.638-130.687 325.682h261.389zM596.981 923.543c184.452-32.907 313.765-173.298 331.772-364.998h-192.897c-7.477 109.772-54.093 231.272-138.875 364.998zM642.49 558.545h-269.010c7.536 95.491 51.986 206.126 134.43 331.387 81.991-125.486 126.504-236.126 134.581-331.387zM422.873 101.216c-182.24 34.259-309.755 173.976-327.626 364.238h188.329c13.928-109.408 60.542-230.672 139.297-364.238zM417.631 921.765c-84.396-132.708-130.553-253.623-137.482-363.22h-184.902c17.7 188.427 142.947 327.285 322.384 363.22zM0 512c0-296.125 215.879-512 512-512 296.129 0 512 215.871 512 512 0 296.121-215.875 512-512 512-296.116 0-512-215.884-512-512z",
  locate: "M465.455 928.678c-202.616-19.667-350.465-167.517-370.133-370.133h-95.322v-93.091h95.322c19.666-202.621 167.514-350.466 370.133-370.133v-95.322h93.091v95.322c202.624 19.666 350.467 167.509 370.133 370.133h95.322v93.091h-95.322c-19.666 202.619-167.512 350.466-370.133 370.133v95.322h-93.091v-95.322zM465.455 835.037v-90.309h93.091v90.31c150.618-18.343 258.148-125.875 276.491-276.491h-90.31v-93.091h90.31c-18.342-150.622-125.87-258.149-276.492-276.492v90.31h-93.091v-90.31c-150.617 18.343-258.148 125.873-276.491 276.491h90.31v93.091h-90.309c18.344 150.614 125.877 258.147 276.491 276.491z",
  login: "M597.688 460.8c-31.384-30.628-71.879-69.421-121.443-116.335l70.393-74.368c206.137 195.118 220.804 209.752 220.804 241.538 0 31.71-14.797 46.54-220.719 242.349l-70.563-74.207c49.409-46.983 89.806-85.856 121.15-116.577h-597.31v-102.4h597.688zM0 332.8c69.62-200.661 257.221-332.8 497.346-332.8 305.979 0 526.654 214.535 526.654 512 0 297.451-220.675 512-526.654 512-240.123 0-427.723-132.145-497.344-332.8h112.805c63.505 141.24 204.399 230.4 384.539 230.4 247.801 0 421.323-168.698 421.323-409.6 0-240.911-173.517-409.6-421.323-409.6-180.146 0-321.038 89.157-384.541 230.4h-112.805z",
  logout: "M1238.495 448c-41.683-41.488-96.133-94.625-163.295-159.352l88.824-92.165c263.175 253.636 287.779 278.632 287.779 318.555 0 40.52-23.607 63.858-288.446 311.844l-87.488-93.434c68.603-64.238 123.94-116.742 165.946-157.449h-857.814v-128h854.495zM952.614 320.968h-153.999c-57.845-108.409-167.785-174.682-306.625-174.682-208.198 0-351.422 149.046-351.422 365.714 0 216.66 143.23 365.714 351.422 365.714 138.219 0 247.8-65.69 305.85-173.245h154.265c-68.349 193.191-240.769 319.531-460.115 319.531-285.826 0-491.991-214.549-491.991-512 0-297.465 206.164-512 491.991-512 219.892 0 392.624 126.961 460.623 320.968z",
  mail: "M930.9 326.9c-42.8 37.8-87.2 75.6-133 113.4-72.8 60-149.6 120-230.2 179.9-33.1 24.6-78.4 24.5-111.4-.2-79.9-60-156.2-119.9-228.9-179.7-46.2-38-91-76.1-134.3-114.2v468.7h837.8V326.9zm-783.8-77.3c44.9 39.6 91.4 79.2 139.5 118.8 71.6 59 146.8 118 225.5 177.1 79.4-59 154.9-118.1 226.6-177.1 48.1-39.6 94.4-79.2 139-118.7-121.9-5.5-243.8-8.2-365.7-8.2-121.6-.1-243.3 2.7-364.9 8.1zm876.9 2.6v542.6c0 51.4-41.7 93.1-93.1 93.1H93.1C41.7 887.9 0 846.2 0 794.8V252.2c0-49.6 38.8-90.4 88.3-93 141.2-7.3 282.5-10.9 423.7-10.9s282.5 3.6 423.7 10.9c49.5 2.6 88.3 43.5 88.3 93z",
  manual: "M486.297 716.8h95.848v102.4h-300.545v-102.4h102.297v-204.8h-102.297v-102.4h204.697v307.2zM0 0h694.829c68.688 0 124.371 55.683 124.371 124.371v776.472c0 67.9-55.044 122.944-122.944 122.944h-696.256v-1023.787zM102.4 102.4v818.987h593.856c11.346 0 20.544-9.198 20.544-20.544v-776.472c0-12.134-9.837-21.971-21.971-21.971h-592.429zM409.6 358.4c-42.415 0-76.8-34.385-76.8-76.8s34.385-76.8 76.8-76.8c42.415 0 76.8 34.385 76.8 76.8s-34.385 76.8-76.8 76.8z",
  media: "M0 512c0-296.125 215.879-512 512-512 296.129 0 512 215.871 512 512 0 296.121-215.875 512-512 512-296.116 0-512-215.884-512-512zM93.091 512c0 244.704 174.205 418.909 418.909 418.909 244.712 0 418.909-174.201 418.909-418.909 0-244.716-174.193-418.909-418.909-418.909-244.708 0-418.909 174.197-418.909 418.909zM325.818 356.651c0-14.979 4.26-29.648 12.283-42.297 23.36-36.827 72.15-47.744 108.977-24.384l5.416 3.483 252.589 145.872 5.587 3.792 1.67 1.32c6.337 5.009 11.915 10.909 16.561 17.517 26.588 37.812 17.489 90.018-22.913 118.301l-255.158 154.95-4.706 2.337c-11.51 5.716-24.187 8.691-37.038 8.691-45.988 0-83.268-37.28-83.268-83.268v-306.314zM418.909 645.871l223.038-135.51-223.038-128.847v264.357z",
  menu: "M0 256v-85.3h1024V256H0zm0 298.7v-85.3h597.3v85.3H0zm0 298.6V768h853.3v85.3H0z",
  "menu-close": "M571.64 495.421l419.203 419.203-76.219 76.219-419.203-419.203-419.203 419.203-76.219-76.219 419.203-419.203-419.203-419.203 76.219-76.219 419.203 419.203 419.203-419.203 76.219 76.219-419.203 419.203z",
  mobile: "M0 744.727v-93.091l0.086-502.678 1.156-19.037c4.431-72.993 64.92-129.921 138.047-129.921h373.264c76.814 0 139.084 62.27 139.084 139.084v745.739c0 76.865-62.312 139.177-139.177 139.177h-371.505c-77.847 0-140.955-63.108-140.955-140.955l0-8.513 0.015-129.805zM93.091 744.727v138.318c0 26.435 21.429 47.864 47.864 47.864h371.505c25.452 0 46.086-20.633 46.086-46.086v-140.096h-465.455zM93.091 651.636h465.455v-512.552c0-25.401-20.592-45.993-45.993-45.993h-373.264c-23.905 0-43.679 18.61-45.127 42.471l-1.070 16.216v499.858zM279.273 837.818c0-27.042 19.504-46.545 46.545-46.545 27.042 0 46.545 19.503 46.545 46.545 0 27.041-19.503 46.545-46.545 46.545-27.041 0-46.545-19.504-46.545-46.545z",
  navigation: "M512.299 116.439c-0.143-0.047-98.99 231.572-296.543 694.855l-33.941 75.172c-1.278 2.83-1.543 6.013-0.752 9.016 1.648 6.255 7.415 10.329 13.623 10.076l257.574-125.038c38.132-16.498 81.359-16.492 121.297 0.848l255.517 124.073c2.249 0.061 4.496-0.417 6.546-1.416 6.988-3.403 9.894-11.828 6.491-18.816l-36.424-74.941-293.388-693.829zM490.031 865.614l-260.243 126.383-8.475 3.14c-56.943 15.002-115.266-18.997-130.268-75.94-6.233-23.659-4.14-48.742 5.928-71.040l33.538-74.254 297.408-698.425 2.472-4.962c25.911-45.073 83.438-60.601 128.497-34.698 14.433 8.297 26.407 20.275 34.7 34.711l2.51 5.057 294.308 696.173 35.401 72.689c25.914 53.211 3.786 117.355-49.425 143.269-22.772 11.090-48.735 13.756-73.285 7.525l-8.881-3.245-259.482-126.047c-14.25-6.17-30.383-6.282-44.701-0.337z",
  offer: "M993.792 490.496l-460.8-460.8c-18.432-18.432-44.032-29.696-72.192-29.696h-358.4c-56.32 0-102.4 46.080-102.4 102.4v358.4c0 28.16 11.264 53.76 30.208 72.704l460.8 460.8c18.432 18.432 44.032 29.696 72.192 29.696s53.76-11.264 72.192-30.208l358.4-358.4c18.944-18.432 30.208-44.032 30.208-72.192s-11.776-54.272-30.208-72.704zM563.2 922.112l-460.8-461.312v-358.4h358.4v-0.512l460.8 460.8-358.4 359.424zM230.4 307.2c42.415 0 76.8-34.385 76.8-76.8s-34.385-76.8-76.8-76.8c-42.415 0-76.8 34.385-76.8 76.8s34.385 76.8 76.8 76.8zM452.918 570.329l-104.232-30.020 91.412-65.772 1.353-109.965 86.515 63.582 105.068-37.943-37.943 105.068 63.582 86.515-109.965 1.353-65.772 91.412-30.020-104.232z",
  pause: "M0 0h170.667v1024h-170.667v-1024zM512 0h170.667v1024h-170.667v-1024z",
  pin: "M186.182 328.824c0-189.593 137.751-328.824 325.818-328.824 188.072 0 325.818 139.229 325.818 328.824 0 144.762-50.577 303.835-133.186 458.165-70.566 131.831-148.965 237.011-192.632 237.011-43.569 0-121.385-104.495-191.924-236.073-83.005-154.83-133.895-314.339-133.895-459.103zM512.037 913.733c5.549-6.752 11.629-14.535 18.045-23.143 29.841-40.034 62.641-91.791 92.478-147.533 76.005-141.992 122.168-287.182 122.168-414.233 0-138.405-96.292-235.733-232.727-235.733-136.43 0-232.727 97.332-232.727 235.733 0 127.004 46.462 272.634 122.848 415.119 29.765 55.521 62.432 107.067 92.098 146.905 6.334 8.505 12.336 16.201 17.818 22.886zM372.364 325.818c0-81.127 58.513-139.636 139.636-139.636 81.127 0 139.636 58.51 139.636 139.636 0 81.123-58.51 139.636-139.636 139.636-81.123 0-139.636-58.513-139.636-139.636z",
  phone: "M704.534 144.872l-50.936-24.438c-0.908-0.428-0.908-0.428-1.828-0.829-23.635-10.11-50.99 0.854-61.1 24.489l-23.104 54.011c-8.179 19.12-2.682 41.346 13.466 54.449l62.925 51.060-221.258 461.28-70.313-39.532c-23.825-12.59-51.981-3.884-63.977 18.851l-32.812 62.184c-11.92 24.751-2.153 52.556 21.021 63.682l55.187 26.495c55.717 26.749 122.579 3.523 149.713-52.008l272.424-557.523c25.666-53.489 3.397-116.835-49.408-142.17zM528.245 329.603l-5.869-4.762c-48.445-39.31-64.936-105.988-40.399-163.348l23.104-54.011c30.33-70.904 112.397-103.796 183.301-73.466 2.76 1.204 2.76 1.204 5.485 2.488l50.936 24.438c99.159 47.575 140.976 166.526 93.401 265.685-0.206 0.429-91.222 186.698-273.046 558.808-49.595 101.498-171.804 143.952-273.643 95.059l-55.187-26.495c-69.522-33.377-98.823-116.793-65.446-186.315 1.161-2.38 1.161-2.38 2.382-4.73l32.812-62.184c33.64-63.754 109.636-90.734 175.117-64.593l147.051-306.573z",
  dashboard: "M541.6 608.6l148.7-148.7 60.3 60.3-154.4 154.5c.7 4.5 1.1 9.2 1.1 13.9 0 47.1-38.2 85.3-85.3 85.3s-85.3-38.2-85.3-85.3c0-47.1 38.2-85.3 85.3-85.3 10.4 0 20.4 1.9 29.6 5.3zM1024 774h-85.3c0-274.7-178.4-469.3-426.7-469.3-248.2 0-426.7 194.6-426.7 469.3H0c0-320.2 214.9-554.7 512-554.7s512 234.5 512 554.7z",
  play: "M136.533 877.765l689.841-352.152-689.841-352.152v704.303zM0 877.765v-704.303c0-75.405 61.128-136.533 136.533-136.533 21.581 0 42.855 5.116 62.077 14.928l689.841 352.152c67.161 34.284 93.812 116.522 59.528 183.682-13.077 25.617-33.91 46.451-59.528 59.528l-689.841 352.152c-67.161 34.284-149.398 7.633-183.682-59.528-9.812-19.222-14.928-40.496-14.928-62.077z",
  profile: "M781.113 486.426l124.634 57.487 4.242 2.228c45.531 26.993 67.884 64.146 77.029 119.33l36.023 228.625c6.628 42.062-21.87 80.974-64.546 84.671-85.628 7.326-308.192 15.101-431.481 15.101h-30.029c-123.289 0-345.852-7.775-431.53-15.105-42.626-3.692-71.125-42.604-64.497-84.666l36.082-228.992c9.085-54.817 31.439-91.97 81.072-121.127l124.471-57.911c21.944 26.124 47.903 48.593 77.216 66.748l-35.135 16.347-123.181 57.056c-19.994 11.853-27.734 24.719-32.546 53.743l-32.74 207.788c95.057 6.542 289.779 13.029 400.787 13.029h30.029c111.023 0 305.782-6.489 400.787-13.029l-32.681-207.422c-4.716-28.455-12.122-41.42-30.735-52.974l-160.79-74.164c29.426-18.136 55.486-40.612 77.518-66.762zM768 255.971c0 148.703-107.28 255.971-256 255.971s-256-107.268-256-255.971c0-148.719 107.28-255.971 256-255.971s256 107.252 256 255.971zM674.909 255.971c0-97.297-65.593-162.88-162.909-162.88s-162.909 65.582-162.909 162.88c0 97.288 65.599 162.88 162.909 162.88s162.909-65.592 162.909-162.88z",
  progress: "M508.369 0v91.11c-226.435 0-409.996 183.562-409.996 409.996s183.562 409.996 409.996 409.996c226.435 0 409.996-183.562 409.996-409.996h91.11c0 276.753-224.353 501.106-501.106 501.106s-501.106-224.353-501.106-501.106c0-276.753 224.353-501.106 501.106-501.106zM554.14 455.551v-91.11h91.11v182.221h-182.221v227.776h-91.11v-318.886h182.221zM599.695 227.776l91.11 136.665h-182.221l91.11-136.665z",
  radio: "M199.874 307.2h711.58c62.157 0 112.546 50.388 112.546 112.546v491.709c0 62.157-50.388 112.546-112.546 112.546h-804.305c-59.177 0-107.149-47.972-107.149-107.149v-491.859c0-58.123 42.098-106.413 97.465-116.044-0.957-27.152 1.788-53.353 8.323-78.5 8.983-34.569 32.082-63.072 64.128-88.071 12.361-9.642 25.347-18.141 38.416-25.596 7.87-4.49 14.39-7.828 17.806-9.437 53.295-27.277 184.393-47.812 736.075-107.051l10.933 101.815c-300.725 32.291-486.325 54.886-589.638 71.069-74.078 11.603-94.705 17.128-110.715 25.322-4.081 1.969-8.198 4.076-13.721 7.227-9.064 5.171-18.026 11.036-26.174 17.392-15.938 12.432-25.797 24.598-28.002 33.084-4.125 15.874-5.829 32.838-5.023 51zM117.791 409.6c-8.5 0-15.391 6.891-15.391 15.391v491.859c0 2.623 2.126 4.749 4.749 4.749h804.305c5.603 0 10.146-4.542 10.146-10.146v-491.709c0-5.603-4.542-10.146-10.146-10.146h-793.663zM204.8 716.8v-102.4h614.4v102.4h-614.4z",
  radiobox: "M0 512c0-295.362 216.642-512 512-512 295.366 0 512 216.634 512 512 0 295.358-216.638 512-512 512-295.354 0-512-216.646-512-512zM146.286 512c0 214.562 151.152 365.714 365.714 365.714 214.57 0 365.714-151.147 365.714-365.714 0-214.574-151.14-365.714-365.714-365.714-214.567 0-365.714 151.145-365.714 365.714zM292.571 512c0-127.485 91.95-219.429 219.429-219.429 127.485 0 219.429 91.944 219.429 219.429 0 127.479-91.944 219.429-219.429 219.429-127.479 0-219.429-91.95-219.429-219.429z",
  range: "M867.381 574.912c110.525 15.501 182.524 22.52 276.719 28.464l7.203-112.868c-90.754-5.727-159.68-12.417-266.115-27.312 15.811-32.019 40.863-74.194 69.856-115.168 7.178-10.144 14.236-20.256 21.174-30.336 116.82 13.55 236.632 20.324 388.639 20.338v448.344h-387.048l-0.003 113.091-0.001 11.439-110.425 0.001c0.001-177.228 0.001-198.339 0.001-335.994zM849.894 300.427c-43.405 63.746-96.248 154.683-96.248 211.573 0 131.227-0 264.195-0.001 398.904-0.001 62.461 50.92 113.096 113.735 113.096l110.427-0.001c62.814-0 113.735-50.634 113.735-113.095l0-11.436h273.316v-113.095h0.222v-448.344c0.085 0 0.169 0 0.254 0l-0.254-60.302v-164.633h-0.222v-113.095h-163.142c-99.662-0-192.049 51.884-243.507 136.754-11.995 19.783-24.515 39.723-37.561 59.82-52.068-7.279-104.741-15.957-160.927-26.034l-20.187 111.299c38.013 6.817 74.53 13.013 110.36 18.588zM1364.857 224.935c-123.202 0-223.953-4.537-319.151-13.602 3.338-5.405 6.639-10.8 9.905-16.186 30.875-50.921 86.307-82.052 146.104-82.052h163.142v111.84zM497.952 574.912c0 137.655 0 158.766 0.001 335.994l-110.425-0.001-0.001-11.439-0.003-113.091h-387.048v-448.344c152.007-0.014 271.819-6.787 388.639-20.338 6.938 10.081 13.997 20.193 21.174 30.336 28.993 40.974 54.045 83.149 69.856 115.168-106.435 14.895-175.362 21.585-266.115 27.312l7.203 112.868c94.195-5.944 166.194-12.963 276.719-28.464zM515.439 300.427c35.829-5.575 72.347-11.771 110.36-18.588l-20.187-111.299c-56.186 10.076-108.858 18.755-160.927 26.034-13.046-20.097-25.566-40.037-37.561-59.82-51.458-84.869-143.845-136.754-243.507-136.754h-163.142v113.095h-0.222v164.633l-0.254 60.302c0.085 0 0.169-0 0.254-0v448.344h0.222v113.095h273.316l0 11.436c0 62.46 50.921 113.095 113.735 113.095l110.427 0.001c62.815-0 113.736-50.635 113.735-113.096-0.001-134.709-0.001-267.677-0.001-398.904 0-56.89-52.843-147.827-96.248-211.573zM0.476 224.935v-111.84h163.142c59.797-0 115.23 31.131 146.104 82.052 3.266 5.386 6.567 10.781 9.905 16.186-95.197 9.065-195.948 13.602-319.151 13.602z",
  reload: "M390.811 258.491c-167.949 14.483-297.72 157.243-297.72 314.864 0 186.41 140.713 334.281 325.818 334.281 180.699 0 325.818-148.8 325.818-334.281 0-100.653-60.997-198.663-155.75-270.212l56.097-74.29c116.059 87.638 192.744 210.854 192.744 344.502 0 236.379-186.269 427.372-418.909 427.372-237.579 0-418.909-190.554-418.909-427.372 0-209.803 173.313-396.413 395.091-408.529-24.508-24.381-55.366-54.584-92.546-90.576l64.748-66.885c171.235 165.764 179.666 174.369 179.666 202.33 0 28.028-8.321 36.484-179.74 201.695l-64.6-67.028c35.011-33.743 64.417-62.376 88.192-85.873z",
  remove: "M0 512c0-296.125 215.879-512 512-512 296.129 0 512 215.871 512 512 0 296.121-215.875 512-512 512-296.116 0-512-215.884-512-512zM93.091 512c0 244.704 174.205 418.909 418.909 418.909 244.712 0 418.909-174.201 418.909-418.909 0-244.716-174.193-418.909-418.909-418.909-244.708 0-418.909 174.197-418.909 418.909zM325.818 558.545v-93.091h372.364v93.091h-372.364z",
  repeat: "M1309.182 365.714c35.202 25.148 76.011 53.925 122.402 86.312l-83.738 119.947c-313.74-219.030-323.845-226.609-323.845-279.402s10.105-60.372 323.845-279.402l83.738 119.947c-46.391 32.387-87.2 61.164-122.402 86.312h177.388c102.129 0 194.88 59.553 237.383 152.417 40.733 88.997 40.733 191.312 0 280.309-42.503 92.864-135.254 152.417-237.383 152.417h-389.427v-146.286h389.427c44.902 0 85.681-26.183 104.368-67.012 23.040-50.338 23.040-108.21 0-158.548-18.687-40.829-59.466-67.012-104.368-67.012h-177.388zM663.659 658.286c-35.202-25.148-76.011-53.925-122.402-86.312l83.738-119.947c313.74 219.030 323.845 226.609 323.845 279.402s-10.105 60.372-323.845 279.402l-83.738-119.947c46.391-32.387 87.2-61.164 122.402-86.312h-395.726c-102.129 0-194.88-59.553-237.383-152.417-40.733-88.997-40.733-191.312 0-280.309 42.503-92.864 135.254-152.417 237.383-152.417h609.65v146.286h-609.65c-44.902 0-85.681 26.183-104.368 67.012-23.040 50.338-23.040 108.21 0 158.548 18.687 40.829 59.466 67.012 104.368 67.012h395.726z",
  rewind: "M90 528.4l394.2 197.1V331.3L90 528.4zm484.2 129.8v67.3c0 14-3.3 27.7-9.5 40.2-22.2 44.4-76.3 62.5-120.7 40.2l-394.2-197c-17.4-8.7-31.5-22.8-40.2-40.2-22.2-44.4-4.2-98.5 40.2-120.7L444 250.8c12.5-6.2 26.3-9.5 40.2-9.5 49.7 0 90 40.3 90 90v68l317-148.7c12.2-5.9 26.2-9.1 40.4-9.1 50.8 0 92.5 40.4 92.5 91v402.8c0 14.3-3.4 28.5-10 41.2-23 44.5-78 62.3-123.3 40.4L574.2 658.2zm0-100.6l356.3 178.5c1.1.5 3.1-.1 3.6-1V332.4c0-.2-.8-1-2.5-1-.6 0-1.2.1-1.6.4L574.2 498.6v59z",
  route: "M0 260.526c0-150.388 113.508-260.526 267.13-260.526 153.626 0 267.13 110.137 267.13 260.526 0 112.795-40.82 236.047-107.407 355.469-57.993 104.009-121.186 185.396-159.723 185.396-38.462 0-101.173-80.844-159.155-184.673-66.904-119.806-107.975-243.39-107.975-356.192zM276.124 684.381c23.508-30.277 49.4-69.499 72.958-111.75 59.844-107.329 96.135-216.905 96.135-312.105 0-100.602-73.048-171.483-178.087-171.483-105.035 0-178.087 70.883-178.087 171.483 0 95.159 36.528 205.073 96.675 312.778 23.502 42.085 49.29 81.149 72.66 111.277 3.042 3.922 5.983 7.61 8.787 11.029 2.857-3.476 5.856-7.232 8.959-11.228zM287.402 717.788c0.282 0.245 0.51 0.433 0.677 0.559-0.063-0.047-0.125-0.095-0.188-0.142l-0.489-0.417zM267.13 178.087c51.733 0 89.043 37.31 89.043 89.043 0 51.731-37.31 89.043-89.043 89.043-51.731 0-89.043-37.313-89.043-89.043 0-51.733 37.313-89.043 89.043-89.043zM44.522 934.957h89.043v89.043h-89.043v-89.043zM222.609 934.957h89.043v89.043h-89.043v-89.043zM400.696 934.957h89.043v89.043h-89.043v-89.043zM578.783 934.957h89.043v89.043h-89.043v-89.043zM756.87 934.957h89.043v89.043h-89.043v-89.043zM934.957 934.957h89.043v89.043h-89.043v-89.043z",
  safety: "M40.367 69.297l52.602-7.044c61.956-8.297 123.567-18.992 184.695-32.059l141.245-30.194 138.744 29.878c61.657 13.278 123.822 24.078 186.344 32.375l53.399 7.086c23.137 3.070 40.422 22.801 40.422 46.141v403.464c0 154.431-79.031 298.119-209.455 380.815l-184.53 117.002c-15.216 9.648-34.633 9.648-49.849 0l-184.53-117.002c-130.423-82.696-209.455-226.384-209.455-380.815v-403.513c0-23.318 17.255-43.038 40.367-46.134zM93.091 156.158v362.785c0 122.548 62.715 236.572 166.213 302.195l159.606 101.199 159.606-101.199c103.497-65.623 166.213-179.647 166.213-302.195v-362.687l-12.977-1.722c-64.989-8.624-129.606-19.851-193.695-33.652l-119.218-25.673-121.713 26.019c-63.479 13.57-127.46 24.675-191.799 33.292l-12.234 1.638zM417.606 500.121l179.818-192.265 67.989 63.588-248.316 265.505-172.020-186.689 68.46-63.081 104.068 112.943z",
  save: "M563.2 644.097c32.843-35.657 74.912-82.251 126.16-139.735l76.435 68.143c-204.119 228.959-220.535 246.417-253.832 246.417-33.26 0-49.775-17.525-254.244-246.375l76.361-68.226c51.509 57.651 93.765 104.349 126.721 140.046v-644.22h102.4v643.95zM358.522-0v102.371h-256.032v819.158h819.11v-819.158h-255.589v-102.371l255.589 0.431c55.488 2.289 102.433 47.047 102.433 101.94v819.158c0 56.554-45.217 102.47-102.433 102.47l-819.11-1.39c-57.216 0-102.315-44.526-102.315-101.080l-0.293-819.158c0-54.903 47.11-99.664 102.608-101.94l256.032-0.431z",
  "screen-close": "M372.364 0h93.091v325.818c0 51.413-41.678 93.091-93.091 93.091h-325.818v-93.091h325.818v-325.818zM369.975 1022.721v-325.818h-325.818v-93.091h325.818c51.413 0 93.091 41.678 93.091 93.091v325.818h-93.091zM744.727 0v325.818h325.818v93.091h-325.818c-51.413 0-93.091-41.678-93.091-93.091v-325.818h93.091zM744.727 1024h-93.091v-325.818c0-51.413 41.678-93.091 93.091-93.091h325.818v93.091h-325.818v325.818z",
  "screen-open": "M93.091 418.909h-93.091v-325.818c0-51.413 41.678-93.091 93.091-93.091h325.977v93.091h-325.977v325.818zM93.091 605.091v325.818h325.687v93.091h-325.687c-51.413 0-93.091-41.678-93.091-93.091v-325.818h93.091zM931.003 418.909v-325.818h-293.108v-93.091h293.108c51.413 0 93.091 41.678 93.091 93.091v325.818h-93.091zM930.909 605.091h93.091v325.818c0 51.413-41.678 93.091-93.091 93.091h-325.755v-93.091h325.755v-325.818z",
  search: "M770.721 679.722l243.657 227.176-63.482 68.088-239.923-223.694c-73.682 69.207-175.028 109.799-292.064 109.799-243.369 0-418.909-175.54-418.909-418.909 0-243.38 175.54-418.909 418.909-418.909 243.38 0 418.909 175.529 418.909 418.909 0 90.371-24.204 171.39-67.097 237.54zM93.091 442.182c0 191.956 133.862 325.818 325.818 325.818 191.963 0 325.818-133.857 325.818-325.818 0-191.968-133.85-325.818-325.818-325.818-191.961 0-325.818 133.855-325.818 325.818z",
  seat: "M842.952 93.091c-25.531 0-46.297 20.566-46.543 46.097-0.775 80.253-13.737 164.191-38.765 251.778-31.641 110.729-78.529 201.322-141.023 271.293-7.682 8.601-11.897 19.747-11.83 31.279l0.575 97.897-46.982-0.163c-112.562-0.39-194.085-1.92-244.961-4.62-70.977-3.766-144.417-4.762-220.319-2.984l52.127 147.24h535.35c104.824-136.68 172.459-278.023 203.367-424.263 29.712-140.58 45.373-278.421 47.013-413.556h-88.008zM511.702 694.084c-0.203-34.595 12.444-68.034 35.489-93.836 52.53-58.814 92.964-136.938 120.945-234.859 22.807-79.816 34.495-155.503 35.187-227.099 0.738-76.59 63.036-138.29 139.63-138.29h180.578l0.53 46.009c1.805 156.762-14.553 316.74-49.034 479.887-35.334 167.181-113.923 327.426-235.244 480.473l-13.976 17.631h-646.372l-74.085-209.264c-17.158-48.465 8.221-101.663 56.686-118.821 9.287-3.288 19.037-5.081 28.886-5.311 78.237-1.833 154.048-0.805 227.433 3.088 41.773 2.216 106.31 3.638 193.369 4.247l-0.023-3.856z",
  "seat-air": "M511.702 694.084c-0.203-34.595 12.444-68.034 35.489-93.836 52.53-58.814 92.964-136.938 120.945-234.859 22.807-79.816 34.495-155.503 35.187-227.099 0.738-76.59 63.036-138.29 139.63-138.29h180.578l0.53 46.009c1.805 156.762-14.553 316.74-49.034 479.887-35.334 167.181-113.923 327.426-235.244 480.473l-13.976 17.631h-646.372l-74.085-209.264c-17.158-48.465 8.221-101.663 56.686-118.821 9.287-3.288 19.037-5.081 28.886-5.311 78.237-1.833 154.048-0.805 227.433 3.088 41.773 2.216 106.31 3.638 193.369 4.247l-0.023-3.856zM842.952 93.091c-25.531 0-46.297 20.566-46.543 46.097-0.775 80.253-13.737 164.191-38.765 251.778-31.641 110.729-78.529 201.322-141.023 271.293-7.682 8.601-11.897 19.747-11.83 31.279l0.575 97.897-46.982-0.163c-112.562-0.39-194.085-1.92-244.961-4.62-70.977-3.766-144.417-4.762-220.319-2.984l52.127 147.24h535.35c104.824-136.68 172.459-278.023 203.367-424.263 29.712-140.58 45.373-278.421 47.013-413.556h-88.008zM385.864 13.72l65.998 65.652c-22.274 22.392-32.953 48.576-32.953 81.189 0 17.59 3.915 31.741 14.77 57.050 2.249 5.211 2.249 5.211 4.643 10.713 18.689 43.061 26.505 69.947 27.082 109.377 1.145 78.22-15.923 135.964-53.080 198.169l-79.918-47.739c28.679-48.011 40.797-89.008 39.918-149.068-0.356-24.307-5.198-40.963-19.396-73.678-2.357-5.419-2.357-5.419-4.801-11.080-15.397-35.897-22.307-60.878-22.307-93.745 0-57.081 20.321-106.906 60.045-146.841zM150.78 15.040l68.524 63.011c-21.58 23.468-32.027 50.688-32.027 83.952 0 17.862 3.828 32.987 13.962 58.891 1.186 3.031 2.376 6.027 4.911 12.387 17.602 44.28 24.89 71.284 25.433 110.17 1.073 76.958-13.936 127.515-50.4 191.574l-80.902-46.052c28.753-50.511 39.044-85.177 38.221-144.224-0.346-24.846-5.112-42.501-18.857-77.080-2.508-6.293-3.805-9.557-5.097-12.858-13.997-35.776-20.361-60.923-20.361-92.808 0-56.43 19.094-106.182 56.594-146.963z",
  services: "M610.191 518.191c200.464 32.455 325.053 169.879 366.928 404.542l0.724 8.177h46.157v93.091h-1024v-93.091h46.934l0.724-8.177c41.829-234.402 166.188-371.781 366.261-404.433-25.655-25.319-41.555-60.498-41.555-99.39 0-77.119 62.517-139.636 139.636-139.636s139.636 62.517 139.636 139.636c0 38.837-15.855 73.971-41.445 99.282zM140.804 930.909h743.168c-42.020-221.899-162.507-327.125-371.584-327.125s-329.564 105.226-371.584 327.125zM512 465.455c25.706 0 46.545-20.839 46.545-46.545s-20.839-46.545-46.545-46.545c-25.706 0-46.545 20.839-46.545 46.545s20.839 46.545 46.545 46.545zM465.455 0h93.091v186.182h-93.091v-186.182zM145.872 116.364l80.619-46.545 93.091 161.238-80.619 46.545-93.091-161.238zM880.993 116.364l-93.091 161.238-80.619-46.545 93.091-161.238 80.619 46.545z",
  settings: "M1023.994 471.062v81.87c0 50.93-36.645 94.257-86.926 102.696l-38.103 6.35c-2.27 5.832-4.691 11.648-7.259 17.444l22.54 31.647c29.5 41.44 24.693 98.075-11.229 133.997l-57.951 57.951c-19.616 19.616-45.843 30.551-73.556 30.551-21.808 0-42.99-6.756-60.515-19.375l-31.421-22.46c-5.764 2.539-11.592 4.95-17.482 7.23l-6.473 38.156c-8.43 50.229-51.757 86.874-102.687 86.874h-82.064c-50.93 0-94.257-36.645-102.696-86.926l-6.36-38.158c-5.19-2.010-10.32-4.115-15.388-6.316l-32.129 22.867c-17.659 12.714-38.649 19.468-60.45 19.468-27.713 0-53.94-10.936-73.556-30.551l-57.79-57.79c-36.241-35.889-40.952-92.63-11.263-134.335l22.021-30.629c-2.577-5.747-5.019-11.548-7.322-17.396l-39.062-6.664c-50.229-8.43-86.874-51.757-86.874-102.687v-81.87c0-50.93 36.645-94.257 86.837-102.681l37.197-6.274c2.256-5.941 4.646-11.817 7.171-17.628l-23.012-32.332c-29.5-41.44-24.693-98.075 11.284-134.052l57.795-57.598c19.462-19.816 46.017-30.849 73.851-30.849 21.808 0 42.99 6.756 60.645 19.468l30.817 22.189c5.74-2.574 11.535-5.013 17.377-7.313l6.664-39.062c8.43-50.229 51.757-86.874 102.687-86.874h81.87c50.93 0 94.257 36.645 102.681 86.837l6.278 37.221c6.682 2.552 13.317 5.289 19.899 8.207l31.409-22.37c17.659-12.714 38.649-19.468 60.45-19.468 27.713 0 53.94 10.936 73.556 30.551l57.79 57.79c36.241 35.889 40.952 92.63 11.338 134.231l-22.46 31.421c2.539 5.764 4.95 11.592 7.23 17.482l38.202 6.481c50.433 8.487 87.14 52.152 86.439 102.679zM922.101 460.182l-93.632-15.884-8.466-27.333c-5.986-19.327-13.646-37.839-22.937-55.407l-13.447-25.427 55.178-77.193c3.172-4.456 2.685-10.321-1.217-14.185l-57.951-57.951c-2.213-2.213-4.786-3.286-7.731-3.286-2.302 0-4.26 0.63-6.251 2.063l-77.423 55.143-25.366-13.388c-18.7-9.869-37.878-17.779-57.343-23.602l-27.741-8.299-15.712-93.153c-0.903-5.381-5.408-9.191-10.88-9.191h-81.87c-5.472 0-9.977 3.81-10.901 9.314l-16.072 94.216-27.206 8.502c-18.904 5.908-37.364 13.691-55.017 23.197l-25.636 13.804-77.106-55.516c-1.725-1.242-3.861-1.923-6.251-1.923-2.967 0-5.558 1.076-7.786 3.341l-58.090 57.895c-3.82 3.82-4.329 9.813-1.214 14.188l55.461 77.922-13.258 25.31c-9.204 17.571-16.686 36.001-22.524 55.404l-8.328 27.679-93.102 15.704c-5.381 0.903-9.191 5.408-9.191 10.88v81.87c0 5.472 3.81 9.977 9.314 10.901l94.216 16.072 8.502 27.206c5.908 18.904 13.691 37.364 23.197 55.017l13.793 25.616-55.166 76.727c-3.172 4.456-2.685 10.321 1.217 14.185l57.951 57.951c2.213 2.213 4.786 3.286 7.731 3.286 2.302 0 4.26-0.63 6.263-2.071l77.942-55.475 25.321 13.287c16.715 8.771 34.663 16.129 53.683 22.062l27.343 8.529 15.59 93.541c0.903 5.381 5.408 9.191 10.88 9.191h82.064c5.472 0 9.977-3.81 10.894-9.272l15.87-93.551 27.333-8.466c19.327-5.986 37.839-13.646 55.407-22.937l25.427-13.447 77.397 55.324c1.725 1.242 3.861 1.923 6.251 1.923 2.945 0 5.518-1.073 7.731-3.286l57.951-57.951c3.82-3.82 4.329-9.813 1.223-14.176l-55.085-77.342 13.262-25.319c9.687-18.493 17.378-36.976 22.946-55.319l8.391-27.642 93.783-15.63c5.381-0.903 9.191-5.408 9.191-10.88l0.004-82.512c0.070-5.083-3.519-9.351-8.807-10.238zM372.364 512c0 82.458 57.179 139.636 139.636 139.636 82.461 0 139.636-57.177 139.636-139.636 0-82.463-57.174-139.636-139.636-139.636-82.46 0-139.636 57.176-139.636 139.636zM279.273 512c0-133.874 98.855-232.727 232.727-232.727 133.875 0 232.727 98.852 232.727 232.727 0 133.872-98.853 232.727-232.727 232.727-133.87 0-232.727-98.857-232.727-232.727z",
  "select-active": "M780.191 344.319h97.524v539.46c0 77.442-62.779 140.221-140.221 140.221h-598.195c-76.933 0-139.299-62.366-139.299-139.299v-174.775c0-203.699 0-203.699 0.003-524.206 0.773-75.952 59.854-136.958 135.679-136.958h547.559v97.524h-547.559c-21.411 0-37.922 17.049-38.158 39.931v698.485c0 23.072 18.703 41.775 41.775 41.775h598.195c23.581 0 42.697-19.116 42.697-42.697v-539.46zM892.814 14.712l68.896 69.023-373.010 372.326-180.534-146.231 61.383-75.783 112.354 91.005 310.911-310.341z",
  "select-inactive": "M146.264 921.6h628.105c24.76 0 44.832-20.072 44.832-44.832v-723.725c0-27.97-22.674-50.643-50.643-50.643h-615.818c-23.514 0-42.643 18.935-42.882 42.448l-7.455 732.441c-0.002 24.672 19.636 44.31 43.862 44.31zM7.462 143.806c0.811-79.657 65.615-143.806 145.277-143.806h615.818c84.524 0 153.043 68.52 153.043 153.043l0 723.725c0 81.314-65.918 147.232-147.232 147.232h-628.105c-80.779 0-146.264-65.485-146.264-146.264 0.001-0.496 2.488-245.14 7.463-733.93z",
  "share-ios": "M512 168.61v497.206h-102.4v-494.539c-30.469 31.174-68.867 71.175-115.153 119.963l-74.287-70.478c195.464-206.029 210.196-220.761 241.944-220.761s46.48 14.732 241.944 220.761l-74.287 70.478c-47.616-50.19-86.885-91.081-117.76-122.629zM819.2 409.58h-102.356v-102.4h204.756v557.173c0 88.171-71.476 159.647-159.647 159.647h-602.306c-88.171 0-159.647-71.476-159.647-159.647l-0-557.173h205.027v102.4h-102.627v454.773c0 31.617 25.63 57.247 57.247 57.247h602.306c31.617 0 57.247-25.63 57.247-57.247v-454.773z",
  "share-android": "M511.5 409.6c.3 5.6.5 11.3.5 17.1 0 54.9-15 104.4-41.5 144.9l148 103.6c28.8-22.3 65.6-35.2 106.8-35.2 97.9 0 170.7 72.7 170.7 170.7 0 97.9-72.7 170.7-170.7 170.7-97.9 0-170.7-72.7-170.7-170.7 0-24.2 4.4-46.8 12.6-67.2L411.1 634.1c-42.3 30.8-95.5 48.5-155.1 48.5-147.5 0-256-108.5-256-256s108.5-256 256-256c113.8 0 204.4 64.6 239.8 161.8l190-84.6c-2.1-11.1-3.1-22.6-3.1-34.5 0-97.9 72.7-170.7 170.7-170.7 97.9 0 170.7 72.7 170.7 170.7 0 97.9-72.7 170.7-170.7 170.7-57.5 0-106.4-25.1-136.9-65.7l-205 91.3zM85.3 426.7c0 100.4 70.3 170.7 170.7 170.7 100.4 0 170.7-70.3 170.7-170.7C426.7 326.3 356.4 256 256 256c-100.4 0-170.7 70.3-170.7 170.7zm554.7 384c0 50.8 34.5 85.3 85.3 85.3s85.3-34.5 85.3-85.3-34.5-85.3-85.3-85.3c-50.8-.1-85.3 34.5-85.3 85.3zm128-597.4c0 50.8 34.5 85.3 85.3 85.3s85.3-34.5 85.3-85.3-34.5-85.3-85.3-85.3-85.3 34.5-85.3 85.3z",
  shuffle: "M824.1 691.5c-24.7-17.6-53.3-37.8-85.8-60.5l58.7-84.1c219.9 153.5 227 158.9 227 195.9s-7.1 42.3-227 195.9l-58.7-84.1c32.5-22.7 61.1-42.9 85.8-60.5h-87.5c-53.1 1-105.5-18.9-145.3-55.5l-75.7-59.5 63.5-80.5 78.6 62c23.4 21.3 50.2 31.4 77.9 30.9h88.5zm0-461.5c-24.7-17.6-53.3-37.8-85.8-60.5L797 85.4c219.9 153.5 227 158.9 227 195.9s-7.1 42.3-227 195.9L738.3 393c32.5-22.7 61.1-42.9 85.8-60.5h-88.4c-27.7-.5-54.6 9.7-75 28.4l-8.2 7.6-308.3 363-3.7 3.9-9.6 9.2c-33.2 31.7-77.4 49.4-123.4 49.4H0V691.5h207.4c19.6 0 38.4-7.5 52.6-21.1l7.7-7.3 308.7-363.4 4.4-4.6 10.6-9.7c39.8-36.6 92.1-56.4 145.3-55.5h87.4v.1zM0 230h207.4c46 0 90.1 17.7 123.4 49.4l80 71.9-68.5 76.3-81.1-72.9c-15.3-14.6-34.1-22.1-53.7-22.1H0V230z",
  size: "M338.302 614.4c62.617 61.32 143.555 138.964 242.723 232.833l-140.787 148.735c-397.607-376.36-440.238-419.134-440.238-483.967s42.631-107.607 440.238-483.967l140.787 148.735c-99.168 93.869-180.107 171.512-242.723 232.833h1573.542c-62.617-61.32-143.555-138.964-242.723-232.833l140.787-148.735c397.607 376.36 440.238 419.134 440.238 483.967s-42.631 107.607-440.238 483.967l-140.787-148.735c99.168-93.869 180.107-171.512 242.723-232.833h-1573.542z",
  skip: "M256 193.8c0-25.3 7.4-50 21.4-71.1C316.6 63.3 396.6 46.9 456 86.2l8.8 5.9L961.6 391l7.7 5.4 3.5 2.8c9.1 7.5 17.2 16.1 24.2 25.6 46.1 62.9 32.4 151.2-34.1 199.7L463 940.8l-5.8 3c-19.6 10.1-41.3 15.4-63.3 15.4-76.1 0-137.9-61.7-137.9-137.9V193.8zm140.5 6.5l-11.1-7.3c-1.4.4-1.5.6-1.5.8v627.6c0 5.5 4.4 9.9 9.9 9.9 1.6 0 3.1-.4 4.5-1.1l492.5-311.3c5.9-4.3 7.1-12.5 2.8-18.4-.6-.8-1.3-1.6-2.1-2.2l-495-298zM0 64h128v896H0V64z",
  "skip-back": "M1170.365 875.729c0 28.89-8.49 57.144-24.416 81.248-44.872 67.918-136.306 86.6-204.224 41.727l-10.019-6.698-567.854-341.571-8.79-6.212-3.948-3.251c-10.386-8.552-19.662-18.369-27.612-29.223-52.649-71.887-37.054-172.844 38.917-228.277l571.339-361.513 6.651-3.44c22.376-11.574 47.2-17.615 72.392-17.615 87.020 0 157.564 70.544 157.564 157.564v717.259zM1009.727 868.302l12.637 8.349c1.652-0.494 1.715-0.706 1.715-0.923v-717.259c0-6.229-5.049-11.278-11.278-11.278-1.803 0-3.58 0.432-5.182 1.261l-562.848 355.824c-6.707 4.912-8.162 14.332-3.25 21.039 0.69 0.942 1.487 1.8 2.374 2.557l565.831 340.43zM0 0h146.286v1024h-146.286v-1024z",
  stop: "M170.667 170.667v682.667h682.667v-682.299c0-0.135-227.556-0.258-682.667-0.368zM170.399 0h682.209c94.817 0 171.392 76.575 171.392 171.035v685.867c0 92.286-74.813 167.099-167.099 167.099h-691.033c-91.607 0-165.868-74.262-165.868-165.868v-687.733c0-94.109 76.29-170.399 170.399-170.399z",
  sound: "M267.137 691.655v-35.693c0-195.563 0-195.563 0-491.127-0.009-12.196-0.009-37.459-0-75.803l0.013-52.923 52.14 9.071c91.707 15.954 157.634 57.201 193.431 124.090 36.249 67.732 26.019 179.391-24.854 340.652l-84.918-26.789c44.046-139.621 52.521-232.129 31.265-271.848-15.363-28.707-40.862-50.241-78.039-64.48 0.001 7.655 0.003 13.653 0.006 17.995v682.215c-0.604 97.847-80.107 176.983-178.094 176.983-98.355 0-178.087-79.732-178.087-178.087s79.732-178.087 178.087-178.087c32.44 0 62.855 8.674 89.050 23.829zM178.087 934.957c49.177 0 89.043-39.866 89.043-89.043s-39.866-89.043-89.043-89.043c-49.177 0-89.043 39.866-89.043 89.043s39.866 89.043 89.043 89.043z",
  "trans-automatic": "M418.909 512h232.727c51.413 0 93.091 41.678 93.091 93.091v93.091c0 51.413-41.678 93.091-93.091 93.091h-232.727v232.727h-93.091v-232.727h-232.727c-51.413 0-93.091-41.678-93.091-93.091v-93.091c0-51.413 41.678-93.091 93.091-93.091h232.727v-512h93.091v512zM93.091 605.091v93.091h558.545v-93.091h-558.545zM698.182 0c27.042 0 46.545 19.503 46.545 46.545 0 27.041-19.503 46.545-46.545 46.545-27.041 0-46.545-19.504-46.545-46.545 0-27.042 19.504-46.545 46.545-46.545zM651.636 325.818c0-27.042 19.504-46.545 46.545-46.545 27.042 0 46.545 19.503 46.545 46.545 0 27.041-19.503 46.545-46.545 46.545-27.041 0-46.545-19.504-46.545-46.545zM698.182 930.909c27.042 0 46.545 19.503 46.545 46.545 0 27.041-19.503 46.545-46.545 46.545-27.041 0-46.545-19.504-46.545-46.545 0-27.042 19.504-46.545 46.545-46.545z",
  "trans-manual": "M139.636 900.748c14.539 12.484 23.273 31.271 23.273 53.434 0 40.561-29.255 69.818-69.818 69.818-40.561 0-69.818-29.257-69.818-69.818 0-22.162 8.734-40.949 23.273-53.433v-777.498c-14.539-12.484-23.273-31.271-23.273-53.432 0-40.563 29.257-69.818 69.818-69.818 40.563 0 69.818 29.255 69.818 69.818 0 22.162-8.733 40.949-23.273 53.433v342.206l232.727 0.014v-342.221c-14.539-12.484-23.273-31.271-23.273-53.432 0-40.563 29.257-69.818 69.818-69.818 40.563 0 69.818 29.255 69.818 69.818 0 22.162-8.733 40.949-23.273 53.433v342.811c128.761 1.473 199.409-15.052 207.633-33.787 14.268-32.506 23.422-138.496 24.887-309.203-14.415-12.484-23.065-31.198-23.065-53.254 0-40.563 29.257-69.818 69.818-69.818 40.563 0 69.818 29.255 69.818 69.818 0 22.259-8.81 41.113-23.464 53.597-1.472 185.443-11.516 297.891-32.753 346.275-29.57 67.369-126.095 91.739-292.874 89.504v341.555c14.539 12.484 23.273 31.271 23.273 53.434 0 40.561-29.255 69.818-69.818 69.818-40.561 0-69.818-29.257-69.818-69.818 0-22.162 8.734-40.949 23.273-53.433v-342.203h-232.727v342.203z",
  twitter: "M512 0C229.7 0 0 229.7 0 512s229.7 512 512 512 512-229.7 512-512S794.3 0 512 0zm228.4 394.8c.3 5 .3 10.2.3 15.3 0 155.7-118.5 335.2-335.3 335.2-64 .1-126.7-18.3-180.6-52.9 62.3 7.5 125.1-10 174.5-48.8-50.4-.9-94.7-33.8-110.1-81.8 17.7 3.4 35.8 2.7 53.2-2-55-11.1-94.5-59.4-94.5-115.5v-1.5c16.4 9.1 34.6 14.1 53.3 14.7-32.8-21.9-52.4-58.7-52.4-98 0-21.6 5.8-41.9 16-59.3 59.8 73.6 148.1 118.4 242.9 123.2-15-63.3 24.2-126.8 87.6-141.8 41.2-9.7 84.4 3.4 113.3 34.3 26.4-5.2 51.7-14.8 74.9-28.6-8.8 27.3-27.3 50.5-51.9 65.2 23.4-2.7 46.2-9 67.7-18.5-15.9 23.6-35.8 44.2-58.9 60.8z",
  upload: "M560.762 161.178v667.774h-97.524v-666.433c-29.183 29.782-66.045 68.092-110.546 114.892l-70.673-67.203c186.485-196.116 200.609-210.208 230.809-210.208 30.273 0 44.21 13.968 230.037 210.289l-70.827 67.041c-44.926-47.463-82.031-86.194-111.275-116.151zM170.667 926.476h682.667v97.524h-682.667v-97.524z",
  "volume-down": "M215.6 352.9H107.8v318.2h107.8V352.9zm107.8-5.7v329.7c18.8 4.4 37.1 11.4 54.2 20.8l254.9 139.6V186.8l-255 139.6c-17.1 9.4-35.3 16.3-54.1 20.8zM0 299c0-29.8 24.1-53.9 53.9-53.9H274c18.1 0 35.9-4.6 51.8-13.2L660.5 48.6c7.9-4.3 16.8-6.6 25.9-6.6 29.8 0 53.9 24.1 53.9 53.9v832.3c0 9-2.3 17.9-6.6 25.9-14.3 26.1-47 35.7-73.2 21.4L325.8 792.1c-15.9-8.7-33.7-13.2-51.8-13.2H53.9C24.1 778.9 0 754.8 0 725V299zm820.2-61.7l84.2-67.3c79.1 98.8 119.6 200.2 119.6 303.1s-40.5 204.3-119.6 303.1l-84.2-67.3c64.7-80.8 96-159.1 96-235.8s-31.3-155-96-235.8z",
  "volume-mute": "M894.5 443.7l61.8-61.8 67.6 67.6-61.8 61.8 61.8 61.8-67.6 67.6-61.8-61.8-61.8 61.8-67.6-67.6 61.8-61.8-61.8-61.8 67.6-67.6 61.8 61.8zM191.3 653.2V370.8H95.6v282.3h95.7zm95.6 5c16.7 3.9 32.9 10.1 48.1 18.4l226.2 123.8v-577L335 347.3c-15.2 8.3-31.4 14.5-48.1 18.4v292.5zM0 323c0-26.4 21.4-47.8 47.8-47.8h195.3c16.1 0 31.9-4 45.9-11.8l297-162.6c7-3.9 14.9-5.9 23-5.9 26.4 0 47.8 21.4 47.8 47.8v738.4c0 8-2 15.9-5.9 23-12.7 23.2-41.7 31.7-64.9 19L289 760.6c-14.1-7.7-29.9-11.8-45.9-11.8H47.8C21.4 748.8 0 727.4 0 701V323z",
  "volume-up": "M178.1 380.6H89v262.9h89V380.6zm89-4.8v272.3c15.6 3.7 30.6 9.4 44.8 17.2l210.6 115.3V243.4L311.9 358.7c-14.2 7.7-29.2 13.5-44.8 17.1zM0 336c0-24.6 19.9-44.5 44.5-44.5h181.8c14.9 0 29.7-3.8 42.8-10.9l276.5-151.4c6.6-3.6 13.9-5.5 21.4-5.5 24.6 0 44.5 19.9 44.5 44.5v687.5c0 7.5-1.9 14.8-5.5 21.4-11.8 21.6-38.9 29.5-60.4 17.7L269.1 743.4c-13.1-7.2-27.8-10.9-42.8-10.9H44.5C19.9 732.5 0 712.5 0 688V336zm851.8-101.2l77.3-44.2C992.2 301 1024 412.5 1024 524.4s-31.8 223.4-94.9 333.7L851.8 814c55.6-97.4 83.2-193.7 83.2-289.6s-27.6-192.2-83.2-289.6zm-174.2 94.8l69.5-55.6c65.3 81.6 98.8 165.4 98.8 250.4 0 85.1-33.5 168.8-98.8 250.4l-69.5-55.6c53.4-66.8 79.3-131.5 79.3-194.8s-25.9-128-79.3-194.8z",
  zoom: "M770.721 679.722l243.657 227.176-63.482 68.088-239.923-223.694c-73.682 69.207-175.028 109.799-292.064 109.799-243.369 0-418.909-175.54-418.909-418.909 0-243.38 175.54-418.909 418.909-418.909 243.38 0 418.909 175.529 418.909 418.909 0 90.371-24.204 171.39-67.097 237.54zM465.455 395.636h93.091v93.091h-93.091v93.091h-93.091v-93.091h-93.091v-93.091h93.091v-93.091h93.091v93.091zM93.091 442.182c0 191.956 133.862 325.818 325.818 325.818 191.963 0 325.818-133.857 325.818-325.818 0-191.968-133.85-325.818-325.818-325.818-191.961 0-325.818 133.855-325.818 325.818z"
};
/**
 * Atom
 * Icon
 * Notes: An SVG icon. Designers should supply you SVGs on a 1024x1024 artboard
 */

/**
 * Note: by adding 'focusable="false"` it fixes a bug in IE11 where
 * SVGs inside a link/button are incorrectly always focussable.
 */

var Icon = function Icon(_ref) {
  var className = _ref.className,
      icon = _ref.icon,
      size = _ref.size,
      fill = _ref.fill,
      sizePX = _ref.sizePX;
  return React.createElement(StyledIcon, {
    className: "ngw-icon ngw-icon-".concat(size, " ").concat(className),
    sizePX: sizePX
  }, React.createElement("svg", {
    fill: fill,
    viewBox: "0 0 1024 1024",
    focusable: "false"
  }, React.createElement("path", {
    d: icons[icon]
  })));
};

Icon.defaultProps = {
  className: "",
  size: null,
  sizePX: null,
  fill: palette.d6.blue800
};

function _templateObject3$1() {
  var data = _taggedTemplateLiteral(["\n  width: 100%;\n  max-width: 670px;\n  display: flex;\n  flex-direction: column;\n  max-height: 90%;\n  background-color: #fff;\n  position: relative;\n  ", "\n  ", "\n\n  button {\n    position: absolute;\n    top: -50px;\n    right: -8px;\n    cursor: pointer;\n    fill: ", ";\n  }\n\n  @media (min-width: ", ") {\n    margin: 0;\n  }\n"]);

  _templateObject3$1 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$2() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  position: fixed;\n  top: 0;\n  left: 0;\n  width: 100vw;\n  height: 100vh;\n  z-index: 3000;\n  background-color: rgba(0, 0, 0, 0.5);\n  align-items: center;\n  justify-content: center;\n"]);

  _templateObject2$2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$4() {
  var data = _taggedTemplateLiteral(["\n  button {\n    border: 0;\n    background: none;\n    cursor: pointer;\n    fill: ", ";\n  }\n"]);

  _templateObject$4 = function _templateObject() {
    return data;
  };

  return data;
}

var ModalContainer = styled.div(_templateObject$4(), palette.d6.blue800);
var StyledModalBackground = styled.div(_templateObject2$2());
var StyledModal = Modal.styled(_templateObject3$1(), D6Spacing("margin-left", "s200"), D6Spacing("margin-right", "s200"), palette.d6.white000, breakpoints.md);

var Modal$1 =
/*#__PURE__*/
function (_Component) {
  _inherits(Modal$$1, _Component);

  function Modal$$1() {
    _classCallCheck(this, Modal$$1);

    return _possibleConstructorReturn(this, _getPrototypeOf(Modal$$1).apply(this, arguments));
  }

  _createClass(Modal$$1, [{
    key: "render",
    value: function render() {
      var _this$props = this.props,
          isOpen = _this$props.isOpen,
          canScroll = _this$props.canScroll,
          elementTrigger = _this$props.elementTrigger,
          handleClick = _this$props.handleClick,
          children = _this$props.children,
          className = _this$props.className;
      return React.createElement(ModalContainer, null, elementTrigger && elementTrigger(this.state), React.createElement(ModalProvider, {
        backgroundComponent: StyledModalBackground
      }, React.createElement(StyledModal, Object.assign({
        className: className,
        isOpen: isOpen,
        allowScroll: canScroll
      }, this.props), children, React.createElement("button", {
        onClick: handleClick
      }, React.createElement(Icon, {
        icon: "menu-close",
        size: "sm",
        fill: "white"
      })))));
    }
  }]);

  return Modal$$1;
}(Component);

Modal$1.defaultProps = {
  className: "",
  isOpen: false,
  canScroll: false
};
/**
 * Molecule Modal
 */

function _templateObject4() {
  var data = _taggedTemplateLiteral(["\n  ", "\n  background-color: ", ";\n  max-height: 50vh;\n  overflow-y: auto;\n\n  h2 {\n    ", "\n    ", "\n    padding-bottom: 0;\n  }\n\n  p {\n    ", "\n    ", "\n    color: ", ";\n  }\n"]);

  _templateObject4 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$2() {
  var data = _taggedTemplateLiteral(["\n  ", "\n  ", "\n  color: ", ";\n  display: block;\n  background: transparent;\n  border: none;\n  padding: 0;\n  position: relative;\n  cursor: pointer;\n\n  i {\n    position: absolute;\n    right: -25px;\n    top: 3px;\n  }\n"]);

  _templateObject3$2 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$3() {
  var data = _taggedTemplateLiteral(["\n  ", ";\n  width: ", "px;\n  height: ", "px;\n  border-radius: 100%;\n"]);

  _templateObject2$3 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$5() {
  var data = _taggedTemplateLiteral(["\n  box-shadow: 0 3px 10px 0 rgba(0,0,0,0.1);\n  display: flex;\n  flex: 1;\n  justify-content: space-between;\n  ", "\n\n  h2, .title {\n    ", "\n    color: ", ";\n    ", "\n  }\n\n  .tooltip {\n    position: relative;\n  }\n\n  .tooltip-text {\n    visibility: hidden;\n    text-align: left;\n    position: absolute;\n    bottom: 100%;\n    z-index: 1;\n    background: ", ";\n    padding: 5px;\n    pointer-events: none;\n    color: ", ";\n    ", "\n    line-height: 20px;\n    border-bottom: 1px solid ", ";\n    bottom: calc(100% + 10px);\n\n    &:after,\n    &:before {\n      left: 50%;\n      border: solid transparent;\n      content: \" \";\n      height: 0;\n      width: 0;\n      position: absolute;\n      pointer-events: none;\n      top: 100%;\n    }\n\n    &:before {\n      border-width: 11px;\n      margin-left: -11px;\n      border-top-color: ", ";\n    }\n\n    &:after {\n      border-width: 10px;\n      margin-left: -10px;\n      border-top-color: ", ";\n    }\n  }\n\n  .tooltip:hover .tooltip-text {\n      visibility: visible;\n  }\n\n  p, .description {\n    ", "\n    ", "\n    color: ", ";\n  }\n\n  b {\n    ", "\n    color: ", ";\n  }\n"]);

  _templateObject$5 = function _templateObject() {
    return data;
  };

  return data;
}
/**
 * Retrieve which information needs to be rendered (backgroundColor or backgroundImage)
 * @param {string} image
 * @return {string} background
 */


var retrieveImage = function retrieveImage(image) {
  if (image.indexOf("#") > -1) {
    return "background-color: ".concat(image);
  }

  return "background: url(".concat(image, ") center center");
};

var StyledCard = styled.article(_templateObject$5(), D6Spacing("padding", "s350"), D6Typography("header/light250"), function (props) {
  return props.theme === "dark" ? palette.d6.white000 : palette.d6.black000;
}, D6Spacing("padding-bottom", "s150"), palette.d6.white000, palette.d6.grey400, D6Typography("body/regular100"), palette.d6.blue800, palette.d6.blue800, palette.d6.white000, D6Typography("body/regular100"), D6Spacing("padding-bottom", "s150"), palette.d6.grey400, D6Typography("body/bold100"), palette.d6.blue800);
var StyledImage = styled.div(_templateObject2$3(), function (_ref) {
  var image = _ref.image;
  return retrieveImage(image.type);
}, function (props) {
  return props.image.width;
}, function (props) {
  return props.image.height;
});
var StyledButton = styled.button(_templateObject3$2(), D6Typography("body/bold100"), D6Spacing("margin-top", "s200"), palette.blue);
var StyledModalBody = styled.div(_templateObject4(), D6Spacing("padding", "d100"), palette.d6.white000, D6Typography("header/light300"), D6Spacing("margin-bottom", "s250"), D6Typography("body/regular150"), D6Spacing("margin-bottom", "s250"), palette.d6.grey400);

var Card = function Card(_ref) {
  var title = _ref.title,
      description = _ref.description,
      bold = _ref.bold,
      image = _ref.image,
      infoButton = _ref.infoButton,
      trimTitle = _ref.trimTitle,
      modalTitle = _ref.modalTitle,
      modalDescription = _ref.modalDescription,
      tooltip = _ref.tooltip,
      props = _objectWithoutProperties(_ref, ["title", "description", "bold", "image", "infoButton", "trimTitle", "modalTitle", "modalDescription", "tooltip"]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setIsOpen = _useState2[1];

  return React.createElement(Col, props, React.createElement(StyledCard, null, React.createElement("header", null, React.createElement(Modal$1, {
    isOpen: isOpen,
    handleClick: function handleClick() {
      return setIsOpen(false);
    },
    onBackgroundClick: function onBackgroundClick() {
      return setIsOpen(false);
    },
    onEscapeKeydown: function onEscapeKeydown() {
      return setIsOpen(false);
    }
  }, (modalTitle || title) && (modalDescription || description) && React.createElement(StyledModalBody, null, React.createElement("h2", null, modalTitle || title), React.createElement(RichText, {
    text: "<p>".concat(modalDescription || description, "</p>"),
    allowedTags: ["strong", "b", "h1", "h2", "h3", "h4", "h5", "p", "a", "ul", "li"]
  }))), trimTitle ? React.createElement("div", {
    className: "tooltip"
  }, tooltip && React.createElement("span", {
    className: "tooltip-text"
  }, title), React.createElement(ClampLines, {
    text: title,
    className: "title",
    lines: 2,
    buttons: false
  })) : React.createElement("h2", null, title), description && !infoButton && React.createElement("p", null, description), description && infoButton && React.createElement(ClampLines, {
    text: description,
    className: "description",
    lines: 2,
    buttons: false
  }), bold && React.createElement("b", null, bold), infoButton && React.createElement(StyledButton, {
    onClick: function onClick() {
      return setIsOpen(true);
    }
  }, "More info", React.createElement(Icon, {
    icon: "add",
    sizePX: "16px"
  }))), React.createElement(StyledImage, {
    image: image
  })));
};

Card.defaultProps = {
  description: "",
  bold: "",
  image: {
    type: "http://placehold.it/132x132",
    width: 132,
    height: 132
  },
  infoButton: false,
  trimTitle: false,
  modalTitle: "",
  modalDescription: "",
  tooltip: false
};
/**
 * Atom Card
 */

function _templateObject2$4() {
  var data = _taggedTemplateLiteral(["\n  ", "\n  ", "; /* [1] */\n"]);

  _templateObject2$4 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$6() {
  var data = _taggedTemplateLiteral(["\n  ", "\n  ", "; /* [1] */\n"]);

  _templateObject$6 = function _templateObject() {
    return data;
  };

  return data;
}
/**
 * default = white border/color but with no background
 * primary = blue background with dark blue text
 * secondary = blue border/color with matching text but with no background
 * tertiary = light blue border with matching text but with no background
 * delete = red button
 */


var StyledCTA = "\n  /**\n   * 1. In CTAs font size doesn't change with breakpoints\n   *    so don't use the typography mixin\n   * 2. Align copy vertically centred within CTA\n   * 3. Avoid transition: all for perf reasons\n   */\n  padding: 0;\n  ".concat(D6Spacing("padding-left", "s250"), "\n  ").concat(D6Spacing("padding-right", "s250"), "\n  background-color: transparent;\n  border: 2px solid ").concat(palette.d6.white000, ";\n  border-radius: 22px;\n  color: ").concat(palette.d6.white000, ";\n  cursor: pointer;\n  display: inline-block;\n  font-family: ").concat(fonts.textRegular, ";\n  font-size: 14px; /* [1] */\n  height: 42px;\n  line-height: 38px; /* [2] */\n  text-align: center;\n  text-decoration: none;\n  transition-duration: 0.3s; /* [3] */\n  transition-property: background-color, border-color, color; /* [3] */\n  width: 100%;\n\n  &:hover,\n  &:focus {\n    border-color: ").concat(palette.d6.grey200, ";\n  }\n\n  &:active {\n    border-color: ").concat(palette.d6.grey300, ";\n  }\n\n  &[disabled] {\n    background-color: transparent;\n    border-color: currentColor;\n    color: ").concat(palette.d6.grey400, ";\n    cursor: not-allowed;\n  }\n\n  &.primary {\n    border-color: transparent;\n    background-color: ").concat(palette.d6.blue200, ";\n    border-radius: 24px;\n    color: ").concat(palette.d6.blue800, ";\n\n    &:hover,\n    &:focus {\n      background-color: ").concat(palette.d6.blue100, ";\n    }\n\n    &:active {\n      background-color: ").concat(palette.d6.blue300, ";\n    }\n\n    &[disabled] {\n      background-color: ").concat(palette.d6.grey100, ";\n      color: ").concat(palette.d6.grey200, ";\n    }\n  }\n\n  &.secondary {\n    background-color: transparent;\n    border-color: currentColor;\n    color: ").concat(palette.d6.blue800, ";\n\n    &:hover,\n    &:focus {\n      color: ").concat(palette.d6.blue700, ";\n    }\n\n    &:active {\n      color: ").concat(palette.d6.blue900, ";\n    }\n\n    &[disabled] {\n      color: ").concat(palette.d6.grey100, ";\n    }\n  }\n\n  &.tertiary {\n    background-color: transparent;\n    border-color: currentColor;\n    color: ").concat(palette.d6.blue200, ";\n\n    &:hover,\n    &:focus {\n      color: ").concat(palette.d6.blue100, ";\n    }\n\n    &:active {\n      color: ").concat(palette.d6.blue300, ";\n    }\n\n    &[disabled] {\n      color: ").concat(palette.d6.grey400, ";\n    }\n  }\n\n  &.delete {\n    background-color: transparent;\n    border-color: currentColor;\n    color: ").concat(palette.d6.red200, ";\n\n    &:hover,\n    &:focus {\n      color: ").concat(palette.d6.red100, ";\n    }\n\n    &:active {\n      color: ").concat(palette.d6.red300, ";\n    }\n\n    &[disabled] {\n      color: ").concat(palette.d6.grey400, ";\n    }\n  }\n");
/**
 * Create a Link and a button component
 * 1. Make sure to allow a prop to set the button to be NOT width: 100%;
 *    Putting this prop in the above templaet literal does not work.
 *    It must be here
 */

var StyledCTALink = styled.a(_templateObject$6(), StyledCTA, function (props) {
  return props.dynamicwidth ? "width: auto;" : "100%";
});
var StyledCTAButton = styled.button(_templateObject2$4(), StyledCTA, function (props) {
  return props.dynamicwidth ? "width: auto;" : "100%";
});
/**
 * MyVW
 * Atom
 * CTA
 * Notes: A <button> or <a> link that looks like a button
 */

var CTA = function CTA(_ref) {
  var className = _ref.className,
      copy = _ref.copy,
      disabled = _ref.disabled,
      dynamicwidth = _ref.dynamicwidth,
      href = _ref.href,
      icon = _ref.icon,
      onClick = _ref.onClick,
      target = _ref.target,
      type = _ref.type;
  return React.createElement(React.Fragment, null, href && React.createElement(StyledCTALink, {
    className: "a-cta ".concat(className),
    dynamicwidth: dynamicwidth,
    href: href,
    onClick: onClick,
    target: target
  }, icon && {
    icon: icon
  }, copy), !href && React.createElement(StyledCTAButton, {
    className: "a-cta ".concat(className),
    disabled: disabled,
    dynamicwidth: dynamicwidth,
    onClick: onClick,
    type: type
  }, icon && {
    icon: icon
  }, copy));
};

CTA.defaultProps = {
  className: "",
  disabled: null,
  dynamicwidth: false,
  href: null,
  icon: null,
  onClick: null,
  target: null,
  type: "button"
};

function _templateObject$7() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  cursor: pointer;\n  background: none;\n  border: none;\n  position: absolute;\n  right: 0;\n  top: 0;\n  transform: ", "; /* [1] */\n  transition: transform 300ms;\n\n  &:active {\n    color: ", ";\n  }\n\n  &:hover,\n  &:focus {\n    color: ", ";\n  }\n\n  svg {\n    fill: currentColor; /* [2] */\n  }\n"]);

  _templateObject$7 = function _templateObject() {
    return data;
  };

  return data;
}
/**
 * 1. Reuse and rotate the icon instead of using different icons
 * 2. Make the SVG icon the same color as the parent - throughout all states
 */


var StyledEditButton = styled.button(_templateObject$7(), palette.d6.blue800, function (props) {
  return props.active ? "none" : "rotate(45deg)";
}, palette.d6.blue900, palette.d6.blue700);
/**
 * MyVW
 * Atom
 * EditButton
 * - A text link / icon which allows users to view an editable version of
 *   information in the organism.
 * - Defined as a `<button>` and not an `<a>` for accessibility.
 * - Also known as `EditTextLink` in MyVW UX documents.
 */

var EditButton = function EditButton(_ref, props) {
  var active = _ref.active,
      className = _ref.className,
      icon = _ref.icon,
      labelActive = _ref.labelActive,
      labelInactive = _ref.labelInactive,
      onClick = _ref.onClick,
      size = _ref.size;
  return React.createElement(StyledEditButton, {
    type: "button",
    active: active,
    "aria-label": active ? labelActive : labelInactive,
    onClick: onClick,
    className: "".concat(className)
  }, React.createElement(Icon, Object.assign({
    icon: icon,
    size: size
  }, props)));
};

EditButton.defaultProps = {
  className: "",
  active: false,
  size: "md"
};

function _templateObject$8() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  ", ";\n  ", ";\n"]);

  _templateObject$8 = function _templateObject() {
    return data;
  };

  return data;
}

var StyledFootNote = styled.section(_templateObject$8(), palette.d6.gray400, D6Typography("body/regular100"), D6Spacing("margin-top", "d100"));
/**
 * PDB
 * Atom
 * FootNote
 * Notes: An optional foot note message that sits beneath a table
 */

var FootNote = function FootNote(_ref) {
  var message = _ref.message;
  return React.createElement(React.Fragment, null, message && React.createElement(StyledFootNote, null, React.createElement(RichText, {
    text: message,
    allowedTags: ["strong", "p", "b", "br"],
    inline: true
  })));
};

FootNote.defaultProps = {
  message: null
};

function _templateObject$9() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  display: block;\n  font-family: ", "; /* [1] */\n  font-size: 12px; /* [1] */\n"]);

  _templateObject$9 = function _templateObject() {
    return data;
  };

  return data;
}
/**
 * 1. This font-size is not responsive
 */


var StyledFormError = styled.span(_templateObject$9(), palette.d6.red200, fonts.textRegular);
/**
 * MyVW
 * Atom
 * FormError
 * Notes: An optional error message that sit beneath a form field
 */

var FormError = function FormError(_ref) {
  var className = _ref.className,
      message = _ref.message;
  return React.createElement(React.Fragment, null, message && React.createElement(StyledFormError, {
    className: className
  }, message));
};

FormError.defaultProps = {
  className: "",
  message: null
};

function _templateObject$a() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  display: block;\n  font-family: ", "; /* [1] */\n  font-size: 12px; /* [1] */\n"]);

  _templateObject$a = function _templateObject() {
    return data;
  };

  return data;
}
/**
 * 1. This font-size is not responsive
 */


var StyledFormHint = styled.span(_templateObject$a(), palette.d6.grey400, fonts.textRegular);
/**
 * MyVW
 * Atom
 * FormHint
 * Notes: An optional hint message that sit beneath a form field
 */

var FormHint = function FormHint(_ref) {
  var message = _ref.message;
  return React.createElement(React.Fragment, null, message && React.createElement(StyledFormHint, null, message));
};

FormHint.defaultProps = {
  message: null
};

function _templateObject$b() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  cursor: text;\n  font-family: ", "; /* [1] */\n  font-size: 16px; /* [1] */\n  left: 0;\n  position: absolute;\n  text-align: initial;\n  top: 0;\n  transform-origin: 0% 100%;\n  transform: translate3d(0, 16px, 0);\n  transition-duration: 0.2s; /* [2] */\n  transition-timing-function: ease-out; /* [2] */\n  transition-property: transform, color; /* [2] */\n\n  &.active {\n    transform: translate3d(0, -6px, 0) scale(0.75);\n    transform-origin: 0 0;\n  }\n\n  &.disabled {\n    color: ", ";\n  }\n\n  .m-form-field:focus-within & {\n    color: ", ";\n  }\n\n  label:hover {\n    color: ", ";\n  }\n"]);

  _templateObject$b = function _templateObject() {
    return data;
  };

  return data;
}
/**
 * 1. This font-size is not responsive
 * 2. Avoid transition: all for perf reasons
 */


var StyledFormLabel = styled.label(_templateObject$b(), palette.d6.blue800, fonts.textRegular, palette.d6.grey100, palette.d6.blue900, palette.d6.blue700);
/**
 * MyVW
 * Atom
 * FormLabel
 * Notes: An label for a form field
 */

var FormLabel = function FormLabel(_ref) {
  var label = _ref.label,
      id = _ref.id,
      className = _ref.className;
  return React.createElement(StyledFormLabel, {
    className: className,
    htmlFor: id
  }, label);
};

FormLabel.defaultProps = {
  className: null
};

function _templateObject$c() {
  var data = _taggedTemplateLiteral(["\n  img {\n    display: block;\n    max-width: 100%;\n  }\n"]);

  _templateObject$c = function _templateObject() {
    return data;
  };

  return data;
}

var ResponsivePicture = styled.picture(_templateObject$c());

var Picture = function Picture(_ref) {
  var viewport = _ref.viewport,
      alt = _ref.alt,
      responsive = _ref.responsive;

  var createMediaSource = function createMediaSource(viewportList, alternativeText) {
    return viewportList.sort(function (firstMediaSource, secondMediaSource) {
      return secondMediaSource.width - firstMediaSource.width;
    }).map(function (_ref2, index) {
      var width = _ref2.width,
          srcset = _ref2.srcset;

      if (index === viewportList.length - 1) {
        return React.createElement(React.Fragment, {
          key: width
        }, React.createElement("source", {
          media: "(min-width:".concat(width, "px)"),
          srcSet: srcset
        }), React.createElement("img", {
          src: srcset,
          alt: alt
        }));
      }

      return React.createElement("source", {
        key: width,
        media: "(min-width:".concat(width, "px)"),
        srcSet: srcset
      });
    }, alternativeText);
  };

  if (responsive) {
    return React.createElement(ResponsivePicture, null, createMediaSource(viewport, alt));
  }

  return React.createElement("picture", null, createMediaSource(viewport, alt));
};

Picture.defaultProps = {
  alt: "",
  responsive: false
};

function _templateObject$d() {
  var data = _taggedTemplateLiteral(["\n  width: 16px;\n  height: 16px;\n  color: ", ";\n  ", "\n  text-decoration: none;\n\n  span {\n    display: flex;\n    align-items: center;\n\n    .ngw-icon {\n      margin-right: 10px;\n      width: 18px;\n      height: 18px;\n    }\n  }\n\n  svg {\n    fill: currentColor;\n  }\n"]);

  _templateObject$d = function _templateObject() {
    return data;
  };

  return data;
}

var StyledAnchor = styled.a(_templateObject$d(), palette.blue, D6Typography("body/bold200"));
/**
 * Atom
 * SectionAnchor
 * Notes: An icon as SectionAnchor
 */

var SectionAnchor = function SectionAnchor(_ref) {
  var alt = _ref.alt,
      icon = _ref.icon,
      href = _ref.href,
      sectionName = _ref.sectionName;

  var goToSection = function goToSection(event) {
    event.preventDefault();
    document.querySelector(event.currentTarget.attributes.href.value).scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest"
    });
  };

  return React.createElement(StyledAnchor, {
    onClick: function onClick(event) {
      return goToSection(event);
    },
    href: "#".concat(href),
    title: alt
  }, React.createElement("span", null, React.createElement(Icon, {
    icon: icon
  }), sectionName));
};

SectionAnchor.defaultProps = {
  icon: "arrow-down",
  href: "section",
  sectionName: ""
};

function _templateObject2$5() {
  var data = _taggedTemplateLiteral(["\n  position: absolute;\n  opacity: 0;\n  visibility: hidden;\n\n  & + div {\n    vertical-align: middle;\n    width: 60px;\n    height: 24px;\n    border: 2px solid ", ";\n    border-radius: 12px;\n    background-color: ", ";\n    transition-duration: 0.4s;\n    transition-property: background - color, box - shadow;\n    cursor: pointer;\n\n    span {\n      position: absolute;\n      color: ", ";\n      margin-top: 12px;\n\n      &:nth-child(1) {\n        margin-left: 15px;\n      }\n\n      &:nth-child(2) {\n        margin-left: 55px;\n      }\n    }\n\n    & > div {\n      float: left;\n      width: 16px;\n      height: 16px;\n      border-radius: inherit;\n      background: ", ";\n      transition-timing-function: cubic- bezier(1, 0, 0, 1);\n      transition-duration: 0.4s;\n      transition-property: transform, background - color;\n      pointer-events: none;\n      margin-top: 2px;\n      margin-left: 2px;\n    }\n  }\n\n  &: checked + div {\n    width: 60px;\n    background-position: 0 0;\n    background: ", ";\n\n    & > div {\n      transform: translate3d(36px, 0, 0);\n      background: ", ";\n    }\n  }\n"]);

  _templateObject2$5 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$e() {
  var data = _taggedTemplateLiteral(["\n  display: inline-block;\n"]);

  _templateObject$e = function _templateObject() {
    return data;
  };

  return data;
}

var StyledContainer = styled.div(_templateObject$e());
var StyledInput = styled.input(_templateObject2$5(), palette.d6.blue800, palette.d6.white000, palette.d6.white000, palette.d6.blue800, palette.d6.blue900, palette.d6.white000);

var Switch =
/*#__PURE__*/
function (_React$Component) {
  _inherits(Switch, _React$Component);

  function Switch(props) {
    var _this;

    _classCallCheck(this, Switch);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Switch).call(this, props));

    _this.handleChange = function () {
      var onChange = _this.props.onChange;

      if (typeof onChange === "function") {
        onChange();
      } else {
        _this.setState(function (prevState) {
          return {
            isChecked: !prevState.isChecked
          };
        });
      }
    };

    _this.state = {
      isChecked: props.isChecked || false
    };
    return _this;
  }

  _createClass(Switch, [{
    key: "render",
    value: function render() {
      var propsIsChecked = this.props.isChecked;
      var isChecked = this.state.isChecked;
      var checked = propsIsChecked === null ? isChecked : propsIsChecked;
      return React.createElement(StyledContainer, {
        onClick: this.handleChange
      }, React.createElement(StyledInput, {
        checked: checked,
        className: "switch",
        type: "checkbox",
        onChange: this.handleChange
      }), React.createElement("div", null, React.createElement("div", null)));
    }
  }]);

  return Switch;
}(React.Component);

Switch.defaultProps = {
  isChecked: null,
  onChange: null
};

function _templateObject$f() {
  var data = _taggedTemplateLiteral(["\n  cursor: pointer;\n  background: none;\n  border: none;\n  z-index: 1;\n"]);

  _templateObject$f = function _templateObject() {
    return data;
  };

  return data;
}

var StyledArrow = styled.button(_templateObject$f());

var SliderArrow = function SliderArrow(_ref, props) {
  var icon = _ref.icon,
      to = _ref.to,
      onClick = _ref.onClick,
      size = _ref.size,
      className = _ref.className;
  return React.createElement(StyledArrow, {
    type: "button",
    onClick: onClick,
    className: "".concat(className),
    "aria-label": to
  }, React.createElement(Icon, Object.assign({
    icon: icon,
    size: size
  }, props)));
};

SliderArrow.defaultProps = {
  size: "",
  className: ""
};

function _templateObject$g() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  text-align: left;\n  margin: 0;\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  &:first-child {\n    ", "\n  }\n"]);

  _templateObject$g = function _templateObject() {
    return data;
  };

  return data;
}

var StyledTableColumn = styled.td(_templateObject$g(), palette.d6.grey500, D6Spacing("padding-top", "s150"), D6Spacing("padding-bottom", "s150"), D6Spacing("padding-left", "s250"), D6Spacing("padding-right", "s250"), D6Typography("body/regular150"), D6Typography("body/bold150"));

var getValue = function getValue(value) {
  if (_typeof(value) === "object") {
    return Object.keys(value).map(function (res) {
      if (_typeof(value[res]) !== "object") {
        return value[res];
      }

      return getValue(value[res]);
    });
  }

  return value;
};

var TableColumn = function TableColumn(_ref) {
  var value = _ref.value;
  return React.createElement(StyledTableColumn, null, getValue(value));
};

TableColumn.defaultProps = {
  value: ""
};
/**
 * Atom TableColumn
 */

function _templateObject$h() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  text-align: left;\n  padding: 8px 16px;\n  ", "\n  margin: 0;\n"]);

  _templateObject$h = function _templateObject() {
    return data;
  };

  return data;
}

var StyledTableHead = styled.th(_templateObject$h(), palette.d6.grey500, D6Typography("body/bold150"));
/**
 * Atom TableHead
 */

var TableHead = function TableHead(_ref) {
  var label = _ref.label;
  return React.createElement(StyledTableHead, null, label);
};

TableHead.defaultProps = {
  label: ""
};
/**
 * Atom TableHead
 */

function _templateObject$i() {
  var data = _taggedTemplateLiteral(["\n  p {\n    ", "\n  }\n\n  p + p {\n    ", "\n  }\n\n  a,\n  button {\n    ", "\n    ", "\n    background: transparent;\n    border: none;\n    color: ", ";\n    cursor: pointer;\n    display: block;\n    padding: 0;\n    text-align: left;\n    text-decoration: none;\n\n    &:hover,\n    &:active {\n      color: ", ";\n    }\n\n    &:focus {\n      color: ", ";\n    }\n  }\n"]);

  _templateObject$i = function _templateObject() {
    return data;
  };

  return data;
}

var StyledVWIDConfirmation = styled.div(_templateObject$i(), D6Typography("body/regular200"), D6Spacing("margin-top", "d100"), D6Spacing("margin-top", "d020"), D6Typography("body/regular150"), palette.d6.blue800, palette.d6.blue700, palette.d6.blue900);
/**
 * MyVW
 * Atom
 * VWIDConfirmation
 * ---
 * Tells people which VWID account is being used and allows them to sign out if it's not right.
 * The email address should be populated from the VWID user name.
 * The Sign out link should take the user to the signed out My VW page
 * (this is performed via JS on the /logout template)
 */

var VWIDConfirmation = function VWIDConfirmation(_ref) {
  var summary = _ref.summary,
      email = _ref.email,
      notYou = _ref.notYou,
      link = _ref.link;
  return React.createElement(StyledVWIDConfirmation, {
    className: "a-vwid-confirmation"
  }, React.createElement("p", null, summary, " ", React.createElement("strong", null, email)), React.createElement("p", null, notYou, link.href && !link.onClick && React.createElement("a", {
    href: link.href
  }, link.copy, " >"), !link.href && link.onClick && React.createElement("button", {
    type: "button",
    onClick: link.onClick
  }, link.copy, " >")));
};

function _templateObject$j() {
  var data = _taggedTemplateLiteral(["\n  cursor: pointer;\n  border: none;\n  > button {\n    padding-bottom: 9px;\n    border-bottom: ", ";\n\n    &:focus,\n    &:hover {\n      color: ", ";\n      border-bottom: ", ";\n    }\n    &:focus {\n      outline: none;\n    }\n  }\n"]);

  _templateObject$j = function _templateObject() {
    return data;
  };

  return data;
}

var StyledButton$1 = styled.div(_templateObject$j(), function (_ref) {
  var active = _ref.active;
  return active ? "1px solid currentColor" : "none";
}, palette.d6.blue600, function (_ref2) {
  var active = _ref2.active;
  return active ? "1px solid ".concat(palette.d6.blue600) : "none";
});

var Tab = function Tab(_ref) {
  var id = _ref.id,
      label = _ref.label,
      active = _ref.active,
      onSelect = _ref.onSelect;
  return React.createElement(Container, {
    horizontalAlign: ContainerHorizontalAlignment.center
  }, React.createElement(StyledButton$1, {
    active: active
  }, React.createElement(LinkButton, {
    onClick: function onClick() {
      return onSelect({
        id: id,
        label: label
      });
    }
  }, label)));
};
/**
 * Default argument values
 */


Tab.defaultProps = {
  active: false
};

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _templateObject3$3() {
  var data = _taggedTemplateLiteral(["\n  border-top: 1px solid ", ";\n  border-bottom: 1px solid ", ";\n  overflow: hidden;\n\n  .toggleButton {\n    cursor: pointer;\n    display: flex;\n    justify-content: space-between;\n    align-items: center;\n    padding: 0;\n    ", "\n    ", "\n    width: 100%;\n    background: none;\n    border: 0;\n\n    & > div {\n      flex: 0 1 80%;\n      text-align: left;\n    }\n  }\n\n  & + & {\n    border-top: 0;\n  }\n\n  ", " {\n    ", "\n  }\n"]);

  _templateObject3$3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$6() {
  var data = _taggedTemplateLiteral(["\n  ", "\n"]);

  _templateObject2$6 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$k() {
  var data = _taggedTemplateLiteral(["\n  transition: transform 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);\n"]);

  _templateObject$k = function _templateObject() {
    return data;
  };

  return data;
}

var StyledIcon$1 = styled(Icon)(_templateObject$k());
var StyledRichText = styled(RichText)(_templateObject2$6(), D6Typography("header/light250"));
var StyledAccordionItemContainer = styled.div(_templateObject3$3(), palette.d6.grey100, palette.d6.grey100, D6Spacing("padding-bottom", "s400"), D6Spacing("padding-top", "s400"), StyledIcon$1, function (props) {
  return props.isOpen ? "" : "transform: rotate(45deg)";
});
/**
 * Atom AccordionItem
 */

var handleClick = function handleClick(event, props) {
  var onClick = props.onClick,
      label = props.label;
  onClick(event, label);
};

var AccordionItem = function AccordionItem(_ref) {
  var _onClick = _ref.onClick,
      isOpen = _ref.isOpen,
      label = _ref.label,
      children = _ref.children,
      buttonMobileSize = _ref.buttonMobileSize,
      buttonDesktopSize = _ref.buttonDesktopSize,
      buttonMobileOffset = _ref.buttonMobileOffset,
      buttonDesktopOffset = _ref.buttonDesktopOffset;
  return React.createElement(StyledAccordionItemContainer, {
    role: "tab",
    tabIndex: "0",
    isOpen: isOpen
  }, React.createElement(Row, null, React.createElement(Col, {
    xs: buttonMobileSize,
    md: buttonDesktopSize,
    "xs-offset": buttonMobileOffset,
    "md-offset": buttonDesktopOffset
  }, React.createElement("button", {
    type: "button",
    onClick: function onClick(event) {
      return handleClick(event, {
        onClick: _onClick,
        label: label
      });
    },
    onKeyDown: function onKeyDown(event) {
      return handleClick(event, {
        onClick: _onClick,
        label: label
      });
    },
    className: "toggleButton"
  }, React.createElement(StyledRichText, {
    text: label,
    allowedTags: ["strong", "b"]
  }), React.createElement(StyledIcon$1, {
    icon: "menu-close"
  })))), isOpen && React.createElement("div", null, children));
};

AccordionItem.defaultProps = {
  buttonMobileSize: 22,
  buttonDesktopSize: 20,
  buttonMobileOffset: 1,
  buttonDesktopOffset: 2
};
/**
 * Atom AccordionItem
 */

var Accordion =
/*#__PURE__*/
function (_Component) {
  _inherits(Accordion, _Component);

  function Accordion(props) {
    var _this;

    _classCallCheck(this, Accordion);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Accordion).call(this, props));

    _this.onClick = function (event, label) {
      var _assertThisInitialize = _assertThisInitialized(_this),
          allowMultipleOpen = _assertThisInitialize.props.allowMultipleOpen,
          openAccordionItems = _assertThisInitialize.state.openAccordionItems;

      var isOpen = !!openAccordionItems[label]; // openAccordionItems[label] ? true : false

      if (event.type === "click" || event.key.toLowerCase() === "enter") {
        if (allowMultipleOpen) {
          _this.setState({
            openAccordionItems: _objectSpread2({}, openAccordionItems, _defineProperty({}, label, !isOpen))
          });
        } else {
          _this.setState({
            openAccordionItems: _defineProperty({}, label, !isOpen)
          });
        }
      }
    };

    var _openAccordionItems = {};
    var children = _this.props.children;
    /**
     * This allows the Accordion to have multiple accordions open
     * By definition normally all of them are closed but it can happen to have one or more open
     */

    children.forEach(function (child) {
      if (child.props.isOpen) {
        _openAccordionItems[child.props.label] = true;
      }
    });
    _this.state = {
      openAccordionItems: _openAccordionItems
    };
    return _this;
  }

  _createClass(Accordion, [{
    key: "render",
    value: function render() {
      var onClick = this.onClick,
          children = this.props.children,
          openAccordionItems = this.state.openAccordionItems;
      return React.createElement("div", null, children.map(function (child) {
        return React.createElement(AccordionItem, {
          isOpen: !!openAccordionItems[child.props.label] // openAccordionItems[child.props.label] ? true : false
          ,
          label: child.props.label,
          key: generateUuid(child.props.label),
          onClick: onClick,
          buttonMobileSize: child.props.buttonMobileSize || 22,
          buttonDesktopSize: child.props.buttonDesktopSize || 20,
          buttonMobileOffset: child.props.buttonMobileOffset || 1,
          buttonDesktopOffset: child.props.buttonDesktopOffset || 2
        }, child.props.children);
      }));
    }
  }]);

  return Accordion;
}(Component);

Accordion.defaultProps = {
  allowMultipleOpen: false
};
/**
 * Molecule Accordion
 */

/**
 * formatCurrencyWithDecimals
 * format a number into £X,XXX or £X.XX
 * @param amount
 * @param decimalCount
 * @param decimal
 * @param thousands
 * @param prefix
 */

var formatCurrencyWithDecimals = function formatCurrencyWithDecimals(amount) {
  var decimalCount = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;
  var decimal = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ".";
  var thousands = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : ",";
  var prefix = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : "£";
  var toReturn = "";

  try {
    var $decimalCount = Math.abs(decimalCount);
    $decimalCount = Number.isNaN($decimalCount) ? 2 : $decimalCount;
    var negativeSign = amount < 0 ? "-" : "";
    var $amount = Math.abs(Number(amount) || 0).toFixed($decimalCount);
    var value = parseInt($amount, 10).toString();
    var commaIndex = value.length > 3 ? value.length % 3 : 0;
    var decimalValue = Math.abs($amount - value).toFixed($decimalCount).slice(2);
    toReturn = "".concat(negativeSign).concat(prefix).concat((commaIndex ? value.substr(0, commaIndex) + thousands : "") + value.substr(commaIndex).replace(/(\d{3})(?=\d)/g, "$1".concat(thousands)) + (decimalValue !== "00" ? decimal + decimalValue : ""));
  } catch (e) {
    return null;
  }

  return toReturn;
};

function _templateObject8() {
  var data = _taggedTemplateLiteral(["\n  background-color: ", ";\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  border-radius: 100%;\n  color: white;\n  width: 24px;\n  height: 24px;\n  ", ";\n  ", ";\n"]);

  _templateObject8 = function _templateObject8() {
    return data;
  };

  return data;
}

function _templateObject7() {
  var data = _taggedTemplateLiteral(["\n  position: absolute;\n  top: -93px;\n  width: 100%;\n\n  img {\n    width: 100%;\n  }\n"]);

  _templateObject7 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6() {
  var data = _taggedTemplateLiteral(["\n  ", "\n"]);

  _templateObject6 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5() {
  var data = _taggedTemplateLiteral([""]);

  _templateObject5 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$1() {
  var data = _taggedTemplateLiteral(["\n  cursor: pointer;\n  color: ", ";\n"]);

  _templateObject4$1 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$4() {
  var data = _taggedTemplateLiteral(["\n  align-items: center;\n  background: none;\n  border: none;\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n"]);

  _templateObject3$4 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$7() {
  var data = _taggedTemplateLiteral(["\n  cursor: pointer;\n"]);

  _templateObject2$7 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$l() {
  var data = _taggedTemplateLiteral(["\n  position: relative;\n  border: 1px solid #dfe4e8;\n  border-radius: 4px;\n  background-color: #ffffff;\n  height: 100%;\n  display: flex;\n  flex-direction: column;\n  align-items: stretch;\n  justify-content: center;\n"]);

  _templateObject$l = function _templateObject() {
    return data;
  };

  return data;
}

var StyledCarCard = styled.div(_templateObject$l());
var StyledCarCardLink = styled.div(_templateObject2$7());
var StyledButton$2 = styled.button(_templateObject3$4());
var StyledAddBtn = styled(StyledButton$2)(_templateObject4$1(), palette.d6.blue600);
var StyledIcon$2 = styled.div(_templateObject5());
var StyledAddIcon = styled(StyledIcon$2)(_templateObject6(), D6Spacing("padding-bottom", "s200"));
var StyledCarImg = styled.div(_templateObject7());
var StyledFuelTypeIcon = styled(StyledIcon$2)(_templateObject8(), function (_ref) {
  var bgColor = _ref.bgColor;
  return bgColor || palette.d6.blue200;
}, D6Spacing("margin-left", "s200"), D6Spacing("padding", "s100"));

var Icon$1 = function Icon(type) {
  return {
    Hybrid: React.createElement(Hybrid, null),
    Electric: React.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      height: "18",
      viewBox: "0 0 190 190",
      width: "18",
      fill: "white"
    }, React.createElement("path", {
      d: "m155.109 74.028a4 4 0 0 0 -3.48-2.028h-52.4l8.785-67.123a4.023 4.023 0 0 0 -7.373-2.614l-63.724 111.642a4 4 0 0 0 3.407 6.095h51.617l-6.962 67.224a4.024 4.024 0 0 0 7.411 2.461l62.671-111.63a4 4 0 0 0 .048-4.027z"
    }))
  }[type];
};
/**
 * Functional component to render Add a car card
 * @param props
 * @returns {*}
 * @constructor
 */


var AddCarCard = function AddCarCard(props) {
  var modelName = props.modelName,
      derivativeId = props.derivativeId,
      derivativeName = props.derivativeName,
      fuelType = props.fuelType,
      price = props.price,
      onAdd = props.onAdd;
  var data = modelName && derivativeId && derivativeName && fuelType && price > 0;
  return React.createElement(React.Fragment, null, !data && React.createElement(EmptyCard, {
    add: onAdd
  }), data && React.createElement(CarCard, props));
};
/**
 * Render this component when data is empty
 * @param add
 * @returns {HTMLElement}
 * @constructor
 */


var EmptyCard = function EmptyCard(_ref) {
  var add = _ref.add;
  return React.createElement(StyledCarCard, null, React.createElement(Container, {
    horizontalAlign: ContainerHorizontalAlignment.center,
    padding: {
      top: ContainerPadding.static300,
      right: ContainerPadding.static300,
      bottom: ContainerPadding.static300,
      left: ContainerPadding.static300
    }
  }, React.createElement(StyledAddBtn, {
    onClick: function onClick() {
      return add();
    }
  }, React.createElement(StyledAddIcon, null, React.createElement(Add, null)), React.createElement(Text, {
    tag: "h3",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.headline250
  }, "Add car"))));
};
/**
 * Render this component when data is present
 * @param modelName
 * @param image
 * @param derivativeId
 * @param derivativeName
 * @param price
 * @param fuelType
 * @param canDelete
 * @param onEdit
 * @param onDelete
 * @returns {*}
 * @constructor
 */


var CarCard = function CarCard(_ref2) {
  var modelName = _ref2.modelName,
      image = _ref2.image,
      derivativeId = _ref2.derivativeId,
      derivativeName = _ref2.derivativeName,
      price = _ref2.price,
      fuelType = _ref2.fuelType,
      canDelete = _ref2.canDelete,
      onEdit = _ref2.onEdit,
      onDelete = _ref2.onDelete;
  return React.createElement(StyledCarCard, null, React.createElement(StyledCarImg, null, React.createElement(Container, {
    horizontalAlign: ContainerHorizontalAlignment.center
  }, React.createElement(Picture, {
    viewport: [{
      width: 992,
      srcset: image
    }, {
      width: 768,
      srcset: image
    }, {
      width: 320,
      srcset: image
    }]
  }))), React.createElement(Container, {
    stretchContent: true,
    horizontalAlign: ContainerHorizontalAlignment.center,
    wrap: ContainerWrap.always,
    padding: {
      top: ContainerPadding.static450,
      right: ContainerPadding.static300,
      bottom: ContainerPadding.static300,
      left: ContainerPadding.static300
    }
  }, React.createElement(Container, {
    padding: {
      top: ContainerPadding.static200
    }
  }, React.createElement(Text, {
    tag: "h3",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.headline300
  }, modelName), fuelType && (fuelType === "Electric" || fuelType === "Hybrid") && React.createElement(Container, {
    horizontalAlign: ContainerHorizontalAlignment.center
  }, React.createElement(StyledFuelTypeIcon, {
    bgColor: fuelType === "Hybrid" && palette.blue
  }, Icon$1(fuelType)))), React.createElement(Container, {
    padding: {
      top: ContainerPadding.static200
    }
  }, React.createElement(Text, {
    tag: "p",
    color: TextColor.inherit,
    appearance: TextAppearance.copy150
  }, derivativeName)), React.createElement(Container, {
    padding: {
      top: ContainerPadding.static200
    }
  }, React.createElement(Text, {
    tag: "p",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.copy200
  }, "From ", formatCurrencyWithDecimals(price))), React.createElement(Container, {
    padding: {
      top: ContainerPadding.static200
    }
  }, React.createElement(Text, {
    tag: "p",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.copy150
  }, fuelType)), React.createElement(Container, {
    padding: {
      top: ContainerPadding.static200
    },
    horizontalAlign: ContainerHorizontalAlignment.spaceBetween
  }, React.createElement(StyledCarCardLink, null, React.createElement(Link, {
    isInline: true,
    onClick: function onClick() {
      return onEdit(derivativeId);
    }
  }, "Edit car")), canDelete && React.createElement(StyledCarCardLink, null, React.createElement(Link, {
    isInline: true,
    onClick: function onClick() {
      return onDelete(derivativeId);
    }
  }, React.createElement(Bin, null))))));
};

AddCarCard.defaultProps = {
  image: "",
  modelName: "",
  derivativeId: "",
  derivativeName: "",
  fuelType: "",
  price: 0,
  canDelete: false,
  onEdit: function onEdit() {},
  onAdd: function onAdd() {},
  onDelete: function onDelete() {}
};
/* eslint-disable react/no-unused-prop-types */

EmptyCard.defaultProps = {
  add: function add() {}
};
CarCard.defaultProps = {
  image: "",
  modelName: "",
  derivativeId: "",
  derivativeName: "",
  fuelType: "",
  price: 0,
  canDelete: false,
  onEdit: function onEdit() {},
  onDelete: function onDelete() {}
};

function _templateObject10() {
  var data = _taggedTemplateLiteral(["\n  display: block;\n  color: ", ";\n  ", "\n"]);

  _templateObject10 = function _templateObject10() {
    return data;
  };

  return data;
}

function _templateObject9() {
  var data = _taggedTemplateLiteral(["\n  background-color: transparent;\n  cursor: pointer;\n  display: inline-block;\n  color: ", ";\n  text-decoration: none;\n  text-align: left;\n  border: none;\n  padding: 0;\n  outline: none;\n  ", "\n  ", "\n  ", "\n\n  &:focus,\n  &:hover {\n    color: ", ";\n  }\n"]);

  _templateObject9 = function _templateObject9() {
    return data;
  };

  return data;
}

function _templateObject8$1() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: baseline;\n  justify-content: space-between;\n  ", "\n  ", "\n  > span {\n    margin-right: auto;\n  }\n  i {\n    ", ";\n  }\n"]);

  _templateObject8$1 = function _templateObject8() {
    return data;
  };

  return data;
}

function _templateObject7$1() {
  var data = _taggedTemplateLiteral(["\n  background-color: ", ";\n  color: ", ";\n  ", "\n  ", "\n  ", "\n\n  &:focus,\n  &:hover {\n    background-color: ", ";\n    color: ", ";\n  }\n\n  &:active {\n    background-color: ", ";\n    color: ", ";\n  }\n"]);

  _templateObject7$1 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6$1() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  border: none;\n  background: none;\n  outline: none;\n  cursor: pointer;\n\n  &:focus,\n  &:hover {\n    color: ", ";\n  }\n\n  &:active {\n    color: ", ";\n  }\n"]);

  _templateObject6$1 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5$1() {
  var data = _taggedTemplateLiteral(["\n  word-break: break-word;\n  flex-grow: 1;\n  ", ";\n  ", ";\n  ", ";\n  ", ";\n"]);

  _templateObject5$1 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$2() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  flex-grow: 1;\n  align-items: center;\n  justify-content: center;\n  flex-direction: column;\n  ", "\n  ", "\n  ", "\n  \n  i {\n    ", "\n  }\n\n  @media screen and (min-width: ", ") {\n    min-height: 300px;\n  }\n"]);

  _templateObject4$2 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$5() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  cursor: pointer;\n  text-decoration: none;\n  flex-grow: 1;\n  display: flex;\n\n  &:focus,\n  &:hover {\n    color: ", ";\n  }\n\n  &:active {\n    color: ", ";\n  }\n"]);

  _templateObject3$5 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$8() {
  var data = _taggedTemplateLiteral(["\n  cursor: pointer;\n  background-color: transparent;\n  border: none;\n  padding: 0;\n  outline: none;\n  overflow: hidden;\n  height: ", ";\n  border-top: ", ";\n  background-image: ", ";\n  background-repeat: no-repeat;\n  background-size: cover;\n"]);

  _templateObject2$8 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$m() {
  var data = _taggedTemplateLiteral(["\n  border: 1px solid ", ";\n  display: flex;\n  width: 100%;\n  flex-direction: column;\n  color: ", ";\n  justify-content: center;\n  align-items: stretch;\n  max-width: 400px;\n\n  hr {\n    border-bottom: 0.3pt solid ", ";\n  }\n"]);

  _templateObject$m = function _templateObject() {
    return data;
  };

  return data;
}

var StyledAddedCarCard = styled.div(_templateObject$m(), palette.blue, palette.black, palette.grayLight);
var Thumbnail = styled.button(_templateObject2$8(), function (props) {
  return props.background ? "138px" : "0";
}, function (props) {
  return props.background ? "0" : "1px solid ".concat(palette.blue);
}, function (props) {
  return "url(".concat(props.background, ")");
});
var AddCarLink = styled.a(_templateObject3$5(), palette.d6.blue800, palette.d6.blue700, palette.d6.blue900);
var EmptyAddCarCard = styled.div(_templateObject4$2(), D6Spacing("padding-bottom", "d100"), D6Spacing("padding-top", "d100"), D6Typography("body/bold200"), D6Spacing("margin-bottom", "s200"), breakpoints.md);
var Tile = styled.div(_templateObject5$1(), D6Spacing("padding-top", "d100"), D6Spacing("padding-left", "d100"), D6Spacing("padding-right", "d100"), D6Spacing("padding-bottom", "d100"));
var Button = styled.button(_templateObject6$1(), palette.d6.blue800, palette.d6.blue700, palette.d6.blue900);
var CollectedButton = styled(Button)(_templateObject7$1(), palette.d6.blue800, palette.white, D6Spacing("padding-bottom", "s250"), D6Spacing("padding-top", "s250"), D6Typography("body/regular200"), palette.d6.blue700, palette.d6.white000, palette.d6.blue900, palette.d6.white000);
var Type = styled.span(_templateObject8$1(), D6Spacing("margin-bottom", "s150"), D6Typography("body/regular200"), D6Spacing("margin-left", "s250"));
var Name = styled.button(_templateObject9(), palette.black, D6Typography("body/bold200"), D6Spacing("margin-top", "s250"), D6Spacing("margin-bottom", "s250"), palette.d6.blue700);
var Details = styled.span(_templateObject10(), palette.gray, D6Typography("body/regular150"));
/**
 * Molecule M-0009 AddedCarCard
 */

var AddedCarCard = function AddedCarCard(_ref) {
  var id = _ref.id,
      image = _ref.image,
      type = _ref.type,
      name = _ref.name,
      details = _ref.details,
      collect = _ref.collect,
      handleAddCar = _ref.handleAddCar,
      handleRemove = _ref.handleRemove,
      handleCollect = _ref.handleCollect,
      handleSettings = _ref.handleSettings,
      handleRedirect = _ref.handleRedirect;

  if (handleAddCar) {
    return React.createElement(StyledAddedCarCard, {
      className: "addedCarCard-empty"
    }, React.createElement(AddCarLink, {
      onClick: handleAddCar
    }, React.createElement(EmptyAddCarCard, null, React.createElement(Icon, {
      size: "lg",
      icon: "add",
      fill: "currentColor"
    }), React.createElement("span", null, "Add a car"))));
  }

  return React.createElement(StyledAddedCarCard, {
    className: "addedCarCard"
  }, React.createElement(Thumbnail, {
    className: "addedCarCard__thumbnail",
    background: image,
    onClick: function onClick() {
      return handleRedirect(id);
    },
    title: "View ".concat(name)
  }), React.createElement(Tile, {
    className: "addedCarCard__tile"
  }, React.createElement(Type, null, type && React.createElement("span", null, type), handleRemove && React.createElement(Button, {
    onClick: function onClick() {
      return handleRemove(id);
    },
    title: "Delete ".concat(name)
  }, React.createElement(Icon, {
    size: "xs",
    icon: "bin",
    fill: "currentColor"
  })), handleSettings && React.createElement(Button, {
    onClick: function onClick() {
      return handleSettings(id);
    },
    title: "Edit ".concat(name)
  }, React.createElement(Icon, {
    size: "xs",
    icon: "settings",
    fill: "currentColor"
  }))), React.createElement("hr", null), name && React.createElement(Name, {
    className: "addedCarCard__name",
    onClick: function onClick() {
      return handleRedirect(id);
    }
  }, name), details && React.createElement(Details, {
    className: "addedCarCard__details"
  }, details)), collect && handleCollect && React.createElement(CollectedButton, {
    className: "addedCarCard__collected",
    onClick: function onClick() {
      return handleCollect(id);
    }
  }, "I've collected my car"));
};

AddedCarCard.defaultProps = {
  collect: false,
  handleAddCar: null,
  handleRemove: null,
  handleCollect: null,
  handleSettings: null,
  handleRedirect: null
};
/**
 * Molecule M-0009 AddedCarCard
 */

var Video = function Video(_ref) {
  var src = _ref.src;
  return (// eslint-disable-next-line jsx-a11y/media-has-caption
    React.createElement("video", {
      controls: true
    }, React.createElement("source", {
      src: src
    }), React.createElement("p", null, "Your browser does not support HTML5 video. Here is a", React.createElement("a", {
      href: src
    }, "link to the video"), " instead."))
  );
};

Video.defaultProps = {
  src: ""
};

function _templateObject$n() {
  var data = _taggedTemplateLiteral(["\n  .slick-list,\n  .slick-slider,\n  .slick-track {\n    position: relative;\n    display: block;\n  }\n  .slick-loading .slick-slide,\n  .slick-loading .slick-track {\n    visibility: hidden;\n  }\n  .slick-slider {\n    box-sizing: border-box;\n    user-select: none;\n    touch-action: pan-y;\n    -webkit-tap-highlight-color: transparent;\n    margin-bottom: 80px;\n  }\n  .slick-list {\n    ", "\n    margin: 0;\n    padding: 0;\n  }\n  .slick-list:focus {\n    outline: 0;\n  }\n  .slick-list.dragging {\n    cursor: pointer;\n    cursor: hand;\n  }\n  .slick-slider .slick-list,\n  .slick-slider .slick-track {\n    transform: translate3d(0, 0, 0);\n  }\n  .slick-track {\n    top: 0;\n    left: 0;\n  }\n  .slick-track:after,\n  .slick-track:before {\n    display: table;\n    content: \"\";\n  }\n  .slick-track:after {\n    clear: both;\n  }\n  .slick-slide {\n    display: none;\n    float: left;\n    height: 100%;\n    min-height: 1px;\n  }\n  .slick-slide img {\n    width: 100%;\n    display: block;\n    margin: 0 auto;\n  }\n  .slick-slide.slick-loading img {\n    display: none;\n  }\n  .slick-slide.dragging img {\n    pointer-events: none;\n  }\n  .slick-initialized .slick-slide {\n    display: block;\n  }\n  .slick-vertical .slick-slide {\n    display: block;\n    height: auto;\n    border: 1px solid transparent;\n  }\n  .slick-arrow.slick-hidden {\n    display: none;\n  }\n  .slick-dots,\n  .slick-next,\n  .slick-prev {\n    position: absolute;\n    display: block;\n    padding: 0;\n  }\n  .slick-next,\n  .slick-prev {\n    bottom: -70px;\n    transform: translate(0, -50%);\n    cursor: pointer;\n    position: absolute;\n\n    &:focus,\n    &:hover {\n      color: transparent;\n      outline: 0;\n      background: 0 0;\n\n      &:before {\n        opacity: 1;\n      }\n    }\n\n    &.slick-disabled:before {\n      opacity: 0.25;\n    }\n  }\n  .slick-prev {\n    left: 10%;\n  }\n  .slick-next {\n    right: 10%;\n  }\n  .slick-dotted.slick-slider {\n    margin-bottom: 30px;\n  }\n  .slick-dots {\n    width: 100%;\n    margin: 6px 0 0 0;\n    list-style: none;\n    text-align: center;\n    li {\n      position: relative;\n      display: inline-flex;\n      align-items: center;\n      cursor: pointer;\n\n      button {\n        font-size: 0;\n        line-height: 0;\n        display: block;\n        padding: 5px;\n        color: transparent;\n        border: 0;\n        outline: none;\n        background: transparent;\n\n        &:before {\n          font-family: slick, serif;\n          font-size: 20px;\n          line-height: 20px;\n          position: absolute;\n          top: 0;\n          left: 0;\n          width: 20px;\n          height: 20px;\n          content: \"\\2022\";\n          text-align: center;\n          opacity: 0.25;\n          color: #000;\n          -webkit-font-smoothing: antialiased;\n        }\n      }\n\n      &.slick-active {\n        button {\n          &:before {\n            opacity: 0.75;\n            color: ", ";\n            font-size: 30px;\n          }\n        }\n      }\n\n      &:first-child {\n        margin-left: 0;\n      }\n    }\n  }\n"], ["\n  .slick-list,\n  .slick-slider,\n  .slick-track {\n    position: relative;\n    display: block;\n  }\n  .slick-loading .slick-slide,\n  .slick-loading .slick-track {\n    visibility: hidden;\n  }\n  .slick-slider {\n    box-sizing: border-box;\n    user-select: none;\n    touch-action: pan-y;\n    -webkit-tap-highlight-color: transparent;\n    margin-bottom: 80px;\n  }\n  .slick-list {\n    ", "\n    margin: 0;\n    padding: 0;\n  }\n  .slick-list:focus {\n    outline: 0;\n  }\n  .slick-list.dragging {\n    cursor: pointer;\n    cursor: hand;\n  }\n  .slick-slider .slick-list,\n  .slick-slider .slick-track {\n    transform: translate3d(0, 0, 0);\n  }\n  .slick-track {\n    top: 0;\n    left: 0;\n  }\n  .slick-track:after,\n  .slick-track:before {\n    display: table;\n    content: \"\";\n  }\n  .slick-track:after {\n    clear: both;\n  }\n  .slick-slide {\n    display: none;\n    float: left;\n    height: 100%;\n    min-height: 1px;\n  }\n  .slick-slide img {\n    width: 100%;\n    display: block;\n    margin: 0 auto;\n  }\n  .slick-slide.slick-loading img {\n    display: none;\n  }\n  .slick-slide.dragging img {\n    pointer-events: none;\n  }\n  .slick-initialized .slick-slide {\n    display: block;\n  }\n  .slick-vertical .slick-slide {\n    display: block;\n    height: auto;\n    border: 1px solid transparent;\n  }\n  .slick-arrow.slick-hidden {\n    display: none;\n  }\n  .slick-dots,\n  .slick-next,\n  .slick-prev {\n    position: absolute;\n    display: block;\n    padding: 0;\n  }\n  .slick-next,\n  .slick-prev {\n    bottom: -70px;\n    transform: translate(0, -50%);\n    cursor: pointer;\n    position: absolute;\n\n    &:focus,\n    &:hover {\n      color: transparent;\n      outline: 0;\n      background: 0 0;\n\n      &:before {\n        opacity: 1;\n      }\n    }\n\n    &.slick-disabled:before {\n      opacity: 0.25;\n    }\n  }\n  .slick-prev {\n    left: 10%;\n  }\n  .slick-next {\n    right: 10%;\n  }\n  .slick-dotted.slick-slider {\n    margin-bottom: 30px;\n  }\n  .slick-dots {\n    width: 100%;\n    margin: 6px 0 0 0;\n    list-style: none;\n    text-align: center;\n    li {\n      position: relative;\n      display: inline-flex;\n      align-items: center;\n      cursor: pointer;\n\n      button {\n        font-size: 0;\n        line-height: 0;\n        display: block;\n        padding: 5px;\n        color: transparent;\n        border: 0;\n        outline: none;\n        background: transparent;\n\n        &:before {\n          font-family: slick, serif;\n          font-size: 20px;\n          line-height: 20px;\n          position: absolute;\n          top: 0;\n          left: 0;\n          width: 20px;\n          height: 20px;\n          content: \"\\\\2022\";\n          text-align: center;\n          opacity: 0.25;\n          color: #000;\n          -webkit-font-smoothing: antialiased;\n        }\n      }\n\n      &.slick-active {\n        button {\n          &:before {\n            opacity: 0.75;\n            color: ", ";\n            font-size: 30px;\n          }\n        }\n      }\n\n      &:first-child {\n        margin-left: 0;\n      }\n    }\n  }\n"]);

  _templateObject$n = function _templateObject() {
    return data;
  };

  return data;
}

var SlickContainer = styled.div(_templateObject$n(), function (props) {
  return props.allowOverflow ? "" : "overflow: hidden;";
}, palette.d6.blue600);

function _templateObject$o() {
  var data = _taggedTemplateLiteral(["\n          ", "\n        "]);

  _templateObject$o = function _templateObject() {
    return data;
  };

  return data;
}

var Carousel = function Carousel(_ref) {
  var className = _ref.className,
      settings = _ref.settings,
      children = _ref.children,
      containerCSS = _ref.containerCSS,
      allowOverflow = _ref.allowOverflow;

  var slideSettings = _objectSpread2({}, settings, {
    prevArrow: React.createElement(SliderArrow, {
      className: "slick-prev",
      to: "prev",
      icon: "chevron-left"
    }),
    nextArrow: React.createElement(SliderArrow, {
      className: "slick-next",
      to: "next",
      icon: "chevron-right"
    })
  });

  var CarouselContainer = containerCSS !== null ? styled(SlickContainer)(_templateObject$o(), containerCSS) : SlickContainer;
  return React.createElement(CarouselContainer, {
    allowOverflow: allowOverflow
  }, React.createElement(Slider, Object.assign({
    className: className
  }, slideSettings), children));
};

Carousel.defaultProps = {
  type: "",
  settings: "",
  className: "",
  containerCSS: null,
  allowOverflow: false
};

function _templateObject9$1() {
  var data = _taggedTemplateLiteral(["\n  position: absolute;\n  right: -25px;\n  top: 2px;\n"]);

  _templateObject9$1 = function _templateObject9() {
    return data;
  };

  return data;
}

function _templateObject8$2() {
  var data = _taggedTemplateLiteral(["\n  ", "\n  ", "\n  color: ", ";\n  display: inline-block;\n  background: transparent;\n  border: none;\n  padding: 0;\n  position: relative;\n  cursor: pointer;\n"]);

  _templateObject8$2 = function _templateObject8() {
    return data;
  };

  return data;
}

function _templateObject7$2() {
  var data = _taggedTemplateLiteral(["\n  display: inline-block;\n  position: relative;\n"]);

  _templateObject7$2 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6$2() {
  var data = _taggedTemplateLiteral(["\n  display: block;\n  max-width: 100%;\n"]);

  _templateObject6$2 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5$2() {
  var data = _taggedTemplateLiteral(["\n  border-radius: 100%;\n  border: 4px solid #fff;\n  width: 48px;\n  height: 48px;\n  top: 50%;\n\n  .ngw-icon {\n    position: absolute;\n    color: ", ";\n    top: 50%;\n    margin-top: -12px;\n    left: 50%;\n    margin-left: -9px;\n  }\n"]);

  _templateObject5$2 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$3() {
  var data = _taggedTemplateLiteral(["\n  position: relative;\n\n  .align-content {\n    position: absolute;\n    height: 100%;\n    width: 100%;\n    display: flex;\n    justify-content: center;\n    align-items: center;\n\n    &::after {\n      content: \"\";\n      background: linear-gradient(\n        180deg,\n        rgba(91, 91, 91, 0) 0%,\n        rgba(28, 28, 28, 0.83) 43.27%,\n        #161616 100%\n      );\n      position: absolute;\n      height: 50%;\n      width: 100%;\n      bottom: 0;\n    }\n  }\n"]);

  _templateObject4$3 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$6() {
  var data = _taggedTemplateLiteral(["\n  &.slick-slider {\n    margin-bottom: 0;\n  }\n  .slick-dots {\n    bottom: 10px;\n    width: 100%;\n    margin: 0;\n    list-style: none;\n    text-align: center;\n  }\n  .slick-dots li {\n    position: relative;\n    display: inline-block;\n    width: 20px;\n    height: 20px;\n    margin: 0 5px;\n    padding: 0;\n    cursor: pointer;\n  }\n  .slick-dots li button {\n    font-size: 0;\n    line-height: 0;\n    display: block;\n    width: 20px;\n    height: 20px;\n    padding: 5px;\n    cursor: pointer;\n    color: transparent;\n    border: 0;\n    outline: 0;\n    background: 0 0;\n  }\n  .slick-dots li button:focus,\n  .slick-dots li button:hover {\n    outline: 0;\n  }\n  .slick-dots li button:focus:before,\n  .slick-dots li button:hover:before {\n    opacity: 1;\n  }\n  .slick-dots li button:before {\n    font-size: 12px;\n    line-height: 21px;\n    position: absolute;\n    top: 0;\n    left: 0;\n    width: 20px;\n    height: 20px;\n    content: \"\u2022\";\n    text-align: center;\n    color: #fff;\n  }\n  .slick-dots li.slick-active button:before {\n    font-size: 22px;\n    color: #fff;\n  }\n"], ["\n  &.slick-slider {\n    margin-bottom: 0;\n  }\n  .slick-dots {\n    bottom: 10px;\n    width: 100%;\n    margin: 0;\n    list-style: none;\n    text-align: center;\n  }\n  .slick-dots li {\n    position: relative;\n    display: inline-block;\n    width: 20px;\n    height: 20px;\n    margin: 0 5px;\n    padding: 0;\n    cursor: pointer;\n  }\n  .slick-dots li button {\n    font-size: 0;\n    line-height: 0;\n    display: block;\n    width: 20px;\n    height: 20px;\n    padding: 5px;\n    cursor: pointer;\n    color: transparent;\n    border: 0;\n    outline: 0;\n    background: 0 0;\n  }\n  .slick-dots li button:focus,\n  .slick-dots li button:hover {\n    outline: 0;\n  }\n  .slick-dots li button:focus:before,\n  .slick-dots li button:hover:before {\n    opacity: 1;\n  }\n  .slick-dots li button:before {\n    font-size: 12px;\n    line-height: 21px;\n    position: absolute;\n    top: 0;\n    left: 0;\n    width: 20px;\n    height: 20px;\n    content: \"\\u2022\";\n    text-align: center;\n    color: #fff;\n  }\n  .slick-dots li.slick-active button:before {\n    font-size: 22px;\n    color: #fff;\n  }\n"]);

  _templateObject3$6 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$9() {
  var data = _taggedTemplateLiteral(["\n  ", "\n  background-color: ", ";\n  max-height: 50vh;\n  overflow-y: auto;\n\n  h1, h2 {\n    ", "\n    ", "\n    padding-bottom: 0;\n  }\n\n  p {\n    ", "\n    ", "\n    color: ", ";\n  }\n"]);

  _templateObject2$9 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$p() {
  var data = _taggedTemplateLiteral(["\n  h3 {\n    ", ";\n    ", "\n    ", "\n  }\n\n  h4 {\n    ", "\n    ", "\n    color: ", ";\n  }\n\n  .description {\n    ", "\n    color: ", ";\n    white-space: initial;\n  }\n"]);

  _templateObject$p = function _templateObject() {
    return data;
  };

  return data;
}

var StyledArticleCard = styled(Col)(_templateObject$p(), D6Typography("header/light250"), D6Spacing("padding-top", "s250"), D6Spacing("padding-bottom", "s250"), D6Typography("body/regular200"), D6Spacing("padding-bottom", "s150"), palette.d6.blue800, D6Typography("body/regular150"), palette.d6.grey400);
var StyledModalBody$1 = styled.div(_templateObject2$9(), D6Spacing("padding", "d100"), palette.d6.white000, D6Typography("header/light300"), D6Spacing("margin-bottom", "s250"), D6Typography("body/regular150"), D6Spacing("margin-bottom", "s250"), palette.d6.grey400);
var ThumbnailCarousel = styled(Carousel)(_templateObject3$6());
var StyledThumbnailWrapper = styled.div(_templateObject4$3());
var StyledYoutubePlayer = styled.div(_templateObject5$2(), palette.d6.white000);
var ResponsiveImg = styled.img(_templateObject6$2());
var StyledButtonContainer = styled.div(_templateObject7$2());
var StyledButton$3 = styled.button(_templateObject8$2(), D6Typography("body/bold200"), D6Spacing("margin-top", "s200"), palette.blue);
var StyledIconButton = styled(StyledButton$3)(_templateObject9$1());

function _templateObject$q() {
  var data = _taggedTemplateLiteral(["\n  position: relative;\n  display: block;\n  width: 100%;\n  padding: 0;\n  overflow: hidden;\n\n  &::before {\n    content: \"\";\n    display: block;\n    padding-top: ", ";\n  }\n\n  iframe {\n    position: absolute;\n    top: 0;\n    bottom: 0;\n    left: 0;\n    width: 100%;\n    height: 100%;\n    border: 0;\n  }\n"]);

  _templateObject$q = function _templateObject() {
    return data;
  };

  return data;
}

var StyledIframeContainer = styled.div(_templateObject$q(), function (props) {
  return function () {
    if (props.aspectRatio === "4:3") {
      return "75%";
    }

    return "56.25%";
  };
});

var ResponsiveYoutube = function ResponsiveYoutube(_ref) {
  var videoId = _ref.videoId,
      aspectRatio = _ref.aspectRatio,
      opts = _ref.opts,
      _onPlay = _ref.onPlay;
  var defaultOpts = {
    enablejsapi: true
  };

  var modalOpts = _objectSpread2({}, defaultOpts, {}, opts);

  return React.createElement(StyledIframeContainer, {
    aspectRatio: aspectRatio
  }, React.createElement(YouTube, {
    videoId: videoId,
    opts: modalOpts,
    onPlay: function onPlay(event) {
      return _onPlay({
        event: event,
        videoId: videoId
      });
    }
  }));
};

ResponsiveYoutube.defaultProps = {
  opts: "",
  aspectRatio: "16:9",
  onPlay: function onPlay() {}
};
/**
 * Molecule ResponsiveYoutube
 */

var defaultOpts = {
  enablejsapi: true
};

var MediaAsset = function MediaAsset(_ref) {
  var asset = _ref.asset,
      onClick = _ref.onClick,
      onMouseDown = _ref.onMouseDown;

  if (asset.video) {
    return React.createElement(React.Fragment, null, React.createElement(StyledThumbnailWrapper, null, React.createElement("button", {
      className: "align-content",
      onClick: onClick,
      onMouseDown: onMouseDown,
      type: "button"
    }, React.createElement(StyledYoutubePlayer, null, React.createElement(Icon, {
      icon: "play",
      fill: "currentColor"
    }))), React.createElement("img", {
      src: asset.image,
      alt: asset.label
    })));
  }

  return React.createElement("img", {
    src: asset.image,
    alt: asset.label
  });
};

var ArticleCard =
/*#__PURE__*/
function (_Component) {
  _inherits(ArticleCard, _Component);

  function ArticleCard(props) {
    var _this;

    _classCallCheck(this, ArticleCard);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(ArticleCard).call(this, props));

    _this.handleMouseDown = function (e) {
      _this.x = e.screenX;
    };

    _this.toggleModal = function (id, asset, e) {
      var delta = Math.abs(e.screenX - _this.x);

      if (delta < 10) {
        // If mouse moved more than threshold, ignore the click event
        _this.setState({
          videoId: id || "",
          isOpen: true,
          selectedAsset: asset
        });
      }

      _this.x = 0;
    };

    _this.closeModal = function () {
      _this.setState({
        isOpen: false
      });
    };

    _this.setVideo = function (index) {
      var assets = _this.props.assets;

      _this.setState({
        videoId: assets.length > 1 ? _this.retrieveYoutubeId(assets[index].video) : ""
      });
    };

    _this.retrieveYoutubeId = function (video) {
      return video ? video.split(".be/")[1] : "";
    };

    _this.carouselSettings = {
      arrows: false,
      afterChange: function afterChange(index) {
        return _this.setVideo(index);
      }
    };
    _this.state = {
      isOpen: false,
      videoId: props.assets.length > 1 ? _this.retrieveYoutubeId(props.assets[0].video) : ""
    };
    return _this;
  }

  _createClass(ArticleCard, [{
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$state = this.state,
          isOpen = _this$state.isOpen,
          videoId = _this$state.videoId,
          selectedAsset = _this$state.selectedAsset;

      var _this$props = this.props,
          title = _this$props.title,
          subtitle = _this$props.subtitle,
          description = _this$props.description,
          assets = _this$props.assets,
          infoButton = _this$props.infoButton,
          onVideoPlay = _this$props.onVideoPlay,
          isCitNow = _this$props.isCitNow,
          canScrollModal = _this$props.canScrollModal,
          props = _objectWithoutProperties(_this$props, ["title", "subtitle", "description", "assets", "infoButton", "onVideoPlay", "isCitNow", "canScrollModal"]);

      return React.createElement(StyledArticleCard, {
        md: props.md || 8,
        "md-offset": props["md-offset"] || 0,
        sm: props.sm || 24,
        "sm-offset": props["sm-offset"] || 0,
        xs: 24,
        className: props.className
      }, React.createElement(Modal$1, Object.assign({
        canScroll: canScrollModal,
        isOpen: isOpen,
        handleClick: this.closeModal,
        onBackgroundClick: this.closeModal,
        onEscapeKeydown: this.closeModal,
        elementTrigger: function elementTrigger() {
          return React.createElement(ThumbnailCarousel, {
            settings: _this2.carouselSettings
          }, assets.map(function (asset) {
            return React.createElement(MediaAsset, {
              asset: asset,
              key: generateUuid(asset.id),
              onMouseDown: _this2.handleMouseDown,
              onClick: function onClick(event) {
                return _this2.toggleModal(_this2.retrieveYoutubeId(asset.video), asset, event);
              }
            });
          }));
        }
      }, props), !isCitNow && videoId && React.createElement(ResponsiveYoutube, {
        videoId: videoId,
        opts: defaultOpts,
        onPlay: function onPlay() {
          return onVideoPlay(selectedAsset);
        }
      }), isCitNow && React.createElement(Video, {
        src: assets[0].video
      }), title && description && React.createElement(StyledModalBody$1, null, React.createElement("h2", null, title), React.createElement(RichText, {
        text: "<p>".concat(description, "</p>"),
        allowedTags: ["strong", "b", "h1", "h2", "h3", "h4", "h5", "p", "a", "ul", "li"]
      }))), title && React.createElement("h3", null, title), subtitle && React.createElement("h4", null, subtitle), description && !infoButton && React.createElement("p", {
        className: "description"
      }, description), description && infoButton && React.createElement(ClampLines, {
        text: description,
        className: "description",
        lines: 2,
        buttons: false
      }), infoButton && React.createElement(StyledButtonContainer, null, React.createElement(StyledButton$3, {
        onClick: function onClick() {
          _this2.setState({
            isOpen: true
          });
        }
      }, "More info"), React.createElement(StyledIconButton, {
        onClick: function onClick() {
          _this2.setState({
            isOpen: true
          });
        }
      }, React.createElement(Icon, {
        icon: "add",
        sizePX: "16px"
      }))));
    }
  }]);

  return ArticleCard;
}(Component);

ArticleCard.defaultProps = {
  subtitle: "",
  description: "",
  infoButton: false,
  isCitNow: false,
  canScrollModal: false,
  onVideoPlay: function onVideoPlay() {}
};
Modal$1.propTypes = {
  /** Extend classname from styled-components */
  className: PropTypes.node.isRequired,

  /** Modal event to show/hide to be inherited from MediaAsset */
  handleClick: PropTypes.func.isRequired
};
/**
 * Molecule ArticleCard
 */

function _templateObject6$3() {
  var data = _taggedTemplateLiteral(["\n  flex: 0 0 39%;\n  ", ";\n\n  @media (min-width: ", ") {\n    flex: 1;\n    ", ";\n  }\n"]);

  _templateObject6$3 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5$3() {
  var data = _taggedTemplateLiteral(["\n  flex: ", ";\n  flex-direction: ", ";\n  border-right: ", ";\n  align-items: flex-start;\n  ", ";\n  @media (min-width: ", ") {\n    ", ";\n  }\n"]);

  _templateObject5$3 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$4() {
  var data = _taggedTemplateLiteral(["\n  align-self: stretch;\n  flex-shrink: 1;\n"]);

  _templateObject4$4 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$7() {
  var data = _taggedTemplateLiteral(["\n  width: 100%;\n  border-bottom: ", ";\n"]);

  _templateObject3$7 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$a() {
  var data = _taggedTemplateLiteral(["\n  flex-direction: column;\n  flex: ", ";\n  div:last-child {\n    border-bottom: none;\n  }\n"]);

  _templateObject2$a = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$r() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: center;\n  justify-content: flex-start;\n"]);

  _templateObject$r = function _templateObject() {
    return data;
  };

  return data;
}

var Flex = styled.div(_templateObject$r());
var StyledTable = styled(Flex)(_templateObject2$a(), function (_ref) {
  var flex = _ref.flex;
  return flex && flex;
});
var StyledRow = styled(Flex)(_templateObject3$7(), function (_ref2) {
  var bold = _ref2.bold;
  return bold ? "2px solid black" : "1px solid black";
});
var StyledCell = styled(Flex)(_templateObject4$4());

var Padding = function Padding(_ref3) {
  var top = _ref3.top,
      bottom = _ref3.bottom,
      left = _ref3.left,
      right = _ref3.right;
  return "\n    ".concat(top ? D6Spacing("padding-top", top) : "", ";\n    ").concat(bottom ? D6Spacing("padding-bottom", bottom) : "", ";\n    ").concat(left ? D6Spacing("padding-left", left) : "", ";\n    ").concat(right ? D6Spacing("padding-right", right) : "", ";\n  ");
};

var LeftCol = styled(StyledCell)(_templateObject5$3(), function (_ref4) {
  var flex = _ref4.flex;
  return flex && flex;
}, function (_ref5) {
  var direction = _ref5.direction;
  return direction && direction;
}, function (_ref6) {
  var borderRight = _ref6.borderRight;
  return borderRight && "2px solid black";
}, function (_ref7) {
  var padding = _ref7.padding;
  return padding.sm && Padding(_objectSpread2({}, padding.sm));
}, breakpoints.md, function (_ref8) {
  var padding = _ref8.padding;
  return padding.md && Padding(_objectSpread2({}, padding.md));
});
var RightCol = styled(StyledCell)(_templateObject6$3(), D6Spacing("padding-left", "s400"), breakpoints.md, D6Spacing("padding-left", "s450"));

var DynamicText = function DynamicText(_ref) {
  var text = _ref.text,
      bold = _ref.bold,
      breakpoint = _ref.breakpoint,
      small = _ref.small,
      medium = _ref.medium;
  return React.createElement(React.Fragment, null, React.createElement(BreakpointWrapper, {
    max: breakpoint
  }, React.createElement(Text, {
    appearance: small,
    bold: bold
  }, text)), React.createElement(BreakpointWrapper, {
    min: breakpoint
  }, React.createElement(Text, {
    appearance: medium,
    bold: bold
  }, text)));
};

var TwoCols = function TwoCols(_ref2) {
  var rows = _ref2.rows;
  return React.createElement(StyledTable, null, rows && rows.map(function (_ref3) {
    var label = _ref3.label,
        value = _ref3.value;
    return React.createElement(StyledRow, {
      key: label
    }, React.createElement(LeftCol, {
      flex: 1,
      justify: "flex-start",
      borderRight: true,
      padding: {
        sm: {
          top: "s250",
          bottom: "s250"
        },
        md: {
          top: "s350",
          bottom: "s350",
          left: "s450"
        }
      }
    }, React.createElement(Container, null, React.createElement(DynamicText, {
      text: label,
      breakpoint: Breakpoints.b960,
      small: TextAppearance.label200,
      medium: TextAppearance.label250
    }))), React.createElement(RightCol, {
      justify: "flex-start"
    }, React.createElement(DynamicText, {
      text: value || "N/A",
      tag: "p",
      breakpoint: Breakpoints.b960,
      small: TextAppearance.label200,
      medium: TextAppearance.label250
    })));
  }));
};

var SingleCol = function SingleCol(_ref4) {
  var rows = _ref4.rows,
      isRight = _ref4.isRight;
  return React.createElement(StyledTable, {
    flex: 1
  }, rows && rows.map(function (_ref5) {
    var label = _ref5.label,
        value = _ref5.value,
        bold = _ref5.bold;
    return React.createElement(StyledRow, {
      key: label,
      bold: bold
    }, React.createElement(LeftCol, {
      flex: 1,
      justify: "flex-start",
      direction: "column",
      borderRight: !isRight,
      breakpoint: Breakpoints.b960,
      padding: {
        sm: {
          top: "s200",
          left: "s250",
          bottom: bold ? "s150" : "s250"
        },
        md: {
          top: "s200",
          bottom: "s250",
          left: "s450"
        }
      }
    }, bold ? React.createElement(Container, null, bold ? React.createElement(DynamicText, {
      text: label,
      breakpoint: Breakpoints.b960,
      small: TextAppearance.label200,
      medium: TextAppearance.label250,
      bold: true
    }) : React.createElement(DynamicText, {
      text: label,
      breakpoint: Breakpoints.b960,
      small: TextAppearance.label200,
      medium: TextAppearance.label250
    })) : React.createElement(Container, {
      padding: {
        bottom: ContainerPadding.static150
      }
    }, bold ? React.createElement(DynamicText, {
      text: label,
      breakpoint: Breakpoints.b960,
      small: TextAppearance.label200,
      medium: TextAppearance.label250,
      bold: true
    }) : React.createElement(DynamicText, {
      text: label,
      breakpoint: Breakpoints.b960,
      small: TextAppearance.label100,
      medium: TextAppearance.label150
    })), value && React.createElement(DynamicText, {
      text: value,
      breakpoint: Breakpoints.b960,
      small: TextAppearance.label200,
      medium: TextAppearance.label250,
      bold: label === "Total"
    })));
  }));
};

var ComparisonTable = function ComparisonTable(_ref6) {
  var _ref6$rows = _ref6.rows,
      rows = _ref6$rows === void 0 ? [] : _ref6$rows;

  var _rows$0$map = rows[0].map(function (col, i) {
    return rows.map(function (row) {
      return row[i];
    });
  }),
      _rows$0$map2 = _slicedToArray(_rows$0$map, 2),
      col1 = _rows$0$map2[0],
      col2 = _rows$0$map2[1];

  return React.createElement(Flex, null, col1.length > 0 && col2.length > 0 && React.createElement(React.Fragment, null, React.createElement(SingleCol, {
    rows: col1
  }), React.createElement(SingleCol, {
    rows: col2,
    isRight: true
  })));
};

var BorderedTable = function BorderedTable(_ref7) {
  var rows = _ref7.rows,
      isComparisonTable = _ref7.isComparisonTable;
  return React.createElement(React.Fragment, null, isComparisonTable ? React.createElement(ComparisonTable, {
    rows: rows
  }) : React.createElement(TwoCols, {
    rows: rows
  }));
};

DynamicText.defaultProps = {
  bold: false
};
TwoCols.defaultProps = {
  rows: null
};
SingleCol.defaultProps = {
  rows: null,
  isRight: false
};
ComparisonTable.defaultProps = {
  rows: []
};
BorderedTable.defaultProps = {
  rows: null,
  isComparisonTable: false
};

function _templateObject2$b() {
  var data = _taggedTemplateLiteral(["\n  cursor: pointer;\n"]);

  _templateObject2$b = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$s() {
  var data = _taggedTemplateLiteral(["\n  background-color: ", ";\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  border-radius: 100%;\n  color: white;\n  width: ", ";\n  height: ", ";\n  flex-shrink: 1;\n  ", ";\n  ", ";\n  ", ";\n"]);

  _templateObject$s = function _templateObject() {
    return data;
  };

  return data;
}

var StyledElectricIcon = styled.div(_templateObject$s(), function (_ref) {
  var bgColor = _ref.bgColor;
  return bgColor || palette.d6.blue200;
}, function (_ref2) {
  var width = _ref2.width;
  return "".concat(width, "px") || "28px";
}, function (_ref3) {
  var height = _ref3.height;
  return "".concat(height, "px") || "28px";
}, D6Spacing("padding", "s100"), D6Spacing("margin-left", "s250"), D6Spacing("margin-bottom", "s100"));
var StyledCarCardLink$1 = styled.div(_templateObject2$b());

var PrimaryCarDetailsIcon = function PrimaryCarDetailsIcon(type) {
  return {
    Electric: React.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      height: "19px",
      viewBox: "0 0 192 192",
      width: "19px",
      fill: "white"
    }, React.createElement("path", {
      fill: "white",
      d: "m155.109 74.028a4 4 0 0 0 -3.48-2.028h-52.4l8.785-67.123a4.023 4.023 0 0 0 -7.373-2.614l-63.724 111.642a4 4 0 0 0 3.407 6.095h51.617l-6.962 67.224a4.024 4.024 0 0 0 7.411 2.461l62.671-111.63a4 4 0 0 0 .048-4.027z"
    })),
    Hybrid: React.createElement(Hybrid, null)
  }[type];
};

var Mobile = function Mobile(_ref) {
  var modelName = _ref.modelName,
      derivativeName = _ref.derivativeName,
      fuelType = _ref.fuelType,
      price = _ref.price;
  return React.createElement("div", null, React.createElement(Container, {
    stretchContent: true,
    horizontalAlign: ContainerHorizontalAlignment.center,
    wrap: ContainerWrap.always,
    padding: {
      top: ContainerPadding.static450,
      bottom: ContainerPadding.static300
    }
  }, React.createElement(Container, {
    verticalAlign: ContainerVerticalAlignment.center,
    padding: {
      top: ContainerPadding.static200
    }
  }, React.createElement(Text, {
    tag: "h3",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.headline200
  }, modelName), React.createElement(StyledElectricIcon, {
    bgColor: fuelType === "Hybrid" && palette.d6.blue900,
    width: 18,
    height: 18
  }, PrimaryCarDetailsIcon(fuelType))), React.createElement(Container, {
    stretchContent: true,
    verticalAlign: ContainerVerticalAlignment.center,
    padding: {
      top: ContainerPadding.static150
    }
  }, React.createElement(Text, {
    tag: "p",
    color: TextColor.inherit,
    textAlign: TextAlignment.left,
    appearance: TextAppearance.copy100
  }, derivativeName)), React.createElement(Container, {
    padding: {
      top: ContainerPadding.static150
    },
    stretchContent: true,
    wrap: ContainerWrap.always
  }, React.createElement(Text, {
    tag: "p",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.copy150
  }, "From ", formatCurrencyWithDecimals(price)), React.createElement(Text, {
    tag: "p",
    bold: true,
    color: TextColor.inherit,
    textAlign: TextAlignment.left,
    appearance: TextAppearance.copy100
  }, fuelType))));
};

var Desktop = function Desktop(_ref2) {
  var derivativeId = _ref2.derivativeId,
      modelName = _ref2.modelName,
      derivativeName = _ref2.derivativeName,
      fuelType = _ref2.fuelType,
      price = _ref2.price,
      onEdit = _ref2.onEdit;
  return React.createElement("div", null, React.createElement(Container, {
    stretchContent: true,
    horizontalAlign: ContainerHorizontalAlignment.center,
    wrap: ContainerWrap.always,
    padding: {
      top: ContainerPadding.static450,
      bottom: ContainerPadding.static300
    }
  }, React.createElement(Container, {
    verticalAlign: ContainerVerticalAlignment.center,
    padding: {
      top: ContainerPadding.static200
    }
  }, React.createElement(Text, {
    tag: "h3",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.headline300
  }, modelName), React.createElement(StyledElectricIcon, {
    bgColor: fuelType === "Hybrid" && palette.d6.blue900
  }, PrimaryCarDetailsIcon(fuelType))), React.createElement(Container, {
    stretchContent: true,
    verticalAlign: ContainerVerticalAlignment.center,
    padding: {
      top: ContainerPadding.static200
    }
  }, React.createElement(Layout, {
    appearance: _defineProperty({}, Breakpoints.default, [{
      name: "a",
      columns: 19
    }, {
      name: ".",
      columns: 1
    }, {
      name: "b",
      columns: 4
    }])
  }, React.createElement(Text, {
    tag: "p",
    color: TextColor.inherit,
    textAlign: TextAlignment.left,
    appearance: TextAppearance.copy250
  }, derivativeName), React.createElement(Text, {
    tag: "p",
    bold: true,
    color: TextColor.inherit,
    textAlign: TextAlignment.right,
    appearance: TextAppearance.copy250
  }, fuelType))), React.createElement(Container, {
    padding: {
      top: ContainerPadding.static200
    }
  }, React.createElement(Text, {
    tag: "p",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.copy250
  }, "From ", formatCurrencyWithDecimals(price))), React.createElement(Container, {
    padding: {
      top: ContainerPadding.static200,
      bottom: ContainerPadding.static150
    },
    horizontalAlign: ContainerHorizontalAlignment.spaceBetween
  }, React.createElement(StyledCarCardLink$1, null, React.createElement(Text, {
    tag: TextTag.p,
    appearance: TextAppearance.copy200,
    bold: true
  }, React.createElement(Link, {
    isInline: true,
    onClick: function onClick() {
      return onEdit(derivativeId);
    }
  }, "Edit car"))))));
};
/**
 * Primary Car Details
 * @returns {*}
 * @constructor
 */


var CarDetails = function CarDetails(props) {
  return React.createElement(React.Fragment, null, React.createElement(BreakpointWrapper, {
    max: Breakpoints.b960
  }, React.createElement(Mobile, props)), React.createElement(BreakpointWrapper, {
    min: Breakpoints.b960
  }, React.createElement(Desktop, props)));
};

Mobile.defaultProps = {
  modelName: "",
  derivativeName: "",
  fuelType: "",
  price: 0
};
Desktop.defaultProps = {
  derivativeId: "",
  modelName: "",
  derivativeName: "",
  fuelType: "",
  price: 0,
  onEdit: function onEdit() {}
};
/**
 * Default argument values
 * @type {{modelName: string, fuelType: string, price: number, derivativeName: string, derivativeId: string}}
 */

CarDetails.defaultProps = {
  derivativeId: "",
  modelName: "",
  derivativeName: "",
  fuelType: "",
  price: 0,
  onEdit: function onEdit() {}
};

function _templateObject$t() {
  var data = _taggedTemplateLiteral(["\n  border: 0;\n  outline: 0;\n  padding: 0;\n  margin: 0;\n  color: #00287e;\n  font-family: ", ";\n  font-size: 14px;\n  line-height: 20px;\n"]);

  _templateObject$t = function _templateObject() {
    return data;
  };

  return data;
}

var Trigger = styled.button(_templateObject$t(), fonts.textRegular);

var CollapsibleContent =
/*#__PURE__*/
function (_Component) {
  _inherits(CollapsibleContent, _Component);

  function CollapsibleContent(props) {
    var _this;

    _classCallCheck(this, CollapsibleContent);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(CollapsibleContent).call(this, props));

    _this.toggle = function () {
      var children = _this.props.children;

      if (!children) {
        return;
      }

      _this.setState(function (prevState) {
        return {
          expanded: !prevState.expanded
        };
      });
    };

    _this.state = {
      expanded: props.expanded
    };
    return _this;
  }

  _createClass(CollapsibleContent, [{
    key: "render",
    value: function render() {
      var expanded = this.state.expanded;
      var _this$props = this.props,
          children = _this$props.children,
          trigger = _this$props.trigger;
      return React.createElement(React.Fragment, null, trigger && React.createElement(Trigger, {
        type: "button",
        onClick: this.toggle
      }, trigger), expanded && children);
    }
  }]);

  return CollapsibleContent;
}(Component);

CollapsibleContent.defaultProps = {
  expanded: false
};

function _templateObject3$8() {
  var data = _taggedTemplateLiteral(["\n  border: 0;\n  border-top: 1px solid\n    ", ";\n  ", "\n"]);

  _templateObject3$8 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$c() {
  var data = _taggedTemplateLiteral(["\n  border-radius: 100%;\n  width: 40px;\n  height: 40px;\n  border: 1px solid ", ";\n  color: ", ";\n  cursor: pointer;\n  padding: 0;\n  ", "\n\n  &::after {\n    content: \"+\";\n    display: flex;\n    font-size: 48px;\n    align-items: center;\n    justify-content: center;\n    height: 100%;\n    width: 100%;\n    margin-top: -8px;\n  }\n"]);

  _templateObject2$c = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$u() {
  var data = _taggedTemplateLiteral(["\n  footer {\n    display: flex;\n    flex-direction: column;\n    align-items: center;\n  }\n\n  .load-more {\n    ", "\n    ", "\n    color: ", ";\n  }\n"]);

  _templateObject$u = function _templateObject() {
    return data;
  };

  return data;
}

var StyledCollapsibleWrapper = styled(Col)(_templateObject$u(), D6Typography("body/regular150"), D6Spacing("padding-bottom", "s150"), palette.d6.blue800);
var StyledLoadMore = styled.button(_templateObject2$c(), palette.d6.blue800, palette.d6.blue800, D6Typography("header/light250"));
var SeparateContent = styled.hr(_templateObject3$8(), function (props) {
  return props.borderColor ? props.borderColor : "#D4DDE0";
}, D6Spacing("margin-bottom", "s300"));

var CollapsibleWrapper =
/*#__PURE__*/
function (_Component) {
  _inherits(CollapsibleWrapper, _Component);

  function CollapsibleWrapper(props) {
    var _this;

    _classCallCheck(this, CollapsibleWrapper);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(CollapsibleWrapper).call(this, props));

    _this.showContent = function (event) {
      event.preventDefault();
      var childrenLength = _this.state.childrenLength;

      _this.setState(function (prevState) {
        var _this$props = _this.props,
            rowsToLoad = _this$props.rowsToLoad,
            data = _this$props.data;
        var rowsToShow = rowsToLoad + prevState.numberOfVisibleRows;
        var isHidden = rowsToShow >= childrenLength;
        return {
          isHidden: isHidden,
          numberOfVisibleRows: rowsToShow,
          childrenToRender: data.slice(0, rowsToShow)
        };
      });
    };

    var _this$props2 = _this.props,
        rowsVisible = _this$props2.rowsVisible,
        _data = _this$props2.data;
    var _childrenLength = _data.length;

    var childrenToRender = _data.slice(0, rowsVisible);

    _this.state = {
      isHidden: _childrenLength <= childrenToRender.length,
      childrenLength: _childrenLength,
      numberOfVisibleRows: rowsVisible,
      childrenToRender: childrenToRender
    };
    return _this;
  }

  _createClass(CollapsibleWrapper, [{
    key: "render",
    value: function render() {
      var _this$state = this.state,
          numberOfVisibleRows = _this$state.numberOfVisibleRows,
          isHidden = _this$state.isHidden,
          childrenToRender = _this$state.childrenToRender;

      var _this$props3 = this.props,
          loadMoreTitle = _this$props3.loadMoreTitle,
          showHR = _this$props3.showHR,
          elementToBeHidden = _this$props3.elementToBeHidden,
          children = _this$props3.children,
          rowsVisible = _this$props3.rowsVisible,
          props = _objectWithoutProperties(_this$props3, ["loadMoreTitle", "showHR", "elementToBeHidden", "children", "rowsVisible"]);

      return React.createElement(StyledCollapsibleWrapper, {
        tag: elementToBeHidden,
        rows: numberOfVisibleRows,
        className: props.className,
        md: props.md,
        "md-offset": props["md-offset"]
      }, React.createElement(Row, null, React.createElement(Col, {
        className: "collapsible-content"
      }, children(childrenToRender))), React.createElement(Row, null, React.createElement(Col, null, showHR && React.createElement(SeparateContent, null), !isHidden && React.createElement("footer", null, React.createElement("p", {
        className: "load-more"
      }, loadMoreTitle), React.createElement(StyledLoadMore, {
        type: "button",
        onClick: this.showContent
      })))));
    }
  }]);

  return CollapsibleWrapper;
}(Component);

CollapsibleWrapper.defaultProps = {
  rowsVisible: 1,
  showHR: false,
  rowsToLoad: 1
};
/**
 * Molecule CollapsibleWrapper
 */

function _templateObject$v() {
  var data = _taggedTemplateLiteral(["\n  font-family: ", ";\n  padding: 10px 0;\n  position: relative;\n\n  & + & {\n    margin-top: 5px;\n  }\n"]);

  _templateObject$v = function _templateObject() {
    return data;
  };

  return data;
}

var StyledFormField = styled.div(_templateObject$v(), fonts.textRegular);

var FormField = function FormField(_ref) {
  var children = _ref.children;
  return React.createElement(StyledFormField, {
    className: "m-form-field"
  }, children);
};

function _templateObject2$d() {
  var data = _taggedTemplateLiteral(["\n  .m-form-field {\n    color: ", ";\n    max-width: 202px;\n\n    :focus-within {\n      color: ", ";\n\n      button {\n        color: currentColor;\n      }\n    }\n  }\n\n  /**\n   * 1. This font-size is not responsive\n   */\n  legend {\n    ", "\n    color: ", ";\n    cursor: text;\n    font-family: ", "; /* [1] */\n    font-size: 16px; /* [1] */\n  }\n\n  input {\n    background-color: transparent;\n    border: 1px solid transparent;\n    border-bottom-color: ", ";\n    border-radius: 0;\n    box-shadow: none;\n    color: ", ";\n    font-family: ", ";\n    font-size: 16px; /* [1] */\n    height: 30px;\n    margin: 0;\n    ", "\n    outline: 0; /* [2] */\n    padding: 0;\n    transition: border 0.3s;\n    width: 100%;\n\n    :hover {\n      border-bottom-color: ", ";\n    }\n\n    :focus {\n      border-bottom-color: ", ";\n    }\n\n    [disabled] {\n      border-bottom-color: currentColor;\n      color: ", ";\n    }\n  }\n\n  button {\n    background: transparent;\n    border: none;\n    color: ", ";\n    position: absolute;\n    right: -10px;\n    top: 13px; /* Firefox renders this too low but \xAF_(\u30C4)_/\xAF */\n\n    :hover {\n      color: ", ";\n    }\n  }\n"], ["\n  .m-form-field {\n    color: ", ";\n    max-width: 202px;\n\n    :focus-within {\n      color: ", ";\n\n      button {\n        color: currentColor;\n      }\n    }\n  }\n\n  /**\n   * 1. This font-size is not responsive\n   */\n  legend {\n    ", "\n    color: ", ";\n    cursor: text;\n    font-family: ", "; /* [1] */\n    font-size: 16px; /* [1] */\n  }\n\n  input {\n    background-color: transparent;\n    border: 1px solid transparent;\n    border-bottom-color: ", ";\n    border-radius: 0;\n    box-shadow: none;\n    color: ", ";\n    font-family: ", ";\n    font-size: 16px; /* [1] */\n    height: 30px;\n    margin: 0;\n    ", "\n    outline: 0; /* [2] */\n    padding: 0;\n    transition: border 0.3s;\n    width: 100%;\n\n    :hover {\n      border-bottom-color: ", ";\n    }\n\n    :focus {\n      border-bottom-color: ", ";\n    }\n\n    [disabled] {\n      border-bottom-color: currentColor;\n      color: ", ";\n    }\n  }\n\n  button {\n    background: transparent;\n    border: none;\n    color: ", ";\n    position: absolute;\n    right: -10px;\n    top: 13px; /* Firefox renders this too low but \xAF\\_(\u30C4)_/\xAF */\n\n    :hover {\n      color: ", ";\n    }\n  }\n"]);

  _templateObject2$d = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$w() {
  var data = _taggedTemplateLiteral(["\n  font-family: ", ";\n  font-size: 14px;\n\n  .react-datepicker-popper[data-placement^=\"bottom\"]\n    .react-datepicker__triangle,\n  .react-datepicker-popper[data-placement^=\"top\"] .react-datepicker__triangle,\n  .react-datepicker__year-read-view--down-arrow,\n  .react-datepicker__month-read-view--down-arrow,\n  .react-datepicker__month-year-read-view--down-arrow {\n    margin-left: -8px;\n    position: absolute;\n  }\n\n  .react-datepicker-popper[data-placement^=\"bottom\"]\n    .react-datepicker__triangle,\n  .react-datepicker-popper[data-placement^=\"top\"] .react-datepicker__triangle,\n  .react-datepicker__year-read-view--down-arrow,\n  .react-datepicker__month-read-view--down-arrow,\n  .react-datepicker__month-year-read-view--down-arrow,\n  .react-datepicker-popper[data-placement^=\"bottom\"]\n    .react-datepicker__triangle::before,\n  .react-datepicker-popper[data-placement^=\"top\"]\n    .react-datepicker__triangle::before,\n  .react-datepicker__year-read-view--down-arrow::before,\n  .react-datepicker__month-read-view--down-arrow::before,\n  .react-datepicker__month-year-read-view--down-arrow::before {\n    border: 8px solid transparent;\n    box-sizing: content-box;\n    height: 0;\n    position: absolute;\n    width: 1px;\n  }\n\n  .react-datepicker-popper[data-placement^=\"bottom\"]\n    .react-datepicker__triangle::before,\n  .react-datepicker-popper[data-placement^=\"top\"]\n    .react-datepicker__triangle::before,\n  .react-datepicker__year-read-view--down-arrow::before,\n  .react-datepicker__month-read-view--down-arrow::before,\n  .react-datepicker__month-year-read-view--down-arrow::before {\n    content: \"\";\n    border-bottom-color: ", ";\n    border-width: 8px;\n    left: -8px;\n    z-index: -1;\n  }\n\n  .react-datepicker-popper[data-placement^=\"bottom\"]\n    .react-datepicker__triangle {\n    margin-top: -8px;\n    top: 0;\n  }\n\n  .react-datepicker-popper[data-placement^=\"bottom\"]\n    .react-datepicker__triangle,\n  .react-datepicker-popper[data-placement^=\"bottom\"]\n    .react-datepicker__triangle::before {\n    border-bottom-color: ", ";\n    border-top: none;\n  }\n\n  .react-datepicker-popper[data-placement^=\"bottom\"]\n    .react-datepicker__triangle::before {\n    border-bottom-color: ", ";\n    top: -1px;\n  }\n\n  .react-datepicker-popper[data-placement^=\"top\"] .react-datepicker__triangle,\n  .react-datepicker__year-read-view--down-arrow,\n  .react-datepicker__month-read-view--down-arrow,\n  .react-datepicker__month-year-read-view--down-arrow {\n    bottom: 0;\n    margin-bottom: -8px;\n  }\n\n  .react-datepicker-popper[data-placement^=\"top\"] .react-datepicker__triangle,\n  .react-datepicker__year-read-view--down-arrow,\n  .react-datepicker__month-read-view--down-arrow,\n  .react-datepicker__month-year-read-view--down-arrow,\n  .react-datepicker-popper[data-placement^=\"top\"]\n    .react-datepicker__triangle::before,\n  .react-datepicker__year-read-view--down-arrow::before,\n  .react-datepicker__month-read-view--down-arrow::before,\n  .react-datepicker__month-year-read-view--down-arrow::before {\n    border-bottom: none;\n    border-top-color: ", ";\n  }\n\n  .react-datepicker-popper[data-placement^=\"top\"]\n    .react-datepicker__triangle::before,\n  .react-datepicker__year-read-view--down-arrow::before,\n  .react-datepicker__month-read-view--down-arrow::before,\n  .react-datepicker__month-year-read-view--down-arrow::before {\n    border-top-color: ", ";\n    bottom: -1px;\n  }\n\n  .react-datepicker-wrapper {\n    display: inline-block;\n  }\n\n  .react-datepicker {\n    background-color: ", ";\n    border: 1px solid ", ";\n    color: ", ";\n    display: inline-block;\n    font-size: 16px;\n    position: relative;\n  }\n\n  .react-datepicker--time-only .react-datepicker__triangle {\n    left: 35px;\n  }\n\n  .react-datepicker__triangle {\n    position: absolute;\n    left: 50px;\n  }\n\n  .react-datepicker-popper {\n    z-index: 1;\n  }\n\n  .react-datepicker__year-read-view {\n    min-width: 79px;\n  }\n\n  .react-datepicker-popper[data-placement^=\"bottom\"] {\n    margin-top: 10px;\n  }\n\n  .react-datepicker-popper[data-placement^=\"top\"] {\n    margin-bottom: 10px;\n  }\n\n  .react-datepicker-popper[data-placement^=\"right\"] {\n    margin-left: 8px;\n  }\n\n  .react-datepicker-popper[data-placement^=\"right\"]\n    .react-datepicker__triangle {\n    left: auto;\n    right: 42px;\n  }\n\n  .react-datepicker-popper[data-placement^=\"left\"] {\n    margin-right: 8px;\n  }\n\n  .react-datepicker-popper[data-placement^=\"left\"] .react-datepicker__triangle {\n    left: 42px;\n    right: auto;\n  }\n\n  .react-datepicker__header {\n    background-color: ", ";\n    border-bottom: 1px solid ", ";\n    padding-top: 8px;\n    position: relative;\n    text-align: center;\n  }\n\n  .react-datepicker__header--time {\n    padding-bottom: 8px;\n    padding-left: 5px;\n    padding-right: 5px;\n  }\n\n  .react-datepicker__year-dropdown-container--select,\n  .react-datepicker__month-dropdown-container--select,\n  .react-datepicker__month-year-dropdown-container--select,\n  .react-datepicker__year-dropdown-container--scroll,\n  .react-datepicker__month-dropdown-container--scroll,\n  .react-datepicker__month-year-dropdown-container--scroll {\n    border: 1px solid ", ";\n    display: inline-block;\n    font-size: 14px;\n    margin: 5px 2px 0;\n    padding: 3px;\n  }\n\n  .react-datepicker__current-month,\n  .react-datepicker-time__header {\n    color: ", ";\n    font-family: ", ";\n    font-size: 16px;\n    margin-top: 0;\n  }\n\n  .react-datepicker-time__header {\n    overflow: hidden;\n    text-overflow: ellipsis;\n    white-space: nowrap;\n  }\n\n  .react-datepicker__navigation {\n    background: none;\n    border: 8px solid transparent;\n    cursor: pointer;\n    height: 10px;\n    line-height: 17px;\n    overflow: hidden;\n    padding: 0;\n    position: absolute;\n    text-align: center;\n    text-indent: -999em;\n    top: 10px;\n    width: 10px;\n    width: 0;\n    z-index: 1;\n  }\n\n  .react-datepicker__navigation--previous {\n    border-right-color: ", ";\n    left: 10px;\n  }\n\n  .react-datepicker__navigation--previous:hover {\n    border-right-color: ", ";\n  }\n\n  .react-datepicker__navigation--previous--disabled,\n  .react-datepicker__navigation--previous--disabled:hover {\n    border-right-color: #e6e6e6;\n    cursor: default;\n  }\n\n  .react-datepicker__navigation--next {\n    border-left-color: ", ";\n    right: 10px;\n  }\n\n  .react-datepicker__navigation--next--with-time:not(.react-datepicker__navigation--next--with-today-button) {\n    right: 80px;\n  }\n\n  .react-datepicker__navigation--next:hover {\n    border-left-color: ", ";\n  }\n\n  .react-datepicker__navigation--next--disabled,\n  .react-datepicker__navigation--next--disabled:hover {\n    border-left-color: ", ";\n    cursor: default;\n  }\n\n  .react-datepicker__navigation--years {\n    position: relative;\n    top: 0;\n    display: block;\n    margin-left: auto;\n    margin-right: auto;\n  }\n\n  .react-datepicker__navigation--years-previous {\n    top: 4px;\n    border-top-color: ", ";\n  }\n\n  .react-datepicker__navigation--years-previous:hover {\n    border-top-color: ", ";\n  }\n\n  .react-datepicker__navigation--years-upcoming {\n    top: -4px;\n    border-bottom-color: ", ";\n  }\n\n  .react-datepicker__navigation--years-upcoming:hover {\n    border-bottom-color: ", ";\n  }\n\n  .react-datepicker__month-container {\n    float: left;\n  }\n\n  .react-datepicker__month {\n    margin: 4px;\n    text-align: center;\n  }\n\n  .react-datepicker__day-names,\n  .react-datepicker__week {\n    white-space: nowrap;\n  }\n\n  .react-datepicker__day-name,\n  .react-datepicker__day,\n  .react-datepicker__time-name {\n    color: ", ";\n    display: inline-block;\n    height: 34px;\n    line-height: 34px;\n    margin: 2px;\n    text-align: center;\n    width: 34px;\n  }\n\n  .react-datepicker__day {\n    cursor: pointer;\n  }\n\n  .react-datepicker__day:hover {\n    background-color: ", ";\n  }\n\n  .react-datepicker__day--today {\n    font-family: ", ";\n  }\n\n  .react-datepicker__day--selected,\n  .react-datepicker__day--in-selecting-range,\n  .react-datepicker__day--in-range {\n    background-color: ", ";\n    color: ", ";\n  }\n\n  .react-datepicker__day--selected:hover,\n  .react-datepicker__day--in-selecting-range:hover,\n  .react-datepicker__day--in-range:hover {\n    background-color: ", ";\n  }\n\n  .react-datepicker__day--keyboard-selected {\n    background-color: ", ";\n    color: ", ";\n  }\n\n  .react-datepicker__day--keyboard-selected:hover {\n    background-color: ", ";\n  }\n\n  .react-datepicker__day--in-selecting-range:not(.react-datepicker__day--in-range) {\n    background-color: rgba(33, 107, 165, 0.5);\n  }\n\n  .react-datepicker__month--selecting-range\n    .react-datepicker__day--in-range:not(.react-datepicker__day--in-selecting-range) {\n    background-color: ", ";\n    color: ", ";\n  }\n\n  .react-datepicker__day--disabled {\n    color: ", ";\n    cursor: default;\n  }\n\n  .react-datepicker__day--disabled:hover {\n    background-color: transparent;\n  }\n\n  .react-datepicker__input-container {\n    display: inline-block;\n    position: relative;\n  }\n\n  .react-datepicker__year-read-view,\n  .react-datepicker__month-read-view,\n  .react-datepicker__month-year-read-view {\n    border: 1px solid transparent;\n  }\n\n  .react-datepicker__year-read-view:hover,\n  .react-datepicker__month-read-view:hover,\n  .react-datepicker__month-year-read-view:hover {\n    cursor: pointer;\n  }\n\n  .react-datepicker__year-read-view:hover\n    .react-datepicker__year-read-view--down-arrow,\n  .react-datepicker__year-read-view:hover\n    .react-datepicker__month-read-view--down-arrow,\n  .react-datepicker__month-read-view:hover\n    .react-datepicker__year-read-view--down-arrow,\n  .react-datepicker__month-read-view:hover\n    .react-datepicker__month-read-view--down-arrow,\n  .react-datepicker__month-year-read-view:hover\n    .react-datepicker__year-read-view--down-arrow,\n  .react-datepicker__month-year-read-view:hover\n    .react-datepicker__month-read-view--down-arrow {\n    border-top-color: ", ";\n  }\n\n  .react-datepicker__year-read-view--down-arrow,\n  .react-datepicker__month-read-view--down-arrow,\n  .react-datepicker__month-year-read-view--down-arrow {\n    border-top-color: ", ";\n    border-width: 6px;\n    float: right;\n    margin-left: 25px;\n    position: relative;\n    top: 5px;\n  }\n\n  .react-datepicker__year-dropdown,\n  .react-datepicker__month-dropdown,\n  .react-datepicker__month-year-dropdown {\n    background-color: ", ";\n    border: 1px solid ", ";\n    left: 25%;\n    position: absolute;\n    top: 30px;\n    text-align: center;\n    width: 50%;\n    z-index: 1;\n  }\n\n  .react-datepicker__year-dropdown:hover,\n  .react-datepicker__month-dropdown:hover,\n  .react-datepicker__month-year-dropdown:hover {\n    cursor: pointer;\n  }\n\n  .react-datepicker__year-dropdown--scrollable,\n  .react-datepicker__month-dropdown--scrollable,\n  .react-datepicker__month-year-dropdown--scrollable {\n    height: 150px;\n    overflow-y: scroll;\n  }\n\n  .react-datepicker__year-option,\n  .react-datepicker__month-option,\n  .react-datepicker__month-year-option {\n    display: block;\n    line-height: 20px;\n    margin-left: auto;\n    margin-right: auto;\n    width: 100%;\n  }\n\n  .react-datepicker__year-option:hover,\n  .react-datepicker__month-option:hover,\n  .react-datepicker__month-year-option:hover {\n    background-color: ", ";\n    color: ", ";\n  }\n\n  .react-datepicker__year-option--selected,\n  .react-datepicker__month-option--selected,\n  .react-datepicker__month-year-option--selected {\n    position: absolute;\n    left: 15px;\n  }\n\n  .react-datepicker__today-button {\n    background: ", ";\n    border-top: 1px solid ", ";\n    cursor: pointer;\n    text-align: center;\n    font-family: ", ";\n    padding: 5px 0;\n    clear: left;\n  }\n"]);

  _templateObject$w = function _templateObject() {
    return data;
  };

  return data;
}

var StyledDatePicker = styled.div(_templateObject$w(), fonts.textRegular, palette.d6.grey100, palette.d6.grey100, palette.d6.grey200, palette.d6.white000, palette.d6.grey100, palette.d6.white000, palette.d6.grey200, palette.d6.black000, palette.d6.grey100, palette.d6.grey200, palette.d6.grey200, palette.d6.black000, fonts.headBold, palette.d6.blue800, palette.d6.blue700, palette.d6.blue800, palette.d6.blue700, palette.d6.grey200, palette.d6.blue800, palette.d6.blue700, palette.d6.blue800, palette.d6.blue700, palette.d6.black000, palette.d6.grey100, fonts.textBold, palette.d6.blue600, palette.d6.white000, palette.d6.blue700, palette.d6.blue800, palette.d6.white000, palette.d6.blue900, palette.d6.grey100, palette.d6.black000, palette.d6.grey300, palette.d6.blue700, palette.d6.blue800, palette.d6.grey100, palette.d6.grey200, palette.d6.blue800, palette.d6.white000, palette.d6.grey100, palette.d6.blue800, fonts.textBold);
var StyledDateInput = styled.fieldset(_templateObject2$d(), palette.d6.blue800, palette.d6.blue700, D6Spacing("margin-bottom", "s200"), function (props) {
  return props.disabled ? palette.d6.grey100 : palette.d6.blue800;
}, fonts.textRegular, palette.d6.grey300, palette.d6.blue800, fonts.textRegular, D6Spacing("margin-bottom", "s150"), palette.d6.grey200, palette.d6.blue900, palette.d6.grey100, function (props) {
  return props.disabled ? palette.d6.grey100 : palette.d6.blue800;
}, palette.d6.blue700);

var DateInput =
/*#__PURE__*/
function (_Component) {
  _inherits(DateInput, _Component);

  function DateInput(props) {
    var _this;

    _classCallCheck(this, DateInput);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(DateInput).call(this, props));

    _this.clickDatePicker = function () {
      if (_this.containerRef) {
        _this.containerRef.querySelector("input").focus();
      }
    };

    _this.createFakeEventObject = function () {
      var input = _this.containerRef.querySelector("input");

      var event = {
        target: {
          name: input.name,
          value: input.value
        }
      };
      return event;
    };

    _this.handleChange = function (date) {
      var onChange = _this.props.onChange;

      _this.setState({
        startDate: date
      }, function () {
        onChange(_this.createFakeEventObject());
      });
    };

    var value = _this.props.value;
    _this.state = {
      startDate: value ? new Date(value) : null
    };
    return _this;
  }

  _createClass(DateInput, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      if (!this.containerRef) {
        return;
      }

      var input = this.containerRef.querySelector("input");
      var pattern = this.props.pattern;

      if (input && pattern) {
        input.setAttribute("pattern", pattern);
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props = this.props,
          disabled = _this$props.disabled,
          error = _this$props.error,
          hint = _this$props.hint,
          id = _this$props.id,
          label = _this$props.label,
          legend = _this$props.legend,
          name = _this$props.name,
          maxDate = _this$props.maxDate,
          placeholder = _this$props.placeholder,
          required = _this$props.required;
      var startDate = this.state.startDate;
      return React.createElement(StyledDateInput, {
        innerRef: function innerRef(ref) {
          _this2.containerRef = ref;
        },
        disabled: disabled
      }, React.createElement("legend", {
        disabled: disabled
      }, legend), React.createElement(FormField, null, React.createElement(FormLabel, {
        className: "".concat(disabled ? "disabled " : "").concat(startDate || placeholder ? "active" : null),
        id: id,
        label: label
      }), React.createElement(StyledDatePicker, null, React.createElement(DatePicker, {
        disabled: disabled,
        id: id,
        name: name,
        selected: startDate,
        onChange: this.handleChange,
        showYearDropdown: true,
        dateFormat: "dd/MM/yyyy",
        dateFormatCalendar: "MMMM",
        scrollableYearDropdown: true,
        yearDropdownItemNumber: 15,
        maxDate: maxDate,
        required: required
      }), React.createElement("button", {
        "aria-hidden": "true",
        tabIndex: "-1",
        type: "button",
        onClick: this.clickDatePicker
      }, React.createElement(Icon, {
        fill: "currentColor",
        icon: "calendar",
        sizePX: "22px"
      }))), React.createElement(FormHint, {
        message: hint
      }), React.createElement(FormError, {
        message: error
      })));
    }
  }]);

  return DateInput;
}(Component);

DateInput.defaultProps = {
  disabled: false,
  error: "",
  hint: "",
  maxDate: new Date(),
  pattern: null,
  placeholder: null,
  required: false,
  value: ""
};
/**
 * Molecule M-0053 DateInput
 */

function _templateObject4$5() {
  var data = _taggedTemplateLiteral(["\n  display: ", ";\n  ", "\n  ", "\n  li {\n    margin: 0 0 15px 0;\n\n    &:last-of-type {\n      margin-bottom: 0;\n    }\n  }\n"]);

  _templateObject4$5 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$9() {
  var data = _taggedTemplateLiteral(["\n  align-items: center;\n  display: inline-flex;\n  background: transparent;\n  border: 0;\n  color: ", ";\n  cursor: pointer;\n  margin: 0;\n  padding: 0;\n  outline: none;\n  ", "\n\n  .ngw-icon {\n    ", "\n  }\n"]);

  _templateObject3$9 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$e() {
  var data = _taggedTemplateLiteral(["\n  background: none;\n  border: 0;\n  border-bottom: 1px solid #dfe4e8;\n  color: ", ";\n  cursor: pointer;\n  line-height: 22px;\n  text-align: left;\n  margin: 0;\n  outline: none;\n  padding: 0 0 15px 24px;\n  position: relative;\n  width: 100%;\n\n  ", "\n\n  ", "\n"]);

  _templateObject2$e = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$x() {
  var data = _taggedTemplateLiteral(["\n  padding: 0;\n  text-align: left;\n"]);

  _templateObject$x = function _templateObject() {
    return data;
  };

  return data;
}

var StyledDropdownMenu = styled.nav(_templateObject$x());
var DropdownItem = styled.button(_templateObject2$e(), palette.d6.blue800, D6Typography("body/regular200"), function (_ref) {
  var isActive = _ref.isActive;
  return isActive && "\n    border-color: ".concat(palette.d6.blue800, ";\n    cursor: default;\n\n    ::before {\n      content: \"\u2713\";\n      position: absolute;\n      left: 8px;\n      top: 0;\n    }\n  ");
});
var DropdownTrigger = styled.button(_templateObject3$9(), palette.d6.blue800, D6Typography("body/regular200"), D6Spacing("margin-left", "s150"));
var DropdownList = styled.ul(_templateObject4$5(), function (props) {
  return props.isOpen ? "block" : "none";
}, function (_ref2) {
  var isOpen = _ref2.isOpen;
  return isOpen && "\n    .ngw-icon {\n      transform: rotate(180deg);\n    }\n  ";
}, D6Spacing("padding-top", "s350"));

var DropdownMenu =
/*#__PURE__*/
function (_Component) {
  _inherits(DropdownMenu, _Component);

  function DropdownMenu(props) {
    var _this;

    _classCallCheck(this, DropdownMenu);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(DropdownMenu).call(this, props));

    _this.setActiveMenu = function (activeMenuId, activeMenuTitle) {
      var replaceMenuTitle = _this.props.replaceMenuTitle;

      _this.setState(function (prevState) {
        return {
          activeMenuId: _defineProperty({}, activeMenuId, activeMenuId),
          menuTitle: replaceMenuTitle ? activeMenuTitle : prevState.menuTitle
        };
      });
    };

    _this.handleClick = function (_ref, event) {
      var onClick = _ref.onClick,
          id = _ref.id,
          children = _ref.children;
      onClick(event);

      _this.setActiveMenu(id, children);
    };

    var _this$props = _this.props,
        title = _this$props.title,
        preSelectedMenuItem = _this$props.preSelectedMenuItem;
    _this.state = {
      menuTitle: title,
      activeMenuId: _defineProperty({}, preSelectedMenuItem, preSelectedMenuItem)
    };
    return _this;
  }

  _createClass(DropdownMenu, [{
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props2 = this.props,
          children = _this$props2.children,
          width = _this$props2.width,
          className = _this$props2.className,
          triggerDropdown = _this$props2.triggerDropdown,
          isOpen = _this$props2.isOpen,
          title = _this$props2.title,
          isTitleControlled = _this$props2.isTitleControlled;
      var _this$state = this.state,
          menuTitle = _this$state.menuTitle,
          activeMenuId = _this$state.activeMenuId;
      return React.createElement(StyledDropdownMenu, {
        className: className,
        style: {
          width: width
        },
        name: title
      }, React.createElement(DropdownTrigger, {
        onClick: triggerDropdown,
        type: "button"
      }, isTitleControlled && title || menuTitle, React.createElement(Icon, {
        icon: "chevron-down"
      })), React.createElement(DropdownList, {
        isOpen: isOpen
      }, children.map(function (child) {
        return React.createElement("li", {
          key: child.props.id
        }, React.createElement(DropdownItem, {
          onClick: function onClick(event) {
            return _this2.handleClick(child.props, event);
          },
          isActive: activeMenuId[child.props.id]
        }, child.props.children));
      })));
    }
  }]);

  return DropdownMenu;
}(Component);

DropdownMenu.defaultProps = {
  replaceMenuTitle: false,
  width: 300,
  preSelectedMenuItem: "",
  className: "",
  isTitleControlled: false
};

function _templateObject$y() {
  var data = _taggedTemplateLiteral(["\n  appearance: none; /* [1] */\n  /* [2] */\n  background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDIxLjAuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCAyNCAyNCIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMjQgMjQ7IiB4bWw6c3BhY2U9InByZXNlcnZlIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KCS5zdDB7ZmlsdGVyOnVybCgjQWRvYmVfT3BhY2l0eU1hc2tGaWx0ZXIpO30KCS5zdDF7ZmlsbDojRkZGRkZGO30KCS5zdDJ7bWFzazp1cmwoI2IpO30KCS5zdDN7ZmlsbDojMDAyODdFO30KPC9zdHlsZT4KPGcgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTEgLTYpIj4KCTxnPgoJCTxwYXRoIGlkPSJhXzFfIiBkPSJNMi4zLDEzLjdsMS41LTEuNGw4LjgsOS41YzAuMiwwLjIsMC42LDAuMywwLjksMGMwLDAsMCwwLDAsMGw4LjgtOS41bDEuNSwxLjRsLTguOCw5LjVjLTEsMS4xLTIuNiwxLjEtMy43LDAuMQoJCQljMCwwLTAuMS0wLjEtMC4xLTAuMUwyLjMsMTMuN0wyLjMsMTMuN3oiLz4KCTwvZz4KCTxkZWZzPgoJCTxmaWx0ZXIgaWQ9IkFkb2JlX09wYWNpdHlNYXNrRmlsdGVyIiBmaWx0ZXJVbml0cz0idXNlclNwYWNlT25Vc2UiIHg9IjEiIHk9IjYiIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCI+CgkJCTxmZUNvbG9yTWF0cml4ICB0eXBlPSJtYXRyaXgiIHZhbHVlcz0iMSAwIDAgMCAwICAwIDEgMCAwIDAgIDAgMCAxIDAgMCAgMCAwIDAgMSAwIi8+CgkJPC9maWx0ZXI+Cgk8L2RlZnM+Cgk8bWFzayBtYXNrVW5pdHM9InVzZXJTcGFjZU9uVXNlIiB4PSIxIiB5PSI2IiB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIGlkPSJiIj4KCQk8ZyBjbGFzcz0ic3QwIj4KCQkJPHBhdGggaWQ9ImEiIGNsYXNzPSJzdDEiIGQ9Ik0yLjMsMTMuN2wxLjUtMS40bDguOCw5LjVjMC4yLDAuMiwwLjYsMC4zLDAuOSwwYzAsMCwwLDAsMCwwbDguOC05LjVsMS41LDEuNGwtOC44LDkuNQoJCQkJYy0xLDEuMS0yLjYsMS4xLTMuNywwLjFjMCwwLTAuMS0wLjEtMC4xLTAuMUwyLjMsMTMuN0wyLjMsMTMuN3oiLz4KCQk8L2c+Cgk8L21hc2s+Cgk8ZyBjbGFzcz0ic3QyIj4KCQk8cGF0aCBjbGFzcz0ic3QzIiBkPSJNMSw2aDI0djI0SDFWNnoiLz4KCTwvZz4KPC9nPgo8L3N2Zz4K');\n  background-color: transparent;\n  background-position: 100% center;\n  background-repeat: no-repeat;\n  background-size: 24px 24px;\n  border: 1px solid transparent;\n  border-bottom-color: ", ";\n  border-radius: 0;\n  box-shadow: none;\n  color: ", ";\n  font-family: ", ";\n  font-size: 16px;\n  height: 30px;\n  margin: 0;\n  ", "\n  outline: 0; /* [3] */\n  padding: 0;\n  text-indent: -2px;  /* [4] */\n  transition: border 0.3s;\n  width: 100%;\n\n  &:active {\n    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDIxLjAuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCAyNCAyNCIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMjQgMjQ7IiB4bWw6c3BhY2U9InByZXNlcnZlIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KCS5zdDB7ZmlsdGVyOnVybCgjQWRvYmVfT3BhY2l0eU1hc2tGaWx0ZXIpO30KCS5zdDF7ZmlsbDojRkZGRkZGO30KCS5zdDJ7bWFzazp1cmwoI2IpO30KCS5zdDN7ZmlsbDojMDA0MEM1O30KPC9zdHlsZT4KPGcgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTEgLTYpIj4KCTxkZWZzPgoJCTxmaWx0ZXIgaWQ9IkFkb2JlX09wYWNpdHlNYXNrRmlsdGVyIiBmaWx0ZXJVbml0cz0idXNlclNwYWNlT25Vc2UiIHg9IjEiIHk9IjYiIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCI+CgkJCTxmZUNvbG9yTWF0cml4ICB0eXBlPSJtYXRyaXgiIHZhbHVlcz0iMSAwIDAgMCAwICAwIDEgMCAwIDAgIDAgMCAxIDAgMCAgMCAwIDAgMSAwIi8+CgkJPC9maWx0ZXI+Cgk8L2RlZnM+Cgk8bWFzayBtYXNrVW5pdHM9InVzZXJTcGFjZU9uVXNlIiB4PSIxIiB5PSI2IiB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIGlkPSJiIj4KCQk8ZyBjbGFzcz0ic3QwIj4KCQkJPHBhdGggaWQ9ImEiIGNsYXNzPSJzdDEiIGQ9Ik0yMy43LDIyLjNsLTEuNSwxLjRsLTguOC05LjVjLTAuMi0wLjItMC42LTAuMy0wLjksMGMwLDAsMCwwLDAsMGwtOC44LDkuNWwtMS41LTEuNGw4LjgtOS41CgkJCQljMS0xLjEsMi42LTEuMSwzLjctMC4xYzAsMCwwLjEsMC4xLDAuMSwwLjFMMjMuNywyMi4zTDIzLjcsMjIuM3oiLz4KCQk8L2c+Cgk8L21hc2s+Cgk8ZyBjbGFzcz0ic3QyIj4KCQk8cGF0aCBjbGFzcz0ic3QzIiBkPSJNMjUsMzBIMVY2aDI0VjMweiIvPgoJPC9nPgo8L2c+Cjwvc3ZnPgo=');\n    border-bottom-color: transparent;\n    color: ", ";\n  }\n\n  &:focus,\n  &:hover {\n    background-image:  url('data:image/svg+xml;base64,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');\n    border-bottom-color: ", ";\n    color: ", ";\n  }\n\n  &[disabled] {\n    background-image: url('data:image/svg+xml;base64,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');\n    border-bottom-color: currentColor;\n    color: ", ";\n  }\n\n  &::-ms-expand {\n    display: none;\n  }\n"]);

  _templateObject$y = function _templateObject() {
    return data;
  };

  return data;
}
/**
 * Notes:
 * 1. We only need -webkit-appearance really but styled-components prefixes this
 * 2. Use a base64 encoded image as this means this image is always defined here
 * 3. Remember to use an alternative to convey focus if outline is removed
 * 4. Remove quirky padding on `<select>`s
 */


var StyledDropdownSelector = styled.select(_templateObject$y(), palette.d6.grey400, palette.d6.blue800, fonts.textRegular, D6Spacing("margin-bottom", "s150"), palette.d6.blue900, palette.d6.grey400, palette.d6.blue700, palette.d6.grey100);
/**
 * Remove the active class if there is no value when the user
 * exits the field
 * @see FormLabel
 *
 * @param {*} event
 */

var onBlurHandler = function onBlurHandler(event) {
  var input = event.currentTarget;
  var field = input.parentNode;
  var label = field.querySelector("label");

  if (!input.value) {
    label.classList.remove("active");
  }
};
/**
 * Toggle the active class if there is(n't) a value when the user
 * exits the field
 * @see FormLabel
 *
 * @param {*} event
 */


var onFocusHandler = function onFocusHandler(event) {
  var input = event.currentTarget;
  var field = input.parentNode;
  var label = field.querySelector("label");
  label.classList.add("active");
};

var DropdownSelector = function DropdownSelector(_ref) {
  var required = _ref.required,
      error = _ref.error,
      hint = _ref.hint,
      id = _ref.id,
      label = _ref.label,
      options = _ref.options,
      value = _ref.value,
      props = _objectWithoutProperties(_ref, ["required", "error", "hint", "id", "label", "options", "value"]);

  return React.createElement(FormField, null, React.createElement(FormLabel, {
    className: value ? "active" : null,
    id: id,
    label: "".concat(required ? "".concat(label, ": *") : "".concat(label, ":"))
  }), React.createElement(StyledDropdownSelector, Object.assign({
    onBlur: onBlurHandler,
    onFocus: onFocusHandler,
    value: value
  }, props), React.createElement("option", {
    value: ""
  }), options.map(function (option) {
    return React.createElement("option", {
      key: option.value,
      value: option.value
    }, option.text);
  })), React.createElement(FormHint, {
    message: hint
  }), React.createElement(FormError, {
    message: error
  }));
};

DropdownSelector.defaultProps = {
  required: false,
  error: "",
  hint: null,
  value: ""
};

function _templateObject$z() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: column;\n\n  &.variant-row {\n    flex-direction: row;\n\n    .copy {\n      ", "\n    }\n  }\n\n  img {\n    width: 50px;\n    height: 50px;\n  }\n\n  .copy {\n    flex-grow: 1;\n    display: flex;\n    flex-direction: column;\n  }\n\n  h3 {\n    ", "\n    ", "\n  }\n\n  p {\n    ", "\n    flex-grow: 1;\n  }\n\n  .a-cta {\n    ", "\n    align-self: baseline;\n  }\n"]);

  _templateObject$z = function _templateObject() {
    return data;
  };

  return data;
}

var StyledFeature = styled.div(_templateObject$z(), D6Spacing("padding-left", "s250"), D6Typography("header/bold250"), D6Spacing("padding-bottom", "s300"), D6Typography("body/bold150"), D6Spacing("margin-top", "d150"));
/**
 * MyVW Organism M-0001: Feature
 */

var Feature = function Feature(_ref) {
  var icon = _ref.icon,
      image = _ref.image,
      title = _ref.title,
      description = _ref.description,
      ctaCopy = _ref.ctaCopy,
      ctaHref = _ref.ctaHref,
      linkCopy = _ref.linkCopy,
      linkHref = _ref.linkHref,
      variant = _ref.variant;
  return React.createElement(StyledFeature, {
    className: "variant-".concat(variant)
  }, icon && React.createElement(Icon, {
    icon: icon,
    size: "xl",
    fill: "white"
  }), image && React.createElement("img", {
    src: image,
    alt: ""
  }), React.createElement("div", {
    className: "copy"
  }, title && React.createElement("h3", null, title), description && React.createElement("p", null, description), linkCopy && linkHref && React.createElement("a", {
    href: linkHref
  }, linkCopy), ctaCopy && ctaHref && React.createElement(CTA, {
    copy: ctaCopy,
    href: ctaHref
  })));
};

Feature.defaultProps = {
  icon: "",
  image: "",
  linkCopy: "",
  linkHref: "",
  ctaCopy: "",
  ctaHref: "",
  variant: "col"
};
/**
 * MyVW Organism M-0001: Feature
 */

function _templateObject4$6() {
  var data = _taggedTemplateLiteral(["\n  background: transparent;\n  border: 1px solid transparent;\n  color: ", ";\n  cursor: pointer;\n  margin: 0 auto 0 0;\n  padding: 0;\n  transition: color 0.3s;\n\n  &:focus,\n  &:active {\n    color: ", ";\n  }\n\n  &:hover {\n    color: ", ";\n  }\n"]);

  _templateObject4$6 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$a() {
  var data = _taggedTemplateLiteral(["\n  flex: none;\n  margin-right: 5px;\n"]);

  _templateObject3$a = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$f() {
  var data = _taggedTemplateLiteral(["\n  flex: 1;\n"]);

  _templateObject2$f = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$A() {
  var data = _taggedTemplateLiteral(["\n  background-color: ", ";\n  color: ", ";\n  ", ";\n  transition: height 0.3s cubic-bezier(0, 1, 0.5, 1);\n  height: ", ";\n  overflow: hidden;\n\n  > div {\n    ", "\n    align-items: center;\n    display: flex;\n    padding-bottom: 10px;\n    padding-top: 10px;\n  }\n\n  svg {\n    fill: currentColor;\n  }\n"]);

  _templateObject$A = function _templateObject() {
    return data;
  };

  return data;
}

var maxHeight = "auto";
var StyledFormNotification = styled.div(_templateObject$A(), palette.grayLight, palette.d6.blue800, D6Typography("body/regular100"), function (props) {
  return props.visible === true ? maxHeight : "0";
}, D6Spacing("padding", "s250"));
var StyledMessage = styled.div(_templateObject2$f());
var StyledIcon$3 = styled(Icon)(_templateObject3$a());
var StyledCloseButton = styled.button(_templateObject4$6(), palette.d6.blue900, palette.d6.blue700, palette.d6.blue800);
/**
 * MyVW
 * Molecule
 * M-0055: FormNotification
 * Handle the closing of the component with its parent component via
 * the onClick function we can pass in.
 */

var FormNotification = function FormNotification(_ref) {
  var icon = _ref.icon,
      message = _ref.message,
      onClick = _ref.onClick,
      visible = _ref.visible;
  return React.createElement(React.Fragment, null, message && React.createElement(StyledFormNotification, {
    "aria-live": "polite",
    role: visible ? "alert" : null,
    visible: visible
  }, React.createElement("div", null, icon && React.createElement(StyledIcon$3, {
    icon: icon,
    sizePX: "16px"
  }), React.createElement(StyledMessage, null, message), React.createElement(StyledCloseButton, {
    type: "button",
    onClick: onClick
  }, React.createElement(Icon, {
    icon: "menu-close",
    sizePX: "16px"
  })))));
};

FormNotification.defaultProps = {
  icon: null,
  message: null,
  visible: false
};

function _templateObject2$g() {
  var data = _taggedTemplateLiteral(["\n  max-height: 65px;\n  overflow: hidden;\n"]);

  _templateObject2$g = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$B() {
  var data = _taggedTemplateLiteral(["\n  .slick-list,\n  .slick-slider,\n  .slick-track {\n    position: relative;\n    display: block;\n  }\n  .slick-loading .slick-slide,\n  .slick-loading .slick-track {\n    visibility: hidden;\n  }\n  .slick-slider {\n    box-sizing: border-box;\n    user-select: none;\n    touch-action: pan-y;\n    -webkit-tap-highlight-color: transparent;\n    margin-bottom: 80px;\n  }\n  .slick-list {\n    overflow: hidden;\n    margin: 0;\n    padding: 0;\n  }\n  .slick-list:focus {\n    outline: 0;\n  }\n  .slick-list.dragging {\n    cursor: pointer;\n    cursor: hand;\n  }\n  .slick-slider .slick-list,\n  .slick-slider .slick-track {\n    transform: translate3d(0, 0, 0);\n  }\n  .slick-track {\n    top: 0;\n    left: 0;\n  }\n  .slick-track:after,\n  .slick-track:before {\n    display: table;\n    content: \"\";\n  }\n  .slick-track:after {\n    clear: both;\n  }\n  .slick-slide {\n    display: none;\n    float: left;\n    height: 100%;\n    min-height: 1px;\n  }\n  .slick-slide img {\n    width: 100%;\n    display: block;\n    margin: 0 auto;\n  }\n  .slick-slide.slick-loading img {\n    display: none;\n  }\n  .slick-slide.dragging img {\n    pointer-events: none;\n  }\n  .slick-initialized .slick-slide {\n    display: block;\n  }\n  .slick-vertical .slick-slide {\n    display: block;\n    height: auto;\n    border: 1px solid transparent;\n  }\n  .slick-arrow.slick-hidden {\n    display: none;\n  }\n  .slick-dots,\n  .slick-next,\n  .slick-prev {\n    position: absolute;\n    display: block;\n    padding: 0;\n  }\n  .slick-next,\n  .slick-prev {\n    bottom: -60px;\n    transform: translate(0, -50%);\n    cursor: pointer;\n    position: absolute;\n\n    &:focus,\n    &:hover {\n      color: transparent;\n      outline: 0;\n      background: 0 0;\n\n      &:before {\n        opacity: 1;\n      }\n    }\n\n    &.slick-disabled:before {\n      opacity: 0.25;\n    }\n  }\n  .slick-prev {\n    left: 10%;\n  }\n  .slick-next {\n    right: 10%;\n  }\n  .slick-dotted.slick-slider {\n    margin-bottom: 30px;\n  }\n  .slick-dots {\n    width: 100%;\n    margin: 6px 0 0 0;\n    list-style: none;\n    text-align: center;\n    li {\n      position: relative;\n      display: inline-flex;\n      align-items: center;\n      cursor: pointer;\n\n      &:first-child {\n        margin-left: 0;\n      }\n    }\n  }\n  .slick-dots {\n    li {\n      margin-left: 20px;\n    }\n  }\n  .slick-dots__thumbnail {\n    width: 100px;\n    max-width: 100%;\n  }\n  .slick-arrow {\n    svg {\n      transition: fill 0.25s ease;\n    }\n    &:hover {\n      svg {\n        fill: ", ";\n      }\n    }\n  }\n\n  @media (max-width: 768px) {\n    .slick-dots {\n      li {\n        margin-left: 0;\n      }\n    }\n    .slick-dots__thumbnail {\n      width: 70px;\n    }\n    .slick-arrow {\n      bottom: -40px;\n    }\n    .ngw-icon {\n      width: 12px;\n      height: 12px;\n    }\n    .slick-prev {\n      left: 10px;\n    }\n    .slick-next {\n      right: 10px;\n    }\n  }\n"]);

  _templateObject$B = function _templateObject() {
    return data;
  };

  return data;
}

var StyledGallery = styled.div(_templateObject$B(), palette.blueLight);
var Thumbnail$1 = styled.div(_templateObject2$g());

var Gallery = function Gallery(_ref) {
  var images = _ref.images;
  var settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    lazyload: true,
    customPaging: function customPaging(i) {
      return React.createElement(Thumbnail$1, null, React.createElement("img", {
        className: "slick-dots__thumbnail",
        src: images[i].thumbnail,
        alt: "".concat(images[i].alt, " thumbnail")
      }));
    },
    prevArrow: React.createElement(SliderArrow, {
      className: "slick-prev",
      to: "prev",
      icon: "chevron-left"
    }),
    nextArrow: React.createElement(SliderArrow, {
      className: "slick-next",
      to: "next",
      icon: "chevron-right"
    })
  };
  return React.createElement(StyledGallery, null, React.createElement(Slider, Object.assign({
    className: "ngw-gallery"
  }, settings), images.map(function (image, i) {
    return React.createElement(Picture, {
      key: generateUuid(i),
      alt: image.alt,
      viewport: image.viewport
    });
  })));
};

Gallery.defaultProps = {
  images: []
};

function _templateObject$C() {
  var data = _taggedTemplateLiteral(["\n  ", ";\n  .icon {\n    fill: ", ";\n  }\n"]);

  _templateObject$C = function _templateObject() {
    return data;
  };

  return data;
}

var GoogleMapStyled = styled.div(_templateObject$C(), function (props) {
  return props.dimensions;
}, palette.d6.blue800);
var grayTheme = [{
  elementType: "geometry",
  stylers: [{
    color: "#f5f5f5"
  }]
}, {
  elementType: "labels.icon",
  stylers: [{
    visibility: "off"
  }]
}, {
  elementType: "labels.text.fill",
  stylers: [{
    color: "#616161"
  }]
}, {
  elementType: "labels.text.stroke",
  stylers: [{
    color: "#f5f5f5"
  }]
}, {
  featureType: "administrative.land_parcel",
  elementType: "labels.text.fill",
  stylers: [{
    color: "#bdbdbd"
  }]
}, {
  featureType: "poi",
  elementType: "geometry",
  stylers: [{
    color: "#eeeeee"
  }]
}, {
  featureType: "poi",
  elementType: "labels.text.fill",
  stylers: [{
    color: "#757575"
  }]
}, {
  featureType: "poi.park",
  elementType: "geometry",
  stylers: [{
    color: "#e5e5e5"
  }]
}, {
  featureType: "poi.park",
  elementType: "labels.text.fill",
  stylers: [{
    color: "#9e9e9e"
  }]
}, {
  featureType: "road",
  elementType: "geometry",
  stylers: [{
    color: "#ffffff"
  }]
}, {
  featureType: "road.arterial",
  elementType: "labels.text.fill",
  stylers: [{
    color: "#757575"
  }]
}, {
  featureType: "road.highway",
  elementType: "geometry",
  stylers: [{
    color: "#dadada"
  }]
}, {
  featureType: "road.highway",
  elementType: "labels.text.fill",
  stylers: [{
    color: "#616161"
  }]
}, {
  featureType: "road.local",
  elementType: "labels.text.fill",
  stylers: [{
    color: "#9e9e9e"
  }]
}, {
  featureType: "transit.line",
  elementType: "geometry",
  stylers: [{
    color: "#e5e5e5"
  }]
}, {
  featureType: "transit.station",
  elementType: "geometry",
  stylers: [{
    color: "#eeeeee"
  }]
}, {
  featureType: "water",
  elementType: "geometry",
  stylers: [{
    color: "#c9c9c9"
  }]
}, {
  featureType: "water",
  elementType: "labels.text.fill",
  stylers: [{
    color: "#9e9e9e"
  }]
}];

var GoogleMaps = function GoogleMaps(_ref) {
  var dimensions = _ref.dimensions,
      config = _ref.config,
      theme = _ref.theme,
      options = _ref.options,
      coords = _ref.coords;

  var themeProvider = function themeProvider() {
    switch (theme) {
      case "grayscale":
        return grayTheme;

      default:
        return "";
    }
  };

  var defaultOptions = {
    zoom: 16,
    styles: themeProvider(theme)
  };

  var mapsOptions = _objectSpread2({}, defaultOptions, {}, options);

  var parseCoords = {
    lat: parseFloat(coords.lat),
    lng: parseFloat(coords.lng)
  };
  return (// Important! Always set the container height explicitly
    React.createElement(GoogleMapStyled, {
      dimensions: dimensions
    }, React.createElement(GoogleMapReact, {
      bootstrapURLKeys: config,
      options: mapsOptions,
      styles: themeProvider,
      defaultCenter: parseCoords,
      defaultZoom: mapsOptions.zoom
    }, React.createElement(Icon, {
      icon: "pin",
      lat: parseFloat(coords.lat),
      lng: parseFloat(coords.lng),
      size: "xl"
    })))
  );
};

GoogleMaps.defaultProps = {
  theme: "",
  dimensions: {
    width: "100%",
    height: "100vh"
  },
  options: ""
};
/**
 * Atom GoogleMaps
 */

function _templateObject4$7() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: column;\n"]);

  _templateObject4$7 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$b() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  flex-shrink: 1;\n  align-items: center;\n  justify-content: center;\n  color: ", ";\n  position: absolute;\n  right: 32px;\n"]);

  _templateObject3$b = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$h() {
  var data = _taggedTemplateLiteral(["\n  width: 100%;\n  display: flex;\n  cursor: pointer;\n  align-items: stretch;\n  position: relative;\n  justify-content: space-between;\n  ", "\n  ", "\n  ", "\n  ", ";\n  ", ";\n"]);

  _templateObject2$h = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$D() {
  var data = _taggedTemplateLiteral(["\n  > div {\n    &:not(:first-child) {\n      border-top: 1px solid ", ";\n    }\n\n    &[disabled] + div {\n      &:not([disabled]) {\n        border-top: 1px solid #f6f7f8};\n      }\n    }\n\n    &:last-child {\n      border-bottom: 1px solid ", ";\n    }\n  }\n"]);

  _templateObject$D = function _templateObject() {
    return data;
  };

  return data;
}

var StyledListGroup = styled.div(_templateObject$D(), palette.grayLight, palette.d6.grey100);
var StyledListGroupItem = styled.div(_templateObject2$h(), function (_ref) {
  var disabled = _ref.disabled;
  return disabled && "\n      opacity: 0.3;\n      pointer-events: none;\n  ";
}, D6Spacing("padding-left", "s400"), D6Spacing("padding-right", "s400"), D6Spacing("padding-top", "s300"), D6Spacing("padding-bottom", "s300"));
var StyledIcon$4 = styled.div(_templateObject3$b(), palette.blue);
var StyledContainer$1 = styled(StyledListGroupItem)(_templateObject4$7());

var Dynamic = function Dynamic(_ref) {
  var mobile = _ref.mobile,
      desktop = _ref.desktop,
      breakpoint = _ref.breakpoint;
  return React.createElement(React.Fragment, null, React.createElement(BreakpointWrapper, {
    max: breakpoint
  }, mobile()), React.createElement(BreakpointWrapper, {
    min: breakpoint
  }, desktop()));
};
/**
 * <ListGroup /> component
 * @param label
 * @param id
 * @param selected
 * @param disabled
 * @returns {*}
 * @constructor
 */


var ListGroup = function ListGroup(_ref2) {
  var label = _ref2.label,
      data = _ref2.data,
      active = _ref2.active,
      selected = _ref2.selected,
      disabled = _ref2.disabled,
      onChange = _ref2.onChange;
  var isControlled = onChange && typeof onChange === "function";

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      selectedItem = _useState2[0],
      setSelected = _useState2[1];

  var onSelect = function onSelect(id) {
    return isControlled ? onChange(id) : setSelected(id);
  };

  var activeId = active || selectedItem;
  return React.createElement(StyledListGroup, null, React.createElement(Dynamic, {
    mobile: function mobile() {
      return React.createElement(Container, {
        padding: {
          left: ContainerPadding.static400,
          bottom: ContainerPadding.dynamic050
        }
      }, React.createElement(Text, {
        tag: "h2",
        color: TextColor.inherit,
        appearance: TextAppearance.headline250
      }, label));
    },
    desktop: function desktop() {
      return React.createElement(Container, {
        padding: {
          bottom: ContainerPadding.dynamic050
        }
      }, React.createElement(Text, {
        tag: "h2",
        color: TextColor.inherit,
        appearance: TextAppearance.headline300
      }, label));
    },
    breakpoint: Breakpoints.b960
  }), data.length > 0 && data.map(function (_ref3) {
    var id = _ref3.id,
        text = _ref3.text,
        price = _ref3.price;
    return React.createElement(Layout, {
      key: id,
      appearance: _defineProperty({}, Breakpoints.default, [{
        name: "a",
        columns: 20
      }, {
        name: ".",
        columns: 1
      }, {
        name: "b",
        columns: 3
      }])
    }, React.createElement(StyledContainer$1, {
      disabled: disabled.includes(id) || selected.includes(id),
      onClick: function onClick() {
        return onSelect(id);
      }
    }, React.createElement(Dynamic, {
      mobile: function mobile() {
        return React.createElement(Text, {
          tag: "p",
          appearance: TextAppearance.copy100,
          bold: id === activeId
        }, text);
      },
      desktop: function desktop() {
        return React.createElement(Text, {
          tag: "p",
          appearance: TextAppearance.copy150,
          bold: id === activeId
        }, text);
      },
      breakpoint: Breakpoints.b960
    }), (disabled.includes(id) || selected.includes(id)) && React.createElement(Container, {
      padding: {
        top: ContainerPadding.static200
      }
    }, React.createElement(Dynamic, {
      mobile: function mobile() {
        return React.createElement(Text, {
          tag: "p",
          color: TextColor.inherit,
          appearance: TextAppearance.copy100,
          bold: true
        }, disabled.includes(id) ? "Currently unavailable" : "Already selected");
      },
      desktop: function desktop() {
        return React.createElement(Text, {
          tag: "p",
          color: TextColor.inherit,
          appearance: TextAppearance.copy150,
          bold: true
        }, disabled.includes(id) ? "Currently unavailable" : "Already selected");
      },
      breakpoint: Breakpoints.b960
    })), !disabled.includes(id) && !selected.includes(id) && price && React.createElement(Dynamic, {
      mobile: function mobile() {
        return React.createElement(Text, {
          tag: "p",
          color: TextColor.inherit,
          appearance: TextAppearance.copy100,
          bold: id === activeId
        }, price);
      },
      desktop: function desktop() {
        return React.createElement(Container, {
          padding: {
            top: ContainerPadding.static200
          }
        }, React.createElement(Text, {
          tag: "p",
          color: TextColor.inherit,
          appearance: TextAppearance.copy150,
          bold: id === activeId
        }, price));
      },
      breakpoint: Breakpoints.b960
    })), React.createElement(StyledContainer$1, {
      disabled: selected.includes(id)
    }, (selected.includes(id) || id === activeId) && React.createElement(StyledIcon$4, null, React.createElement(Checkmark, null))));
  }));
};

ListGroup.defaultProps = {
  data: [],
  active: "",
  selected: [],
  disabled: [],
  onChange: null
};

var MediaItem = function MediaItem(_ref) {
  var heading = _ref.heading,
      sources = _ref.sources,
      alt = _ref.alt;
  return React.createElement("div", {
    className: "media-asset__background-image"
  }, heading, React.createElement(Picture, {
    alt: alt,
    viewport: sources
  }));
};

MediaItem.defaultProps = {
  heading: ""
};

function _templateObject7$3() {
  var data = _taggedTemplateLiteral(["\n  hr {\n    border-width: 1px;\n  }\n"]);

  _templateObject7$3 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6$4() {
  var data = _taggedTemplateLiteral(["\n  position: relative;\n  display: flex;\n  flex: 1;\n  align-items: center;\n  justify-content: ", ";\n  height: 100%;\n\n  > a {\n    border: none;\n    cursor: pointer;\n  }\n"]);

  _templateObject6$4 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5$4() {
  var data = _taggedTemplateLiteral(["\n  background-color: ", ";\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  border-radius: 100%;\n  color: white;\n  width: 18px;\n  height: 18px;\n  ", ";\n  ", ";\n"]);

  _templateObject5$4 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$8() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: center;\n  ", ";\n\n  + p {\n    ", "\n    ", ";\n  }\n\n  > h2 {\n    margin: 0;\n    flex-basis: unset;\n  }\n"]);

  _templateObject4$8 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$c() {
  var data = _taggedTemplateLiteral(["\n  width: 100%;\n  max-width: 280px;\n"]);

  _templateObject3$c = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$i() {
  var data = _taggedTemplateLiteral(["\n  position: absolute;\n  top: 0;\n\n  &:focus {\n    outline: none;\n    border: none;\n  }\n"]);

  _templateObject2$i = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$E() {
  var data = _taggedTemplateLiteral(["\n  ", ";\n"]);

  _templateObject$E = function _templateObject() {
    return data;
  };

  return data;
}

var StyledPrimaryCarSelection = styled.div(_templateObject$E(), function (_ref) {
  var isDisabled = _ref.isDisabled;
  return isDisabled && "\n    opacity: 0.3;\n    pointer-events: none;\n  ";
});
var StyledRadioButton = styled.div(_templateObject2$i());
var StyledImage$1 = styled.img(_templateObject3$c());
var StyledModelName = styled.div(_templateObject4$8(), D6Spacing("margin-bottom", "s150"), function (props) {
  return props.isMobile ? "max-width: 150px;" : "";
}, D6Spacing("margin-bottom", "s150"));
var StyledIcon$5 = styled.div(_templateObject5$4(), function (_ref2) {
  var bgColor = _ref2.bgColor;
  return bgColor || palette.d6.blue200;
}, D6Spacing("margin-left", "s200"), D6Spacing("padding", "s100"));
var StyledContainer$2 = styled.div(_templateObject6$4(), function (_ref3) {
  var justify = _ref3.justify,
      pushItemsToEnd = _ref3.pushItemsToEnd;
  return pushItemsToEnd && "flex-end" || justify || "flex-start";
});
var StyleDividerContainer = styled.div(_templateObject7$3());

var Icon$2 = function Icon(type) {
  return {
    Hybrid: React.createElement(Hybrid, null),
    Electric: React.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      height: "18",
      viewBox: "0 0 190 190",
      width: "18",
      fill: "white"
    }, React.createElement("path", {
      d: "m155.109 74.028a4 4 0 0 0 -3.48-2.028h-52.4l8.785-67.123a4.023 4.023 0 0 0 -7.373-2.614l-63.724 111.642a4 4 0 0 0 3.407 6.095h51.617l-6.962 67.224a4.024 4.024 0 0 0 7.411 2.461l62.671-111.63a4 4 0 0 0 .048-4.027z"
    }))
  }[type];
};
/**
 * Render the component on mobile view
 * @param derivativeId
 * @param derivativeName
 * @param icon
 * @param image
 * @param modelName
 * @param price
 * @param showChevron
 * @param radioChecked
 * @param onChange
 * @param onClick
 * @returns {*}
 * @constructor
 */


var Mobile$1 = function Mobile(_ref) {
  var derivativeId = _ref.derivativeId,
      derivativeName = _ref.derivativeName,
      fuelType = _ref.fuelType,
      image = _ref.image,
      modelName = _ref.modelName,
      price = _ref.price,
      showChevron = _ref.showChevron,
      radioChecked = _ref.radioChecked,
      onChange = _ref.onChange,
      _onClick = _ref.onClick,
      isDisabled = _ref.isDisabled,
      disabledCopy = _ref.disabledCopy;
  return React.createElement(React.Fragment, null, React.createElement(Layout, {
    appearance: _defineProperty({}, Breakpoints.default, [{
      name: ".",
      columns: 2
    }, {
      name: "a",
      columns: 10
    }, {
      name: "b",
      columns: 10
    }, {
      name: ".",
      columns: 2
    }])
  }, React.createElement(StyledContainer$2, {
    justify: "flex-start"
  }, React.createElement(StyledRadioButton, null, React.createElement(RadioButton, {
    checked: radioChecked,
    onClick: function onClick() {
      return onChange(derivativeId, !radioChecked);
    }
  })), React.createElement(StyledImage$1, {
    src: image
  })), React.createElement(StyledContainer$2, {
    justify: "flex-end"
  }, React.createElement("div", null, React.createElement(StyledModelName, {
    isMobile: true
  }, React.createElement(Text, {
    tag: "h2",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.headline200
  }, modelName), fuelType && React.createElement(Container, {
    horizontalAlign: ContainerHorizontalAlignment.center
  }, React.createElement(StyledIcon$5, {
    bgColor: fuelType === "Hybrid" && palette.blue
  }, Icon$2(fuelType)))), React.createElement(Text, {
    tag: "p",
    color: TextColor.inherit,
    textAlign: TextAlignment.left,
    appearance: TextAppearance.copy100
  }, derivativeName), React.createElement(StyledContainer$2, {
    justify: "space-between"
  }, React.createElement(Text, {
    tag: "p",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.copy150
  }, isDisabled ? disabledCopy : "From ".concat(formatCurrencyWithDecimals(price))), React.createElement(LinkButton, {
    disabled: !showChevron,
    onClick: function onClick() {
      return _onClick(derivativeId);
    }
  }, React.createElement(ChevronRight, null)))))), React.createElement(Container, {
    stretchContent: true,
    padding: {
      top: _defineProperty({}, Breakpoints.default, ContainerPadding.static250),
      bottom: _defineProperty({}, Breakpoints.default, ContainerPadding.static250)
    }
  }, React.createElement(StyleDividerContainer, null, React.createElement(Divider, null))));
};
/**
 * Render the component on desktop view
 * @param derivativeId
 * @param derivativeName
 * @param icon
 * @param image
 * @param modelName
 * @param price
 * @param showChevron
 * @param radioChecked
 * @param onChange
 * @param onClick
 * @returns {*}
 * @constructor
 */


var Desktop$1 = function Desktop(_ref3) {
  var derivativeId = _ref3.derivativeId,
      derivativeName = _ref3.derivativeName,
      fuelType = _ref3.fuelType,
      image = _ref3.image,
      modelName = _ref3.modelName,
      price = _ref3.price,
      showChevron = _ref3.showChevron,
      radioChecked = _ref3.radioChecked,
      onChange = _ref3.onChange,
      _onClick2 = _ref3.onClick,
      isDisabled = _ref3.isDisabled,
      disabledCopy = _ref3.disabledCopy;
  return React.createElement(React.Fragment, null, React.createElement(Layout, {
    appearance: _defineProperty({}, Breakpoints.default, [{
      name: ".",
      columns: 4
    }, {
      name: "a",
      columns: 2
    }, {
      name: "b",
      columns: 3
    }, {
      name: ".",
      columns: 1
    }, {
      name: "c",
      columns: 6
    }, {
      name: "d",
      columns: 3
    }, {
      name: ".",
      columns: 1
    }, {
      name: "e",
      columns: 1
    }, {
      name: ".",
      columns: 3
    }])
  }, React.createElement(StyledContainer$2, null, React.createElement(RadioButton, {
    checked: radioChecked,
    onClick: function onClick() {
      return onChange(derivativeId, !radioChecked);
    }
  })), React.createElement(StyledContainer$2, null, React.createElement(StyledImage$1, {
    src: image
  })), React.createElement(Container, {
    horizontalAlign: ContainerHorizontalAlignment.left,
    gutter: ContainerGutter.static200,
    wrap: ContainerWrap.always
  }, React.createElement(StyledModelName, null, React.createElement(Text, {
    tag: "h2",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.headline250
  }, modelName), fuelType && (fuelType === "Electric" || fuelType === "Hybrid") && React.createElement(Container, {
    horizontalAlign: ContainerHorizontalAlignment.center
  }, React.createElement(StyledIcon$5, {
    bgColor: fuelType === "Hybrid" && palette.blue
  }, Icon$2(fuelType)))), React.createElement(Text, {
    tag: "p",
    color: TextColor.inherit,
    textAlign: TextAlignment.left,
    appearance: TextAppearance.copy150
  }, derivativeName)), React.createElement(StyledContainer$2, null, React.createElement(Text, {
    tag: "p",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.headline200
  }, isDisabled ? disabledCopy : "From ".concat(formatCurrencyWithDecimals(price)))), React.createElement(StyledContainer$2, {
    pushItemsToEnd: true
  }, React.createElement(LinkButton, {
    disabled: !showChevron,
    onClick: function onClick() {
      return _onClick2(derivativeId);
    }
  }, React.createElement(ChevronRight, null)))), React.createElement(Layout, {
    appearance: _defineProperty({}, Breakpoints.default, [{
      name: ".",
      columns: 3
    }, {
      name: "a",
      columns: 18
    }, {
      name: ".",
      columns: 3
    }])
  }, React.createElement(Container, {
    stretchContent: true,
    padding: {
      top: _defineProperty({}, Breakpoints.default, ContainerPadding.static400),
      bottom: _defineProperty({}, Breakpoints.default, ContainerPadding.static450)
    }
  }, React.createElement(StyleDividerContainer, null, React.createElement(Divider, null)))));
};
/**
 * Root component for primary car selection
 * @param selected
 * @param isDisabled
 * @param onClick
 * @param onChange
 * @param rest
 * @returns {*}
 * @constructor
 */


var ModelListItem = function ModelListItem(_ref6) {
  var selected = _ref6.selected,
      isDisabled = _ref6.isDisabled,
      _onClick3 = _ref6.onClick,
      onChange = _ref6.onChange,
      rest = _objectWithoutProperties(_ref6, ["selected", "isDisabled", "onClick", "onChange"]);

  var _useState = useState(selected),
      _useState2 = _slicedToArray(_useState, 2),
      checked = _useState2[0],
      setChecked = _useState2[1];

  var change = function change(id, radioVal) {
    var isControlled = onChange && typeof onChange === "function";
    return isControlled ? onChange(id) : setChecked(radioVal);
  };

  return React.createElement(StyledPrimaryCarSelection, {
    isDisabled: isDisabled
  }, React.createElement(BreakpointWrapper, {
    max: Breakpoints.b960
  }, React.createElement(Mobile$1, Object.assign({
    radioChecked: checked,
    onChange: change,
    onClick: function onClick(derivativeId) {
      return _onClick3(derivativeId);
    },
    isDisabled: isDisabled
  }, rest))), React.createElement(BreakpointWrapper, {
    min: Breakpoints.b960
  }, React.createElement(Desktop$1, Object.assign({
    radioChecked: checked,
    onChange: change,
    onClick: function onClick(derivativeId) {
      return _onClick3(derivativeId);
    },
    isDisabled: isDisabled
  }, rest))));
};

Mobile$1.defaultProps = {
  derivativeId: "",
  derivativeName: "",
  fuelType: "",
  image: "",
  modelName: "",
  price: 0,
  showChevron: false,
  radioChecked: false,
  isDisabled: false,
  disabledCopy: "",
  onChange: function onChange() {},
  onClick: function onClick() {}
};
Desktop$1.defaultProps = {
  derivativeId: "",
  derivativeName: "",
  fuelType: "",
  image: "",
  modelName: "",
  price: 0,
  showChevron: false,
  radioChecked: false,
  isDisabled: false,
  disabledCopy: "",
  onChange: function onChange() {},
  onClick: function onClick() {}
};
/**
 * Default prop values
 * @type {{
 *  image: string,
 *  modelName: string,
 *  showChevron: boolean,
 *  price: number,
 *  icon: null,
 *  isDisabled: boolean,
 *  derivativeName: string,
 *  selected: boolean,
 *  derivativeId: string
 * }}
 */

ModelListItem.defaultProps = {
  derivativeId: "",
  derivativeName: "",
  fuelType: "",
  image: "",
  isDisabled: false,
  modelName: "",
  price: 0,
  selected: false,
  showChevron: false,
  disabledCopy: "",
  onClick: function onClick() {},
  onChange: null
};

function _templateObject3$d() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  display: block;\n  ", ";\n"]);

  _templateObject3$d = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$j() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  display: block;\n  ", ";\n  ", ";\n"]);

  _templateObject2$j = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$F() {
  var data = _taggedTemplateLiteral(["\n  ", ";\n  ", ";\n"]);

  _templateObject$F = function _templateObject() {
    return data;
  };

  return data;
}

var StyledOrganismIntro = styled.div(_templateObject$F(), D6Spacing("padding-top", "d150"), D6Spacing("padding-bottom", "d150"));
var Title = styled.span(_templateObject2$j(), palette.black, D6Typography("header/bold350"), D6Spacing("padding-bottom", "d100"));
var Description = styled.span(_templateObject3$d(), palette.black, D6Typography("body/regular200"));
/**
 * Molecule M-0005: Organism Intro
 */

var OrganismIntro = function OrganismIntro(_ref) {
  var title = _ref.title,
      description = _ref.description;
  return React.createElement(StyledOrganismIntro, null, React.createElement(Col, {
    sm: 16,
    md: 16,
    lg: 12
  }, React.createElement(Title, null, title), description && React.createElement(Description, null, description)));
};

OrganismIntro.defaultProps = {
  description: ""
};
/**
 * Molecule M-0005: Organism Intro
 */

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _templateObject10$1() {
  var data = _taggedTemplateLiteral(["\n  //min-height: 330px;\n"]);

  _templateObject10$1 = function _templateObject10() {
    return data;
  };

  return data;
}

function _templateObject9$2() {
  var data = _taggedTemplateLiteral(["\n  min-height: 64px;\n  height: 100%;\n"]);

  _templateObject9$2 = function _templateObject9() {
    return data;
  };

  return data;
}

function _templateObject8$3() {
  var data = _taggedTemplateLiteral(["\n  width: 100%;\n"]);

  _templateObject8$3 = function _templateObject8() {
    return data;
  };

  return data;
}

function _templateObject7$4() {
  var data = _taggedTemplateLiteral(["\n  position: absolute;\n  top: -140px;\n  width: 100%;\n"]);

  _templateObject7$4 = function _templateObject7() {
    return data;
  };

  return data;
}

function _templateObject6$5() {
  var data = _taggedTemplateLiteral(["\n  cursor: pointer;\n  > button {\n    border-bottom: 1px solid currentColor;\n\n    &:focus,\n    &:hover {\n      color: ", ";\n      border-bottom: 1px solid ", ";\n    }\n\n    &:focus {\n      outline: none;\n    }\n  }\n"]);

  _templateObject6$5 = function _templateObject6() {
    return data;
  };

  return data;
}

function _templateObject5$5() {
  var data = _taggedTemplateLiteral(["\n  background-color: ", ";\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  border-radius: 100%;\n  color: white;\n  width: 18px;\n  height: 18px;\n  ", ";\n  ", ";\n"]);

  _templateObject5$5 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$9() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: center;\n  ", ";\n  + p {\n    max-width: 150px;\n    ", ";\n  }\n  > h2 {\n    margin: 0;\n  }\n"]);

  _templateObject4$9 = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$e() {
  var data = _taggedTemplateLiteral(["\n  width: 100%;\n  max-width: ", ";\n"]);

  _templateObject3$e = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$k() {
  var data = _taggedTemplateLiteral(["\n  position: relative;\n  display: flex;\n  flex: 1;\n  align-items: center;\n  justify-content: ", ";\n  flex-direction: ", ";\n  height: 100%;\n\n  > a {\n    border: none;\n    cursor: pointer;\n  }\n"]);

  _templateObject2$k = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$G() {
  var data = _taggedTemplateLiteral(["\n  position: relative;\n  border: ", ";\n  border-radius: 4px;\n  min-height: ", ";\n  background-color: #ffffff;\n"]);

  _templateObject$G = function _templateObject() {
    return data;
  };

  return data;
}

var StyledPrimaryCarCard = styled.div(_templateObject$G(), function (_ref) {
  var border = _ref.border;
  return border && "1px solid #dfe4e8";
}, function (_ref2) {
  var minHeight = _ref2.minHeight;
  return minHeight || "";
});
var StyledContainer$3 = styled.div(_templateObject2$k(), function (_ref3) {
  var justify = _ref3.justify;
  return justify || "flex-start";
}, function (_ref4) {
  var direction = _ref4.direction;
  return direction || "row";
});
var StyledImage$2 = styled.img(_templateObject3$e(), function (_ref5) {
  var mxWidth = _ref5.mxWidth;
  return "".concat(mxWidth, "px") || "";
});
var StyledModelName$1 = styled.div(_templateObject4$9(), D6Spacing("margin-bottom", "s150"), D6Spacing("margin-bottom", "s150"));
var StyledIcon$6 = styled.div(_templateObject5$5(), function (_ref6) {
  var bgColor = _ref6.bgColor;
  return bgColor || palette.d6.blue200;
}, D6Spacing("margin-left", "s200"), D6Spacing("padding", "s100"));
var StyledButton$4 = styled.div(_templateObject6$5(), palette.d6.blue600, palette.d6.blue600);
var StyledCarImg$1 = styled.div(_templateObject7$4());
var StyledBottomContainer = styled.div(_templateObject8$3());
var StyledTextWrapper = styled.div(_templateObject9$2());
var StyledDesktop = styled.div(_templateObject10$1());

var Icon$3 = function Icon(type) {
  return {
    Hybrid: React.createElement(Hybrid, null),
    Electric: React.createElement("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      height: "24",
      viewBox: "0 0 190 190",
      width: "24",
      fill: "white"
    }, React.createElement("path", {
      d: "m155.109 74.028a4 4 0 0 0 -3.48-2.028h-52.4l8.785-67.123a4.023 4.023 0 0 0 -7.373-2.614l-63.724 111.642a4 4 0 0 0 3.407 6.095h51.617l-6.962 67.224a4.024 4.024 0 0 0 7.411 2.461l62.671-111.63a4 4 0 0 0 .048-4.027z"
    }))
  }[type];
};

var Mobile$2 = function Mobile(_ref) {
  var derivativeId = _ref.derivativeId,
      modelName = _ref.modelName,
      image = _ref.image,
      derivativeName = _ref.derivativeName,
      fuelType = _ref.fuelType,
      price = _ref.price,
      onEdit = _ref.onEdit;
  return React.createElement(Layout, {
    appearance: _defineProperty({}, Breakpoints.default, [{
      name: ".",
      columns: 1
    }, {
      name: "a",
      columns: 10
    }, {
      name: ".",
      columns: 1
    }, {
      name: "b",
      columns: 11
    }, {
      name: ".",
      columns: 1
    }])
  }, React.createElement(StyledContainer$3, {
    justify: "space-between",
    direction: "column"
  }, React.createElement(StyledImage$2, {
    src: image,
    mxWidth: "145"
  }), React.createElement(StyledButton$4, null, React.createElement(LinkButton, {
    onClick: function onClick() {
      return onEdit(derivativeId);
    }
  }, React.createElement(Text, {
    tag: "p",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.copy100
  }, "Edit Car")))), React.createElement(StyledContainer$3, {
    justify: "flex-end"
  }, React.createElement("div", null, React.createElement(StyledModelName$1, null, React.createElement(Text, {
    tag: "h2",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.headline200
  }, modelName), fuelType && ["Hybrid", "Electric"].includes(fuelType) && React.createElement(Container, {
    horizontalAlign: ContainerHorizontalAlignment.center
  }, React.createElement(StyledIcon$6, {
    bgColor: fuelType === "Hybrid" && palette.blue
  }, Icon$3(fuelType)))), React.createElement(Text, {
    tag: "p",
    color: TextColor.inherit,
    textAlign: TextAlignment.left,
    appearance: TextAppearance.copy100
  }, derivativeName), React.createElement(StyledContainer$3, {
    justify: "space-between"
  }, React.createElement(Text, {
    tag: "p",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.copy150
  }, formatCurrencyWithDecimals(price))), React.createElement(Container, {
    padding: {
      top: ContainerPadding.static150
    }
  }, React.createElement(Text, {
    tag: "p",
    bold: true,
    color: TextColor.inherit,
    textAlign: TextAlignment.left,
    appearance: TextAppearance.copy100
  }, fuelType)))));
};

var Desktop$2 = function Desktop(_ref3) {
  var derivativeId = _ref3.derivativeId,
      modelName = _ref3.modelName,
      image = _ref3.image,
      derivativeName = _ref3.derivativeName,
      fuelType = _ref3.fuelType,
      price = _ref3.price,
      isCarousel = _ref3.isCarousel,
      onEdit = _ref3.onEdit;
  return React.createElement(StyledDesktop, null, React.createElement(StyledCarImg$1, null, React.createElement(Container, {
    horizontalAlign: ContainerHorizontalAlignment.center
  }, React.createElement(StyledImage$2, {
    src: image,
    mxWidth: isCarousel ? 336 : 480
  }))), derivativeId !== "" && React.createElement(Container, {
    stretchContent: true,
    horizontalAlign: ContainerHorizontalAlignment.center,
    wrap: ContainerWrap.always,
    padding: {
      top: isCarousel ? ContainerPadding.static400 : ContainerPadding.static600,
      right: ContainerPadding.static300,
      bottom: isCarousel ? 0 : ContainerPadding.static300,
      left: ContainerPadding.static300
    }
  }, React.createElement(Container, {
    verticalAlign: ContainerVerticalAlignment.center,
    padding: {
      top: ContainerPadding.static200
    }
  }, React.createElement(Text, {
    tag: "h3",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.headline300
  }, modelName), fuelType && ["Hybrid", "Electric"].includes(fuelType) && React.createElement(StyledElectricIcon, {
    bgColor: fuelType === "Hybrid" && palette.blue
  }, Icon$3(fuelType))), React.createElement(Container, {
    stretchContent: true,
    verticalAlign: ContainerVerticalAlignment.center,
    wrap: ContainerWrap.always,
    padding: {
      top: ContainerPadding.static250
    }
  }, React.createElement(Layout, {
    appearance: _defineProperty({}, Breakpoints.default, [{
      name: "a",
      columns: 14
    }, {
      name: ".",
      columns: 1
    }, {
      name: "b",
      columns: 9
    }])
  }, React.createElement(StyledTextWrapper, null, React.createElement(Text, {
    tag: "p",
    color: TextColor.inherit,
    textAlign: TextAlignment.left,
    appearance: TextAppearance.copy250
  }, derivativeName)), React.createElement(Text, {
    tag: "p",
    bold: true,
    color: TextColor.inherit,
    textAlign: TextAlignment.right,
    appearance: TextAppearance.copy250
  }, fuelType))), React.createElement(StyledBottomContainer, null, React.createElement(Container, {
    padding: {
      top: ContainerPadding.static250
    }
  }, React.createElement(Text, {
    tag: "p",
    bold: true,
    color: TextColor.inherit,
    appearance: TextAppearance.copy250
  }, formatCurrencyWithDecimals(price))), React.createElement(Container, {
    padding: {
      top: ContainerPadding.static250,
      bottom: isCarousel ? ContainerPadding.static250 : ContainerPadding.static370
    },
    horizontalAlign: ContainerHorizontalAlignment.spaceBetween
  }, React.createElement(StyledCarCardLink$1, null, React.createElement(Text, {
    tag: TextTag.p,
    appearance: TextAppearance.copy200,
    bold: true
  }, React.createElement(Link, {
    isInline: true,
    onClick: function onClick() {
      return onEdit(derivativeId);
    }
  }, "Edit car")))))));
};
/**
 * Primary Car Card component
 * @param derivativeId
 * @param modelName
 * @param derivativeName
 * @param fuelType
 * @param price
 * @param onEdit
 * @returns {*}
 * @constructor
 */


var PrimaryCarCard = function PrimaryCarCard(_ref5) {
  var rest = _extends({}, _ref5);

  return React.createElement(StyledPrimaryCarCard, {
    border: !rest.isCarousel
  }, React.createElement(Container, {
    stretchContent: true,
    padding: {
      top: ContainerPadding.static200,
      bottom: rest.isCarousel ? 0 : ContainerPadding.static200
    }
  }, React.createElement(BreakpointWrapper, {
    max: Breakpoints.b960
  }, React.createElement(Mobile$2, rest)), React.createElement(BreakpointWrapper, {
    min: Breakpoints.b960
  }, React.createElement(Desktop$2, rest))));
};
/**
 * Default prop values
 */


Mobile$2.defaultProps = {
  derivativeId: "",
  modelName: "",
  image: "",
  derivativeName: "",
  fuelType: "",
  price: 0,
  onEdit: function onEdit() {}
};
/**
 * Prop def
 */

/**
 * Default prop values
 */

Desktop$2.defaultProps = {
  derivativeId: "",
  modelName: "",
  image: "",
  derivativeName: "",
  fuelType: "",
  price: 0,
  isCarousel: false,
  onEdit: function onEdit() {}
};
/**
 * Prop def
 */

/**
 * Default prop values
 */

PrimaryCarCard.defaultProps = {
  derivativeId: "",
  modelName: "",
  image: "",
  derivativeName: "",
  fuelType: "",
  price: 0,
  onEdit: function onEdit() {},
  isCarousel: false
};

function _templateObject3$f() {
  var data = _taggedTemplateLiteral(["\n  padding-left: 36px;\n"]);

  _templateObject3$f = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$l() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  font-size: 14px; /* [1] */\n"]);

  _templateObject2$l = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$H() {
  var data = _taggedTemplateLiteral(["\n  min-height: ", ";\n  min-width: 130px;\n  position: relative;\n\n  [type=\"radio\"],\n  label {\n    border: none;\n    color: ", ";\n    display: inline-block;\n    padding: 0;\n    width: auto;\n  }\n\n  [type=\"radio\"] {\n    height: 1px;\n    opacity: 0;\n    width: 1px;\n  }\n\n  /**\n   * 1. This font-size is not responsive\n   */\n  label {\n    ", "\n    display: block;\n    font-size: 16px; /* [1] */\n    min-height: ", ";\n    padding-left: 36px; /* MAGIC NUMBER: 24px width PLUS 's100' (12px) = 36px */\n    padding-top: 2px;\n    position: relative;\n  }\n\n  label::before,\n  label::after {\n    border-radius: 50%;\n    content: \"\";\n    position: absolute;\n  }\n\n  label::before {\n    background: transparent;\n    border: 2px solid;\n    height: ", ";\n    left: 0;\n    top: ", ";\n    width: ", ";\n  }\n\n  label::after {\n    display: block;\n    height: ", ";\n    left: 6px;\n    top: ", ";\n    width: ", ";\n  }\n\n  /* :checked & :active styles */\n  [type=\"radio\"]:checked + label,\n  [type=\"radio\"]:active + label {\n    color: ", ";\n  }\n\n  [type=\"radio\"]:checked + label::before,\n  [type=\"radio\"]:active + label::before {\n    border-color: currentColor;\n  }\n\n  [type=\"radio\"]:checked + label::after,\n  [type=\"radio\"]:active + label::after {\n    background-color: currentColor;\n  }\n\n  /* :focus styles */\n  [type=\"radio\"]:focus + label {\n    color: ", ";\n  }\n\n  /* disabled styles */\n  [type=\"radio\"][disabled] + label {\n    color: ", ";\n    cursor: not-allowed;\n  }\n\n  [type=\"radio\"][disabled]:active + label::after {\n    background-color: transparent;\n  }\n\n  [type=\"radio\"][disabled]:active:checked + label::after {\n    background-color: ", ";\n  }\n\n  /* secondary theme */\n  &.secondary {\n    [type=\"radio\"],\n    label {\n      color: ", ";\n    }\n\n    /* :checked & :active styles */\n    [type=\"radio\"]:checked + label,\n    [type=\"radio\"]:active + label {\n      color: ", ";\n    }\n\n    /* :focus styles */\n    [type=\"radio\"]:focus + label {\n      color: ", ";\n    }\n\n    /* disabled styles */\n    [type=\"radio\"][disabled] + label {\n      color: ", ";\n    }\n\n    [type=\"radio\"][disabled]:active + label::after {\n      background-color: transparent;\n    }\n\n    [type=\"radio\"][disabled]:active:checked + label::after {\n      background-color: ", ";\n    }\n  }\n"]);

  _templateObject$H = function _templateObject() {
    return data;
  };

  return data;
}
/**
 * Hide the input (but not fully - just a litte so it is still a11y)
 * and make the label fully clickable.
 * - Use generated content on ::after and ::before to create the ring and the dot
 * of the radio button.
 * - Use pseudo classes like :checked to style a checked button.
 * - To work it needs the following HTML structure:
 * `<input type="radio" id="x" /><label htmlFor="x">Label</label>`
 */


var widthHeight = "24px";
var widthHeightDot = "12px";
var StyledRadioButton$1 = styled.div(_templateObject$H(), widthHeight, palette.d6.blue800, D6Typography("body/regular150"), widthHeight, widthHeight, function (props) {
  return props.hint ? "10px" : "1px";
}, widthHeight, widthHeightDot, function (props) {
  return props.hint ? "16px" : "7px";
}, widthHeightDot, palette.d6.blue900, palette.d6.blue700, palette.d6.grey100, palette.d6.grey100, palette.d6.white000, palette.d6.blue200, palette.d6.grey100, palette.d6.grey400, palette.d6.grey400);
/**
 * 1. This font-size is not responsive
 */

var StyledRadioButtonHint = styled.div(_templateObject2$l(), palette.d6.grey300);
/**
 * 1. This font-size is not responsive
 */

var StyledRadioButtonError = styled(FormError)(_templateObject3$f());

var RadioButton$1 = function RadioButton$$1(_ref) {
  var checked = _ref.checked,
      className = _ref.className,
      error = _ref.error,
      hint = _ref.hint,
      id = _ref.id,
      label = _ref.label,
      name = _ref.name,
      value = _ref.value,
      props = _objectWithoutProperties(_ref, ["checked", "className", "error", "hint", "id", "label", "name", "value"]);

  return React.createElement(StyledRadioButton$1, {
    className: className,
    hint: hint
  }, React.createElement("input", Object.assign({
    defaultChecked: checked,
    type: "radio",
    id: id,
    name: name,
    value: value
  }, props)), React.createElement("label", {
    htmlFor: id
  }, label, hint && React.createElement(StyledRadioButtonHint, null, hint)), React.createElement(StyledRadioButtonError, {
    message: error
  }));
};

RadioButton$1.defaultProps = {
  checked: false,
  className: "",
  error: null,
  hint: ""
};

function _templateObject2$m() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  flex-wrap: wrap;\n"]);

  _templateObject2$m = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$I() {
  var data = _taggedTemplateLiteral(["\n  display: block;\n  font-family: ", ";\n  font-size: 16px; /* [1] */\n  margin: 0;\n  ", "\n  ", "\n\n  legend {\n    ", "\n    color: ", ";\n    display: block;\n    font-size: 12px; /* [1] */\n    line-height: 1.5;\n    max-width: 175px;\n  }\n\n  .a-form-error--radio-group {\n    ", "\n  }\n"]);

  _templateObject$I = function _templateObject() {
    return data;
  };

  return data;
}
/**
 * 1. Fonts are not responsive
 */


var StyledRadioButtonGroup = styled.fieldset(_templateObject$I(), fonts.textRegular, D6Spacing("margin-bottom", "s450"), D6Spacing("margin-top", "s450"), D6Spacing("margin-bottom", "s150"), palette.d6.blue800, D6Spacing("margin-top", "s200"));
var StyledRadioButtonGroupFields = styled.div(_templateObject2$m());
/**
 * MyVW
 * Molecule
 * M-0059: Radio Button Group
 * A list of 1 or more `<RadioButton>`s inside a `<fieldset>` with a `<legend>`
 */

var RadioButtonGroup = function RadioButtonGroup(_ref) {
  var error = _ref.error,
      legend = _ref.legend,
      name = _ref.name,
      onChange = _ref.onChange,
      options = _ref.options,
      required = _ref.required,
      value = _ref.value;
  return React.createElement(StyledRadioButtonGroup, null, React.createElement("legend", null, legend), React.createElement(StyledRadioButtonGroupFields, null, options && options.map(function (option) {
    return React.createElement(RadioButton$1, {
      key: option.id,
      checked: value === option.value,
      id: option.id,
      label: option.label,
      name: name,
      onChange: onChange,
      required: required,
      value: option.value
    });
  })), React.createElement(FormError, {
    className: "a-form-error--radio-group",
    message: error
  }));
};

RadioButtonGroup.defaultProps = {
  error: null,
  required: null,
  value: null
};

function _templateObject5$6() {
  var data = _taggedTemplateLiteral(["\n  ", ";\n  margin-top: 32px;\n"]);

  _templateObject5$6 = function _templateObject5() {
    return data;
  };

  return data;
}

function _templateObject4$a() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  margin-bottom: 10px;\n\n  button {\n    background: transparent;\n    border: none;\n    border-bottom: 2px solid ", ";\n    color: ", ";\n    display: block;\n    font-size: 14px;\n    line-height: 1;\n    min-width: 90px;\n    padding: 12px 22px 12px 0;\n    text-align: left;\n  }\n\n  button + button {\n    padding-left: 22px;\n  }\n\n  button:focus,\n  button:hover {\n    border-bottom-color: currentColor;\n    color: ", ";\n    outline: none;\n  }\n\n  button[aria-selected=\"true\"] {\n    border-bottom-color: currentColor;\n    color: ", ";\n  }\n"]);

  _templateObject4$a = function _templateObject4() {
    return data;
  };

  return data;
}

function _templateObject3$g() {
  var data = _taggedTemplateLiteral(["\n  min-height: 300px;\n  position: relative;\n"]);

  _templateObject3$g = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$n() {
  var data = _taggedTemplateLiteral(["\n  ", "\n  background: ", ";\n  border: 1px solid ", ";\n  color: inherit;\n  display: inline-block;\n  padding: 5px;\n  position: absolute;\n  right: 10px;\n  text-decoration: none;\n  top: 10px;\n  z-index: 1;\n\n  &:hover {\n    border-color: ", ";\n  }\n\n  &:focus {\n    border-color: ", ";\n  }\n"]);

  _templateObject2$n = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$J() {
  var data = _taggedTemplateLiteral(["\n  [role=\"tabpanel\"] {\n    background: ", ";\n    position: relative;\n  }\n\n  .gm-style > div {\n    cursor: default !important;\n  }\n"]);

  _templateObject$J = function _templateObject() {
    return data;
  };

  return data;
}

var StyledRetailerResults = styled.div(_templateObject$J(), palette.d6.white000);
var StyledRetailerResultsButton = styled.a(_templateObject2$n(), D6Typography("body/regular100"), palette.d6.white000, palette.d6.grey100, palette.d6.grey200, palette.d6.grey300);
var StyledResponsiveImage = styled.div(_templateObject3$g());
var StyledRetailerResultsTabs = styled.div(_templateObject4$a(), palette.d6.grey100, palette.d6.blue700, palette.d6.blue800, palette.d6.blue900);
var StyledRetailerResultsError = styled.div(_templateObject5$6(), D6Typography("body/regular150"));

var RetailerResults =
/*#__PURE__*/
function (_Component) {
  _inherits(RetailerResults, _Component);

  function RetailerResults(props) {
    var _this;

    _classCallCheck(this, RetailerResults);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(RetailerResults).call(this, props));

    _this.buildMap = function (latitude, longitude) {
      var _this$props = _this.props,
          config = _this$props.config,
          mapWidth = _this$props.mapWidth,
          mapHeight = _this$props.mapHeight;
      var googleMapsProps = {
        coords: {
          lat: latitude,
          lng: longitude
        },
        dimensions: {
          width: mapWidth,
          height: mapHeight
        },
        config: config,
        options: {
          disableDefaultUI: true,
          draggable: false,
          scrollwheel: false,
          panControl: false,
          maxZoom: 16,
          minZoom: 16,
          zoom: 16
        },
        theme: "grayscale"
      };

      _this.setState({
        hideMap: true
      });

      _this.setState({
        googleMapsProps: googleMapsProps
      }, function () {
        _this.setState({
          hideMap: false
        });
      });
    };

    _this.selectRetailer = function (value) {
      var retailers = _this.props.retailers;
      var selectedRetailer = retailers.filter(function (retailer) {
        return retailer.id.toString() === value.toString();
      });

      if (selectedRetailer && selectedRetailer[0]) {
        _this.setState({
          selectedRetailer: selectedRetailer[0]
        }, function () {
          _this.buildMap(selectedRetailer[0].latitude, selectedRetailer[0].longitude);
        });
      }
    };

    _this.toggleTabs = function (event) {
      var buttons = _this.containerRef.querySelectorAll("[role='tab']");

      var button = event.currentTarget;

      var tabPanels = _this.containerRef.querySelectorAll("[role='tabpanel']");

      var isAlreadySelected = button.getAttribute("aria-selected").toString() === "true";

      if (isAlreadySelected) {
        return;
      }

      for (var i = 0; i < buttons.length; i += 1) {
        buttons[i].setAttribute("aria-selected", "false");
      }

      button.setAttribute("aria-selected", "true");

      for (var _i = 0; _i < tabPanels.length; _i += 1) {
        if (tabPanels[_i].id === button.getAttribute("aria-controls")) {
          tabPanels[_i].removeAttribute("hidden");
        } else {
          tabPanels[_i].setAttribute("hidden", "");
        }
      }
    };

    _this.onKeyDownHandler = function (event) {
      var keys = {
        LEFT: 37,
        RIGHT: 39
      };

      var list = _this.containerRef.querySelector("[role='tablist']");

      var tabs;
      var tab;
      var tabPrevious;
      var tabNext;
      var tabNew;

      if (!event.keyCode || !list) {
        return;
      }

      if (list) {
        tabs = list.querySelectorAll("[role='tab']");
        tab = list.querySelector("[aria-selected='true']");
        tabPrevious = tab.previousElementSibling;
        tabNext = tab.nextElementSibling;
      } // Stop page from physically moving as we go left/right


      if (event.keyCode === keys.LEFT || event.keyCode === keys.RIGHT) {
        event.preventDefault();
      } // Work out which is the next/previosu tab then activate it


      if (event.keyCode === keys.LEFT) {
        if (tabPrevious) {
          tabNew = tabPrevious;
        } else {
          tabNew = tabs[tabs.length - 1];
        }
      } else if (event.keyCode === keys.RIGHT) {
        if (tabNext) {
          tabNew = tabNext;
        } else {
          tabNew = tabs[0]; // eslint-disable-line prefer-destructuring
        }
      }

      if (tabNew) {
        tabNew.click();
        tabNew.focus();
      }
    };

    _this.isChecked = function (id) {
      var value = _this.props.value;
      var selectedRetailer = _this.state.selectedRetailer; // handle state locally if a value has not been passed in by parent/Redux

      if (!value && selectedRetailer) {
        return selectedRetailer.id.toString() === id.toString();
      }

      return value.toString() === id.toString();
    };

    _this.onChangeHandler = function (event, id) {
      var onChange = _this.props.onChange;

      _this.selectRetailer(id);

      return onChange(event);
    };

    _this.state = {
      hideMap: true,
      selectedRetailer: false,
      googleMapsProps: null,
      mapViewId: "id-map-view",
      listViewId: "id-list-view"
    };
    return _this;
  }

  _createClass(RetailerResults, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      var _this$props2 = this.props,
          tabs = _this$props2.tabs,
          value = _this$props2.value;

      if (tabs && value) {
        this.selectRetailer(value);
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props3 = this.props,
          hideAddresses = _this$props3.hideAddresses,
          name = _this$props3.name,
          required = _this$props3.required,
          retailers = _this$props3.retailers,
          tabs = _this$props3.tabs,
          purpose = _this$props3.purpose;
      var _this$state = this.state,
          googleMapsProps = _this$state.googleMapsProps,
          hideMap = _this$state.hideMap,
          selectedRetailer = _this$state.selectedRetailer,
          mapViewId = _this$state.mapViewId,
          listViewId = _this$state.listViewId;
      return React.createElement(StyledRetailerResults, {
        innerRef: function innerRef(ref) {
          _this2.containerRef = ref;
        },
        onKeyDown: this.onKeyDownHandler
      }, tabs && React.createElement(StyledRetailerResultsTabs, {
        "aria-orientation": "horizontal",
        role: "tablist"
      }, React.createElement("button", {
        "aria-selected": "true",
        "aria-controls": listViewId,
        id: "tab-".concat(listViewId),
        onClick: this.toggleTabs,
        role: "tab",
        type: "button"
      }, "List"), React.createElement("button", {
        "aria-selected": "false",
        "aria-controls": mapViewId,
        id: "tab-".concat(mapViewId),
        onClick: this.toggleTabs,
        role: "tab",
        type: "button"
      }, "Map")), retailers && React.createElement(React.Fragment, null, React.createElement("div", {
        "aria-labelledby": "tab-".concat(listViewId),
        id: listViewId,
        role: "tabpanel"
      }, retailers.map(function (retailer) {
        return React.createElement(RadioButton$1, {
          key: retailer.id,
          checked: _this2.isChecked(retailer.id),
          hint: !hideAddresses && retailer.location,
          id: purpose ? "retailer-".concat(retailer.id, "-").concat(purpose) : "retailer-".concat(retailer.id),
          label: retailer.name,
          name: name,
          onChange: function onChange(event) {
            return _this2.onChangeHandler(event, retailer.id);
          },
          required: required,
          value: retailer.id
        });
      })), tabs && React.createElement("div", {
        "aria-labelledby": "tab-".concat(mapViewId),
        hidden: true,
        id: mapViewId,
        role: "tabpanel"
      }, selectedRetailer && React.createElement(React.Fragment, null, googleMapsProps && !hideMap && React.createElement(StyledResponsiveImage, null, React.createElement(StyledRetailerResultsButton, {
        href: "https://maps.google.co.uk/?q=\n                            ".concat(selectedRetailer.latitude, ",\n                            ").concat(selectedRetailer.longitude),
        target: "_blank",
        rel: "noopener noreferrer",
        title: "View Google map for \n                            ".concat(selectedRetailer.location, " (opens in new window)")
      }, "View map"), React.createElement(GoogleMaps, googleMapsProps)), React.createElement(RadioButton$1, {
        checked: true,
        hint: selectedRetailer.location,
        id: "selected-retailer-".concat(selectedRetailer.id),
        label: selectedRetailer.name,
        name: "selected-".concat(name),
        required: required,
        value: selectedRetailer.id
      })), !selectedRetailer && React.createElement(StyledRetailerResultsError, null, "Choose a retailer from the list to display it on the map"))));
    }
  }]);

  return RetailerResults;
}(Component);

RetailerResults.defaultProps = {
  hideAddresses: false,
  mapHeight: "300px",
  mapWidth: "100%",
  purpose: "",
  retailers: [],
  required: false,
  tabs: false,
  value: ""
};

function _templateObject$K() {
  var data = _taggedTemplateLiteral(["\n  background-color: transparent;\n  border: 1px solid transparent;\n  border-bottom-color: ", ";\n  border-radius: 0;\n  box-shadow: none;\n  color: ", ";\n  font-family: ", ";\n  font-size: 16px; /* [1] */\n  height: 30px;\n  margin: 0;\n  ", "\n  outline: 0; /* [2] */\n  padding: 0;\n  transition: border 0.3s;\n  width: 100%;\n\n  &:hover {\n    border-bottom-color: ", ";\n  }\n\n  &:focus {\n    border-bottom-color: ", ";\n  }\n\n  &[disabled] {\n    border-bottom-color: currentColor;\n    color: ", ";\n  }\n"]);

  _templateObject$K = function _templateObject() {
    return data;
  };

  return data;
}
/**
 * 1. 16px needed for iOS to prevent zooming in onFocus
 * 2. Remember to use an alternative to convey focus
 */


var StyledTextInput = styled.input(_templateObject$K(), palette.d6.grey300, palette.d6.blue800, fonts.textRegular, D6Spacing("margin-bottom", "s150"), palette.d6.grey200, palette.d6.blue900, palette.d6.grey100);
var textInputClasses = {
  active: "active"
};
/**
 * Remove the active class if there is no value when the user
 * exits the field
 * @see FormLabel
 *
 * @param {object} event - onBlur Event
 */

var onBlurHandler$1 = function onBlurHandler(event) {
  var input = event.currentTarget;
  var field = input.parentNode;
  var placeholder = input.placeholder;
  var label = field.querySelector("label");

  if (!input.value && !placeholder) {
    label.classList.remove(textInputClasses.active);
  }
};
/**
 * Adds the active class when the user focusses on the input
 * @see FormLabel
 *
 * @param {object} event - onFocus Event
 */


var onFocusHandler$1 = function onFocusHandler(event) {
  var input = event.currentTarget;
  var field = input.parentNode;
  var label = field.querySelector("label");
  label.classList.add(textInputClasses.active);
};

var TextInput = function TextInput(_ref) {
  var disabled = _ref.disabled,
      error = _ref.error,
      hint = _ref.hint,
      id = _ref.id,
      label = _ref.label,
      placeholder = _ref.placeholder,
      required = _ref.required,
      type = _ref.type,
      value = _ref.value,
      props = _objectWithoutProperties(_ref, ["disabled", "error", "hint", "id", "label", "placeholder", "required", "type", "value"]);

  return React.createElement(FormField, null, React.createElement(FormLabel, {
    className: "".concat(disabled ? "disabled " : "").concat(value || placeholder ? textInputClasses.active : ""),
    id: id,
    label: "".concat(required ? "".concat(label, ": *") : "".concat(label, ":"))
  }), React.createElement(StyledTextInput, Object.assign({
    disabled: disabled,
    id: id,
    onBlur: onBlurHandler$1,
    onFocus: onFocusHandler$1,
    placeholder: placeholder,
    required: required,
    type: type,
    value: value
  }, props)), React.createElement(FormHint, {
    message: hint
  }), React.createElement(FormError, {
    message: error
  }));
};

TextInput.defaultProps = {
  disabled: false,
  error: "",
  icon: "",
  hint: "",
  placeholder: "",
  required: false,
  type: "text",
  value: ""
};

function _templateObject3$h() {
  var data = _taggedTemplateLiteral(["\n  background: ", ";\n  border: 1px solid ", ";\n  display: block;\n  left: 0;\n  position: absolute;\n  top: 41px; /* height of the input */\n  width: 100%;\n  z-index: 1;\n\n  ul,\n  li {\n    margin: 0;\n    padding: 0;\n  }\n\n  button {\n    ", "\n    background: transparent;\n    border: none;\n    color: ", ";\n    display: block;\n    text-align: left;\n    transition-duration: 0.2s; /* [1] */\n    transition-timing-function: ease-out; /* [1] */\n    transition-property: background, color; /* [1] */\n    width: 100%;\n\n    &:hover {\n      background-color: ", ";\n      color: ", ";\n    }\n\n    &:focus {\n      background-color: ", ";\n      color: ", ";\n    }\n  }\n\n  button + button {\n    border-top: 1px solid ", ";\n  }\n\n  .ngw-icon {\n    margin-right: 5px;\n    position: relative;\n    top: -1px;\n    vertical-align: middle;\n  }\n\n  svg {\n    fill: currentColor;\n  }\n"]);

  _templateObject3$h = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$o() {
  var data = _taggedTemplateLiteral(["\n  ", "\n  display: flex;\n  position: relative;\n\n  > div {\n    flex: 1;\n    position: relative;\n  }\n\n  .m-form-field {\n\n  }\n\n  .a-cta {\n    ", "\n    ", "\n    ", "\n    width: auto;\n  }\n"]);

  _templateObject2$o = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$L() {
  var data = _taggedTemplateLiteral([""]);

  _templateObject$L = function _templateObject() {
    return data;
  };

  return data;
}

var StyledRetailerSearchBar = styled.div(_templateObject$L()); // [input] [button] search combo field

var StyledSearchButton = styled.div(_templateObject2$o(), D6Spacing("margin-top", "d250"), D6Spacing("margin-left", "s300"), D6Spacing("padding-left", "s400"), D6Spacing("padding-right", "s400"));
/**
 * 1. Avoid transition: all for perf reasons
 */

var StyledSearchDropdown = styled.div(_templateObject3$h(), palette.d6.white000, palette.d6.grey100, D6Spacing("padding", "s200"), palette.d6.black000, palette.d6.blue900, palette.d6.white000, palette.d6.blue700, palette.d6.white000, palette.d6.grey100);

var RetailerSearchBar =
/*#__PURE__*/
function (_Component) {
  _inherits(RetailerSearchBar, _Component);

  function RetailerSearchBar(props) {
    var _this;

    _classCallCheck(this, RetailerSearchBar);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(RetailerSearchBar).call(this, props));

    _this.getField = function () {
      var input = _this.props.input;

      var field = _this.containerRef.querySelector("#".concat(input.id));

      return field;
    };

    _this.isSearchTermValid = function () {
      var validSearchTerm = false;

      var field = _this.getField();

      if (field && field.checkValidity() === true) {
        validSearchTerm = true;
      }

      _this.setState({
        validSearchTerm: validSearchTerm
      });
    };

    _this.hideDropdown = function () {
      _this.setState({
        showDropdown: false
      });
    };

    _this.showDropdown = function () {
      var field = _this.getField();

      _this.filterRetailers(field.value);

      _this.setState({
        showDropdown: true
      });
    };

    _this.copyAndSearchRetailer = function (searchTerm, currentLocation, retailerId) {
      _this.setState({
        searchTerm: searchTerm
      }, function () {
        _this.isSearchTermValid();
      });

      _this.hideDropdown(); // if the user has selected to use their location AND
      // allowed their location to be used then make sure we send that
      // value to our searchRetailer function
      // but if a retailerId has been passed in, use that instead


      var _this$props = _this.props,
          searchRetailer = _this$props.searchRetailer,
          geolocationErrorCallback = _this$props.geolocationErrorCallback;

      if (currentLocation) {
        _this.setState({
          checkingGeo: true
        });

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(function (_ref) {
            var _ref$coords = _ref.coords,
                latitude = _ref$coords.latitude,
                longitude = _ref$coords.longitude;

            _this.setState({
              checkingGeo: false
            });

            searchRetailer("".concat(latitude, ",").concat(longitude));
          }, geolocationErrorCallback);
        }
      } else if (retailerId) {
        searchRetailer(retailerId);
      } else {
        searchRetailer(searchTerm);
      }
    };

    _this.onKeyDownHandler = function (event) {
      var keys = {
        UP: 38,
        DOWN: 40,
        ESC: 27
      };

      var list = _this.containerRef.querySelector("ul");

      var buttons;
      var isButton = false;

      if (list) {
        buttons = list.querySelectorAll("button");
        isButton = event.target.nodeName.toUpperCase() === "BUTTON";
      }

      if (!event.keyCode || !buttons) {
        return;
      } // Stop page from physically moving as we go up/down


      if (event.keyCode === keys.UP || event.keyCode === keys.DOWN) {
        event.preventDefault();
      }

      if (event.keyCode === keys.UP) {
        if (isButton && event.target !== buttons[0]) {
          event.target.parentNode.previousElementSibling.querySelector("button").focus();
        }
      } else if (event.keyCode === keys.DOWN) {
        if (!isButton) {
          buttons[0].focus();
        } else if (event.target !== buttons[buttons.length - 1]) {
          event.target.parentNode.nextElementSibling.querySelector("button").focus();
        }
      } else if (event.keyCode === keys.ESC) {
        var field = _this.getField();

        field.focus();
      }
    };

    _this.onBackgroundClick = function (event) {
      var field = _this.getField();

      var isInput = event.target.id === field.id;
      var isButton = event.target.nodeName.toUpperCase() === "BUTTON";

      if (!isInput && !isButton) {
        _this.hideDropdown();
      }
    };

    _this.onEscKeyDown = function (event) {
      var ESC = 27;

      if (event.keyCode === ESC) {
        _this.hideDropdown();
      }
    };

    _this.filterRetailers = function (value) {
      var retailers = _this.props.retailers;
      retailers = retailers.filter(function (retailer) {
        return retailer.name.toLowerCase().indexOf(value.toLowerCase()) > -1;
      });

      _this.setState({
        retailers: retailers
      });
    };

    _this.onChangeHandler = function (event) {
      _this.setState({
        searchTerm: event.target.value
      });

      _this.isSearchTermValid();

      _this.showDropdown();
    };

    var _this$props2 = _this.props,
        defaultSearchTerm = _this$props2.defaultSearchTerm,
        _retailers = _this$props2.retailers;
    _this.state = {
      checkingGeo: false,
      searchTerm: defaultSearchTerm,
      showDropdown: false,
      retailers: _retailers,
      validSearchTerm: false
    };
    return _this;
  }

  _createClass(RetailerSearchBar, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.isSearchTermValid();
      document.addEventListener("keydown", this.onEscKeyDown);
      document.addEventListener("click", this.onBackgroundClick);
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      document.removeEventListener("keydown", this.onEscKeyDown);
      document.removeEventListener("click", this.onBackgroundClick);
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props3 = this.props,
          currentLocationCopy = _this$props3.currentLocationCopy,
          input = _this$props3.input,
          searchRetailer = _this$props3.searchRetailer,
          submitButtonCopy = _this$props3.submitButtonCopy;
      var _this$state = this.state,
          checkingGeo = _this$state.checkingGeo,
          retailers = _this$state.retailers,
          searchTerm = _this$state.searchTerm,
          showDropdown = _this$state.showDropdown,
          validSearchTerm = _this$state.validSearchTerm;
      return React.createElement(StyledRetailerSearchBar, {
        innerRef: function innerRef(ref) {
          _this2.containerRef = ref;
        }
      }, React.createElement(StyledSearchButton, null, React.createElement("div", null, React.createElement(TextInput, Object.assign({}, input, {
        autoComplete: "off",
        onClick: this.showDropdown,
        onChange: this.onChangeHandler,
        onKeyDown: this.onKeyDownHandler,
        value: searchTerm
      })), showDropdown && React.createElement(StyledSearchDropdown, null, React.createElement("ul", null, React.createElement("li", null, React.createElement("button", {
        type: "button",
        onClick: function onClick() {
          return _this2.copyAndSearchRetailer(currentLocationCopy, true);
        },
        onKeyDown: this.onKeyDownHandler
      }, React.createElement(Icon, {
        fill: "currentColor",
        icon: "locate",
        sizePX: "16px"
      }), " ", currentLocationCopy)), searchTerm && React.createElement(React.Fragment, null, searchTerm !== currentLocationCopy && React.createElement("li", null, React.createElement("button", {
        type: "button",
        onClick: function onClick() {
          return _this2.copyAndSearchRetailer(searchTerm, false);
        },
        onKeyDown: this.onKeyDownHandler
      }, React.createElement(Icon, {
        fill: "currentColor",
        icon: "pin",
        sizePX: "16px"
      }), " ", searchTerm)), retailers && searchTerm.length > 1 && retailers.map(function (retailer) {
        return React.createElement("li", {
          key: retailer.id
        }, React.createElement("button", {
          key: retailer.id,
          type: "button",
          onClick: function onClick() {
            return _this2.copyAndSearchRetailer(retailer.name, false, retailer.id);
          },
          onKeyDown: _this2.onKeyDownHandler
        }, React.createElement(Icon, {
          fill: "currentColor",
          icon: "contact-dealer",
          sizePX: "16px"
        }), " ", retailer.name));
      }))))), React.createElement(CTA, {
        className: "secondary",
        copy: checkingGeo ? "Requesting location..." : submitButtonCopy,
        disabled: !validSearchTerm || checkingGeo,
        onClick: function onClick() {
          return searchRetailer(searchTerm);
        },
        type: "button"
      })));
    }
  }]);

  return RetailerSearchBar;
}(Component);

RetailerSearchBar.defaultProps = {
  defaultSearchTerm: "",
  retailers: [],
  geolocationErrorCallback: function geolocationErrorCallback() {}
};
/**
 * Molecule M-0056 RetailerSearchBar
 */

function _templateObject$M() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  ", "\n\n  > div + div {\n    ", "\n  }\n"]);

  _templateObject$M = function _templateObject() {
    return data;
  };

  return data;
}

var StyledSummary = styled.div(_templateObject$M(), palette.d6.grey400, D6Typography("body/regular200"), D6Spacing("margin-top", "s150"));
/**
 * Summary
 * Called FormSummary M0050 in MyVW project
 */

var Summary = function Summary(_ref) {
  var className = _ref.className,
      options = _ref.options;
  return React.createElement(StyledSummary, {
    className: className
  }, options && options.map(function (option) {
    return React.createElement("div", {
      key: option.id
    }, option.label && React.createElement("strong", null, option.label, ":"), " ", option.value);
  }));
};

Summary.defaultProps = {
  className: "",
  options: []
};

function _templateObject$N() {
  var data = _taggedTemplateLiteral(["\n  ", ";\n  box-sizing: border-box;\n  color: ", ";\n  display: inline-block;\n  position: relative;\n  transition: color 0.3s;\n\n  input,\n  label {\n    border: none;\n    display: inline-block;\n    padding: 0;\n    width: auto;\n  }\n\n  input {\n    height: 1px;\n    opacity: 0;\n    width: 1px;\n  }\n\n  /**\n   * 1. Fonts are not resposnive so we don't need the typography mixin\n   * 2. vertically center the button copy\n   * 3. Avoid transition: all for perf reasons\n   */\n  label {\n    background-color: transparent;\n    border: 2px solid;\n    border-radius: 22px;\n    cursor: pointer;\n    display: block;\n    font-family: ", ";\n    font-size: 14px; /* [1] */\n    height: 44px;\n    line-height: 44px;  /* [2] */\n    min-width: 160px;\n    ", ";\n    ", ";\n    position: relative;\n    text-align: center;\n    transition-duration: 0.3s; /* [3] */\n    transition-property: background-color, border-color, color; /* [3] */\n  }\n\n  /* :checked styles */\n  input:checked + label {\n    background-color: ", ";\n    border-color: transparent;\n    color: ", ";\n  }\n\n  /* :active styles */\n  input:active + label {\n    background-color: ", ";\n    border-color: transparent;\n    color: ", ";\n  }\n\n  /* :hover & :focus styles */\n  label:hover,\n  input:focus + label {\n    background-color: transparent;\n    border-color: currentColor;\n    color: ", ";\n  }\n\n  /* :hover & :focus styles on :checked buttons */\n  &:hover input:checked + label,\n  input:checked:focus + label {\n    background-color: ", ";\n    border-color: transparent;\n    color: ", ";\n  }\n\n  /* disabled styles */\n  input[disabled] + label {\n    background-color: transparent;\n    border-color: currentColor;\n    color: ", ";\n    cursor: not-allowed;\n  }\n\n  /* disabled styles on :checked buttons */\n  input[disabled]:checked + label,\n  input[disabled]:checked:active + label {\n    background-color: ", ";\n    border-color: transparent;\n    color: ", ";\n  }\n"]);

  _templateObject$N = function _templateObject() {
    return data;
  };

  return data;
}
/**
 * Hide the input (but not fully - just a litte so it is still a11y)
 * and make the label fully clickable.
 * - Use pseudo classe slike :checked to style a checked button.
 * - To work it needs the following HTML structure:
 * <input type="radio" id="x" /><label htmlFor="x">Label</label>`
 */


var StyledRadioButtonContainer = styled.div(_templateObject$N(), D6Spacing("margin-right", "s350"), palette.d6.blue800, fonts.textRegular, D6Spacing("padding-left", "s400"), D6Spacing("padding-right", "s400"), palette.d6.blue500, palette.d6.white000, palette.d6.blue900, palette.d6.white000, palette.d6.blue700, palette.d6.blue700, palette.d6.white000, palette.d6.grey100, palette.d6.grey100, palette.d6.white000);

var StyledRadioButton$2 = function StyledRadioButton(_ref) {
  var id = _ref.id,
      name = _ref.name,
      label = _ref.label,
      value = _ref.value,
      props = _objectWithoutProperties(_ref, ["id", "name", "label", "value"]);

  return React.createElement(StyledRadioButtonContainer, null, React.createElement("input", Object.assign({
    type: "radio",
    id: id,
    name: name,
    value: value
  }, props)), React.createElement("label", {
    htmlFor: id
  }, label));
};

function _templateObject3$i() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  text-align: left;\n  padding: 8px 16px;\n  margin: 0;\n  ", "\n  &:first-child {\n    ", "\n  }\n"]);

  _templateObject3$i = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2$p() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  text-align: left;\n  padding: 8px 16px;\n  ", "\n  margin: 0;\n"]);

  _templateObject2$p = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$O() {
  var data = _taggedTemplateLiteral(["\n  tbody &:nth-child(odd) {\n    background-color: transparent;\n  }\n  tbody &:nth-child(even) {\n    background-color: ", ";\n  }\n"]);

  _templateObject$O = function _templateObject() {
    return data;
  };

  return data;
}

var StyledTr = styled.tr(_templateObject$O(), function (props) {
  return props.stripes ? palette.d6.grey100 : "transparent";
});
var StyledTh = styled.th(_templateObject2$p(), palette.d6.grey500, D6Typography("body/bold150"));
var StyledTd = styled.td(_templateObject3$i(), palette.d6.grey500, D6Typography("body/regular150"), D6Typography("body/bold150"));
/**
 * Atom TableRow
 */

var TableRow = function TableRow(_ref) {
  var children = _ref.children,
      stripes = _ref.stripes;
  return React.createElement(StyledTr, {
    stripes: stripes
  }, children);
};

TableRow.defaultProps = {
  stripes: true
};
/**
 * Atom TableRow
 */

function _templateObject2$q() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  ", "\n  text-align: center;\n  ", "\n  ", "\n  border-top: ", " 1px solid;\n\n  & .ngw-icon {\n    display: block;\n    margin: 0 auto;\n    cursor: pointer;\n  }\n"]);

  _templateObject2$q = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$P() {
  var data = _taggedTemplateLiteral(["\n  padding: 0;\n  margin: 0;\n  border: 0;\n"]);

  _templateObject$P = function _templateObject() {
    return data;
  };

  return data;
}

var StyledTable$1 = styled.table(_templateObject$P());
var StyledViewAllRows = styled.div(_templateObject2$q(), palette.d6.blue800, D6Typography("body/regular150"), D6Spacing("padding-top", "d100"), D6Spacing("margin-top", "s500"), palette.d6.grey200);

var Table =
/*#__PURE__*/
function (_Component) {
  _inherits(Table, _Component);

  function Table() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, Table);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(Table)).call.apply(_getPrototypeOf2, [this].concat(args)));
    _this.state = {
      isShowingAllRows: false
    };

    _this.showAllRows = function () {
      return _this.setState({
        isShowingAllRows: true
      });
    };

    return _this;
  }

  _createClass(Table, [{
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props = this.props,
          data = _this$props.data,
          thead = _this$props.thead,
          stripes = _this$props.stripes,
          className = _this$props.className,
          limitRows = _this$props.limitRows,
          limitRowsLabel = _this$props.limitRowsLabel;
      var isShowingAllRows = this.state.isShowingAllRows;
      var limitedRows = limitRows > 0 && !isShowingAllRows ? Object.keys(data).slice(0, limitRows) : Object.keys(data);
      return React.createElement(React.Fragment, null, React.createElement(StyledTable$1, {
        className: className
      }, thead && React.createElement("thead", null, React.createElement(TableRow, null, thead.map(function (column) {
        return React.createElement(TableHead, {
          key: column.id,
          label: column.label
        });
      }))), React.createElement("tbody", null, limitedRows.map(function (row) {
        return React.createElement(TableRow, {
          key: row,
          stripes: stripes
        }, Object.keys(data).map(function (column) {
          return React.createElement(TableColumn, Object.assign({
            key: row + column,
            value: data[column]
          }, _this2.props));
        }));
      }))), limitRows > 0 && !isShowingAllRows && React.createElement(StyledViewAllRows, {
        onClick: function onClick() {
          return _this2.showAllRows();
        }
      }, limitRowsLabel, React.createElement(Icon, {
        icon: "add"
      })));
    }
  }]);

  return Table;
}(Component);

Table.defaultProps = {
  stripes: true,
  thead: false,
  className: "",
  limitRows: 0,
  limitRowsLabel: ""
};

function _templateObject$Q() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  width: 100%;\n  border-bottom: 1px solid ", ";\n"]);

  _templateObject$Q = function _templateObject() {
    return data;
  };

  return data;
}

var StyledTabs = styled.div(_templateObject$Q(), palette.d6.grey100);

var Tabs =
/*#__PURE__*/
function (_React$Component) {
  _inherits(Tabs, _React$Component);

  function Tabs(props) {
    var _this;

    _classCallCheck(this, Tabs);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Tabs).call(this, props));

    _this.selectTab = function (label, onChange) {
      return _this.isControlled ? onChange(label) : _this.setState({
        localActiveTab: label
      });
    };

    _this.isActive = function (label, controlledActive, internalActive) {
      return _this.isControlled ? label === controlledActive : label === internalActive;
    };

    var _this$props = _this.props,
        activeTab = _this$props.activeTab,
        _onChange = _this$props.onChange;
    _this.state = {
      localActiveTab: activeTab
    };
    _this.isControlled = _onChange && typeof _onChange === "function";
    return _this;
  }

  _createClass(Tabs, [{
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props2 = this.props,
          activeTab = _this$props2.activeTab,
          tabs = _this$props2.tabs,
          onChange = _this$props2.onChange;
      var localActiveTab = this.state.localActiveTab;
      return React.createElement(StyledTabs, null, tabs.map(function (tab) {
        return React.createElement(Tab, {
          key: tab.id,
          id: tab.id,
          label: tab.label,
          active: _this2.isActive(tab.label, activeTab, localActiveTab),
          onSelect: function onSelect(_ref) {
            var label = _ref.label;
            return _this2.selectTab(label, onChange);
          }
        });
      }));
    }
  }]);

  return Tabs;
}(React.Component);
/**
 * Default argument values
 */


Tabs.defaultProps = {
  onChange: null
};

function _templateObject$R() {
  var data = _taggedTemplateLiteral(["\n  h1, h2 {\n    ", ";\n    ", "\n  }\n\n  p {\n    ", ";\n    ", "\n    color: ", ";\n  }\n\n  .description {\n    ", "\n    background-color: ", ";\n    max-height: 50vh;\n    overflow-y: auto;\n  }\n"]);

  _templateObject$R = function _templateObject() {
    return data;
  };

  return data;
}

var StyledModal$1 = styled.section(_templateObject$R(), D6Typography("header/light300"), D6Spacing("margin-bottom", "s250"), D6Typography("body/regular150"), D6Spacing("margin-bottom", "s250"), palette.d6.grey400, D6Spacing("padding", "d100"), palette.d6.white000);
var defaultOpts$1 = {
  enablejsapi: true
};

var VideoModal =
/*#__PURE__*/
function (_Component) {
  _inherits(VideoModal, _Component);

  function VideoModal() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, VideoModal);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(VideoModal)).call.apply(_getPrototypeOf2, [this].concat(args)));
    _this.state = {
      isOpen: false
    };

    _this.toggleModal = function () {
      _this.setState(function (prevState) {
        return {
          isOpen: !prevState.isOpen
        };
      });
    };

    return _this;
  }

  _createClass(VideoModal, [{
    key: "render",
    value: function render() {
      var _this2 = this;

      var isOpen = this.state.isOpen;
      var _this$props = this.props,
          videoId = _this$props.videoId,
          title = _this$props.title,
          description = _this$props.description,
          opts = _this$props.opts,
          _elementTrigger = _this$props.elementTrigger;

      var modalOpts = _objectSpread2({}, defaultOpts$1, {}, opts);

      return React.createElement(StyledModal$1, null, React.createElement(Modal$1, {
        isOpen: isOpen,
        videoId: videoId,
        handleClick: this.toggleModal,
        onBackgroundClick: this.toggleModal,
        onEscapeKeydown: this.toggleModal,
        elementTrigger: function elementTrigger() {
          return React.createElement("button", {
            type: "button",
            onClick: _this2.toggleModal
          }, _elementTrigger());
        }
      }, React.createElement(ResponsiveYoutube, {
        videoId: videoId,
        opts: modalOpts
      }), title && description && React.createElement("div", {
        className: "description"
      }, React.createElement("h2", null, title), React.createElement(RichText, {
        text: description,
        allowedTags: ["strong", "b", "h1", "h2", "h3", "h4", "h5", "p", "a", "ul", "li"]
      }))));
    }
  }]);

  return VideoModal;
}(Component);

VideoModal.defaultProps = {
  title: "",
  description: "",
  opts: {}
};
/**
 * Organism O-013 VideoModal
 */

function _templateObject2$r() {
  var data = _taggedTemplateLiteral(["\n  .slick-dots {\n    li {\n      margin-left: 20px;\n      overflow: hidden;\n    }\n  }\n  .slick-dots__thumbnail {\n    width: 100px;\n    max-width: 100%;\n  }\n\n  @media (max-width: 768px) {\n    .slick-dots {\n      li {\n        margin-left: 0;\n      }\n    }\n    .slick-dots__thumbnail {\n      width: 70px;\n    }\n    .slick-arrow {\n      bottom: -43px;\n    }\n    .ngw-icon {\n      width: 12px;\n      height: 12px;\n    }\n  }\n"]);

  _templateObject2$r = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$S() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: center;\n"]);

  _templateObject$S = function _templateObject() {
    return data;
  };

  return data;
}

var ThumbnailWrapper = styled.figure(_templateObject$S());
var StyledCarousel = styled(Carousel)(_templateObject2$r());

var LargeCarousel = function LargeCarousel(_ref) {
  var content = _ref.content;
  var settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    lazyload: true,
    customPaging: function customPaging(index) {
      return React.createElement("img", {
        className: "slick-dots__thumbnail",
        src: content[index].url,
        alt: "thumbnail-".concat(content[index].url)
      });
    }
  };
  return React.createElement(StyledCarousel, {
    settings: settings
  }, content.map(function (image, index) {
    return (// eslint-disable-next-line
      React.createElement(ThumbnailWrapper, {
        key: index
      }, React.createElement("img", {
        src: image.url,
        alt: "headerImage_".concat(index)
      }))
    );
  }));
};

function _templateObject2$s() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n  text-align: center;\n  width: 56.94%;\n"]);

  _templateObject2$s = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$T() {
  var data = _taggedTemplateLiteral(["\n  height: 500px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n"]);

  _templateObject$T = function _templateObject() {
    return data;
  };

  return data;
}

var Section = styled(section)(_templateObject$T());
var Heading = styled(h1)(_templateObject2$s(), palette.blue);

var Welcome = function Welcome(_ref) {
  var message = _ref.message;
  return React.createElement(Section, null, React.createElement(Heading, null, message));
}; // layout


export { Col, Container$1 as Container, Row, AnchorLink, BodyCopy, Card, CTA, EditButton, FootNote, FormError, FormHint, FormLabel, Icon, Modal$1 as Modal, Picture, RichText, SectionAnchor, Switch, SliderArrow, TableColumn, TableHead, TableHead as TableRow, VWIDConfirmation, Tab, Accordion, AddCarCard, AddedCarCard, ArticleCard, BorderedTable, CarDetails, Carousel, CollapsibleContent, CollapsibleWrapper, DateInput, DropdownMenu, DropdownSelector, Feature, FormField, FormNotification, Gallery, GoogleMaps, ListGroup, MediaItem, ModelListItem, OrganismIntro, PrimaryCarCard, RadioButton$1 as RadioButton, RadioButtonGroup, RetailerResults, RetailerSearchBar, Summary, StyledRadioButton$2 as StyledRadioButton, Table, Tabs, TextInput, VideoModal, LargeCarousel, Welcome, fonts, breakpoints, palette, D6Spacing as spacings, D6Typography as typography, section, h1, p, generateUuid, formatPhoneNumber, formatTime, timeToDisplay, openingHoursAdapter };